import { conditionLabel, ETATS_DE_CHECKLIST, matchesField, queryColumns, VALEUR_MOI } from './field-query.js';
import type { FieldCondition, QueryField } from './field-query.js';
import { resoudreElementsCadre } from '@taki/data-model/cadre-canvas';
import { FUSEAU_PAR_DEFAUT, libelleDuType } from '@taki/data-model';
import { useInstallationPort } from '../lib/installation.js';
import { useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { typesDePorte, vueDeCollection, vueDeFicheDArrivee } from './doorViews.js';
import type { ReactNode, RefObject } from 'react';
import { CHANGE_EVENT as PREF_CHANGE_EVENT, readPref, writePref, usePref } from '../lib/prefs.js';
import { liaScreenState, publishLiaScreen } from '../lib/liaScreenContext.js';
// Les TEXTES des deux fenetres de confirmation vivent la-bas, en fabriques a
// nombre (le chantier 3_0 les reprend telles quelles pour le lot). On les
// APPELLE, on ne les recopie pas : une phrase ecrite deux fois est une phrase
// qui divergera.
import {
  archiveConfirm,
  enfantsRetraitConfirm,
  removeConfirm,
  templateRemoveConfirm,
} from '../components/ConfirmDialog.js';
// LES MODELES DE FICHES : le PORT, jamais le SDK (le socle est construit avant
// lui). Le moteur lit le port pour savoir si le geste « Enregistrer comme
// modele » est DISPONIBLE ; sans port, l'entree du menu est grisee, pas absente.
// `raisonDesModeles` vit LA-BAS et pas ici : le chassis pose exactement la meme
// question a l'autre bout du geste, et deux copies d'un meme calcul divergent.
//
// LES ENFANTS D'UN MODELE (chantier « modeles-enfants ») viennent du meme
// fichier, et pour la meme raison : toutes ces regles sont PURES, elles se
// trompent en silence, et un test `node --test` les tient sans React. Le moteur
// ne fait que les enchainer : lire les fiches liees a l'enregistrement
// (`enfantsAEnregistrer`), retirer les liens entrants (`sansChampsDeLien`), et
// rendre le tableau `children` que le port recoit EN ENTIER (`enfantModifie`,
// `avecEnfant`, `sansEnfants`, `nouvelEnfant`).
import {
  avecEnfant,
  cheminDEnfant,
  cheminsDesFreres,
  enfantModifie,
  enfantsAEnregistrer,
  lireCheminDEnfant,
  modeleDuBrouillon,
  nouvelEnfant,
  pseudoFicheDEnfant,
  pseudoFicheDeModele,
  raisonDeRetraitDuModele,
  raisonDesModeles,
  sansChampsDeLien,
  sansEnfants,
  useModelesPort,
} from '../lib/templates.js';
import type {
  EnfantAEnregistrer,
  EnfantDeModele,
  ModeleDeFiche,
  ModelesPort,
} from '../lib/templates.js';
// L'ACCES DE L'UTILISATEUR SUR LA PORTE AFFICHEE, meme patron que les modeles :
// un PORT que l'hote remplit, jamais le SDK. LA SECURITE EST DANS LES RULES ; le
// moteur ne fait que GRISER les gestes d'ecriture avec leur raison, pour que
// l'ecran dise la verite avant que la base ne refuse. Les phrases vivent
// LA-BAS (`RAISONS_ACCES`), la decision aussi (`raisonDeLectureSeule`) : le
// shell pose la meme question sur les memes portes, et deux copies divergent.
import { raisonDeLectureSeule, useAccesPort } from '../lib/acces.js';
// LES PLANS RECURRENTS : le meme patron que les modeles (un PORT, jamais le SDK).
import { FAMILLE_DES_PLANS, raisonDesRecurrences, useRecurrencesPort } from '../lib/recurrences.js';
import type { RecurrencesPort } from '../lib/recurrences.js';
// LES ANNUAIRES : le NOM d'une fiche qu'une colonne DESIGNE, charge une fois par
// jeu, par le socle. Le moteur ne fait que deux choses avec eux : declencher
// leur chargement pour la porte affichee, et les descendre a la projection.
// `invaliderAnnuaires` part avec `reload()` : l'invalidation, c'est le
// RECHARGEMENT et rien d'autre (pas de peremption au temps).
import { invaliderAnnuaires, useAnnuaires } from '../lib/annuaires.js';
import { onExternalRecordMutation } from '../lib/externalRecordMutation.js';
// LE PORT DES LIENS, pour la meme raison que celui des modeles : le socle
// decrit la forme du service, l'HOTE la remplit avec le SDK. Sans port, aucun
// annuaire ne se charge et les cellules gardent leurs ids.
// LE REFUS DE SUPPRIMER UNE FICHE LIEE (« a la Odoo », fondateur 09/09/2026) :
// le compte se demande au SERVEUR par le meme port, les deux phrases sont PURES
// et vivent LA-BAS. Ecrites ici, elles auraient existe deux fois : le menu de la
// fiche et le lot posent le meme refus, sur le meme compte.
import {
  colonnesDeVue,
  comptesLies,
  phraseDesLies,
  refusDeSuppression,
  useLiensPort,
} from '../lib/liens.js';
import type { CompteLie, ValeurImposee } from '../lib/liens.js';
// LA NAVIGATION ENTRE APPS : le port du SHELL, pour la fleche d'une reference
// que cet ecran ne montre dans aucune porte (`references-entre-familles`).
import { useNavigationPort } from '../lib/navigation.js';
// LA CREATION EN FICHE : le PORT de l'hote, l'id du brouillon, et les regles
// PURES (la fiche vide, ce qui manque, la phrase du refus). Meme partage que les
// modeles et les liens : `@taki/ui` n'importe jamais `@taki/sdk`, donc ce qui
// traverse la frontiere est une FORME. Ce qui se prouve sans React vit LA-BAS,
// pour la meme raison que `champsApresEcriture` vit ici : un test le tient.
import {
  BROUILLON_ID,
  RAISONS_CREATION,
  avecTitreDuModele,
  champsDuBrouillon,
  ficheVide,
  manquantsDuBrouillon,
  phraseDesManquants,
  resoudreDefauts,
  useCreationPort,
} from '../lib/creation.js';
// LES PERSONNES DE L'ORG : l'annuaire qui nomme un champ `personne` (et la
// trace `createdBy`), et la personne connectee que le jeton de creation
// `socle:personneConnectee` designe. Le port de l'hote, comme les liens.
import { estReferenceDePersonnes, TYPE_ANNUAIRE_PAR_UID, usePersonnesPort } from '../lib/personnes.js';
import type { CreationPort, ValeurDeDepart } from '../lib/creation.js';
// L'INTENTION ADRESSEE A UNE PORTE (« + Ajouter » d'une carte liee) : c'est le
// GABARIT qui la consomme, et la regle qui le dit vit LA-BAS
// (`destinataireDeLIntention`), pas ici.
import {
  useDeclarerAppDeLEcran,
  usePendingAppActionSetter,
  usePendingDoorAction,
  usePendingDoorTarget,
} from '../lib/appAction.js';
import type { RetourDeCreation } from '../lib/appAction.js';
import { DEFAULT_SEARCH_PLACEHOLDER } from '../components/ScreenLayout.js';
import type { CacheDeProjection } from './project.js';
import type {
  ScreenAction,
  ScreenCanvas,
  ScreenConfirm,
  ScreenStat,
} from '../components/ScreenLayout.js';
import type {
  Annuaires,
  BulkEditField,
  Cell,
  Column,
  ColumnReference,
  Cursor,
  EditorKind,
  ReglagesDeColonne,
  FieldMap,
  LienParLeChampLie,
  LinkedDoor,
  ModeleOuvert,
  Option,
  OptionsDeSaut,
  ProjectedRow,
  SaveState,
  SaveTarget,
  ScreenFavorite,
  ScreenQuery,
  SortLevel,
  ViewBinding,
  ViewColumn,
  ViewId,
  ViewLevel,
  ViewOption,
  ViewProps,
  ViewStatus,
  ViewType,
} from './contract.js';
/*
 * Le catalogue des champs modifiables en lot est declare dans le CONTRAT (il
 * ne parle que de roles, de colonnes et d'editeurs, tous definis la-bas), mais
 * il se LIT sur l'etat d'ecran : on le re-exporte ici pour que la fenetre de
 * lot l'importe au meme endroit que le reste de ce qu'elle recoit, sans avoir
 * a deviner lequel des deux fichiers le porte.
 */
export type { BulkEditField } from './contract.js';
/*
 * LE VOCABULAIRE PARTAGE FICHE / LOT, importe et jamais recopie. Le catalogue
 * de champs modifiables se construit ici, mais il decrit des champs que la
 * FICHE sait deja nommer et editer : la clef opaque d'un champ (`fieldKeyOf`)
 * et la deduction du type d'editeur (`editorKindOf`) doivent donc etre LES
 * MEMES des deux cotes, au caractere pres.
 *
 * Les deux ont d'abord ete RECOPIEES ici. C'est le mode de panne exact que
 * l'extraction de `CellEditor.tsx` voulait fermer : une regle ajoutee a
 * `editorKindOf` et la fiche monte le nouvel editeur pendant que la fenetre de
 * lot continue de monter un champ texte, donc le lot ecrit une chaine dans un
 * champ booleen, sur douze fiches d'un coup, sans qu'un seul build rougisse.
 *
 * `normalizeForEditor` VIENT DU MEME ENDROIT ET POUR LA MEME RAISON : c'est la
 * regle qui decide "ces deux valeurs sont la MEME case" (une `Date` et la
 * chaine `YYYY-MM-DD` qu'on vient de taper, un nombre et son texte). La fiche
 * s'en sert pour ne pas ecrire quand rien n'a change ; le mode Modeles s'en
 * sert pour PROUVER qu'une valeur ecrite se relit (`champsApresEcriture`). Deux
 * copies de cette regle, et l'un des deux cotes conclurait "different" la ou
 * l'autre conclut "identique".
 */
import { editorKindOf, fieldKeyOf, normalizeForEditor } from './core/CellEditor.js';
import { clefsDuChoixMultiple, elementsDeListe } from './fields.js';
import { celluleDeListe, lireBooleen } from './champs-types.js';
import { assertBinding, unmetRequirement } from './assertBinding.js';
import { EMPTY_CURSOR, buildCursor, firstOf, freezeIds } from './cursor.js';
import {
  joinFilterIds,
  pickScreenSearch,
  readModesParams,
  screenSearchKey,
  splitFilterIds,
  useScreenUrlOwnership,
  writeArchivesParam,
  writeModelesParam,
} from './screenUrl.js';
import type { OrigineDEtape, OrigineDeFiche, ScreenSearch } from './screenUrl.js';
import {
  applyColumnPatch,
  applyRolePatch,
  cleDeJeu,
  compareCells,
  matchesSearch,
  nommerLignesDeModeles,
  optionsOfBinding,
  projectRecord,
  projectRecordsEnCache,
  idsDesReferences,
  referencesDeBindings,
  resolvesSortKey,
  sameCell,
  sortValueOf,
  titreAReclamer,
  titreEstTexte,
  titreLieDe,
  FICHE_LIEE_INTROUVABLE,
  isRecordNew,
} from './project.js';
import type { NewRecordsInput } from './project.js';
/* LA PHRASE D'UN REFUS, quand l'app en a lance un : c'est la SEULE facon dont
   un message d'app atteint l'utilisateur, et elle est ecrite une fois
   (`views/refus.ts`). */
import { raisonDeRefus } from './refus.js';
import { instantaneRemplaceLaFiche } from './ficheSuivie.js';
import {
  markSeen,
  newBadgePrefKey,
  newMemoryPrefKey,
  readNewMemory,
  writeNewMemory,
} from './newRecords.js';
import type { NewMemory } from './newRecords.js';
// LE NOM DU SAC, importe et jamais recopie : c'est l'assemblage qui pose les
// chemins de stockage (`cheminDeStockage`), et l'ecriture du brouillon doit lire
// EXACTEMENT le meme prefixe pour rendre a la preuve une clef nue.
import { SAC_SUR_MESURE } from './screens.js';
import { estLienParListe } from './contract.js';
import {
  cheminDuChampLie,
  ecrireDansParam,
  fichesDuParent,
  libellesDuChemin,
  lireDansParam,
  memeDans,
  resoudreDans,
} from './porteDans.js';
import type { CheminDePorte, DansUneFiche } from './porteDans.js';
import { creationEnLecture, creeeApresOuverture, decisionSolo } from './fiche-creee.js';

/**
 * useScreen : L'ETAT D'ECRAN, tenu par le core.
 *
 * Il est appele DANS le fichier d'ecran, pas cache dans un contexte : quand
 * une vue se comporte mal, on lit l'etat la ou il est cree. Changer de vue
 * n'est qu'un setState ici, donc l'etat partage (recherche, tri, fenetre,
 * selection, curseur) survit par construction : aucune vue ne refiltre, ne
 * retrie, ne recharge.
 *
 * Ce que ce hook possede : la requete, la selection, la vue active, la
 * derniere vue collection, l'element ouvert, le curseur, l'etat jetable de
 * chaque vue, et l'ECRITURE (optimiste). Ce que l'app possede : l'appel de
 * donnees (`load`), les bindings, les catalogues (`options()`), l'ecriture
 * reelle (`set()`).
 *
 * DEUX MODES DE CHARGEMENT, choisis par l'ecran (`pagination`), et c'est le
 * SEUL endroit ou la difference existe : aucune vue ne sait lequel tourne.
 *
 * - `client` (DEFAUT, comportement historique) : le chargeur rend tout d'un
 *   coup, le core filtre, trie et fenetre en memoire, avec un plafond visible
 *   (`DEFAULT_LIMIT`) et un bandeau au-dela. Honnete tant que la collection
 *   tient dans un onglet.
 * - `server` : le chargeur rend UNE PAGE (`query.limit` lignes au plus, apres
 *   `query.after`), le core les EMPILE, et `loadMore()` demande la suivante.
 *   Le tri devient un parametre de requete (le changer repart de la page 1),
 *   et "il reste des pages" se lit "la derniere page etait pleine", le contrat
 *   du SDK (`runList`, @taki/sdk).
 *
 * CE QUE LE MODE SERVEUR NE FAIT PAS, et qu'il faut dire a l'utilisateur
 * plutot que lui laisser croire : Firestore n'a pas de recherche plein texte,
 * donc la RECHERCHE et les PASTILLES restent CLIENTES, sur les pages deja
 * chargees. Le bandeau du `ViewHost` le formule au lieu de laisser penser
 * qu'un resultat absent n'existe pas.
 *
 * Le champ `demand` de chaque vue reste declare honnetement : c'est ce qui a
 * evite d'avoir a rouvrir les cinq vues en passant au mode serveur.
 *
 * LES PORTES (gabarit v2). La zone 1 n'est plus la segmentation d'une
 * collection : chaque entree du menu est une PORTE, un JEU DE DONNEES de l'app
 * (decision fondateur du 01/08/2026 : la zone 1 dit QUEL JEU, la zone 4 dit
 * QUELLE TRANCHE). Une porte peut apporter son chargement, son catalogue de
 * vues, son catalogue de filtres, ses actions, ses stats, son placeholder de
 * recherche et ses presets de favoris (`ScreenDoor`) ; ce qu'elle ne declare
 * pas retombe sur les options de l'ecran. L'id de porte voyage dans le
 * parametre `s` de l'adresse, exactement la ou voyageait la section qu'il
 * remplace : meme parametre, meme sens ("quel jeu"). CHANGER DE PORTE remet a
 * zero la recherche, les filtres et la pile de tri, referme la fiche, vide la
 * selection et recharge : un autre jeu de donnees ne garde rien du precedent
 * (le placeholder, les actions et les stats suivent la porte par
 * construction, l'ecran les lit sur `state.door`).
 *
 * RETRO-COMPATIBILITE, non negociable : un ecran sans `doors` = une porte
 * IMPLICITE unique reprenant les options de l'ecran, et `section` garde
 * exactement son sens libre d'avant (des tranches du MEME jeu : aucune remise
 * a zero au changement, le core ne connait pas leur catalogue). Les ecrans
 * ecrits avant les portes compilent et se comportent a l'identique.
 *
 * PERSISTANCE, quatre etages. L'ADRESSE passe devant tout le reste quand elle
 * porte quelque chose : un lien partage doit montrer ce qu'il annonce, pas la
 * derniere fiche que CE navigateur avait ouverte.
 * - l'URL `?view=&id=&s=&f=` : l'etat PARTAGEABLE, branche par le shell via
 *   `ScreenUrlProvider` (voir screenUrl.tsx). `f` est une liste d'ids separes
 *   par des virgules depuis le gabarit v2 (multi-filtres additifs). Absent
 *   hors shell : le hook retombe alors exactement sur les etages ci-dessous ;
 * - sessionStorage `taki.screen.<appId>.<screenId>` : l'etat VIVANT de la
 *   session (vue active, element ouvert, derniere vue collection) ;
 * - pref locale `app.<appId>.<screenId>.view` : la DERNIERE vue collection
 *   utilisee, ecrite toute seule a chaque bascule ;
 * - pref locale `app.<appId>.<screenId>.view.pinned` : la vue EPINGLEE par
 *   l'utilisateur dans les reglages de l'app. Vide = "derniere vue utilisee".
 *   C'est un choix explicite, donc il passe devant la memoire automatique ;
 * - pref locale `app.<appId>.<screenId>.<porte>.favorites` : les FAVORIS
 *   gardes par l'utilisateur (zone 4b), une liste PAR PORTE, meme mecanique
 *   d'event `taki:pref-change` que les prefs de vues (le hub Parametres et
 *   l'ecran se resynchronisent tout seuls, tant qu'ils sont dans le MEME
 *   document : l'event ne franchit pas l'onglet).
 * - pref locale `app.<appId>.<screenId>.<porte>.nouveau` : la memoire du
 *   reperage des NOUVEAUTES, une par porte (date de reference + ids deja
 *   ouverts, `NEW_SEEN_MAX` au plus). Elle s'allume ou s'eteint pour toute l'app par
 *   `app.<appId>.nouveau`. Tout est dans `newRecords.ts`, y compris pourquoi
 *   c'est local au poste et ou il faudra toucher le jour du multi-utilisateur.
 * Une cle par ECRAN, jamais par app : deux sections d'une meme app n'ont pas
 * les memes vues. La pile de tri, elle, ne persiste NULLE PART : ni adresse
 * (meme decision que la recherche), ni session ; elle vit et meurt avec
 * l'ecran, et les favoris sont sa seule memoire nommee.
 *
 * ECRITURE, quatre garanties tenues ici et nulle part ailleurs (une app ne
 * recode aucune des quatre) :
 * - une ecriture plus RECENTE annule l'effet d'une plus ancienne (jeton de
 *   sequence par couple id + cle), sinon une reponse lente ecrase un etat frais ;
 * - une ecriture reussie RECHARGE le jeu (`reload`) : c'est notre seule parade
 *   au multi-utilisateur dans ce lot, et elle reconcilie les stats de la zone 3 ;
 * - un echec devient VISIBLE (`saveState.mode === 'error'` + `retrySave`), au
 *   lieu de disparaitre dans la console ;
 * - la PILE D'ANNULATION vit le temps de la session de fiche : elle NAIT vide a
 *   l'ouverture, survit aux fleches precedent / suivant, et meurt a la sortie.
 *   Un JETON DE SESSION (distinct du jeton de sequence, qui est par cible)
 *   garantit le bord d'apres : une ecriture partie avant la sortie et revenue
 *   apres ne repeuple ni la pile ni `saveState`.
 */

/** Plafond de fenetre du mode CLIENT (500 lignes, bandeau au-dela). */
export const DEFAULT_LIMIT = 500;

/**
 * Taille de page du mode SERVEUR. Volontairement bien plus petite que le
 * plafond client : une page, c'est ce qu'un ecran affiche, pas ce qu'un onglet
 * supporte. 50 lignes remplissent un tableau sans faire attendre.
 */
export const DEFAULT_PAGE_SIZE = 50;

/**
 * Plafond dur de la fenetre CLIENTE. Aligne sur le plafond de page du SDK
 * (`MAX_LIST_LIMIT`, @taki/sdk), recopie ici plutot qu'importe : le design
 * system ne depend pas de la couche donnees. Un ecran client qui relaie
 * `query.limit` au SDK ne peut donc pas, a force de clics, demander une page
 * que le SDK refusera.
 */
export const MAX_CLIENT_WINDOW = 1000;

/**
 * Profondeur de la pile d'annulation. On coupe par la TETE (les pas les plus
 * anciens tombent) : un plafond qui jetterait les pas recents rendrait
 * `Cmd+Z` imprevisible.
 */
const UNDO_LIMIT = 20;

/** Vue de repli universelle quand l'ecran n'en impose aucune. */
const FALLBACK_VIEW_ID: ViewId = 'core.liste';

const SESSION_PREFIX = 'taki.screen.';

/** Espace de memoire commun aux preferences et a la session de la porte. */
function viewMemoryKey(appId: string, screenId: string, doorId?: string): string {
  return `${appId}.${screenId}${doorId === undefined ? '' : `.door.${encodeURIComponent(doorId)}`}`;
}

/** Cle de la derniere vue collection utilisee, par porte si elle est indiquee. */
export function lastViewPrefKey(appId: string, screenId: string, doorId?: string): string {
  return `app.${viewMemoryKey(appId, screenId, doorId)}.view`;
}

/** Cle de la vue EPINGLEE par l'utilisateur dans les reglages de l'app. */
export function pinnedViewPrefKey(appId: string, screenId: string, doorId?: string): string {
  return `app.${viewMemoryKey(appId, screenId, doorId)}.view.pinned`;
}

/**
 * Cle de la VUE DE FICHE choisie par l'utilisateur (« Ouvrir les fiches sur »,
 * chantier `fiche-suivi`). La meme portee que la vue epinglee d'une collection,
 * une clef a part : les deux niveaux se choisissent independamment, et une
 * vue de fiche n'est jamais une vue d'arrivee de collection.
 */
export function pinnedRecordViewPrefKey(appId: string, screenId: string, doorId?: string): string {
  return `app.${viewMemoryKey(appId, screenId, doorId)}.recordView.pinned`;
}

/**
 * Cle de la DERNIERE VUE DE FICHE utilisee sur une porte (chantier
 * `fiche-canva-v2`), ecrite quand la personne change de vue de fiche par le
 * selecteur et relue a l'ouverture d'une fiche quand la porte le demande
 * (`ScreenDoor.recordViewLast`). Meme portee que `lastViewPrefKey`, une clef a
 * part : elle ne se confond ni avec la derniere vue de collection ni avec le
 * choix de « Ouvrir les fiches sur ».
 */
export function lastRecordViewPrefKey(appId: string, screenId: string, doorId?: string): string {
  return `app.${viewMemoryKey(appId, screenId, doorId)}.recordView.last`;
}

/**
 * LA VUE DE FICHE D'ARRIVEE D'UNE PORTE, preferences lues : le choix de chacun
 * (`pinnedRecordViewPrefKey`) et la derniere vue utilisee
 * (`lastRecordViewPrefKey`), a donner a `vueDeFicheDArrivee`. Un seul lieu de
 * lecture pour les trois entrees dans une fiche (clic, adresse a froid, saut).
 */
function vueDeFicheChoisie(
  types: readonly ViewType[],
  porte: { recordView?: ViewId; recordViewLast?: boolean } | null | undefined,
  appId: string,
  screenId: string,
  doorId: string | undefined,
): ViewId | undefined {
  return vueDeFicheDArrivee(
    types,
    porte,
    readPref<string | null>(pinnedRecordViewPrefKey(appId, screenId, doorId), null),
    porte?.recordViewLast === true ? readPref<string | null>(lastRecordViewPrefKey(appId, screenId, doorId), null) : null,
  );
}

/**
 * Cle des favoris GARDES par l'utilisateur (zone 4b). Une liste PAR PORTE :
 * les filtres d'un jeu de donnees ne veulent rien dire dans un autre (le
 * proto montrait les memes favoris partout, simplification de proto a ne pas
 * reproduire). L'ecran sans portes range les siens sous la porte implicite.
 */
export function favoritesPrefKey(appId: string, screenId: string, doorId: string): string {
  return `app.${appId}.${screenId}.${doorId}.favorites`;
}

/**
 * Cle des LARGEURS DE COLONNES reglees a la main (`core.tableau`). Meme portee
 * que les favoris, PLUS la vue : deux vues tableau d'une meme porte n'affichent
 * pas les memes colonnes, donc une largeur reglee sur l'une ne veut rien dire
 * sur l'autre. Valeur stockee : `{ [cleDeColonne]: largeurEnRem }`, en rem et
 * jamais en px (le shell corrige la racine rem selon la densite de l'ecran,
 * `packages/shell/src/uiScale.ts`).
 */
export function columnWidthsPrefKey(
  appId: string,
  screenId: string,
  doorId: string,
  viewId: string,
): string {
  return `app.${appId}.${screenId}.${doorId}.${viewId}.columnWidths`;
}

/**
 * L'id de la porte IMPLICITE d'un ecran sans `doors`. Il ne sort JAMAIS dans
 * l'adresse (le parametre `s` d'un ecran sans portes reste la section libre) :
 * il ne sert qu'a donner une cle de pref aux favoris et une identite a
 * `state.door`.
 */
export const IMPLICIT_DOOR_ID = 'default';

/**
 * Le placeholder de recherche du gabarit v2 quand la porte n'en declare pas.
 * La constante VIT dans ScreenLayout (c'est le chassis qui la rend en 2b, et
 * qui la sert de defaut aux ecrans V1) ; elle est re-exportee ici, ou la
 * resolution de porte l'utilise, pour garder la surface publique historique.
 * Aucune IA n'est cablee derriere : c'est un placeholder, la promesse
 * appartient au shell (Lia de Taki), pas a l'ecran.
 */
export { DEFAULT_SEARCH_PLACEHOLDER };

/**
 * Oublie l'etat de session d'un ecran. Appele quand l'utilisateur epingle une
 * vue dans les reglages : sans ca, la vue en cours de session passerait devant
 * son choix tout neuf, et l'epinglage n'aurait aucun effet visible avant la
 * fermeture de l'onglet.
 */
export function forgetScreenSession(appId: string, screenId: string, doorId?: string): void {
  try {
    window.sessionStorage.removeItem(`${SESSION_PREFIX}${viewMemoryKey(appId, screenId, doorId)}`);
  } catch {
    /* sessionStorage indisponible : rien a oublier. */
  }
}

/* ------------------------------------------------------------------ */
/* Types publics du hook                                               */
/* ------------------------------------------------------------------ */

/**
 * Un filtre declare par l'ecran (V1) ou par une porte (v2), vu par le core.
 * Le `match` est ce qui permet au CORE de filtrer : sans lui, le filtre est
 * purement decoratif (a l'app de filtrer dans son `load`). En multi-filtres
 * additifs, un filtre sans `match` ne contribue donc RIEN au OU : il ne
 * neutralise pas les autres (un "laisse tout passer" dans un OU rendrait les
 * voisins muets), et la lecture V1 (une seule pastille active, sans `match`,
 * qui montre tout) reste exactement ce qu'elle etait.
 */
export interface ScreenFilterDef<T> {
  id: string;
  label: string;
  match?(record: T): boolean;
}

/**
 * Un filtre tel que l'UI le recoit (`ScreenState.filters`). Superset de la
 * forme que `ScreenLayout.filters` accepte deja (id + label) : les ecrans V1
 * le passent tel quel a leurs pastilles, le popover de l'outil filtre (v2) y
 * lit en plus le compteur.
 */
export interface ScreenFilterOption {
  id: string;
  label: string;
  /**
   * Combien de lignes de la PORTE BRUTE ce filtre retient (archives exclues,
   * recherche et autres filtres ignores : regle "un compteur ne bouge pas
   * quand on filtre"). Absent quand le filtre n'a pas de `match` : le core ne
   * peut pas compter ce qu'il ne sait pas evaluer, et un zero serait un
   * mensonge.
   */
  count?: number;
}

/**
 * UNE PORTE (zone 1 du gabarit v2) : un JEU DE DONNEES de l'app, avec tout ce
 * qui l'accompagne. Chaque champ optionnel retombe sur l'option de meme nom de
 * l'ecran (`UseScreenOptions`) : trois portes qui partagent le meme chargeur
 * ne le declarent qu'une fois et le distinguent par `query.section`, exactement
 * comme les sections d'avant.
 */
/**
 * S'ABONNER A UNE FICHE : la forme de `observe` du SDK (`observe-ops.ts`),
 * redite ici parce que `@taki/ui` ne depend pas de la couche donnees. Rend la
 * fonction qui se desabonne.
 */
export type ObserverUneFiche<T> = (
  id: string,
  onChange: (record: T | null, meta: { local: boolean }) => void,
  onError?: (error: unknown) => void,
) => () => void;

export interface ScreenDoor<T> {
  /** Id stable : l'entree de menu (zone 1b) ET la valeur du parametre `s`. */
  id: string;
  /** Libelle de l'entree de menu. Le compteur, lui, reste calcule par l'ecran (jeu complet, gele). */
  label: string;
  /** Le chargement de CE jeu de donnees. Absent : le `load` de l'ecran. */
  load?(query: ScreenQuery): Promise<T[]>;
  /**
   * Chargement d'UN enregistrement (entree a froid). Absent : le `get` de
   * l'ecran.
   *
   * L'OUVERTURE A FROID CONNAIT SA PORTE, ET C'EST ICI QUE CA SE JOUE : le
   * socle appelle le `get` de la porte ACTIVE, jamais celui de l'ecran quand
   * la porte en declare un, et il le resout dans le MEME tick que le
   * changement de porte (`openDoor`), pas au rendu d'apres. C'est ce qui fait
   * marcher un id venu d'AILLEURS : le clic sur une ligne de carte liee change
   * de porte puis ouvre la fiche, et l'id est celui du jeu de la porte LIEE.
   * Une porte qui montre un autre jeu que l'ecran DOIT donc declarer le sien.
   * Sans ca, le `get` de l'ecran cherche dans la mauvaise famille et rend
   * `null`, c'est-a-dire « Fiche introuvable » sur une fiche qui existe.
   */
  get?(id: string): Promise<T | null>;
  /**
   * SUIVRE LA FICHE OUVERTE (`sdk.<famille>.observe`). Absent : l'option de
   * meme nom de l'ecran ; absente aussi, la fiche ouverte ne suit rien et ne
   * change qu'aux relectures de l'ecran (apres une ecriture, au rechargement).
   *
   * Le core s'abonne a la fiche ouverte (ni brouillon, ni modele) des qu'elle
   * est chargee, et se desabonne en changeant de fiche ou en la fermant. Il ne
   * retient que les instantanes du SERVEUR qui changent la fiche, hors
   * ecriture en vol (`instantaneRemplaceLaFiche`, `views/ficheSuivie.ts`), et
   * les pose par le chemin de la fiche relue apres une ecriture. Meme
   * resolution que `get` : c'est la porte ACTIVE qui le donne. L'ecran
   * declare ne le pose que sur un jeu a champ automatique (`calcul`, `numero`).
   */
  observe?: ObserverUneFiche<T>;
  /**
   * ARCHIVER une fiche (`sdk.<famille>.archive`). Absent : l'option de meme
   * nom de l'ecran ; absente aussi, l'entree du menu de la rangee 3c est
   * GRISEE avec sa raison (regle G16), JAMAIS absente. Un geste qui apparait
   * et disparait selon l'app fabrique exactement le "pourquoi je ne peux pas
   * archiver ici" qu'on veut eviter.
   *
   * Le core se charge du reste : la fenetre de confirmation, la fermeture de
   * la fiche, le rechargement de la porte et le compte rendu de l'ecriture
   * (`saveState`). La porte ne fait qu'ECRIRE.
   */
  archive?(record: T): Promise<void>;
  /** DESARCHIVER une fiche. Meme patron, meme repli que `archive`. */
  unarchive?(record: T): Promise<void>;
  /**
   * SUPPRIMER une fiche (`sdk.<famille>.remove` : les trois etats du socle,
   * `archivedAt` + `deletedAt`). Meme patron, meme repli que `archive`. Pour
   * l'utilisateur, supprime veut dire supprime : la fiche quitte les listes ET
   * les archives, et rien dans l'interface ne la recupere.
   */
  remove?(record: T): Promise<void>;
  /** Le catalogue de vues de la porte. Absent : les `types` de l'ecran. */
  types?: ViewType[];
  /** Vues activees par la declaration de la porte, filtrees dans son catalogue. */
  viewIds?: readonly ViewId[];
  /** Un binding par type de vue. Absent : les `bindings` de l'ecran. */
  bindings?: ViewBinding<T>[];
  /** Vue d'arrivee quand la vue courante n'existe pas derriere cette porte. */
  defaultView?: ViewId;
  /**
   * Le CATALOGUE DE FILTRES de la porte (popover de l'outil filtre, zone 3b).
   * Multi-choix ADDITIF : l'etat est une liste d'ids de ce catalogue
   * (`query.filterIds`), vide au passage de la porte. Absent : les `filters`
   * de l'ecran, comme les autres replis.
   */
  filters?: ScreenFilterDef<T>[];
  /**
   * `false` = cette porte ne se TRANCHE pas (ex. Livraisons) : aucun filtre,
   * aucun favori, l'ETOILE, l'outil FILTRE et le tiroir sont masques. Le menu
   * « ... » des modes, lui, reste (regle G20 : toute porte offre ses
   * archives), comme le tri. Le mot vient du vocabulaire du fondateur
   * ("sections" = les tranches d'un jeu), pas des sections V1 de la zone 1.
   * Defaut : true.
   */
  sections?: boolean;
  /**
   * Les actions de la porte (zone 3a). La regle du libelle universel
   * ("+ Ajouter", premier, lime) est tenue par le gabarit, pas ici : la porte
   * declare, l'ecran passe `state.door.actions` au gabarit.
   */
  actions?: ScreenAction[];
  /**
   * Les stats de la porte (zone 2c, plafond 3 tenu par le gabarit). Forme
   * fonction : elle recoit le jeu VIVANT de la porte (archives exclues, AVANT
   * recherche et filtres : doctrine "stats sur la porte brute, insensibles aux
   * filtres"), a chaque rendu. Forme tableau : des valeurs deja calculees par
   * l'ecran.
   */
  stats?: ScreenStat[] | ((live: T[]) => ScreenStat[]);
  /** Placeholder de la recherche (2b). Absent : `DEFAULT_SEARCH_PLACEHOLDER`. */
  searchPlaceholder?: string;
  /**
   * Les favoris PRESETS de la porte (zone 4c), servis avant ceux que
   * l'utilisateur garde. Ignores quand `sections` est false.
   */
  presets?: ScreenFavorite[];
  /**
   * CETTE PORTE EST UN SPACE CANVAS (regle G23, doctrine dans
   * `docs/GABARIT-ECRAN.md` section 9) : sa zone 5 est libre, l'app y rend ce
   * qu'elle veut, et les zones 3 et 4 sont absorbees sauf ce que `keeps`
   * garde. Deux consequences tenues ICI, pas par l'ecran : la porte n'a AUCUNE
   * vue du socle (catalogue vide, donc pas de selecteur en 3c), et elle ne se
   * tranche pas tant qu'elle ne garde pas 3b (ni filtres, ni favoris).
   *
   * La derogation se demande AVANT au fondateur et se DECLARE dans la fiche
   * `taki.app.json` de l'app : ce champ la met en oeuvre, il ne l'accorde pas.
   * L'ecran passe ensuite `state.door.canvas` au gabarit, tel quel.
   */
  canvas?: { elements?: readonly string[]; alimente?: readonly string[]; keeps?: readonly ('3a' | '3b')[] };
  /**
   * LE JEU DE CETTE PORTE : la famille du SDK (`assets`) et le sous-type de
   * l'app (`materiel`). Il ARRIVE TOUT SEUL sur une porte declaree : l'ecran
   * etale `portesDe(ECRAN)` sur ses portes, et `PorteAssemblee` le porte
   * desormais. Une porte ecrite a la main peut le poser elle-meme.
   *
   * A QUOI IL SERT, ET A QUOI IL NE SERT PAS. Il ne change RIEN au chargement
   * ni a l'ecriture : la porte declare deja `load`, `get` et ses trois verbes,
   * et le moteur ne devinera jamais un appel de SDK. Il sert aux gestes
   * STANDARD qui ont besoin de NOMMER le jeu sans savoir l'ecrire : les modeles
   * de fiches, qui rangent un modele dans une famille et ne proposent que ceux
   * du bon sous-type. Absent, ces gestes se rendent GRISES avec leur raison
   * (G16), comme un verbe d'archivage manquant.
   */
  jeu?: { family: string; type?: string };
  /**
   * LES MODELES DE FICHES SONT-ILS ALLUMES SUR CETTE PORTE ? Absent ou `false` :
   * non, et les deux gestes se rendent GRISES avec leur raison (G16, G18), comme
   * un verbe d'archivage que la porte ne declare pas.
   *
   * POURQUOI CA S'ALLUME AU LIEU DE SUIVRE LE JEU. Le jeu dit OU ranger un
   * modele ; il ne dit pas si la porte en VEUT. Une porte de suivi (« ce qui est
   * en maintenance ») partage le jeu d'une porte de saisie sans qu'on ait la
   * moindre envie d'y creer des fiches depuis un modele. Le defaut est donc non,
   * et c'est celui qui construit l'app qui dit oui, porte par porte, avec
   * `"modeles": true` dans `taki.screens.json`.
   *
   * IL ARRIVE TOUT SEUL, comme `jeu` : `portesDe(ECRAN)` le recopie sur la porte
   * assemblee, et l'ecran l'etale. Une porte ecrite a la main peut le poser.
   */
  modeles?: boolean;
  /**
   * LES PLANS RECURRENTS SONT-ILS ALLUMES SUR CETTE PORTE ? Meme contrat que
   * `modeles` (« Créer un plan récurrent »), et il arrive de la
   * meme facon.
   */
  recurrences?: boolean;
  /**
   * LE TABLEAU DE CETTE PORTE S'EDITE EN PLACE (cellules, titre). Absent = non :
   * le tableau se lit, un clic sur la ligne ouvre la fiche. Meme contrat que
   * `modeles` : seul le `true` se lit, et il arrive tout seul depuis
   * `taki.screens.json`. Un ecran code a la main le pose pareil.
   */
  modifierDansLeTableau?: boolean;
  /**
   * CE QUE LA FICHE VIDE PORTE DEJA quand on ouvre la creation ici : des
   * valeurs par defaut, en CHEMINS DE STOCKAGE resolus (`status`,
   * `customFields.category`).
   *
   * IL ARRIVE TOUT SEUL, comme `jeu` et `modeles` : `portesDe(ECRAN)` le
   * resout depuis `creation.defauts` du fichier d'ecran et l'ecran l'etale.
   * Une porte ecrite a la main peut le poser.
   *
   * ABSENT = le brouillon s'ouvre VIDE, ce qui est le cas ordinaire.
   */
  creation?: { defauts: ValeurDeDepart[] };
  /**
   * LES LIENS SORTANTS DE CETTE PORTE : les jeux qui POINTENT ses fiches
   * (`{ id, door, by, family, type? }`, resolus par l'assemblage).
   *
   * IL ARRIVE TOUT SEUL, comme `jeu` et `modeles` : une porte assemblee le
   * porte deja et l'ecran l'etale (`...porte`). Une
   * porte ecrite a la main peut le poser.
   *
   * A QUOI IL SERT ICI, ET A RIEN D'AUTRE : le moteur COMPTE ce qui pointe la
   * fiche ouverte avant de la supprimer, et REFUSE quand ce compte n'est pas
   * nul (« a la Odoo », decision du fondateur du 09/09/2026). Il ne sert ni a
   * charger, ni a afficher : les cartes liees de la FICHE, elles, arrivent par
   * le binding (`LinkedCardDef`), et une porte peut parfaitement avoir des
   * liens sans en montrer aucune carte.
   *
   * ABSENT OU VIDE : rien ne change, la suppression garde sa confirmation
   * d'aujourd'hui. Aucune declaration nouvelle nulle part : la politique est le
   * DEFAUT de toute porte qui a des liens.
   */
  links?: LinkedDoor[];
  /**
   * AU CLIC SUR UNE FICHE, depuis une vue de collection de cette porte : au
   * lieu d'ouvrir la fiche, on ENTRE dans la porte liee (`link.door`),
   * filtree sur la fiche cliquee (`ScreenQuery.dans`), dans la vue `view`
   * (absente : l'arrivee de la porte liee). La fiche reste ouverte par tous
   * les autres chemins (adresse, fleches, lien, recherche). Absent : la fiche
   * s'ouvre, comme partout. Le detail : `docs/socle/porte-fiche.md`.
   */
  auClic?: { link: LinkedDoor; view?: ViewId };
  /**
   * LA VUE DE FICHE D'ARRIVEE DE CETTE PORTE (`auClic: { vueDeFiche }`, chantier
   * `fiche-suivi`) : une vue de niveau `record` de son catalogue, ou la fiche
   * s'ouvre quand la personne n'a rien choisi (« Ouvrir les fiches sur »).
   * Absente : la premiere vue de fiche, `core.fiche`.
   */
  recordView?: ViewId;
  /**
   * LA FICHE S'OUVRE DANS LA DERNIERE VUE DE FICHE QUE LA PERSONNE A UTILISEE
   * sur cette porte (`auClic: { derniereVue: true }`, chantier `fiche-canva-v2`,
   * `lastRecordViewPrefKey`). Tant qu'elle n'en a pas choisi, ou si cette vue a
   * disparu du catalogue, le defaut `recordView`, puis `core.fiche`. Le choix de
   * chacun (« Ouvrir les fiches sur ») passe devant.
   */
  recordViewLast?: boolean;
  /**
   * LA FICHE S'OUVRE DANS UNE AUTRE PORTE (`auClic: { porte, app? }`, chantier
   * `fiche-suivi`) : au clic sur une ligne d'une vue de collection, on change
   * de porte avec la fiche (`openLinked`), un seul Retour ramene ici. Une
   * porte d'une AUTRE app (`app`) est le saut de l'ecran (`NavigationPort`) :
   * le moteur ne la connait pas, il appelle `ouvrirAilleurs`.
   */
  ouvreDans?: { door: string; app?: string };
  /**
   * `false` : LA PORTE NE S'AFFICHE PAS DANS LA NAVIGATION. On n'y entre que
   * par un lien d'une autre porte (`auClic`, une carte liee) ; le menu allume
   * alors la porte d'ou l'on vient (`ScreenState.chemin.porteDuMenu`). C'est
   * l'ecran qui construit son menu : il lit ce drapeau. Absent : oui.
   */
  navigation?: boolean;
  /**
   * LES LIENS ENTRANTS DE CETTE PORTE, en CHEMINS DE STOCKAGE : les champs de
   * SES fiches qui DESIGNENT une fiche d'une AUTRE porte (`assetId`,
   * `customFields.materielId`). Le sens inverse de `links`, deduit par
   * l'assemblage (`liensEntrants`, `views/screens.ts`), jamais declare.
   *
   * IL ARRIVE TOUT SEUL, comme `links` : la porte assemblee le porte et l'ecran
   * l'etale (`...porte`). Une porte ecrite a la main peut le poser.
   *
   * A QUOI IL SERT ICI, ET A RIEN D'AUTRE : « Enregistrer comme modele » RETIRE
   * ces chemins, de la fiche racine comme de chaque fiche liee emportee
   * (`sansChampsDeLien`). Un modele est un PATRON : garder l'id d'un chantier
   * dans un patron ferait naitre toutes les fiches creees depuis lui accrochees
   * au chantier de l'original, sans qu'une seule ecriture n'echoue.
   *
   * ABSENT OU VIDE : rien n'est retire, ce qui est le cas de toute porte que
   * personne ne pointe.
   */
  entrants?: string[];
}

/**
 * Une porte se TRANCHE-t-elle ? (filtres, favoris, etoile). Trois cas, un seul
 * endroit : la porte implicite se tranche, un Space Canvas ne se tranche que
 * s'il a garde le groupe d'outils (3b), une porte normale suit son champ
 * `sections`. Ecrit une fois parce que le moteur pose cette question a QUATRE
 * moments : l'initialisation, le rendu, le changement de porte et la lecture
 * de l'adresse.
 */
function doorHasSections<T>(door: ScreenDoor<T> | null | undefined): boolean {
  if (door == null) return true;
  if (door.canvas != null) return resoudreElementsCadre({ level: 'space', ...door.canvas }).some((element) => ['filtres', 'tiroir'].includes(element));
  return door.sections !== false;
}

/**
 * Cette porte a-t-elle ABSORBE le groupe d'outils (3b) ? Vrai d'un Space Canvas
 * qui ne le garde pas, faux partout ailleurs, `sections: false` compris : une
 * porte qui ne se tranche pas garde son outil filtre et son entree Archives
 * (regle G20), un canvas qui absorbe 3b n'a plus RIEN pour montrer ni eteindre
 * un mode. C'est la question que se pose le MODE ARCHIVES avant de s'allumer.
 */
function doorAbsorbsTools<T>(door: ScreenDoor<T> | null | undefined): boolean {
  return door?.canvas != null && !resoudreElementsCadre({ level: 'space', ...door.canvas }).includes('menu');
}

/**
 * LE MODE MODELES PEUT-IL S'ALLUMER ICI ? La question se pose sur QUATRE
 * chemins (l'adresse de depart, `fromSearch`, un changement de porte, un
 * `setQuery` d'app), et la reponse est ECRITE ICI, une fois.
 *
 * QUATRE CONDITIONS, ET AUCUNE N'EST DECORATIVE :
 *  - DES PORTES. Sans elles, l'ecran ne passe pas `tools` au gabarit : ni tag,
 *    ni tiroir, ni « tout effacer ». Le mode s'allumerait sans qu'aucune
 *    surface ne le montre ni ne puisse l'eteindre (meme verrou que le mode
 *    Archives) ;
 *  - UNE PORTE QUI GARDE SES OUTILS. Un Space Canvas qui absorbe 3b n'a rien
 *    pour montrer ni eteindre un mode : c'est une nasse, ouverte par un lien ;
 *  - LA CONFIGURATION COMPLETE DES MODELES (`raisonDesModeles` : le port de
 *    l'hote, le `jeu` de la porte, `modeles: true`). C'EST LE VERROU QUI
 *    MANQUAIT : une adresse retouchee `?s=<porte de suivi>&mod=1` listait,
 *    modifiait et creait des modeles sur une porte dont la configuration dit
 *    explicitement NON, et sur une porte sans `jeu` le « + Ajouter » appelait
 *    `create({ family: '' })`, qui echoue a tous les coups. L'adresse ne peut
 *    pas contourner la frontiere « configuration => absence », qui est la
 *    decision centrale de ce chantier.
 *
 * PURE ET EXPORTEE EXPRES : c'est une regle de securite d'affichage qui ne
 * rougit nulle part quand elle se relache (l'ecran marche, il montre juste ce
 * qu'il ne devrait pas), et un test ne peut la tenir que sans React ni SDK.
 */
export function modelesAllumables<T>(etat: {
  /** L'ecran a-t-il des portes (`options.doors`) ? */
  hasDoors: boolean;
  /** La porte VISEE (l'active, ou celle qu'on ouvre), jamais une autre. */
  door: ScreenDoor<T> | null | undefined;
  /** Le port de l'hote (`useModelesPort()`), `null` quand personne ne le fournit. */
  port: ModelesPort | null;
  /**
   * LA PORTE MONTRE UN ENFANT DE MODELE (sa fiche, adresse `<modeleId>/<id>`).
   *
   * POURQUOI C'EST UNE DEROGATION, ET POURQUOI ELLE EST SURE. Un enfant s'ouvre
   * dans la porte LIEE (Interventions), qui n'allume pas les modeles et n'a
   * aucune raison de le faire : elle ne montre pas de modeles, elle montre UNE
   * fiche d'enfant. Sans cette derogation, `nextQuery` eteindrait `mod=1` au
   * saut, l'adresse cesserait de dire ce qu'elle ouvre, et le retour au modele
   * parent (la puce de sortie) ramenerait a la liste ORDINAIRE de la porte.
   *
   * CE N'EST PAS UNE NASSE (le pourquoi de la garde, plus haut) : le tag « Mode
   * Modeles » du tiroir est rendu des que le mode est allume, avec sa croix, et
   * ETEINDRE ne passe pas par cette garde. Seul le PORT reste exige : sans lui,
   * il n'y a ni parent a lire ni enfant a montrer.
   */
  enfant?: boolean;
}): boolean {
  if (!etat.hasDoors) return false;
  if (doorAbsorbsTools(etat.door)) return false;
  if (etat.enfant === true) return etat.port != null;
  return (
    raisonDesModeles({
      port: etat.port,
      jeu: etat.door?.jeu ?? null,
      modeles: etat.door?.modeles === true,
    }) == null
  );
}

/**
 * LA PORTE ACTIVE, RESOLUE : ce que l'ecran passe au gabarit sans rien
 * recalculer (placeholder, actions, stats). Recomposee a chaque rendu ; pour
 * un ecran sans portes elle decrit la porte implicite (valeurs neutres).
 */
export interface ScreenDoorState {
  id: string;
  label: string;
  searchPlaceholder: string;
  /**
   * La porte est un SPACE CANVAS, resolu et pret a passer au gabarit
   * (`canvas={screen.door.canvas}`), ou `null` pour une porte native. Le
   * `level` est pose par le moteur : une porte ne peut etre qu'un Space Canvas.
   */
  canvas: ScreenCanvas | null;
  /** false = pas de filtres ni de favoris ici (outil filtre, etoile et tiroir masques). */
  sections: boolean;
  actions: ScreenAction[];
  stats: ScreenStat[];
  /**
   * LE JEU DE LA PORTE ACTIVE (`ScreenDoor.jeu`), resolu et pret a passer au
   * gabarit avec le reste (`tools={screen}`). `null` quand la porte n'en
   * declare pas, ou pour la porte implicite : le gabarit grise alors la fleche
   * de « Ajouter » plutot que de deviner une famille.
   */
  jeu: { family: string; type?: string } | null;
  /**
   * LA PORTE ACTIVE ALLUME-T-ELLE LES MODELES (`ScreenDoor.modeles`) ? Resolu
   * ici, en un seul endroit : `true` seulement si la porte l'a ecrit, donc faux
   * pour la porte implicite et pour toute porte qui se tait. Le gabarit le lit
   * tel quel, il ne le redevine pas.
   */
  modeles: boolean;
  /**
   * CE QUE LA FICHE VIDE PORTE DEJA sur cette porte (`ScreenDoor.creation`),
   * resolu ici : `{ defauts: [] }` quand la porte ne declare rien, jamais
   * `null`. Le moteur le lit pour construire le brouillon ; le gabarit l'ignore.
   */
  creation: { defauts: ValeurDeDepart[] };
}

/**
 * LE BROUILLON DE FICHE : ce que le moteur tient pendant qu'on remplit une
 * fiche qui n'existe PAS ENCORE (decision du fondateur, 09/09/2026 :
 * « + Ajouter » ouvre une fiche vide, plus une fenetre).
 *
 * CE QU'IL N'EST PAS : une fiche. Rien n'est ecrit en base tant que « Creer »
 * n'a pas ete clique, et le seul document qui existe est en memoire, dans le
 * moteur (`brouillon.fiche`, qui ne sort jamais d'ici). Ce qui sort, c'est ce
 * que le CHASSIS doit rendre : deux boutons, une fenetre de sortie, des cases
 * a cercler.
 */
export interface BrouillonState {
  /** L'id de la porte ou la fiche naitra. */
  porte: string;
  /** Au moins une case a ete saisie depuis l'ouverture. */
  touche: boolean;
  /** `creerBrouillon` est en vol. */
  enCreation: boolean;
  /** Les cases obligatoires vides apres un « Creer » refuse (`fieldKeyOf`). Vide sinon. */
  manquants: readonly string[];
  /** La fenetre « Fiche non creee » est demandee (une sortie attend la reponse). */
  sortieEnAttente: boolean;
  /** Le titre saisi, pour la fenetre ; chaine vide sinon. */
  titre: string;
  /** Pourquoi « Creer » est grise : acces, port absent. `null` = vivant. */
  raison: string | null;
  /**
   * CE BROUILLON AJOUTE UN ENFANT A UN MODELE, pas une fiche reelle (pivot du
   * 10/09/2026). Pose par le « + Ajouter » d'une carte liee d'un modele ouvert,
   * qui PASSE dans la porte liee et y ouvre la fiche vide.
   *
   * IL SORT DE L'ETAT parce que le chassis en a besoin pour rien du tout, mais
   * qu'un ecran qui lit `tools.brouillon` doit pouvoir savoir ce que « Creer »
   * va faire. Le moteur, lui, s'en sert pour aiguiller `creerBrouillon`.
   */
  enfantDe?: { modele: string; lien: LienParLeChampLie; libelle: string };
  /**
   * CE BROUILLON VIENT D'UN MODELE, choisi dans le menu de « Ajouter »
   * (23/09/2026) : la fiche vide est pre-remplie par ses champs et son titre,
   * ses fiches liees se voient en apercu, et « Creer » passera par le port des
   * MODELES, qui cree la fiche ET ses fiches liees. Relaye pour qui lit
   * `tools.brouillon`, comme `enfantDe`.
   */
  depuisModele?: { id: string; name: string };
}

export interface UseScreenOptions<T> {
  /** Id de l'app (cle de persistance). */
  appId: string;
  /**
   * Le fuseau de l'org (nom IANA) : il dit quel jour il est pour le jeton
   * `socle:aujourdhui` de `creation.defauts`, resolu a l'ouverture du
   * brouillon. Absent (app ecrite a la main), le moteur lit celui de
   * l'installation, puis `FUSEAU_PAR_DEFAUT`.
   */
  fuseau?: string;
  /** Id de l'ecran dans l'app (cle de persistance : une cle par ecran). */
  screenId: string;
  /** L'appel de donnees. Recoit la requete courante ; c'est l'APP qui parle au SDK. */
  load(query: ScreenQuery): Promise<T[]>;
  /** L'identite d'un enregistrement. */
  getId(record: T): string;
  /**
   * Chargement d'UN enregistrement, pour l'entree a froid et l'element sorti
   * du filtre : on affiche la fiche sans charger 10 000 lignes. Absent, une
   * fiche hors du jeu courant affiche "Fiche introuvable".
   *
   * C'est le REPLI, valable pour toutes les portes qui n'en declarent pas :
   * une porte qui montre un AUTRE jeu declare le sien (`ScreenDoor.get`, ou la
   * regle est ecrite).
   */
  get?(id: string): Promise<T | null>;
  /** SUIVRE LA FICHE OUVERTE, pour toutes les portes. Repli de `ScreenDoor.observe`. */
  observe?: ObserverUneFiche<T>;
  /**
   * ARCHIVER une fiche, pour TOUTES les portes de l'ecran (repli de
   * `ScreenDoor.archive`). C'est ici qu'un ecran a une seule porte declare son
   * geste, comme il declare deja `load` et `get`.
   */
  archive?(record: T): Promise<void>;
  /** DESARCHIVER une fiche, pour toutes les portes. Repli de `ScreenDoor.unarchive`. */
  unarchive?(record: T): Promise<void>;
  /** SUPPRIMER une fiche, pour toutes les portes. Repli de `ScreenDoor.remove`. */
  remove?(record: T): Promise<void>;
  /** Les types de vues proposes par cet ecran, importes EXPLICITEMENT. */
  types: ViewType[];
  /** Un binding par type de vue : la meme donnee ne se raconte pas pareil partout. */
  bindings: ViewBinding<T>[];
  /** Vue par defaut a la premiere ouverture (avant toute preference utilisateur). */
  defaultView?: ViewId;
  /**
   * LA PILE DE TRI D'ARRIVEE, appliquee a la premiere ouverture.
   *
   * POURQUOI ELLE EXISTE : la declaration d'un ecran peut nommer un tri par
   * defaut (« les plus recents d'abord »), et jusqu'ici rien ne l'appliquait.
   * On ecrivait donc une pile dans le fichier, la CI la validait niveau par
   * niveau contre le catalogue de tri, le studio l'affichait, et l'ecran
   * arrivait sans tri. Une declaration que personne n'applique est une
   * promesse, pas un reglage.
   *
   * ELLE NE VAUT QU'A LA NAISSANCE de l'etat : l'adresse, la session et les
   * favoris passent apres, comme pour la vue d'arrivee. Trier autrement puis
   * revenir ne doit pas ramener le defaut par surprise.
   *
   * LES CLEFS NE SONT PAS VERIFIEES ICI, et c'est assume : le catalogue de tri
   * vit dans le gabarit (`sortOptions` de `ScreenLayout`), que le core ne voit
   * pas. C'est le fichier de declaration qui les verifie, niveau par niveau,
   * avant qu'elles n'arrivent ici. Une clef inconnue ne casse rien : elle trie
   * sur un champ absent, donc elle ne trie pas.
   */
  defaultSort?: readonly SortLevel[];
  /**
   * LES PORTES de l'ecran (gabarit v2) : les jeux de donnees de la zone 1.
   * Des qu'il y en a au moins une, `section` devient l'id de la porte active
   * (verifie contre ce catalogue, contrairement aux sections libres : ici le
   * core LE connait), `setSection` fait la remise a zero de changement de
   * porte, et les champs non declares d'une porte retombent sur les options
   * de l'ecran. Absent ou vide : l'ecran est sa propre porte implicite, et
   * TOUT garde le comportement d'avant les portes.
   */
  doors?: ScreenDoor<T>[];
  /**
   * Filtres de l'ecran (lecture V1 : pastilles a choix unique via `setFilter`,
   * une active en permanence). Les portes declarent le leur (`ScreenDoor.filters`,
   * multi-choix additif) ; celui-ci sert de repli quand la porte n'en a pas.
   */
  filters?: ScreenFilterDef<T>[];
  /**
   * Pastille active au demarrage (lecture V1 seulement). Defaut : la premiere
   * declaree. Ignoree des que des portes existent : derriere une porte, l'etat
   * de depart des filtres est VIDE ("Tout le parc"), le catalogue n'a pas de
   * pseudo-entree "Tous".
   */
  defaultFilterId?: string;
  /**
   * Section active au demarrage : l'entree de menu de la zone 1 (`Tous`,
   * `Camera`, `Lumiere`...). L'ecran passe ensuite `screen.section` a
   * `activeMenuId` et `screen.setSection` a `onMenuSelect` ; il ne tient PLUS
   * cet etat en `useState` chez lui, et il n'a plus besoin d'un effet qui
   * rappelle `reload()` quand la section change (le core recharge tout seul,
   * la section fait partie de la requete).
   *
   * Le core ne CONNAIT pas la liste des sections : elle vit dans les
   * `menuItems` que l'ecran donne au gabarit. Une adresse qui porte une section
   * inconnue rend donc ce que le chargeur rend pour elle, en general rien, et
   * l'ecran affiche son etat vide. C'est honnete, et c'est le seul cout de ne
   * pas dupliquer ce catalogue dans deux endroits.
   *
   * AVEC DES PORTES, cette option designe la porte d'arrivee : elle est alors
   * VERIFIEE (le catalogue est connu ici), et une valeur inconnue retombe sur
   * la premiere porte plutot que sur un ecran vide.
   */
  defaultSection?: string;
  /**
   * CLE DE RECHARGEMENT EXTERNE. Quand la SOURCE que le chargeur lit change en
   * dehors de l'ecran (une lecture partagee entre plusieurs ecrans, rechargee
   * par l'un d'eux), l'app passe ici sa generation : un horodatage, un compteur,
   * n'importe quelle valeur qui change quand la source change.
   *
   * Elle fait partie de la cle de chargement, donc le core recharge TOUT SEUL,
   * exactement comme pour la section. C'est ce qui evite le
   * `useEffect(() => reload(), [source])` maison que la checklist interdit
   * (item 7) : ce n'etait pas un caprice de style, un effet d'app rechargeait en
   * plus du rechargement que le core fait deja apres chaque ecriture reussie,
   * donc deux lectures completes par ecriture.
   *
   * Laisser `undefined` quand le chargeur est la seule source de verite.
   */
  reloadKey?: string | number;
  /**
   * Qui pagine. Defaut `client` : le chargeur rend TOUT et le core fenetre en
   * memoire (comportement historique, inchange).
   *
   * `server` : le chargeur ne rend qu'une page. Il DOIT alors honorer trois
   * champs de la requete qu'il recevait deja sans les lire, sinon la
   * pagination ment : `limit` (taille de page), `after` (curseur, l'id du
   * dernier element rendu precedemment) et `sort` (tri serveur). En pratique,
   * c'est un passe-plat vers `sdk.<famille>.list()`, qui prend exactement ces
   * arguments.
   */
  pagination?: 'client' | 'server';
  /**
   * Mode client : plafond de fenetre (defaut `DEFAULT_LIMIT`).
   * Mode serveur : TAILLE DE PAGE (defaut `DEFAULT_PAGE_SIZE`).
   */
  limit?: number;
}

/** L'etat d'ecran complet, passe tel quel au `ViewHost`. */
export interface ScreenState<T> {
  /* Donnees */
  /** Les enregistrements bruts rendus par `load` (archives compris). */
  records: T[];
  /** Les lignes que la vue active doit rendre (fenetre courante). */
  rows: ProjectedRow[];
  /** Le jeu ORDONNE courant, complet : ce qui alimente le curseur de fiche. */
  orderedIds: string[];
  /** Les colonnes du binding actif. */
  columns: ViewColumn[];
  /** Chargement, erreur, vide, tronque. */
  status: ViewStatus;

  /* Requete */
  /**
   * La fiche demandee par l'adresse existe mais a ete SUPPRIMEE (`deletedAt`) :
   * elle ne se rouvre pas comme une archivee, la vue dit « Fiche supprimee ».
   */
  openDeleted?: boolean;
  query: ScreenQuery;
  setQuery(patch: Partial<ScreenQuery>): void;
  setSearch(value: string): void;
  /**
   * Le catalogue de filtres de la porte active, pret pour l'UI : les pastilles
   * V1 le passent tel quel a `ScreenLayout`, le popover v2 y lit en plus les
   * compteurs (calcules sur la porte brute, insensibles au filtrage courant).
   */
  filters: ScreenFilterOption[];
  filterFields: QueryField[];
  fieldConditions: FieldCondition[];
  saveFieldCondition(condition: FieldCondition): void;
  fieldSortOptions: { key: string; label: string }[];
  /**
   * Lecture V1 : l'id quand EXACTEMENT UN filtre est actif, undefined sinon.
   * Les pastilles historiques s'allument avec ; les tags v2 lisent
   * `activeFilterIds`.
   */
  activeFilterId: string | undefined;
  /** Lecture V1 : REMPLACE l'etat par ce seul filtre (la pastille a choix unique). */
  setFilter(id: string): void;
  /* ---- Multi-filtres additifs (gabarit v2) ---- */
  /** Les filtres actifs, dans l'ordre de pose : ce que les tags 4a rendent. */
  activeFilterIds: string[];
  /** Pose ou retire un filtre du catalogue (popover 3b). Id inconnu : rien. */
  toggleFilter(id: string): void;
  /** Retire UN filtre (la croix d'un tag 4a). Absent : rien. */
  removeFilter(id: string): void;
  /** Retire tous les filtres (l'entree de sortie du popover, "Tout le parc"). */
  clearFilters(): void;
  /* ---- Tri en pile (gabarit v2) ---- */
  /**
   * Les clefs que la porte active sait reellement trier : colonnes triables et
   * cinq roles du socle presents dans au moins un de ses bindings. Le gabarit
   * s'en sert pour retrancher du catalogue global les champs des autres portes.
   */
  sortKeys: string[];
  /** La pile de tri, ORDONNEE (= `query.sortStack`) : ce que les tags de tri rendent. */
  sortStack: SortLevel[];
  /**
   * Un clic du popover de tri : AJOUTE un niveau ascendant en bout de pile si
   * la cle n'y est pas, la RETIRE sinon. L'ordre des clics est l'ordre des
   * niveaux : rien a glisser.
   */
  toggleSort(key: string): void;
  /** Inverse le sens d'UN niveau (le corps d'un tag de tri, le second clic d'un en-tete). */
  invertSort(key: string): void;
  /** Retire UN niveau (la croix d'un tag de tri). Absent : rien. */
  removeSort(key: string): void;
  /** Vide la pile : retour a l'ordre du chargeur ("Ordre d'origine"). */
  clearSort(): void;
  /**
   * Vide les filtres ET la pile de tri EN UN SEUL RENDU : le mot "Effacer
   * filtres" de la zone 4a. Ce n'est PAS `clearFilters()` suivi de
   * `clearSort()` : deux setState en cascade recalculent la liste deux fois
   * (elle clignote) et reecrivent l'adresse deux fois. Rien d'autre ne bouge :
   * ni la recherche, ni la porte, ni la fiche ouverte.
   *
   * IL NE TOUCHE PAS AUX MODES (chantier `modes-menu-trois-points`, 28/09/2026) :
   * un mode n'est plus un tag du tiroir, il change le JEU (le titre le dit) et
   * les filtres le tranchent comme n'importe quel jeu. Vider les filtres en
   * mode Archives laisse donc toutes les archives, pas le travail courant.
   */
  clearAll(): void;
  /* ---- Le MODE ARCHIVES (gabarit v2.2, chantier 2_0) ---- */
  /**
   * LE MODE EST-IL ALLUME. Allume, l'ecran montre les fiches archivees et RIEN
   * d'autre ; eteint, on n'en voit aucune.
   *
   * CE N'EST PAS UN FILTRE, c'est un ETAT A PART de l'ecran : il n'entre pas
   * dans `activeFilterIds` et decide du JEU, puis les filtres, le tri et la
   * recherche le tranchent EXACTEMENT comme le travail courant (decision du
   * fondateur du 28/09/2026 : dans un mode, tout marche comme en mode normal).
   */
  archivesMode: boolean;
  /**
   * Allumer / eteindre le mode. Le chassis prefixe alors le titre
   * (« Archives : Journal ») et remplace « Ajouter » par « Quitter Archives ».
   * Le mode voyage dans l'adresse (`arch=1`) et s'eteint au changement de
   * porte ; `clearAll()` ne l'eteint pas.
   */
  setArchivesMode(on: boolean): void;
  /* ---- Le MODE MODELES (chantier core/modeles) ---- */
  /**
   * LE MODE EST-IL ALLUME. Allume, la liste montre les MODELES de la porte et
   * RIEN d'autre : on les y voit, on les MODIFIE case par case comme une fiche,
   * et on en ajoute.
   *
   * LE JUMEAU DU MODE ARCHIVES, exclusif de lui : il n'entre pas dans
   * `activeFilterIds`, il decide du jeu et les filtres le tranchent ensuite
   * comme ailleurs (un `match` y lit les champs du modele). Il ne DEPEND PAS des donnees : une porte
   * sans un seul modele l'allume quand meme et affiche son etat vide, qui dit
   * comment en fabriquer un premier. Un mode qui se refuserait a s'allumer
   * quand sa liste est vide serait exactement l'ecran qui ne reagit pas au
   * clic.
   */
  modelesMode: boolean;
  /**
   * Allumer / eteindre le mode. Meme chemin que son jumeau : titre prefixe,
   * « Quitter Modeles » a la place d'« Ajouter », adresse (`mod=1`), extinction
   * au changement de porte. Il ETEINT
   * `archivesMode` dans le MEME patch : deux modes qui decident du jeu ne
   * peuvent pas etre allumes ensemble.
   */
  setModelesMode(on: boolean): void;
  /**
   * LES GESTES RARES DE LA FICHE (menu "..." de la rangee 3c, niveau record) :
   * Archiver / Desarchiver et Supprimer, construits depuis les verbes de la
   * porte (`archive`, `unarchive`, `remove`) avec leur repli sur les options
   * de meme nom de l'ecran.
   *
   * RIEN A CABLER : `tools={screen}` les porte, `ScreenLayout` les lit dans
   * `ScreenToolset`. C'est ce qui tient la promesse du chantier, "dans toutes
   * les apps, sur toutes les portes, sans qu'aucune app n'ecrive une ligne" :
   * tant qu'ils n'arrivaient que par une prop explicite, ils etaient ABSENTS
   * (et non grises) sur toute app qui n'y avait pas pense, ce qui est
   * exactement le bouton qui apparait et disparait selon l'app que le brief
   * interdit. La prop de meme nom de `ScreenLayout` ne reste qu'une SURCHARGE.
   *
   * `recordMenuActions` et pas `recordActions` : a cote de `quickActions` et
   * `viewActions`, qui vivent tous deux dans la rangee 3a, un troisieme
   * `*Actions` qui atterrit dans le menu "..." de la 3c induit en erreur. Le
   * nom dit OU ca sort.
   *
   * Trois garanties tenues ici, et nulle part ailleurs :
   * - une porte qui ne declare pas le verbe rend l'entree GRISEE avec sa
   *   raison complete (G16), jamais absente ;
   * - "Archiver" DEVIENT "Desarchiver" quand la fiche ouverte est archivee :
   *   une entree, deux sens, jamais deux dont une grisee ;
   * - apres l'action, le core referme la fiche et recharge la porte ;
   *   l'ecriture rend compte par `saveState`, et un echec se rejoue par
   *   `retrySave` comme n'importe quelle autre ecriture.
   * Tableau VIDE quand aucune fiche n'est ouverte : il n'y a alors rien sur
   * quoi agir, et le menu n'est de toute facon rendu qu'au niveau record.
   */
  recordMenuActions: ScreenAction[];
  /**
   * Monte de un a chaque modele enregistre depuis la fiche ouverte. Le chassis
   * relit la liste des modeles de la porte quand il change (« Depuis un
   * modele ») : c'est un signal, pas une donnee.
   */
  modelesRevision: number;
  /* ---- Le tiroir de la zone 4 (tags + favoris) ---- */
  /**
   * Le tiroir est-il deplie. Il s'ouvre TOUT SEUL au premier tag pose (jamais
   * aux suivants : refermer a la main reste respecte), l'etoile (3b) le
   * commande ensuite, et il se referme au passage d'une porte. Escape et le
   * clic dehors ferment les popovers, JAMAIS lui.
   */
  drawerOpen: boolean;
  /** L'etoile : ouvre / referme le tiroir a la demande. */
  setDrawerOpen(open: boolean): void;
  /**
   * Le bouton « Nouveautes » (3b) est-il allume : seules les fiches marquees
   * « nouveau » restent, dans toutes les vues de la porte. ETAT DU MOTEUR, comme
   * le tiroir : il survit a l'ouverture d'une fiche, au changement de vue, aux
   * filtres, au tri, a la recherche et aux favoris, et il repart ETEINT au
   * passage d'une porte. Il n'entre ni dans l'adresse, ni dans les favoris, ni
   * dans « Reinitialiser » : seul son bouton le commande.
   */
  nouveautesSeules: boolean;
  /** Le bouton « Nouveautes » : allume / eteint le filtre. */
  setNouveautesSeules(on: boolean): void;
  /**
   * Le bouton est-il offert : le reperage des nouveautes est allume pour l'app
   * (`app.<appId>.nouveau`) et on n'est pas en mode Modeles (un modele n'est
   * jamais nouveau). Eteint, `nouveautesSeules` ne filtre plus rien.
   */
  nouveautesOffertes: boolean;
  /* ---- Favoris (gabarit v2, zone 4b + 4c) ---- */
  /**
   * Les favoris de la porte active : ses presets d'abord, puis ceux que
   * l'utilisateur a gardes (pref locale par app+ecran+porte). Vide quand la
   * porte declare `sections: false`.
   */
  favorites: ScreenFavorite[];
  /**
   * Le favori qui correspond EXACTEMENT a l'etat courant (memes filtres, sans
   * ordre ; meme pile de tri, ordonnee), ou null. C'est lui que la zone 4c
   * allume.
   */
  activeFavoriteId: string | null;
  /** Applique un favori : pose ses filtres et sa pile de tri. Id inconnu : rien. */
  applyFavorite(id: string): void;
  /**
   * Garde l'etat courant en favori, sous un nom auto ("Favori 1", "Favori 2",
   * premier numero libre). Sans effet quand `canKeepFavorite` est faux.
   */
  keepFavorite(): void;
  /**
   * Le bouton 4b est-il actif : il y a un etat a garder (au moins un tag) ET
   * cet etat n'est pas deja un favori. Faux = bouton desactive, avec sa raison
   * en title (le gabarit la formule).
   */
  canKeepFavorite: boolean;
  /** Oublie un favori GARDE par l'utilisateur. Les presets de la porte, eux, ne s'oublient pas. */
  forgetFavorite(id: string): void;
  /* ---- Porte active ---- */
  /**
   * La porte active, RESOLUE : id, libelle, placeholder, actions (3a), stats
   * (2c), et si elle se tranche (`sections`). L'ecran la passe au gabarit sans
   * rien recalculer ; changer de porte change donc actions et placeholder par
   * construction. Ecran sans portes : la porte implicite, neutre.
   */
  door: ScreenDoorState;
  /**
   * La SECTION active (zone 1) : l'id de PORTE quand l'ecran en declare, la
   * section libre d'avant sinon. A passer a `ScreenLayout.activeMenuId`.
   * `null` quand l'ecran n'a pas de menu.
   */
  section: string | null;
  /**
   * Change de section : a passer a `ScreenLayout.onMenuSelect`. Recharge (la
   * section est un parametre de requete) et REFERME la fiche ouverte, qui
   * n'appartient pas forcement a la nouvelle section. AVEC des portes, c'est
   * LE changement de porte : recherche, filtres, selection et tiroir sont
   * remis a zero ; les niveaux de tri encore compris par la nouvelle porte
   * survivent avec leur priorite et leur sens.
   */
  setSection(id: string): void;

  /* ---- SELECTION MULTIPLE ET ACTIONS EN LOT (chantier 3_0) ---- */
  /**
   * LES LIGNES CHOISIES, dans l'ordre ou elles ont ete prises.
   *
   * L'INVARIANT, le plus important de ce chantier : cette liste ne contient
   * QUE des ids AFFICHES. Une ligne qui sort de l'ecran (filtre, recherche,
   * rechargement, mode Archives) en sort dans le MEME rendu, et le compte
   * suit. Une selection invisible est une bombe : on croit agir sur trois
   * fiches et on en modifie douze.
   *
   * VIDE hors tableau, toujours (voir `selectionLevel`).
   */
  selection: string[];
  /**
   * Poser la selection : la liste COMPLETE voulue, jamais un delta. Le core
   * la repasse par l'invariant, donc un id qu'on ne voit pas n'y entre pas.
   */
  setSelection(ids: string[]): void;
  /** Vider la selection : la croix de la barre de lot, et `Echap`. */
  clearSelection(): void;
  /**
   * QUELLE SURFACE PORTE LA SELECTION, ou `null` quand aucune ne la porte.
   *
   * `'table'` en vue tableau, `null` PARTOUT AILLEURS : ni liste, ni kanban,
   * ni calendrier, ni fiche. Ce n'est pas une limite d'implementation, c'est
   * un arbitrage : agir sur un lot demande de VOIR les valeurs sur lesquelles
   * on agit, et le tableau est la seule vue qui les montre toutes. Choisir
   * douze fiches dans un kanban, c'est choisir douze titres.
   *
   * COROLLAIRE : QUITTER LE TABLEAU ANNULE LA SELECTION, au meme titre que
   * changer de porte ou de filtre. Une selection gardee en sommeil derriere
   * une vue qui ne la montre pas EST une selection invisible.
   */
  selectionLevel: 'table' | null;
  /**
   * LES CHAMPS MODIFIABLES de la vue active : ce que la fenetre de lot
   * propose. Vide = rien ne s'ecrit ici, et ca se DIT (voir
   * `bulkAvailability.edit`) ; la fenetre ne s'ouvre pas vide.
   */
  bulkEditFields: BulkEditField[];
  /**
   * LES TROIS PRIMITIVES A DOCUMENT UNIQUE. Elles ecrivent UN document et
   * rendent la main, rien de plus : ni boucle, ni progression, ni compte
   * rendu, ni rejeu (tout cela appartient au moteur de lot, qui les appelle).
   *
   * ELLES NE RATTRAPENT AUCUNE ERREUR : elle remonte telle quelle, pour que
   * l'appelant puisse NOMMER la fiche tombee. Une primitive qui avale son
   * echec rend un lot a moitie ecrit indiscernable d'un lot reussi.
   *
   * Elles ne posent PAS d'ecriture optimiste, et c'est un choix : sinon les
   * lignes modifiees quitteraient la vue une par une PENDANT l'ecriture, et
   * l'invariant les retirerait du lot en cours de route. Le rechargement de
   * la porte a la fin du lot fait le rendu complet, d'un seul coup.
   */
  archiveOne(id: string): Promise<void>;
  /** Jumelle d'`archiveOne` : en mode Archives, le lot RESTAURE au lieu d'archiver. */
  unarchiveOne(id: string): Promise<void>;
  removeOne(id: string): Promise<void>;
  /**
   * Ecrire UN champ sur UN document. `fieldKey` est la cle OPAQUE d'un
   * `BulkEditField`, rendue telle quelle (elle porte son espace de cles).
   */
  writeCell(id: string, fieldKey: string, value: unknown): Promise<void>;
  /**
   * CE QUE LA PORTE SAIT FAIRE, par geste : `null` quand il est disponible,
   * sinon LA RAISON, ecrite pour celui qui survole le bouton et pas pour
   * celui qui a ecrit l'app (regle G16 : grise avec sa raison, jamais cache).
   */
  bulkAvailability: {
    archive: string | null;
    unarchive: string | null;
    remove: string | null;
    edit: string | null;
  };
  /**
   * POURQUOI LES GESTES D'ECRITURE SONT ETEINTS SUR CETTE PORTE, ou `null`
   * quand ils sont vivants. Deja applique par le moteur partout ou il decide
   * (les gestes de la fiche, le lot, l'edition en place) ; il sort ICI pour le
   * CHASSIS, qui rend les deux gestes que le moteur ne calcule pas
   * (« Ajouter » et la fleche des modeles) et n'a pas a refaire le calcul.
   *
   * IL PASSE PAR `tools={screen}`, comme `bulkAvailability` : une app ne peut
   * pas le faire disparaitre en oubliant une prop.
   *
   * CE N'EST PAS UNE SECURITE, et ca ne le sera jamais : la securite est dans
   * les rules (`firebase/firestore.rules`). C'est la phrase qu'on affiche pour
   * que l'ecran dise la verite avant que la base ne refuse.
   */
  raisonAcces: string | null;

  /* Vues */
  /** Les entrees du selecteur, grisees avec leur raison quand elles ne sont pas satisfaites. */
  views: ViewOption[];
  viewId: ViewId;
  setView(id: ViewId): void;
  /** La derniere vue de niveau collection : ou la puce de retour ramene. */
  lastCollectionViewId: ViewId;
  /** Le descripteur de la vue active. */
  activeType: ViewType | null;
  /** Le sac libre du binding actif (`ViewBinding.options`). */
  viewOptions: Record<string, unknown>;
  /**
   * CE QUE LA VUE ACTIVE RECOIT, assemble UNE fois par le core. Le `ViewHost`
   * le passe tel quel a la vue ; il sert aussi a calculer `viewActions`.
   */
  viewProps: ViewProps;
  /**
   * Les actions que la vue active propose a la rangee 3a (`ViewType.actions`),
   * deja forcees en SECONDAIRE : le bouton lime unique reste la propriete de
   * l'ecran. A passer a `ScreenLayout` (prop `viewActions`), qui les rend sous
   * les `quickActions` de l'ecran. Tableau vide quand la vue n'en declare pas.
   */
  viewActions: ScreenAction[];

  /* Fiche */
  openId: string | null;
  openRecord(id: string | null): void;
  /**
   * L'APERCU D'UNE VUE HTML EN BROUILLON (clef d'adresse `apercu`, chantier
   * `fiche-suivi`) : l'id du brouillon, `null` hors apercu. Il ne vit qu'au
   * niveau fiche ; quitter la fiche le referme.
   */
  apercu: string | null;
  fermerApercu(): void;
  /**
   * L'ENREGISTREMENT OUVERT, BRUT : celui du jeu en memoire, ou celui lu seul
   * a l'entree a froid. `null` sans fiche, ou introuvable. Ce qu'une vue de
   * fiche qui n'est pas du socle recoit (une vue Canvas, une vue HTML).
   */
  openRecordValue: T | null;
  /**
   * LES ANNUAIRES CHARGES de la porte (par clef de jeu) : de quoi NOMMER ce
   * qu'une fiche designe. Le suivi s'en sert pour annoncer une fiche par le
   * titre qu'elle affiche, jamais par l'id d'une reference
   * (`titreAffichableDeLaFiche`, `@taki/app-runtime`).
   */
  annuaires: Annuaires;
  /**
   * Ouvrir une fiche d'une autre porte de l'ecran (ou y passer, sans `id`),
   * comme depuis une carte liee : la puce de sortie et un seul Retour ramenent
   * d'ou l'on vient. Sert aux surfaces qui ne sont pas des vues (une porte
   * Canvas) ; une vue recoit `ViewProps.onOpenLinked`.
   */
  openLinked(door: string, id?: string, options?: OptionsDeSaut): boolean;
  /**
   * Ouvrir la fiche vide d'une porte de l'ecran depuis une telle surface : la
   * puce (qui dit `origine`), « Annuler » et la fiche creee y ramenent.
   */
  creerDepuis(door: string, origine: string): boolean;
  cursor: Cursor;
  /* ---- LA CREATION EN FICHE (chantier « creation-en-fiche ») ---- */
  /**
   * LE BROUILLON EN COURS, ou `null` (le cas ordinaire). Sa seule presence dit
   * au chassis que la fiche affichee n'existe pas encore : il rend « Creer » et
   * « Annuler » dans la rangee 3, « nouveau » a la place du rang, et la fenetre
   * « Fiche non creee » quand une sortie l'attend.
   *
   * AU PREMIER NIVEAU DE L'ETAT, comme les modes et la barre de lot : c'est ce
   * qui permet a `tools={screen}` de le porter sans qu'aucune app ne recable
   * quoi que ce soit.
   */
  brouillon: BrouillonState | null;
  /**
   * OUVRIR UNE FICHE VIDE, sur la porte visee (la porte active par defaut).
   * Rend `true` quand le brouillon est ouvert (ou l'etait deja), `false` sur un
   * refus : lecture seule, mode Archives ou Modeles, porte sans jeu ou sans
   * action d'ajout tenue par le socle.
   *
   * `defaults` EST CE QUE LE GESTE IMPOSE, pas ce que l'utilisateur a saisi : le
   * « + Ajouter » d'une carte liee impose le champ du lien, et la case se rend
   * verrouillee. Les valeurs par defaut de la porte, elles, arrivent toutes
   * seules et se corrigent.
   *
   * `depuisModele` N'IMPOSE RIEN NON PLUS : ses champs sont une couche de
   * valeurs de DEPART, posee sur les defauts de la porte, et chaque case se
   * corrige comme sur une fiche vide. Refuse en mode Modeles (on n'y cree pas
   * de fiche) et avec `enfantDe` (deux destinations contradictoires).
   */
  ouvrirBrouillon(contexte?: {
    porte?: string;
    defaults?: ValeurImposee[];
    /**
     * LA FICHE VIDE AJOUTERA UN ENFANT A CE MODELE (voir `BrouillonState`).
     * Relaye tel quel depuis l'intention posee par la carte liee.
     */
    enfantDe?: { modele: string; lien: LienParLeChampLie; libelle: string };
    /**
     * LA FICHE VIDE EST PRE-REMPLIE PAR CE MODELE (le menu de « Ajouter ») :
     * ses champs, son nom comme titre quand ses champs n'en portent pas, et ses
     * fiches liees en apercu. « Creer » la cree avec ses fiches liees.
     */
    depuisModele?: ModeleDeFiche;
    /**
     * LA FICHE CREEE REMPLIRA LE CHAMP D'UNE AUTRE FICHE, puis on y reviendra
     * (le « Ajouter » d'un champ de lien, `PendingAppAction.retour`). Refuse
     * avec `enfantDe` et `depuisModele` : un enfant de modele n'a pas d'id de
     * fiche a rendre, et deux origines se contrediraient.
     */
    retour?: RetourDeCreation;
  }): boolean;
  /**
   * Creer la fiche pour de bon (bouton « Creer »). Elle RESTE ouverte ensuite,
   * SAUF quand le brouillon porte un `retour` : l'id cree remplit alors le champ
   * d'origine, et l'on y revient.
   */
  creerBrouillon(): Promise<void>;
  /** Abandonner la creation (bouton « Annuler », `Echap`). Demande avant si quelque chose a ete saisi. */
  annulerBrouillon(): void;
  /** « Quitter sans creer » : la fenetre a repondu oui, la sortie en attente se fait. */
  confirmerSortieDuBrouillon(): void;
  /** « Revenir » : la fenetre se referme, le brouillon reste ouvert tel quel. */
  reprendreBrouillon(): void;
  /**
   * LA SORTIE D'UNE FICHE ARRIVEE PAR UN LIEN, ou `undefined` (le cas courant).
   *
   * Presente, elle REMPLACE ce que dit et ce que fait la puce de sortie de la
   * rangee 3 : « Sony FX9 n°1 » au lieu de « Liste », et le retour a la FICHE du
   * materiel au lieu de la collection des interventions. Une seule puce dans les
   * deux cas, jamais deux (`docs/GABARIT-ECRAN.md`, regle G13 et section 3.5).
   *
   * Un ecran cable sur `tools={screen}` la recoit sans ecrire une ligne : elle
   * vit au premier niveau de l'etat, comme le mode Archives et la barre de lot.
   */
  sortie?: { libelle: string; retour(): void };
  /**
   * LA PORTE OUVERTE DANS UNE FICHE (`ScreenDoor.auClic`, `porteDans.ts`) : le
   * titre de la zone 2, la porte que le menu allume, le retour au debut et
   * l'acces a la fiche parente. `null` hors du chemin. Sur une fiche du
   * chemin, le retour a l'etape d'avant est la `sortie` ci-dessus.
   */
  chemin: CheminDePorte | null;
  /**
   * MARQUER UNE FICHE LUE sur ce poste, sans l'ouvrir. Meme memoire, meme
   * regle de file, meme porte que `openRecord` : c'est le meme geste, sans
   * l'affichage.
   *
   * A APPELER APRES UNE CREATION FAITE DEPUIS L'ECRAN, juste avant `reload()`.
   * Le modele est la boite mail : un message qu'on a envoye soi-meme ne
   * s'affiche pas comme non lu. Sans cet appel, la fiche qu'on vient de saisir
   * revient du serveur avec son lisere et sa pastille, et l'utilisateur doit
   * l'ouvrir pour eteindre ce qu'il a lui-meme ecrit.
   *
   * Sans effet quand l'id est vide ou deja le dernier lu.
   */
  markRead(id: string): void;

  /* Ecriture */
  /**
   * Ecrire un ROLE. Signature FIGEE : le kanban l'appelle depuis un
   * glisser-deposer (`onPatchRecord(id, 'groupKey', valeur)`).
   */
  patchRecord(id: string, role: string, value: Cell): Promise<void>;
  /** Ecrire une COLONNE. Meme optimisme, meme rollback, meme pile d'annulation. */
  patchColumn(id: string, key: string, value: Cell): Promise<void>;
  /**
   * LA VALEUR QUE L'ECRAN MONTRE pour une case, l'ecriture optimiste comprise :
   * celle du sac de `patchRecord` (`'role'`) ou de `patchColumn` (`'column'`)
   * tant qu'elle est en vol, sinon `stockee`. Le meme sac, donc la meme vie :
   * posee au depart, retiree quand la fiche relue la confirme, remise a la
   * valeur de la base si l'ecriture echoue. Sert a ce qui lit une case hors
   * des lignes projetees : la piste des etapes de la rangee 3, dont le champ
   * n'est pas toujours une colonne de la Fiche (chantier `fiche-suivi`).
   */
  valeurEnCours(kind: 'role' | 'column', id: string, key: string, stockee: Cell): Cell;
  /**
   * L'etat d'enregistrement de l'ecran, a passer a `ScreenLayout` : il le rend
   * dans la rangee 3, au niveau `record` uniquement.
   */
  saveState: SaveState;
  /** Rejoue la DERNIERE ecriture echouee, telle quelle. Sans echec en attente : rien. */
  retrySave(): void;
  /**
   * Annule / retablit le dernier pas. Une annulation est une ECRITURE
   * ORDINAIRE : meme chemin, meme optimisme, meme rollback, jamais un mecanisme
   * parallele.
   */
  undo(): void;
  redo(): void;
  canUndo: boolean;
  canRedo: boolean;

  /* Etat jetable, cloisonne par vue */
  viewState: unknown;
  setViewState(state: unknown): void;

  /* Divers */
  optionsOf(role: string): Option[];
  /**
   * Le mode de chargement effectif. Lu par le `ViewHost` pour formuler son
   * bandeau : "affinez la recherche" (client, tout est charge, on en cache) et
   * "il reste des pages" (serveur, la recherche ne porte que sur le charge) ne
   * disent pas la meme chose a l'utilisateur.
   */
  pagination: 'client' | 'server';
  reload(): void;
  /**
   * RELIRE UNE FICHE DU JEU et la poser a sa place (la pose d'une fiche relue
   * apres une ecriture, sans rechargement de la porte ni de la page). Pour ce
   * que le serveur a ecrit hors de l'ecran : un flux lance par un smart bouton.
   * `false` : rien relu (pas de lecture a l'unite, fiche absente, relecture ratee).
   */
  relireLaFiche(id: string): Promise<boolean>;
  loadMore(): void;
  setRange(from: Date, to: Date): void;
  /** La zone 5 : a passer a `ScreenLayout` (prop `scrollRef`), pretee aux vues. */
  scrollRef: RefObject<HTMLElement | null>;

  /**
   * L'ETAT D'ECRAN VU COMME UNE ADRESSE, et l'inverse. Branches tout seuls sur
   * le routeur quand le shell fournit le port d'URL (screenUrl.tsx) : un ecran
   * n'appelle ni l'un ni l'autre, ils restent exposes pour les tests et pour
   * une surface qui voudrait fabriquer un lien.
   */
  toSearch(): Record<string, string>;
  fromSearch(params: Record<string, string | undefined>): void;
}

/* ------------------------------------------------------------------ */
/* Outils internes                                                     */
/* ------------------------------------------------------------------ */

/** Un id de vue valide est namespace. Protege des valeurs venues du storage. */
function asViewId(value: unknown): ViewId | null {
  return typeof value === 'string' && value.includes('.') ? (value as ViewId) : null;
}

interface ScreenSession {
  viewId?: string;
  openId?: string | null;
  lastCollectionViewId?: string;
}

/** Lecture defensive du sessionStorage : indisponible = pas de reprise, jamais d'exception. */
function readSession(key: string): ScreenSession {
  try {
    const raw = window.sessionStorage.getItem(SESSION_PREFIX + key);
    return raw == null ? {} : (JSON.parse(raw) as ScreenSession);
  } catch {
    return {};
  }
}

function writeSession(key: string, value: ScreenSession): void {
  try {
    window.sessionStorage.setItem(SESSION_PREFIX + key, JSON.stringify(value));
  } catch {
    // Storage indisponible (mode prive, quota) : on n'ecrit pas, sans casser.
  }
}

/**
 * LE NIVEAU QUE L'ADRESSE IMPOSE A LA VUE DE DEPART, et rien d'autre.
 *
 * « Un lien partage montre ce qu'il annonce, pas la derniere fiche ouverte sur
 * ce poste » (`docs/VUES-CONTRAT.md` section 5, paragraphe URL) : la reprise de
 * session passe DERRIERE l'adresse, et « l'`id` fait foi sur le niveau ».
 *
 * - `'collection'` : l'adresse dit quelque chose (une porte, une vue, un
 *   filtre, un mode) SANS annoncer de fiche. Aucune vue de niveau fiche ne peut
 *   alors etre choisie, d'ou qu'elle vienne (session, epinglage, preference,
 *   defaut declare). Le defaut visait la session : arriver sur
 *   `?s=<porte>` juste apres avoir quitte une fiche rendait l'ecran au niveau
 *   FICHE, sans `openId`, donc « Fiche introuvable » et un curseur `.. / ..`,
 *   alors que l'adresse n'annoncait aucune fiche.
 * - `null` : l'adresse annonce une fiche (l'`id` fait foi, le niveau est
 *   deja tranche ailleurs), ou elle ne dit RIEN du tout (pas de shell, ou
 *   `/app/<id>` nu) et la reprise de session retrouve alors legitimement la
 *   fiche ou l'on s'etait arrete.
 */
export function niveauImposeParLAdresse(search: ScreenSearch | null): 'collection' | null {
  if (search == null || Object.keys(search).length === 0) return null;
  const id = search['id'];
  return id == null || id === '' ? 'collection' : null;
}

/**
 * L'`id` SORT-IL DANS L'ADRESSE ? Quand la vue active est de niveau fiche, comme
 * toujours ; ET sur une porte CANVAS qui tient une fiche ouverte (chantier
 * `fiche-suivi`) : elle n'a pas de vue du socle, et c'est sa surface qui montre
 * la fiche (`ProprietesDeCanvas.ficheOuverte`). Sans cette seconde regle,
 * l'adresse perdait l'`id` au battement suivant l'arrivee : le moteur croyait
 * la fiche refermee, depilait, et le saut rebondissait.
 */
export function idDansLAdresse(
  niveau: ViewLevel | undefined,
  porteCanvas: boolean,
  openId: string | null,
): boolean {
  if (openId == null) return false;
  return niveau === 'record' || porteCanvas;
}

/**
 * L'ADRESSE D'UNE FICHE LIEE : celle qu'un clic sur une ligne de carte liee
 * ecrit (`openLinked`), donc celle qui se partage et se recharge.
 *
 * TROIS CLEFS AU PLUS, et chacune se justifie : la PORTE cible (`s`, on change
 * de porte, on n'empile pas une fiche dans une fiche), la FICHE (`id`, qui fait
 * foi sur le niveau), et la VUE FICHE DE LA PORTE CIBLE (`view`) quand elle en
 * a une : deux portes n'ont pas forcement le meme catalogue, et ecrire la
 * notre y designerait une vue absente. Sans vue fiche du tout, `view` n'est pas
 * ecrit : l'`id` suffit a dire le niveau, et l'ecriture d'apres reposera
 * l'adresse avec la vue reellement active.
 *
 * `types` est le catalogue de la porte CIBLE, ses replis d'ecran deja resolus.
 */
export function adresseDeFicheLiee(
  doorId: string,
  id: string,
  types: readonly ViewType[],
  /**
   * LE MODE MODELES SUIT LE SAUT (pivot du 10/09/2026), et il n'y a qu'un cas :
   * la ligne d'une carte liee d'un MODELE ouvert, qui ouvre un ENFANT dans la
   * porte liee. L'adresse doit alors dire `mod=1`, sinon la porte d'arrivee
   * montre son jeu ORDINAIRE derriere la fiche, et la puce de sortie ramene a
   * la liste des interventions au lieu de la liste des modeles.
   */
  modeles = false,
  /**
   * LA VUE DE FICHE D'ARRIVEE DE LA PORTE CIBLE (`vueDeFicheDArrivee`, chantier
   * `fiche-suivi`) : absente ou hors du catalogue, la premiere vue de fiche.
   */
  vue?: ViewId,
): ScreenSearch {
  const cible = types.find((type) => type.level === 'record' && type.id === vue)
    ?? types.find((type) => type.level === 'record');
  const mod = writeModelesParam(modeles);
  return {
    s: doorId,
    id,
    ...(cible === undefined ? {} : { view: cible.id }),
    ...(mod === undefined ? {} : { mod }),
  };
}

/**
 * L'ADRESSE D'UNE PORTE LIEE, SANS FICHE : celle qu'ecrit le « + Ajouter » d'une
 * carte liee, qui PASSE dans la porte pour y creer.
 *
 * UNE SEULE CLEF, `s`, PLUS `mod` DEPUIS LE PIVOT DU 10/09/2026. Pas d'`id` (il
 * n'y a rien a ouvrir : la fiche n'existe
 * pas encore), et pas de `view` : c'est le changement de porte qui choisit ou
 * l'on atterrit (`openDoor` garde la vue courante si la porte la propose, sinon
 * la vue d'arrivee declaree), et ecrire une vue ici doublerait cette regle a un
 * endroit qui la connait moins bien.
 *
 * ELLE NE PREND PAS LE CATALOGUE DE LA PORTE CIBLE, contrairement a sa jumelle,
 * et c'est la meme raison vue de l'autre bout : `adresseDeFicheLiee` doit
 * nommer la vue FICHE de la porte visee (deux portes n'ont pas forcement le
 * meme catalogue, et ecrire la notre y designerait une vue absente), alors
 * qu'ici il n'y a pas de fiche a ouvrir. Un catalogue en parametre aurait fait
 * croire qu'il sert a quelque chose.
 *
 * FONCTION PURE ET EXPORTEE EXPRES, comme sa jumelle : l'adresse est ce qui se
 * partage et ce qui se recharge, et une clef de trop y survit a tout.
 */
export function adresseDePorteLiee(doorId: string, modeles = false): ScreenSearch {
  const mod = writeModelesParam(modeles);
  return { s: doorId, ...(mod === undefined ? {} : { mod }) };
}

/**
 * LA COLONNE QUI STOCKE CE CHEMIN, ou `undefined`.
 *
 * `path ?? key`, ET C'EST TOUTE LA REGLE : le CHEMIN DE STOCKAGE est le seul
 * vocabulaire que l'intention de creation, le `linkKey` d'un enfant et
 * l'assemblage partagent (`docs/VUES-CONTRAT.md`, 16.10). Comparer les clefs
 * nues marcherait sur un champ natif (`assetId`) et raterait tous les champs
 * sur-mesure (`customFields.materielId`) : la case du lien resterait alors
 * ouverte et sans nom, sans qu'une seule ligne ne rougisse.
 *
 * DEUX APPELANTS, UNE SEULE RECETTE : le libelle d'une valeur imposee sur un
 * brouillon, et celui de la case du lien d'un ENFANT. Le troisieme endroit qui
 * compare des chemins (`casesVerrouillees`, plus bas) balaie les COLONNES et
 * non les chemins, mais il lit la meme paire `path ?? key` : trois lectures qui
 * divergeraient finiraient par nommer une case qu'une autre laisse ouverte.
 */
function colonneDuChemin<T>(
  columns: readonly Column<T>[] | undefined,
  chemin: string,
): Column<T> | undefined {
  return (columns ?? []).find((colonne) => (colonne.path ?? colonne.key) === chemin);
}

/**
 * LA PORTE OU VIT LE MODELE PARENT D'UN ENFANT OUVERT, ou `null`.
 *
 * A QUOI CA SERT, ET A RIEN D'AUTRE : REFAIRE L'ORIGINE A L'ENTREE A FROID
 * (lien colle, nouvel onglet : aucune etape ne la porte). Un enfant
 * s'adresse tout seul (`?s=interventions&mod=1&id=<modele>/<enfant>`), donc son
 * adresse se recharge et se colle ; mais la provenance, elle, vit dans l'ETAT
 * de l'etape d'historique (`OrigineDEtape`), que ni un lien colle ni un nouvel
 * onglet n'ont. La puce de sortie disait alors « Liste » et ramenait a la liste
 * des MODELES de la porte liee, qui est vide par construction (une intervention n'a pas de modele a
 * elle) : la pire sortie possible, sur la seule fiche qui n'a nulle part
 * d'autre ou aller. Le parent, lui, EST relu (`ModelesPort.get`), et il suffit
 * a redire d'ou l'on vient.
 *
 * LES DEUX CONDITIONS SE COMPLETENT, ET IL FAUT LES DEUX :
 *  - LE JEU DU MODELE (`jeu.family === modele.type`, la famille du repository,
 *    et `jeu.type === modele.targetType`, le sous-type) dit quelles portes
 *    peuvent MONTRER ce modele. Le sous-type seul melangerait deux familles
 *    qui le partagent, la famille seule melangerait deux portes d'un meme pack.
 *  - LE LIEN VERS LA PORTE COURANTE PAR LE MEME CHAMP (`lien.door` et
 *    `lien.by === enfant.linkKey`) dit laquelle, parmi elles, a la CARTE d'ou
 *    cet enfant est sorti. Sans cette seconde condition, une porte
 *    « Materiel » qui ne pointerait pas les interventions serait quand meme
 *    nommee par la puce, et le clic ouvrirait un modele dont la fiche ne
 *    contient pas l'enfant qu'on regardait.
 *
 * LA PREMIERE DANS L'ORDRE DE DECLARATION GAGNE. Deux portes qui remplissent
 * les deux conditions montrent le meme modele par le meme lien : elles sont
 * interchangeables pour cette sortie, et un depart au hasard serait pire qu'un
 * depart stable.
 *
 * `null` EST UNE REPONSE ORDINAIRE (un hote qui ne relaie pas la famille du
 * modele, un ecran monte sans la porte parente) : la puce retombe alors sur
 * « Liste », comme avant ce correctif.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : elle se trompe SANS PLANTER, et son erreur
 * ne se voit qu'au clic, sur une fiche qu'on n'attendait pas.
 */
export function porteParenteDUnEnfant<
  D extends {
    id: string;
    jeu?: { family: string; type?: string } | null;
    links?: readonly LinkedDoor[];
  },
>(
  portes: readonly D[],
  modele: { type?: string; targetType?: string },
  enfant: { linkKey: string },
  porteCourante: string,
): D | null {
  /* SANS FAMILLE, PAS DE CANDIDATE : comparer `undefined` a `undefined`
     nommerait la premiere porte sans jeu venue. */
  if (modele.type == null || modele.type === '') return null;
  return (
    portes.find(
      (porte) =>
        porte.jeu?.family === modele.type &&
        porte.jeu?.type === modele.targetType &&
        (porte.links ?? []).some(
          (lien) =>
            lien.door === porteCourante &&
            !estLienParListe(lien) &&
            lien.by === enfant.linkKey &&
            lien.multiple !== true,
        ),
    ) ?? null
  );
}

/**
 * LA PUCE DE SORTIE D'UNE FICHE ARRIVEE PAR UN LIEN : que dit-elle, et que fait
 * son clic. FONCTION PURE, exportee expres : c'est la seule regle du chantier
 * qui se trompe SANS PLANTER (une puce qui reste apres une fleche « suivant »
 * ramene a un parent qui n'a plus rien a voir avec la fiche affichee).
 *
 * L'ORIGINE APPARTIENT A L'ETAPE D'HISTORIQUE (`OrigineDEtape`,
 * `screenUrl.tsx`) : le navigateur la garde avec l'etape, donc F5, Precedent,
 * Suivant et sauts entre apps la retrouvent, et chaque etape garde la sienne,
 * meme quand une meme fiche est ouverte deux fois depuis deux parents. Un lien
 * copie et un nouvel onglet n'en ont pas : « je viens de quelque part » ne se
 * partage pas (regle du 09/09/2026, `docs/VUES-CONTRAT.md` section 5). Plus
 * rien n'est efface a la main : la puce ne parle que tant que l'etape montre la
 * fiche visee, et c'est cette fonction qui le juge.
 *
 * `null` = pas de puce de lien, et le gabarit retombe alors sur la sortie
 * ordinaire (« Liste », « Tableau »). Trois cas la rendent nulle, et ce sont
 * exactement les trois que le fondateur a nommes : aucune origine (entree par la
 * collection, lien colle, nouvel onglet), on n'est plus au niveau fiche, et on
 * n'est plus sur LA fiche visee (fleche suivant ou precedent, clic sur une autre
 * ligne). Une fleche REMPLACE l'etape : la puce se tait, et elle revient si la
 * fleche ramene a la fiche visee, parce que l'etape derriere est toujours celle
 * du parent. C'est voulu.
 *
 * `geste` : on ne DEPILE que si l'etape qu'on occupe est juste apres celle du
 * parent (`etat.rang === origine.rang + 1`, un remplacement garde le rang), sinon
 * on NAVIGUE vers l'adresse du parent. Jamais de retour a l'aveugle. C'est la
 * meme regle que la sortie ordinaire de fiche (`urlRecordOriginRef`, plus bas).
 */
export function sortieDeFiche(
  origine: OrigineDEtape | null,
  etat: {
    /** Le niveau de la vue active : la puce n'existe qu'au niveau fiche. */
    niveau: ViewLevel;
    /** La porte active. */
    porte: string | null;
    /** La fiche ouverte. */
    id: string | null;
    /** Le rang de l'etape OU L'ON EST, lu au port d'adresse, ou `null` (pas d'historique). */
    rang: number | null;
  },
): { libelle: string; geste: 'depiler' | 'naviguer' } | null {
  if (origine == null) return null;
  if (etat.niveau !== 'record') return null;
  if (etat.id !== origine.id || etat.porte !== origine.porte) return null;
  const geste =
    origine.rang != null && etat.rang != null && etat.rang === origine.rang + 1
      ? 'depiler'
      : 'naviguer';
  return { libelle: origine.titre, geste };
}

/** Suppression logique du socle : `archivedAt` non nul. Les vues ne voient jamais un archive. */
function isArchived(record: unknown): boolean {
  return (record as { archivedAt?: unknown } | null)?.archivedAt != null;
}

/**
 * Deux memoires du "nouveau" disent-elles la meme chose ? Relire le stockage
 * fabrique un objet neuf a chaque fois : sans cette comparaison, chaque event
 * de pref (y compris ceux des autres cles) ferait reprojeter tout l'ecran.
 */
function sameNewMemory(a: NewMemory, b: NewMemory): boolean {
  return (
    a.since === b.since &&
    a.seen.length === b.seen.length &&
    a.seen.every((id, index) => id === b.seen[index])
  );
}

/**
 * Empile une page sur les precedentes SANS doublon.
 *
 * Le dedoublonnage n'est pas de la prudence gratuite : une ecriture concurrente
 * peut deplacer une ligne d'une page a l'autre entre deux appels, et la meme
 * ligne reviendrait deux fois. Deux lignes de meme `id` dans le jeu, et le
 * curseur de fiche ("3 / 47") se met a mentir.
 */
function mergeById<T>(previous: T[], page: T[], getId: (record: T) => string): T[] {
  if (page.length === 0) return previous;
  const seen = new Set(previous.map(getId));
  const fresh = page.filter((record) => !seen.has(getId(record)));
  return fresh.length === 0 ? previous : [...previous, ...fresh];
}

/**
 * Le curseur de pagination pointe un document disparu (`CursorLostError` du
 * SDK). Reconnu par le NOM et non par `instanceof` : `@taki/ui` ne depend pas
 * de `@taki/sdk` et ne doit pas commencer maintenant (le design system ne
 * connait pas la couche donnees, c'est ce qui lui permet de tourner dans
 * un banc d'essai sans Firebase). Le nom fait partie du contrat, il est teste cote SDK.
 */
function isCursorLost(cause: unknown): boolean {
  return (cause as { name?: unknown } | null)?.name === 'CursorLostError';
}

/** Deux tris identiques ? Sert a ne pas repartir de la page 1 pour rien. */
function sameSort(a: ScreenQuery['sort'], b: ScreenQuery['sort']): boolean {
  if (a == null || b == null) return a == b;
  return a.key === b.key && a.dir === b.dir;
}

/** Deux intervalles identiques ? Meme role que `sameSort`. */
function sameRange(a: ScreenQuery['range'], b: ScreenQuery['range']): boolean {
  if (a == null || b == null) return a == b;
  return a.from.getTime() === b.from.getTime() && a.to.getTime() === b.to.getTime();
}

/**
 * LE SEUL ENDROIT OU LA REQUETE CHANGE, `loadMore` mis a part (qui, lui, pose
 * le curseur au lieu de l'annuler). Deux responsabilites, dans cet ordre :
 *
 * 1. LES MIROIRS. `filterIds` / `sortStack` sont l'etat canonique,
 *    `filterId` / `sort` leurs lectures V1 (contract.ts). Chaque patch, d'ou
 *    qu'il vienne (les mutations v2 patchent le canonique, l'en-tete du
 *    tableau patche encore `sort`, un ecran V1 peut patcher `filterId`), est
 *    renormalise ICI : les deux paires ne peuvent pas diverger puisqu'une
 *    seule fonction les ecrit. Le canonique gagne quand un patch porte les
 *    deux a la fois.
 *
 * 2. LA PEREMPTION (mode serveur). Tout champ que le CHARGEUR exploite perime
 *    les pages deja empilees : les changer sans remettre `after` a null
 *    ferait empiler la page suivante d'une requete sous les pages d'une autre
 *    (une page du mois de mars sous celles de fevrier, une page du nouveau
 *    tri sous celles de l'ancien). Cette liste doit donc rester alignee sur
 *    `loadKey` : un champ ajoute a l'un sans l'autre produit exactement ce
 *    melange, en silence. Le tri s'y juge sur le MIROIR (le niveau 1, seul
 *    parametre serveur) : empiler ou inverser un niveau 2 ne perime rien, il
 *    raffine en memoire.
 *
 * 3. LES DEUX MODES. Leur EXCLUSIVITE et la CONFIGURATION que le mode Modeles
 *    exige se normalisent ici, et NULLE PART AILLEURS : c'est le point le plus
 *    bas par lequel toute mutation de `query` transite. Les tenir dans les deux
 *    setters laissait `setQuery({ archivesMode: true })` allumer les DEUX (deux
 *    tags, une adresse `arch=1&mod=1` que la relecture interprete en
 *    « archives » : l'ecran et l'adresse partagee divergeaient), et laissait un
 *    `mod=1` retouche a la main allumer le mode sur une porte qui ne declare
 *    pas les modeles. Un chemin de plus, aujourd'hui ou dans six mois, ne peut
 *    plus produire ni l'un ni l'autre : il passe forcement ici.
 *
 * EXPORTEE POUR ETRE TESTEE, et pas pour etre appelee de l'exterieur : c'est le
 * point de normalisation, donc le seul endroit ou un test peut tenir
 * l'exclusivite des modes et la garde de configuration pour TOUS les chemins a
 * la fois. La tester au niveau des setters ne prouverait rien des autres.
 */
export function nextQuery(
  previous: ScreenQuery,
  patch: Partial<ScreenQuery>,
  server: boolean,
  /**
   * LE MODE MODELES EST-IL ALLUMABLE sur la porte que cet etat vise
   * (`modelesAllumables`) ? Passe en parametre plutot que lu ici, parce que la
   * reponse depend du PORT (un contexte React) et de la porte : `openDoor` la
   * calcule sur la porte CIBLE, les autres appelants sur la porte active.
   */
  modelesAutorises: boolean,
): ScreenQuery {
  const next = { ...previous, ...patch };
  /*
   * L'EXCLUSIVITE : CELUI QUE LE PATCH ALLUME GAGNE. Les deux modes decident du
   * jeu affiche ; allumes ensemble, ils diraient deux listes a la fois. C'est
   * l'INTENTION du patch qui tranche (allumer les archives eteint les modeles,
   * et reciproquement), et non l'ordre des clefs : un appelant qui demande un
   * mode l'obtient, il ne se le fait pas refuser en silence par l'autre.
   *
   * LES DEUX ALLUMES DANS LE MEME PATCH : `arch` gagne, exactement comme dans
   * l'adresse (`readModesParams`). Les liens `arch=1` deja partages ne changent
   * pas de sens, et il n'y a qu'un seul arbitrage a retenir dans tout le
   * produit.
   */
  if (patch.archivesMode === true) next.modelesMode = false;
  else if (patch.modelesMode === true) next.archivesMode = false;
  else if (next.archivesMode && next.modelesMode) next.modelesMode = false;
  /*
   * ET LA CONFIGURATION PASSE APRES L'EXCLUSIVITE : un mode qu'aucune surface
   * ne peut montrer ni eteindre, ou qu'une porte n'a pas allume, ne s'allume
   * pas, quel que soit le chemin (adresse retouchee, `fromSearch`, changement
   * de porte, `setQuery` d'une app).
   */
  if (!modelesAutorises) next.modelesMode = false;
  if ('filterIds' in patch) {
    next.filterId = next.filterIds.length === 1 ? (next.filterIds[0] ?? null) : null;
  } else if ('filterId' in patch) {
    next.filterIds = next.filterId == null ? [] : [next.filterId];
  }
  if ('sortStack' in patch) {
    next.sort = next.sortStack[0] ?? null;
  } else if ('sort' in patch) {
    next.sortStack = next.sort == null ? [] : [next.sort];
  }
  if (!server) return next;
  const stale =
    (('sort' in patch || 'sortStack' in patch) && !sameSort(previous.sort, next.sort)) ||
    ('range' in patch && !sameRange(previous.range, next.range)) ||
    ('section' in patch && previous.section !== next.section) ||
    ('includeArchived' in patch && previous.includeArchived !== next.includeArchived) ||
    // Le MODE ARCHIVES change le JEU que le chargeur rend : empiler la page
    // suivante des archives sous les pages des actifs melangerait deux
    // collections que rien a l'ecran ne distingue.
    ('archivesMode' in patch && previous.archivesMode !== next.archivesMode) ||
    /* LE MODE MODELES N'EST PAS DANS CETTE LISTE, ET C'EST VOULU : il ne change
       RIEN a ce que le chargeur rend. Les modeles ne viennent pas de `load`,
       ils viennent du port des modeles, lu par le socle dans un effet a part.
       Perimer les pages du chargeur en entrant dans le mode rechargerait la
       porte pour un jeu que personne ne regarde, et en ressortir ferait
       repartir de la page 1 une liste qu'on n'avait jamais quittee. La liste de
       `loadKey` porte la meme absence, pour la meme raison. */
    ('limit' in patch && previous.limit !== next.limit) ||
    // LE CHEMIN change le jeu rendu, comme la section.
    ('dans' in patch && !memeDans(previous.dans, next.dans));
  if (stale) next.after = null;
  return next;
}

/** Deux listes d'ids identiques, ORDRE COMPRIS (l'ordre des tags est l'ordre de pose). */
function sameIdList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, index) => b[index] === id);
}

/**
 * L'etat courant correspond-il a ce favori. Les filtres se comparent EN
 * ENSEMBLE (poser Camera puis Optique ou l'inverse, c'est le meme etat : un
 * OU n'a pas d'ordre), la pile de tri EN SEQUENCE (le niveau 1 commande le
 * niveau 2, l'inverser n'est pas le meme ordre).
 */
function matchesFavorite(
  favorite: ScreenFavorite,
  filterIds: string[],
  sortStack: SortLevel[],
): boolean {
  return (
    favorite.filterIds.length === filterIds.length &&
    favorite.filterIds.every((id) => filterIds.includes(id)) &&
    favorite.sortStack.length === sortStack.length &&
    favorite.sortStack.every(
      (level, index) =>
        sortStack[index]?.key === level.key && sortStack[index]?.dir === level.dir,
    )
  );
}

/**
 * Relecture DEFENSIVE d'une liste de favoris venue du localStorage : une pref
 * est du JSON que n'importe quelle version passee de l'app a pu ecrire, on ne
 * lui fait pas confiance. Une entree malformee est JETEE en silence (le favori
 * est un confort, pas une donnee) plutot que de faire planter l'ecran entier.
 */
function sanitizeFavorites(value: unknown): ScreenFavorite[] {
  if (!Array.isArray(value)) return [];
  const kept: ScreenFavorite[] = [];
  for (const item of value) {
    const raw = item as {
      id?: unknown;
      label?: unknown;
      filterIds?: unknown;
      sortStack?: unknown;
    } | null;
    if (raw == null || typeof raw.id !== 'string' || typeof raw.label !== 'string') continue;
    if (!Array.isArray(raw.filterIds) || !Array.isArray(raw.sortStack)) continue;
    if (!raw.filterIds.every((id) => typeof id === 'string')) continue;
    const stack: SortLevel[] = [];
    let broken = false;
    for (const level of raw.sortStack) {
      const cast = level as { key?: unknown; dir?: unknown } | null;
      if (cast == null || typeof cast.key !== 'string' || (cast.dir !== 'asc' && cast.dir !== 'desc')) {
        broken = true;
        break;
      }
      stack.push({ key: cast.key, dir: cast.dir });
    }
    if (broken) continue;
    kept.push({
      id: raw.id,
      label: raw.label,
      filterIds: raw.filterIds as string[],
      sortStack: stack,
    });
  }
  return kept;
}

/**
 * Retire UN patch optimiste du sac, une fois que la donnee fraiche a pris le
 * relais pour cette cible. Laisser l'overlay en place le ferait survivre a la
 * verite : il masquerait la prochaine valeur venue du serveur.
 */
function dropPatch(
  bag: Record<string, Record<string, Cell>>,
  id: string,
  key: string,
): Record<string, Record<string, Cell>> {
  const rowBag = bag[id];
  if (rowBag == null || !(key in rowBag)) return bag;
  const nextRow = { ...rowBag };
  delete nextRow[key];
  const next = { ...bag };
  if (Object.keys(nextRow).length === 0) delete next[id];
  else next[id] = nextRow;
  return next;
}

/** Les roles d'un binding, lus sans le mensonge de type de l'intersection. */
function rolesOf<T>(binding: ViewBinding<T> | undefined): Record<string, FieldMap<T> | undefined> {
  return (binding?.roles ?? {}) as Record<string, FieldMap<T> | undefined>;
}

/**
 * LA COLONNE QU'UNE ECRITURE VISE, cherchee d'abord dans les CHAMPS DE LA
 * PORTE (`fieldColumns`) et pas dans les seules colonnes AFFICHEES (`columns`),
 * comme le tri, la fenetre de lot et la fiche qui la rend. `fieldColumns` est l'union
 * des champs de la porte, `columns` en est un sous-ensemble ordonne : une fiche
 * ecrit un champ que sa vue ne montre pas en colonne.
 *
 * UNE SEULE LECTURE POUR LES TROIS CHEMINS D'ECRITURE (la fiche, le brouillon,
 * l'enfant de modele). Le cas qui l'a montre (recette du 27/10) : la piste des
 * etapes de To-do et des taches de Projets ecrit `status`, que `core.fiche`
 * n'a pas dans ses `columns`, et chaque clic finissait en « n'expose pas
 * set() » sans rien ecrire.
 */
function colonneDEcriture<T>(binding: ViewBinding<T> | undefined, key: string): Column<T> | undefined {
  /* Les colonnes affichees en repli : un binding ecrit a la main peut y poser
     une colonne que ses champs ne portent pas, et elle s'ecrivait avant. */
  const parLaCle = (candidate: Column<T>): boolean => candidate.key === key;
  return binding?.fieldColumns?.find(parLaCle) ?? binding?.columns?.find(parLaCle);
}

/** Les seuls roles qui ont un sens de tri defini par le socle. */
const SORTABLE_ROLES = ['title', 'subtitle', 'status', 'dateStart', 'dateEnd'] as const;

/**
 * LES CLEFS DE TRI D'UNE PORTE, deduites de TOUS ses bindings.
 *
 * Une colonne explicitement non triable n'entre pas. Un role libre de vue
 * custom n'entre pas non plus : le moteur ne sait comparer sans colonne que
 * les cinq roles fermes du contrat. Le Set porte sur la CLEF, jamais sur le
 * libelle : deux champs tous deux nommes « Nom » restent deux tris distincts.
 */
export function sortKeysOf<T>(bindings: readonly ViewBinding<T>[], pagination: 'client' | 'server' = 'client'): string[] {
  const keys = new Set<string>();
  for (const binding of bindings) {
    for (const role of SORTABLE_ROLES) {
      if (rolesOf(binding)[role] !== undefined) keys.add(role);
    }
    for (const column of binding.fieldColumns ?? binding.columns ?? []) {
      /* EN PAGINATION SERVEUR, UNE LECTURE NE SE TRIE PAS : sa valeur n'est
         stockee nulle part, le serveur n'a rien a ordonner (`Column.triServeur`). */
      if (column.sortable !== false && !(pagination === 'server' && column.triServeur === false)) keys.add(column.key);
    }
  }
  return [...keys];
}

/**
 * Garde les niveaux encore compris par une porte, dans leur ordre exact.
 * Les objets sont recopies pour qu'un favori stocke ne devienne jamais l'etat
 * mutable de l'ecran.
 */
export function keepAvailableSortLevels(
  levels: readonly SortLevel[],
  availableKeys: readonly string[],
): SortLevel[] {
  const available = new Set(availableKeys);
  return levels
    .filter((level) => available.has(level.key))
    .map((level) => ({ ...level }));
}

/** Les deux cibles d'ecriture. Deux espaces de cles distincts, jamais melanges. */
type WriteKind = 'role' | 'column';

/** Une demande d'ecriture, telle qu'elle traverse le hook. */
interface WriteRequest {
  kind: WriteKind;
  /**
   * L'ID DE CE QU'ON ECRIT, ET IL A TROIS FORMES depuis le pivot du
   * 10/09/2026 : une FICHE (ou un modele, meme espace d'ids), le BROUILLON
   * (`~nouveau`), ou un ENFANT DE MODELE (`<modeleId>/<enfantId>`,
   * `cheminDEnfant`). `commitWrite` aiguille sur cette seule forme, sans champ
   * de plus : c'est l'id qui dit ou la valeur doit se ranger, et il voyage deja
   * partout (la pile d'annulation, le jeton de sequence, `saveState.target`).
   */
  id: string;
  key: string;
  value: Cell;
}

/**
 * UN PAS D'ANNULATION : de quoi refaire le chemin inverse par le MEME
 * `commitWrite`. On garde l'`id` parce qu'annuler une modification faite sur une
 * autre fiche doit d'abord y ramener.
 *
 * ET UN PAS D'ENFANT N'A RIEN DE PLUS A PORTER : son `id` est le CHEMIN de
 * l'enfant (`<modeleId>/<enfantId>`), donc `undo` rouvre exactement la fiche
 * qui portait la case, et `commitWrite` y reconnait tout seul une ecriture
 * d'enfant. La version d'avant transportait un sac `enfant` (le modele, la
 * relecture, l'editeur) parce que la case vivait dans une carte du PARENT ;
 * elle vit maintenant dans une fiche a elle.
 */
interface WriteStep extends WriteRequest {
  /** La valeur d'avant : ce que `undo` reecrit. */
  before: Cell;
}

/** La cle du jeton de sequence : une ecriture n'annule que SA propre cible. */
function writeKeyOf(kind: WriteKind, id: string, key: string): string {
  return `${kind}:${id}:${key}`;
}

/**
 * LES TROIS GESTES DE FICHE du chantier 2_0 : ils portent sur le DOCUMENT
 * entier, pas sur une case, d'ou un chemin a part de `commitWrite` (aucune
 * valeur d'avant a retenir, donc rien a annuler : archiver n'entre pas dans la
 * pile d'annulation, elle est faite pour les cellules).
 */
type RecordActionKind = 'archive' | 'unarchive' | 'remove';

/**
 * LES REFUS D'ECRITURE PROPRES AU MODE MODELES. Meme adresse au lecteur que
 * `RECORD_ACTION_REASON` : celui qui vient de taper, pas celui qui a ecrit
 * l'app.
 */
const MODELE_WRITE_REASON = {
  /**
   * UN MODELE SANS NOM N'EST PLUS RETROUVABLE : son nom est ce qui le distingue
   * dans le menu de la fleche de « Ajouter » et dans la liste du mode, et deux
   * modeles vides y seraient deux lignes identiques. On refuse donc l'ecriture
   * plutot que d'enregistrer un blanc qu'aucun ecran ne sait rattraper.
   */
  nomVide: 'Un modèle ne peut pas rester sans nom.',
  /** Le modele a quitte la liste entre l'affichage et la validation. */
  disparu: "Ce modèle n'est plus dans la liste.",
  /** Le port a disparu sous les pieds de l'ecriture (hote demonte, test). */
  sansPort: "L'hôte ne fournit pas les modèles.",
  /**
   * ON NE SAIT PAS OU RANGER CETTE VALEUR pour que la case la relise. Voir
   * `champsApresEcriture` : le rangement se PROUVE par la relecture, et quand
   * aucun des deux endroits ne se relit (une colonne dont la valeur est
   * CALCULEE, un role compose, un binding qui lit ailleurs), on refuse.
   *
   * UNE CASE QUI DIT NON EST HONNETE ; une case qui avale la saisie et la perd
   * ne l'est pas. C'est exactement le bug qu'on ferme : la coche verte
   * s'affichait, l'optimisme tombait, et la valeur revenait a l'ancienne,
   * indefiniment, sans une erreur.
   */
  champIntrouvable: 'Ce champ ne se modifie pas depuis un modèle.',
} as const;

/**
 * LA SAISIE D'UNE DATE, RAMENEE EN `Date`, et pourquoi ce detour existe.
 *
 * L'editeur de date du socle emet une chaine `YYYY-MM-DD` (date PURE, jamais un
 * instant : voir `Column.set` dans `contract.ts`), et c'est l'APP qui la
 * retraduit vers son champ metier. En mode Modeles il n'y a pas d'app au bout :
 * la valeur part telle quelle dans les `fields` du modele, a cote de valeurs
 * copiees d'une vraie fiche, qui sont des `Date`. Stocker la chaine ferait un
 * modele dont un champ est un texte la ou le schema attend une date, et la
 * panne n'arriverait que PLUS TARD, a l'instanciation d'une fiche depuis ce
 * modele, loin du geste qui l'a causee.
 *
 * CONSTRUITE SUR LES COMPOSANTES LOCALES, jamais par `new Date('2026-09-08')`,
 * qui lit la chaine en UTC et recule d'un jour a l'ouest de Greenwich. C'est la
 * meme regle que `toDateInput` dans l'autre sens (AGENTS.md section 7 : une
 * date pure ne traverse pas un fuseau).
 *
 * EXPORTEE POUR ETRE TESTEE, et pas par confort : le decalage d'un jour est le
 * mode de panne le plus silencieux du produit (il ne se voit qu'a l'ouest de
 * Greenwich, et seulement sur certains jours), et il ne peut se tenir que par
 * un test qui compare des COMPOSANTES LOCALES.
 */
export function dateDeSaisie(raw: string): Date | null {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (parts == null) return null;
  const date = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * LES FORMES SOUS LESQUELLES UNE SAISIE PEUT SE RANGER (dans les `fields` d'un
 * modele, ou dans la fiche d'un brouillon), DANS L'ORDRE DE PREFERENCE, ET
 * SELON LE RANGEMENT.
 *
 * LE RANGEMENT DICTE LA FORME, il ne se devine pas. A la `racine`, un champ
 * NATIF de la famille porte une `Date` (c'est ce que le SDK a copie, c'est ce
 * que `z.date()` attend) : `[Date, chaine]` pour une date, `[Date, ISO]` pour un
 * datetime, la chaine etant le repli quand la relecture refuse la `Date`. Dans
 * le sac `personnalise` (`customFields`), JAMAIS de `Date` : le sac ne stocke que
 * des valeurs de document, la date pure en `YYYY-MM-DD` et l'instant en
 * `toISOString()` (`versValeurDeSac`, `packages/app-runtime/src/acces.ts`).
 *
 * POURQUOI C'EST UNE REGLE ET PAS UNE ESPERANCE. La relecture d'un champ du sac
 * par l'ecran declare rend une `Date` telle quelle, et `normalizeForEditor` la
 * ramene a la meme chaine que la saisie : la `Date` « se relirait » donc, serait
 * gardee dans `customFields`, et partirait au SDK, que Zod refuse (« La fiche
 * n'a pas pu etre creee »). Un modele dont le sac porte une `Date` casse de meme
 * plus tard, a l'instanciation. La preuve par la relecture choisit l'ETAGE ;
 * elle ne peut pas choisir la FORME d'un sac, qu'on ne lui offre donc pas.
 *
 * `racine` PAR DEFAUT : l'ordre historique, pour les appelants qui n'ont pas de
 * rangement (la forme native, celle qu'une fiche copiee porte deja).
 *
 * TOUT LE RESTE (texte, nombre, valeur de catalogue, `null`) n'a qu'une forme :
 * il se stocke tel quel, comme une fiche le stockerait. Une chaine de date que
 * la forme attendue ne reconnait pas part elle aussi TELLE QUELLE : refuser
 * l'ecriture perdrait la saisie sans rien apprendre a personne, et l'editeur du
 * socle n'emet de toute facon que `YYYY-MM-DD` ou `null`.
 */
export function valeursPourModele(
  editeur: EditorKind,
  value: Cell,
  rangement: RangementDeModele = 'racine',
): readonly unknown[] {
  /* UN CHOIX MULTIPLE SE RANGE EN LISTE, comme la colonne l'ecrirait sur une
     vraie fiche (`colonnesDe`) : la chaine « lun\nven » se relirait pareil,
     mais partirait telle quelle au SDK, qui attend un tableau. */
  if (editeur === 'multiselect') return [clefsDuChoixMultiple(value)];
  /* LES LISTES DES TYPES DU 27/10/2026 (etiquettes, liens multiples, liste de
     controle) se rangent en tableau pour la meme raison, dans la forme que la colonne
     ecrirait (`elementsDeListe`). Un Oui / Non se range en booleen, jamais en
     chaine : c'est ce que le sac relit. */
  if (editeur === 'etiquettes' || editeur === 'liens' || editeur === 'checklist') {
    return [elementsDeListe(editeur, value)];
  }
  if (editeur === 'boolean') {
    const lu = lireBooleen(value);
    return [lu === null ? value : lu];
  }
  const sac = rangement === 'personnalise';
  if (editeur === 'datetime' && value instanceof Date) {
    return sac ? [value.toISOString()] : [value, value.toISOString()];
  }
  if (editeur !== 'date' || typeof value !== 'string') return [value];
  /* Dans le sac, la date pure reste la chaine : aucune `Date` n'est construite,
     donc aucun fuseau n'est traverse. A la racine, composantes LOCALES. */
  if (sac) return [value];
  const date = dateDeSaisie(value);
  return date == null ? [value] : [date, value];
}

/**
 * LES DEUX ENDROITS OU UNE VALEUR PEUT SE RANGER dans les `fields` d'un modele.
 *
 * `racine` : a plat, `fields.reference`. C'est la ou vivent les champs que la
 * FAMILLE porte elle-meme (`name`, `reference`, `status`), ceux que le fichier
 * d'ecran nomme `socle:` et que l'app lit par `natif()`.
 *
 * `personnalise` : sous `fields.customFields.brand`. C'est la ou vivent les
 * champs DECLARES dans `taki.fields.json`, que l'app lit par `lire()`. Le SDK
 * ne retire PAS `customFields` d'un modele (`STANDARD_FIELDS`,
 * `packages/sdk/src/templates.ts`) : le sac traverse la copie IMBRIQUE, et
 * c'est tout le probleme.
 */
export type RangementDeModele = 'racine' | 'personnalise';

/** L'ordre d'essai. Voir `champsApresEcriture` : c'est la relecture qui tranche. */
const RANGEMENTS_DE_MODELE: readonly RangementDeModele[] = ['racine', 'personnalise'];

/**
 * POSE UNE VALEUR a l'un des deux endroits, sans muter l'entree : les `fields`
 * viennent d'un etat React que d'autres relisent (la projection, la ligne
 * ouverte), les muter ferait mentir toutes ces relectures.
 *
 * `customFields` EST RECOPIE CLEF PAR CLEF, jamais remplace : les autres champs
 * du sac (ceux des autres apps du pack, `demoKey`...) survivent intacts, comme
 * du cote des fiches reelles.
 */
export function poserDansModele(
  champs: Record<string, unknown>,
  rangement: RangementDeModele,
  key: string,
  valeur: unknown,
): Record<string, unknown> {
  if (rangement === 'racine') return { ...champs, [key]: valeur };
  const sac = champs['customFields'];
  const precedent =
    typeof sac === 'object' && sac !== null && !Array.isArray(sac)
      ? (sac as Record<string, unknown>)
      : {};
  return { ...champs, customFields: { ...precedent, [key]: valeur } };
}

/**
 * LES `fields` D'UN MODELE APRES UNE ECRITURE DE CASE, ou `null` quand on ne
 * sait pas ou ranger la valeur. C'EST LA REGLE CENTRALE DU MODE MODELES.
 *
 * LE PROBLEME QU'ELLE FERME, EN UNE PHRASE : le socle ecrivait `fields[key]` a
 * PLAT, la porte relisait `asset.customFields?.[key]`, donc la coche verte
 * s'affichait, l'optimisme tombait, et la case revenait a son ancienne valeur.
 * Indefiniment, sans une seule erreur. Un modele cree vide etait entierement
 * inmodifiable.
 *
 * POURQUOI ON NE PEUT PAS SE CONTENTER D'UNE TABLE. Le socle voit la CLEF d'une
 * colonne (`brand`, `reference`) mais pas son origine : `screens.ts` retire le
 * prefixe `socle:` en fabriquant la colonne, et la lecture qui reste est une
 * CLOSURE de l'app (`column.value`, `role.get`). Deviner "les champs declares
 * vont dans le sac" marcherait pour une app et se tromperait en silence
 * chez la premiere qui range autrement.
 *
 * LA REGLE EST DONC UNE PREUVE, PAS UNE DEVINETTE : on POSE la valeur a un
 * endroit, on RELIT la pseudo-fiche PAR LE MEME CHEMIN que la projection, et on
 * ne garde le rangement que si la relecture rend ce qu'on vient d'ecrire.
 * Ecriture et lecture sont alors symetriques PAR CONSTRUCTION : il n'existe pas
 * d'etat ou l'une passe et l'autre pas.
 *
 * LA COMPARAISON SE FAIT DANS L'ESPACE DE L'EDITEUR (`normalizeForEditor`),
 * jamais sur la valeur brute, et c'est la meme regle qui decide deja "rien n'a
 * change" a la fermeture d'un editeur : une date relue en `Date` et la chaine
 * `YYYY-MM-DD` qu'on a tapee sont LA MEME case, un nombre relu en chaine aussi.
 * Comparer brut refuserait des ecritures parfaitement valides.
 *
 * `null` = REFUS, ET C'EST UNE REPONSE. La case dira « ce champ ne se modifie
 * pas depuis un modele » (rangee 3, cadre rouge) au lieu d'avaler la saisie.
 * Les cas concernes sont ceux ou aucun rangement ne se relit : une colonne dont
 * la valeur est CALCULEE, un role COMPOSE, un binding qui lit une source que le
 * modele ne porte pas.
 *
 * SI LA VALEUR EST DEJA EN PLACE, on rend les `fields` INCHANGES plutot que
 * `null` : ce n'est pas un refus, c'est un no-op, et poser une clef parasite
 * "au cas ou" salirait le modele pour rien.
 *
 * LA PREUVE CHOISIT L'ETAGE, PAS LA FORME : pour chaque rangement, les formes
 * essayees sont celles que cet etage peut porter (`valeursPourModele`). Le sac
 * ne recoit jamais de `Date`, meme si sa relecture la ramene a la chaine tapee.
 *
 * UNE LECTURE QUI JETTE VAUT UNE LECTURE QUI NE CORRESPOND PAS : le binding est
 * du code d'app, il peut supposer une forme que la pseudo-fiche n'a pas. On
 * essaie le rangement suivant, on ne fait pas tomber l'ecriture.
 */
/**
 * LE TITRE D'UN BROUILLON, lu par le ROLE `title` du binding : c'est le mot
 * que la fenetre « Fiche non creee » et la fenetre « Nom du modele » montrent.
 * `null` quand le binding n'a pas de titre, ou qu'il est vide.
 */
function titreDuBrouillon<T>(fiche: T, binding: ViewBinding<T> | undefined): string | null {
  const role = binding === undefined ? undefined : rolesOf(binding)['title'];
  if (role === undefined) return null;
  /* UN TITRE QUI REPREND UNE FICHE LIEE PORTE UN ID (ou son nom calcule, que le
     brouillon ne sait pas encore lire) : les fenetres ne nomment pas une fiche
     par un identifiant que personne n'a ecrit. */
  if (titreLieDe(binding) !== undefined) return null;
  let lu: Cell;
  try {
    lu = role.get(fiche);
  } catch {
    return null;
  }
  const texte = lu == null ? '' : String(lu).trim();
  return texte === '' ? null : texte;
}

/**
 * LE BROUILLON SANS SES DEPENDANTS DEVENUS INCOMPATIBLES : le patch du sac
 * (`Column.videAussi`) applique a la fiche a creer. `null` retire la clef, une
 * liste la remplace. Jamais de mutation : le brouillon courant reste intact.
 */
export function sacSansDependants(
  fiche: Record<string, unknown>,
  patch: Readonly<Record<string, readonly string[] | null>>,
): Record<string, unknown> {
  const sac = fiche['customFields'];
  const suivant: Record<string, unknown> = { ...(sac !== null && typeof sac === 'object' ? (sac as Record<string, unknown>) : {}) };
  for (const [clef, valeur] of Object.entries(patch)) {
    if (valeur === null) delete suivant[clef];
    else suivant[clef] = [...valeur];
  }
  return { ...fiche, customFields: suivant };
}

/**
 * LE BROUILLON AVEC SES CALCULS A JOUR : chaque colonne `calcul` du binding
 * (`Column.calcule`, l'evaluateur du data-model) recalcule sur la fiche a
 * creer, et sa valeur se range par la MEME preuve qu'une saisie
 * (`champsApresEcriture`). C'est ce qui montre le total avant « Creer » ; rien
 * n'en part au serveur pour autant, la creation retire les calculs et les
 * recalcule elle-meme (`champsDeCreation`, puis le declencheur). Jamais de
 * mutation ; sans colonne calculee, la meme fiche revient.
 */
export function ficheAvecCalculs<T>(
  fiche: Record<string, unknown>,
  colonnes: readonly Column<T>[] | undefined,
): Record<string, unknown> {
  let suivante = fiche;
  for (const colonne of colonnes ?? []) {
    if (colonne.calcule === undefined) continue;
    const chemin = colonne.path ?? colonne.key;
    const posee = champsApresEcriture({
      champs: suivante,
      key: chemin.startsWith(SAC_SUR_MESURE) ? chemin.slice(SAC_SUR_MESURE.length) : chemin,
      editeur: 'number',
      valeur: colonne.calcule(suivante as unknown as T),
      relire: (essai) => colonne.value(essai as unknown as T),
    });
    if (posee != null) suivante = posee;
  }
  return suivante;
}

export function champsApresEcriture(demande: {
  /** Les `fields` actuels du modele. Jamais mutes. */
  champs: Record<string, unknown>;
  /** La clef metier de la case (celle de la colonne, ou du role). */
  key: string;
  /** Le type d'editeur, qui donne l'espace de comparaison. */
  editeur: EditorKind;
  /** La valeur saisie, telle que la vue l'emet. */
  valeur: Cell;
  /**
   * LA RELECTURE, celle-la MEME que la projection : `column.value` ou
   * `role.get` appliques a la pseudo-fiche du modele. C'est ce parametre qui
   * fait tenir la symetrie ; lui en passer une autre la romprait.
   */
  relire: (champs: Record<string, unknown>) => Cell;
}): Record<string, unknown> | null {
  const { champs, key, editeur, valeur, relire } = demande;
  const vise = normalizeForEditor(editeur, valeur);
  /** Cette version des champs se relit-elle comme la valeur qu'on vient de saisir ? */
  const correspond = (essai: Record<string, unknown>): boolean => {
    let lu: Cell;
    try {
      lu = relire(essai);
    } catch {
      return false;
    }
    return sameCell(normalizeForEditor(editeur, lu), vise);
  };

  if (correspond(champs)) return champs;

  for (const rangement of RANGEMENTS_DE_MODELE) {
    for (const forme of valeursPourModele(editeur, valeur, rangement)) {
      const essai = poserDansModele(champs, rangement, key, forme);
      if (correspond(essai)) return essai;
    }
  }
  return null;
}

/**
 * LES `fields` A REMETTRE APRES UNE ECRITURE ECHOUEE : la SEULE clef ecrite,
 * reposee sur les champs COURANTS.
 *
 * LE DEFAUT QU'ELLE FERME (09/09/2026), ET IL ETAIT SILENCIEUX. Le rollback
 * reposait la map `fields` ENTIERE telle qu'elle etait AU CLIC. Deux ecritures
 * qui se chevauchent sur le meme enfant (on tape k1, puis k2 avant que k1 ne
 * revienne) : k1 echoue, son rollback remet les champs d'avant k1, et k2
 * DISPARAIT DE L'ECRAN alors que sa tache va reussir. On voit alors une coche
 * verte sur une valeur revenue a l'ancienne, et il faut recharger pour
 * apprendre que le serveur, lui, a bien pris k2.
 *
 * ON REPART DONC DES CHAMPS COURANTS, relus au moment de l'echec, et on n'y
 * repose QUE la clef qui vient d'echouer, par la meme preuve que l'ecriture
 * (`champsApresEcriture`) : ecrire et defaire passent par le meme chemin, sinon
 * l'un range la valeur dans le sac et l'autre la remet a la racine.
 *
 * `prouve: false` = ON NE SAIT PAS OU LA REMETTRE, et on rend alors les champs
 * COURANTS TELS QUELS. C'est le moins mauvais des trois : remettre les champs du
 * clic effacerait les autres ecritures, poser la valeur au hasard salirait le
 * modele, et ne rien rendre laisserait l'appelant sans reponse. L'appelant le
 * DIT au journal : l'ecran garde alors une valeur que le serveur n'a pas.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : un rollback ne se voit qu'a l'echec d'une
 * ecriture, c'est-a-dire presque jamais, et il n'y a rien a l'ecran qui dise
 * qu'il s'est trompe.
 */
export function champsApresRollback(demande: {
  /** Les `fields` COURANTS de l'enfant, relus a l'echec. JAMAIS ceux du clic. */
  champs: Record<string, unknown>;
  /** La clef metier de la case qui vient d'echouer. */
  key: string;
  /** L'editeur de cette case, celui qui a servi a l'ecrire. */
  editeur: EditorKind;
  /** La valeur d'AVANT l'ecriture echouee : celle que l'ecran doit retrouver. */
  avant: Cell;
  /** La relecture de la case, la meme que celle de l'ecriture. */
  relire: (champs: Record<string, unknown>) => Cell;
}): { champs: Record<string, unknown>; prouve: boolean } {
  const { champs, key, editeur, avant, relire } = demande;
  const repose = champsApresEcriture({ champs, key, editeur, valeur: avant, relire });
  return repose == null ? { champs, prouve: false } : { champs: repose, prouve: true };
}

/**
 * La raison G16 d'un geste que la porte ne declare pas. Le bouton reste LA,
 * grise, avec sa raison : un geste qui apparait et disparait selon l'app
 * fabrique exactement le "pourquoi je ne peux pas supprimer ici" qu'on veut
 * eviter.
 *
 * ELLE S'ADRESSE A CELUI QUI SURVOLE LE BOUTON, pas a celui qui a ecrit l'app.
 * Le fondateur n'est pas developpeur, l'utilisateur final encore moins : une
 * infobulle qui lui demande de declarer une fonction lui apprend seulement que
 * le message n'est pas pour lui. Elle dit donc UN fait, celui qu'il peut
 * verifier a l'ecran : ce geste n'existe pas ici. La consigne technique, elle,
 * vit dans la jsdoc de `ScreenDoor.archive` et dans le `console.warn` du
 * chemin refuse, la ou un developpeur regarde.
 */
const RECORD_ACTION_REASON: Record<RecordActionKind, string> = {
  archive: "Cet écran ne permet pas d'archiver ses fiches.",
  unarchive: 'Cet écran ne permet pas de désarchiver ses fiches.',
  remove: 'Cet écran ne permet pas de supprimer ses fiches.',
};

/**
 * LE COMPTE DES FICHES LIEES N'A PAS PU SE FAIRE, DONC ON REFUSE.
 *
 * ON NE PROUVE PAS UNE ABSENCE AVEC UNE LECTURE QUI N'A PAS EU LIEU. Le refus
 * « a la Odoo » repose entierement sur un compte serveur : si ce compte tombe
 * (reseau, droits, index), laisser passer la suppression reviendrait a traiter
 * « je ne sais pas » comme « il n'y a rien », et a effacer un materiel qui tient
 * peut-etre douze interventions. Le refus, lui, ne coute qu'un geste a refaire.
 *
 * ELLE EST DISTINCTE DU REFUS ORDINAIRE, qui porte un COMPTE : celle-ci dit
 * qu'on n'a pas pu regarder, l'autre dit ce qu'on a trouve. Les confondre ferait
 * chercher des fiches liees qui n'existent peut-etre pas.
 */
const LIEES_ILLISIBLES = 'Fiches liées illisibles, suppression refusée.';

/**
 * LA RAISON D'UN GESTE D'ECRITURE, quand DEUX choses peuvent l'eteindre : ce
 * que l'UTILISATEUR n'a pas le droit de faire ici (`raisonAcces`), et ce que
 * l'ECRAN ne sait pas faire (le verbe absent, le mode Archives, les modeles
 * non actives). `null` des deux cotes : le geste reste vivant.
 *
 * L'ACCES PASSE DEVANT, ET C'EST LA REGLE, pas un detail d'ecriture. C'est le
 * meme ordre que `raisonDesModeles` (`lib/templates.tsx`) : du plus GENERAL au
 * plus LOCAL, donc de ce que le lecteur ne peut pas changer vers ce qu'il
 * change en un clic. Dire « cet ecran ne permet pas d'archiver » a quelqu'un
 * qui est en lecture seule sur toute la porte l'envoie chercher une cause qui
 * n'est pas la sienne, et il la cherchera longtemps.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : sept gestes du moteur l'appellent (les
 * trois de la fiche, les trois du lot, les modeles), et un test tient cet
 * ordre sans React, sans DOM et sans SDK.
 */
export function raisonDeGeste(
  raisonAcces: string | null,
  raisonLocale: string | null,
): string | null {
  return raisonAcces ?? raisonLocale;
}

/**
 * LES LIGNES, PRIVEES DE TOUTE AFFORDANCE D'ECRITURE. Une copie ou les deux
 * marqueurs que les vues lisent (`row.editable[role]` pour le glisser-deposer
 * du kanban, `row.cells[cle].editable` pour l'edition en place de la fiche)
 * tombent a `false`.
 *
 * POURQUOI PASSER PAR LA LIGNE, ET PAS PAR UN DRAPEAU DE PLUS. Le contrat des
 * vues (`views/contract.ts`) n'a pas de « lecture seule » : une vue decide
 * qu'une case s'edite en lisant CES deux marqueurs, et rien d'autre. Les
 * eteindre ici, c'est parler la langue que les cinq vues du socle et les vues
 * custom comprennent DEJA, sans qu'aucune n'ait une ligne a ajouter. Le refus
 * cote `commitWrite` reste le filet : une vue qui ecrirait quand meme se voit
 * refuser, avec la raison a l'ecran.
 *
 * LES CLEFS SURVIVENT, SEULE LEUR VALEUR TOMBE : rendre `editable: {}` ferait
 * disparaitre l'information « ce role existe » d'une vue qui la lit autrement
 * qu'en comparant a `true`.
 *
 * NE MUTE RIEN : les lignes viennent de la projection memoisee, les muter
 * ferait mentir tout ce qui la relit sans repasser par ici.
 */
export function lignesEnLectureSeule(rows: readonly ProjectedRow[]): ProjectedRow[] {
  return rows.map((row) => {
    const editable: Record<string, boolean> = {};
    for (const name of Object.keys(row.editable)) editable[name] = false;
    const cells: ProjectedRow['cells'] = {};
    for (const [key, cell] of Object.entries(row.cells)) cells[key] = { ...cell, editable: false };
    return { ...row, editable, cells };
  });
}

/* ------------------------------------------------------------------ */
/* La SELECTION et les actions en LOT (chantier 3_0)                   */
/* ------------------------------------------------------------------ */

/**
 * LA SEULE VUE QUI PORTE LA SELECTION. Le test se fait sur l'id, en un seul
 * endroit, et c'est assume : il n'existe pas de "cette vue sait choisir" dans
 * le contrat, et en inventer un ferait croire qu'une vue custom n'a qu'a le
 * declarer pour recevoir une barre de lot qu'elle ne sait pas dessiner.
 *
 * La raison n'est pas technique : agir sur un lot demande de VOIR les valeurs
 * sur lesquelles on agit, et le tableau est la seule vue qui les montre toutes.
 */
const SELECTION_VIEW_ID: ViewId = 'core.tableau';

/**
 * LA SELECTION VIDE, d'identite STABLE. Rendre un `[]` neuf a chaque fois
 * ferait repartir tous les memos qui en dependent a chaque frappe de
 * recherche. NE JAMAIS LA MUTER.
 */
const NO_SELECTION: string[] = [];

/**
 * Le delai apres lequel un lot termine recharge la porte.
 *
 * Le lot ecrit un document a la fois : entre deux, le compteur d'ecritures en
 * vol repasse par zero. Recharger la porte a ce moment-la la rechargerait
 * douze fois pour douze fiches. On attend donc un souffle apres la DERNIERE
 * ecriture ; toute ecriture qui repart dans cette fenetre annule l'attente.
 * Cale large : ce qui se joue entre deux fiches d'un meme lot est un rendu
 * React, jamais un dixieme de seconde.
 */
const BULK_RELOAD_DELAY_MS = 400;

/**
 * Les raisons G16 des gestes de lot que la porte ne sait pas rendre. Les deux
 * verbes manquants reprennent MOT POUR MOT celles de la fiche
 * (`RECORD_ACTION_REASON`) : c'est le meme fait, il ne se raconte pas de deux
 * facons selon l'endroit ou on le lit.
 */
const BULK_REASON = {
  /** Aucune colonne ni aucun role de la vue ne porte d'ecriture. */
  noEditableField: 'Aucun champ de ce tableau ne se modifie.',
  /**
   * Mode Modeles : les trois verbes du lot portent sur des FICHES. Archiver ou
   * supprimer un modele n'est pas le meme geste (il se retire, et un par un,
   * depuis le menu de sa fiche), et modifier douze modeles d'un coup n'a jamais
   * ete demande par personne. UNE SEULE PHRASE POUR LES TROIS : ce qui est vrai
   * ici est le MODE, pas le verbe, et trois formulations feraient croire a
   * trois causes differentes.
   */
  modelesMode: "Les actions de lot ne s'appliquent pas aux modèles.",
} as const;

/**
 * LE NOM FRANCAIS D'UN TYPE D'EDITEUR, annonce DANS l'option du champ. Il est
 * LU DANS LE CATALOGUE DES TYPES (`libelleDuType`, `@taki/data-model`) des
 * que l'editeur porte le nom d'un type, c'est-a-dire presque toujours : le
 * meme champ a le meme mot dans le Studio et dans la fenetre de lot. Seuls
 * restent ici les editeurs sans type du meme nom.
 */
function libelleDEditeur(editor: EditorKind): string {
  if (editor === 'text') return 'texte';
  if (editor === 'multiline') return libelleDuType('textarea');
  /* Une colonne de REFERENCE : ce qui se saisit est une FICHE, pas un id, meme
     si c'est l'id qui se stocke. */
  if (editor === 'lien') return 'fiche liée';
  /* Des liens MULTIPLES (un `lien` qui porte `plusieurs`) : l'editeur n'est
     pas un type du catalogue, il se dit par ce qu'il saisit. */
  if (editor === 'liens') return 'fiches liées';
  return libelleDuType(editor);
}

/** Les éditeurs du lot partagent les contrôles de saisie de la fiche. */
export function editeurModifiableEnLot(_editor: EditorKind): boolean {
  return true;
}

/**
 * Les roles MULTI-VALUES, exclus du lot. Un lot ecrit UNE valeur, et le patch
 * de role n'en garde qu'une : l'appliquer a un role qui en porte plusieurs
 * effacerait les autres en silence, sur douze fiches d'un coup. La fiche paie
 * deja cette lecon (elle refuse d'editer un couloir multi-value) ; ici on ne
 * peut meme pas juger ligne par ligne, puisque la valeur est saisie une fois
 * pour tout le lot.
 *
 * `couleur` et `photo` y sont aussi : leur geste est celui de la pastille de la
 * fiche (une grille de couleurs, un fichier a envoyer), aucun editeur de lot ne
 * sait le rendre, et une photo n'a de sens que pour UNE fiche.
 */
const BULK_EXCLUDED_ROLES = new Set(['badges', 'lane', 'couleur', 'photo']);

/** Libelles de repli des roles du core, quand l'ecran n'en declare pas. */
const BULK_ROLE_LABEL: Record<string, string> = {
  title: 'Nom',
  subtitle: 'Sous-titre',
  avatar: 'Image',
  status: 'Statut',
  groupKey: 'Groupe',
  dateStart: 'Début',
  dateEnd: 'Fin',
};

/*
 * LA CLEF D'UN CHAMP DE LOT est celle de la FICHE, la meme fonction et pas une
 * copie : `fieldKeyOf`, importee de `core/CellEditor.js` en tete de fichier.
 * `WriteKind` ('role' | 'column') est exactement son premier parametre.
 */

/**
 * Les DEUX PREFIXES, lus de la fabrique elle-meme et jamais reecrits a la main.
 * C'est ce qui rend la lecture inverse INCAPABLE de diverger : changer la forme
 * dans `fieldKeyOf` change les deux prefixes ici, dans le meme geste. Ecrits en
 * clair, ils auraient rejoue le defaut qu'on vient de fermer, un cran plus loin.
 */
const ROLE_KEY_PREFIX = fieldKeyOf('role', '');
const COLUMN_KEY_PREFIX = fieldKeyOf('column', '');

/**
 * La lecture inverse. Une clef SANS prefixe est acceptee et lue comme une
 * COLONNE d'abord, un role ensuite (meme priorite que `sortValueOf`) : un
 * appel ecrit a la main ne doit pas echouer sur une convention interne.
 */
function parseBulkFieldKey(fieldKey: string): { kind: WriteKind | null; key: string } {
  if (fieldKey.startsWith(ROLE_KEY_PREFIX)) {
    return { kind: 'role', key: fieldKey.slice(ROLE_KEY_PREFIX.length) };
  }
  if (fieldKey.startsWith(COLUMN_KEY_PREFIX)) {
    return { kind: 'column', key: fieldKey.slice(COLUMN_KEY_PREFIX.length) };
  }
  return { kind: null, key: fieldKey };
}

/**
 * La valeur saisie, ramenee dans le vocabulaire pauvre qui traverse la
 * frontiere core / ecran (`Cell`). Une valeur d'un autre genre (un objet, une
 * fonction) est REFUSEE plutot qu'ecrite : elle ne passerait de toute facon
 * pas la validation Zod du SDK, mais elle le ferait une fiche sur douze et
 * sans dire laquelle.
 */
function asCell(value: unknown): Cell {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (value instanceof Date) return value;
  throw new Error("Cette valeur ne peut pas être enregistrée telle quelle.");
}

/**
 * Les CATALOGUES des colonnes du binding actif, par clef, resolus UNE fois.
 * Ils servent a traduire une valeur en libelle dans les patches optimistes :
 * sans eux, une colonne a catalogue affiche sa valeur stockee le temps de
 * l'aller-retour serveur, puis son libelle au rechargement, ce qui clignote a
 * chaque choix.
 *
 * Seules les colonnes qui declarent `options()` sont resolues : on n'appelle
 * jamais le catalogue d'une colonne que personne n'edite.
 */
/**
 * LES RENDUS PAR VALEUR des colonnes du binding actif (`Column.rendreValeur`),
 * par clef : le troisieme jumeau, pour que la case optimiste se dessine comme
 * la case confirmee (la pastille d'un choix tout de suite, pas un mot nu).
 */
function columnRendus<T>(binding: ViewBinding<T> | undefined): Record<string, (valeur: Cell) => ReactNode> {
  const rendus: Record<string, (valeur: Cell) => ReactNode> = {};
  for (const column of binding?.columns ?? []) {
    const rendre = column.rendreValeur;
    if (rendre !== undefined) rendus[column.key] = (valeur) => rendre.call(column, valeur);
  }
  return rendus;
}

function columnCatalogs<T>(binding: ViewBinding<T> | undefined): Record<string, Option[]> {
  const catalogs: Record<string, Option[]> = {};
  for (const column of binding?.columns ?? []) {
    if (typeof column.options === 'function') catalogs[column.key] = column.options();
  }
  return catalogs;
}

/**
 * Les REFERENCES des colonnes du binding actif, par clef. Le jumeau de
 * `columnCatalogs`, et pour le meme usage : traduire une valeur en libelle dans
 * les patches optimistes. Un catalogue traduit un code en mot, un annuaire
 * traduit un id en nom ; sans eux, la case affiche la valeur stockee le temps de
 * l'aller-retour serveur, puis autre chose au rechargement.
 */
function columnReferences<T>(
  binding: ViewBinding<T> | undefined,
): Record<string, ColumnReference | undefined> {
  const references: Record<string, ColumnReference | undefined> = {};
  for (const column of binding?.columns ?? []) {
    if (column.reference !== undefined) references[column.key] = column.reference;
  }
  return references;
}

/**
 * LE NOM D'UNE FICHE DESIGNEE, ou rien. Ecrit UNE fois : deux endroits du moteur
 * posent la meme question (le patch OPTIMISTE, qui doit montrer le nom choisi
 * sans attendre le serveur, et le sac de repli du TRI, qui doit ranger sur ce
 * nom meme quand la vue active ne rend pas la colonne), et deux copies de « la
 * clef du jeu, puis l'id » divergeraient au premier renommage de la clef.
 *
 * `undefined` COUVRE TROIS FAITS QUI SE VALENT ICI : la colonne n'est pas une
 * reference, l'annuaire n'est pas charge, il ne connait pas cet id. Dans les
 * trois cas il n'y a pas de nom a montrer, et l'id nu reste ce qu'on affiche.
 */
function libelleDeReference(
  reference: ColumnReference | undefined,
  value: Cell,
  annuaires: Annuaires,
): string | undefined {
  if (reference === undefined || value === null || value === undefined || value === '') {
    return undefined;
  }
  const annuaire = annuaires.get(cleDeJeu(reference.family, reference.type));
  const titre = annuaire?.fiches.get(String(value))?.title;
  if (titre !== undefined) return titre;
  /* UNE REFERENCE A REPLI NE LAISSE JAMAIS PASSER SA VALEUR (une personne) :
     rien tant que l'annuaire manque, son mot quand il ne la connait pas. Meme
     regle que la cellule (`referenceDeCellule`). */
  if (reference.repli !== undefined) return annuaire === undefined ? '' : reference.repli;
  return undefined;
}

/**
 * Rejoue les deux sacs de patches sur une ligne deja projetee. Le memo
 * `projected` le fait pour la fenetre courante ; `openRow` en a besoin pour ses
 * deux replis, qui reprojettent depuis le record BRUT.
 */
function replayPatches<T>(
  row: ProjectedRow,
  rolePatches: Record<string, Cell> | undefined,
  cellPatches: Record<string, Cell> | undefined,
  roles: Record<string, FieldMap<T> | undefined>,
  catalogs: Record<string, Option[]>,
  annuaires?: Annuaires,
  rendus?: Record<string, (valeur: Cell) => ReactNode>,
): ProjectedRow {
  let patched = row;
  for (const [role, value] of Object.entries(rolePatches ?? {})) {
    patched = applyRolePatch(patched, role, value, roles[role], annuaires);
  }
  for (const [key, value] of Object.entries(cellPatches ?? {})) {
    patched = applyColumnPatch(patched, key, value, catalogs[key], undefined, rendus?.[key]);
  }
  return patched;
}

/**
 * La valeur COURANTE d'une cible, patch optimiste compris. C'est le `before`
 * d'un pas d'annulation : le record en memoire porte encore la valeur d'avant
 * la derniere ecriture optimiste, s'y fier ferait remonter une valeur perimee.
 * Lecture par `in` et non par `??` : `null` est une valeur legitime.
 */
function currentValueOf(
  bag: Record<string, Record<string, Cell>>,
  id: string,
  key: string,
  fallback: Cell,
): Cell {
  const rowBag = bag[id];
  if (rowBag != null && key in rowBag) return rowBag[key] as Cell;
  return fallback;
}

/* ------------------------------------------------------------------ */
/* Le hook                                                             */
/* ------------------------------------------------------------------ */

export function useScreen<T>(options: UseScreenOptions<T>): ScreenState<T> {
  const { appId, screenId } = options;
  const sessionKey = `${appId}.${screenId}`;
  /* L'ECRAN MONTE DECLARE SON APP au fournisseur d'intentions : c'est elle qui
     SIGNE le « + Ajouter » qu'une carte liee posera (`lib/appAction.tsx`). Sans
     cette signature, une intention nommant la porte « interventions » pouvait
     etre consommee par l'ecran d'une AUTRE app portant une porte du meme nom. */
  useDeclarerAppDeLEcran(appId);
  const pagination = options.pagination ?? 'client';
  const limitDefault =
    options.limit ?? (pagination === 'server' ? DEFAULT_PAGE_SIZE : DEFAULT_LIMIT);
  // Le mode est lu dans des callbacks stables (`loadMore`, `setQuery`, le
  // chemin d'ecriture) : par ref, pour ne pas les recreer a chaque rendu.
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  // Les callbacks et tableaux fournis par l'ecran sont recrees a chaque rendu :
  // on les lit par ref pour ne jamais les mettre en dependance d'effet.
  //
  // ATTENTION, DEUX TEMPS D'ASSIGNATION : ici les valeurs d'ECRAN, puis, une
  // fois la requete declaree et la porte active resolue (plus bas), les
  // valeurs de PORTE prennent le dessus pour le reste du rendu. Tout ce qui
  // consomme ces refs (memos, effets, callbacks) vit apres ce second temps.
  const loadRef = useRef(options.load);
  loadRef.current = options.load;
  const getIdRef = useRef(options.getId);
  getIdRef.current = options.getId;
  const getOneRef = useRef(options.get);
  getOneRef.current = options.get;
  const observeRef = useRef(options.observe);
  observeRef.current = options.observe;
  // Les TROIS VERBES de la fiche (chantier 2_0), meme patron et meme repli que
  // `load` et `get` : les valeurs d'ECRAN ici, celles de la PORTE plus bas.
  const archiveRef = useRef(options.archive);
  archiveRef.current = options.archive;
  const unarchiveRef = useRef(options.unarchive);
  unarchiveRef.current = options.unarchive;
  const removeRef = useRef(options.remove);
  removeRef.current = options.remove;
  const typesRef = useRef(options.types);
  typesRef.current = options.types;
  const bindingsRef = useRef(options.bindings);
  bindingsRef.current = options.bindings;
  const filterDefsRef = useRef(options.filters ?? []);
  filterDefsRef.current = options.filters ?? [];
  /** Les portes declarees. Tableau vide = pas de portes (ecran V1). */
  const doorsRef = useRef(options.doors ?? []);
  doorsRef.current = options.doors ?? [];
  const hasDoors = doorsRef.current.length > 0;
  /**
   * Les options d'ECRAN entieres, pour `openDoor` : les refs ci-dessus portent
   * les catalogues de la porte ACTIVE, or le changement de porte doit resoudre
   * ceux de la porte CIBLE (ses replis compris) dans le meme tick.
   */
  const optionsRef = useRef(options);
  optionsRef.current = options;

  /* ---- L'ADRESSE, lue AVANT tout le reste ---- */

  // Le port d'URL, fourni par le shell. `null` hors shell, ET null pour le
  // SECOND etat d'ecran d'une meme route (une adresse ne decrit qu'un ecran,
  // voir screenUrl.tsx) : dans les deux cas, l'ecran garde exactement son
  // comportement d'avant ce chantier, en memoire.
  const urlPort = useScreenUrlOwnership(sessionKey);
  // L'adresse du PREMIER rendu, figee : c'est elle qui decide de l'etat de
  // depart. Les suivantes passent par l'effet de relecture plus bas.
  const initialSearchRef = useRef<ScreenSearch>(pickScreenSearch(urlPort?.params ?? {}));
  // "L'adresse dit quelque chose" se juge sur le JEU ENTIER, pas cle par cle.
  // Sinon un lien vers une collection (`?view=core.tableau`, sans `id`)
  // laisserait la reprise de session rouvrir la derniere fiche de CE
  // navigateur : le destinataire du lien ne verrait pas ce qu'on lui a envoye.
  const urlHasState = Object.keys(initialSearchRef.current).length > 0;
  const initialSearch = urlHasState ? initialSearchRef.current : null;

  /* ---- La PORTE DE DEPART, resolue une fois pour toutes ---- */

  /**
   * Contrairement a une section libre, une porte venue de l'adresse EST
   * verifiable : le catalogue vit ici, dans les options. Un `?s=` inconnu
   * (faute de frappe, porte retiree depuis) retombe donc sur la premiere
   * porte plutot que sur un ecran vide inexplicable, et c'est cette porte-la
   * qui fournit les catalogues (vues, filtres) dont l'etat de depart a besoin.
   * `null` = pas de portes : tout l'etat de depart se resout comme avant.
   */
  const initialDoorRef = useRef<ScreenDoor<T> | null>(null);
  const initialDoorResolvedRef = useRef(false);
  if (!initialDoorResolvedRef.current) {
    initialDoorResolvedRef.current = true;
    if (hasDoors) {
      const doors = doorsRef.current;
      const asked = initialSearch?.['s'] ?? options.defaultSection ?? null;
      initialDoorRef.current = doors.find((door) => door.id === asked) ?? doors[0] ?? null;
    }
  }
  const initialDoor = initialDoorRef.current;
  const [session] = useState<ScreenSession>(() =>
    readSession(viewMemoryKey(appId, screenId, initialDoor?.id)),
  );
  // Une porte a sa propre memoire. Un choix « Derniere vue » (null) ne
  // reprend jamais un ancien epinglage global d'une autre porte.
  const initialPinned = readPref<string | null>(pinnedViewPrefKey(appId, screenId, initialDoor?.id), null);
  const initialLast = readPref<string | null>(lastViewPrefKey(appId, screenId, initialDoor?.id), null);
  /*
   * LE TRI D'ARRIVEE EST LU CONTRE LA PORTE D'ARRIVEE. Le catalogue declare
   * reste global a l'ecran, mais une clef d'un autre jeu ne doit jamais naitre
   * dans les tags sous son nom technique, y compris apres un rechargement.
  */
  const initialSortKeys = sortKeysOf(initialDoor?.bindings ?? options.bindings, options.pagination ?? 'client');
  const initialSortStack = keepAvailableSortLevels(options.defaultSort ?? [], initialSortKeys);
  /**
   * Le catalogue de vues du PREMIER rendu : celui de la porte de depart, VIDE
   * si elle est un canvas. Sans cette regle, arriver directement sur `?s=<porte
   * canvas>` (lien partage, F5, memoire du dock) resolvait la vue de depart
   * dans le catalogue de l'ECRAN, alors que le rendu et `openDoor` la savent
   * vide : les deux chemins d'arrivee, le clic et l'adresse, divergeaient.
   */
  const initialTypes = typesDePorte(initialDoor, options.types);

  // Le catalogue de vues d'un ecran est fige : la vue collection de repli se
  // calcule une seule fois, au premier rendu (celui de la porte de depart ;
  // `openDoor` recalcule le sien au passage d'une porte).
  const firstCollectionRef = useRef<ViewId | null>(null);
  if (firstCollectionRef.current == null) {
    firstCollectionRef.current =
      vueDeCollection(initialTypes, initialDoor?.bindings ?? options.bindings, null, initialDoor?.defaultView) ??
      initialTypes[0]?.id ??
      FALLBACK_VIEW_ID;
  }
  const firstCollectionId = firstCollectionRef.current;

  /**
   * LA VUE DE DEPART, resolue une fois pour toutes.
   *
   * Toute vue candidate est verifiee contre le CATALOGUE de l'ecran, d'ou
   * qu'elle vienne (adresse, session, epinglage, pref). Une vue inconnue ne
   * doit jamais passer : `activeType` vaudrait null, le `ViewHost` n'aurait
   * rien a monter, et la zone 5 resterait BLANCHE, sans message ni erreur, avec
   * un selecteur ou rien n'est allume. Une faute de frappe dans un lien
   * (`?view=core.list`) suffisait, et la valeur se recopiait ensuite en session
   * et dans l'adresse : l'ecran blanc survivait au rechargement.
   */
  const startViewRef = useRef<ViewId | null>(null);
  if (startViewRef.current == null) {
    // Le catalogue de reference est celui de la PORTE DE DEPART : une vue de
    // session ou d'epinglage qui n'existe pas derriere cette porte est rejetee
    // ici, exactement comme une faute de frappe.
    //
    // ET LE NIVEAU QUE L'ADRESSE ANNONCE EST UN REFUS DE PLUS, au meme endroit
    // (`niveauImposeParLAdresse`) : une adresse qui ne porte pas d'`id`
    // n'annonce AUCUNE fiche, donc aucun candidat de niveau fiche ne passe,
    // fut-il celui de la session. Sans ce refus, la reprise de session
    // repassait DEVANT l'adresse sur le seul niveau.
    const niveauImpose = niveauImposeParLAdresse(initialSearch);
    const known = (candidate: string | null | undefined): ViewId | null => {
      const view = asViewId(candidate);
      if (view == null) return null;
      const type = initialTypes.find((candidateType) => candidateType.id === view);
      if (type == null) return null;
      const binding = (initialDoor?.bindings ?? options.bindings).find((b) => b.type === view);
      if (type.level === 'collection' && unmetRequirement(type, binding) != null) return null;
      return niveauImpose === 'collection' && type.level === 'record' ? null : view;
    };
    let resolved: ViewId | null = null;
    if (initialSearch != null) {
      // L'`id` FAIT FOI SUR LE NIVEAU : une adresse qui porte un element ouvre
      // la fiche, meme si `view` dit autre chose. `view` n'est honore ici que
      // s'il designe lui aussi une vue de niveau fiche (une app peut en avoir
      // une a elle). Le raccourci est ensuite REECRIT dans l'adresse par
      // l'effet d'ecriture : le lien qu'on recopie dit la verite.
      if (initialSearch['id'] != null) {
        const asked = initialTypes.find((type) => type.id === initialSearch['view']);
        resolved =
          asked?.level === 'record'
            ? asked.id
            : (vueDeFicheChoisie(initialTypes, initialDoor, appId, screenId, initialDoor?.id) ?? null);
      }
      resolved ??= known(initialSearch['view']);
    }
    /*
     * TOUS LES CANDIDATS PASSENT PAR `known`, LE DERNIER COMPRIS.
     *
     * `options.defaultView` etait le seul a ne pas etre confronte au catalogue,
     * et c'etait un trou : une app qui declare `defaultView: 'core.kanban'`
     * sans monter le kanban obtenait une vue active qui n'existe pas. Le socle
     * ne trouvait alors aucun type, et la zone 5 rendait un bloc VIDE, sans
     * erreur, sans message, et la valeur se recopiait ensuite dans la session
     * et dans l'adresse.
     *
     * Un defaut hors catalogue est une faute de frappe comme une autre : on
     * retombe sur la premiere vue de collection, qui existe toujours.
     */
    resolved ??=
      (initialDoor == null ? known(session.viewId) : known(initialPinned)) ??
      (initialDoor == null ? known(initialPinned) : known(session.viewId)) ??
      known(initialLast) ??
      known(initialDoor?.defaultView) ??
      known(options.defaultView) ??
      firstCollectionId;
    startViewRef.current = resolved;
  }
  const startView = startViewRef.current;

  const [viewId, setViewIdState] = useState<ViewId>(startView);
  const [lastCollectionViewId, setLastCollectionViewId] = useState<ViewId>(() => {
    // La vue de depart est une COLLECTION : c'est elle la sortie de fiche, pas
    // la premiere vue declaree. Sans ca, un lien vers un kanban rendait une
    // puce de retour marquee "Liste", et refermer une fiche ouverte depuis ce
    // kanban posait l'utilisateur sur une vue qu'il n'avait jamais choisie.
    // `setView` n'est PAS passe par la (la vue de depart ne vient pas d'un
    // clic), donc personne d'autre ne l'ecrit.
    const startLevel = initialTypes.find((type) => type.id === startView)?.level;
    if (startLevel === 'collection') return startView;
    /* UNE FICHE OUVERTE A FROID DANS UN CHEMIN (`dans`, un lien recopie) revient
       sur la vue que le clic impose (le Kanban des taches), pas sur la liste
       d'un poste qui n'a jamais ouvert la porte (recette 2). La memoire de
       l'onglet, elle, garde la main : c'est la vue qu'on regardait. */
    const dansDeDepart = resoudreDans(options.doors ?? [], lireDansParam(initialSearch?.['dans']), {
      section: initialDoor?.id ?? null,
      openId: initialSearch?.['id'] ?? null,
    });
    const vueDuChemin =
      dansDeDepart?.place === 'enfant' && dansDeDepart.parente.auClic?.link.id === dansDeDepart.dans.lien
        ? dansDeDepart.parente.auClic.view
        : undefined;
    const candidate = initialDoor == null ? asViewId(session.lastCollectionViewId)
      : asViewId(session.lastCollectionViewId) ?? vueDuChemin ?? asViewId(initialLast) ?? asViewId(initialPinned);
    return vueDeCollection(initialTypes, initialDoor?.bindings ?? options.bindings, candidate, initialDoor?.defaultView) ?? firstCollectionId;
  });
  const [openId, setOpenId] = useState<string | null>(() => {
    const collectionEpinglee = initialDoor != null && initialPinned === startView &&
      initialTypes.find((type) => type.id === startView)?.level === 'collection';
    const demande = initialSearch != null ? (initialSearch['id'] ?? null)
      : collectionEpinglee ? null : (session.openId ?? null);
    /*
     * `id=~nouveau` A L'ARRIVEE N'OUVRE PAS UNE FICHE : elle n'existe pas, et
     * aucun `get()` ne la trouvera jamais. Le brouillon se ROUVRE (vide) par un
     * effet de montage, quand la porte l'accepte ; sinon l'adresse se reecrit
     * sans `id`. Poser l'id ici afficherait « Fiche introuvable » le temps d'un
     * battement, sur une adresse parfaitement legitime.
     */
    return demande === BROUILLON_ID ? null : demande;
  });
  /**
   * L'ADRESSE D'ARRIVEE DEMANDAIT UN BROUILLON (F5 sur `~nouveau`, lien colle).
   * Consommee UNE fois par l'effet de montage : le sens 2 de l'adresse ne
   * repasse pas par la (son premier tour n'applique rien, par construction).
   */
  /**
   * L'APERCU D'UNE VUE HTML EN BROUILLON (clef d'adresse `apercu`, chantier
   * `fiche-suivi`) : l'id du brouillon que `vue_proposer` a depose, montre sur
   * la fiche ouverte par l'ecran qui sait le lire (`EcranDeclare`). Le moteur
   * ne fait que le PORTER : il vit avec la fiche, et tombe quand on la quitte.
   */
  const [apercu, setApercu] = useState<string | null>(() => initialSearch?.['apercu'] ?? null);
  const apercuRef = useRef(apercu);
  apercuRef.current = apercu;
  const fermerApercu = useCallback((): void => {
    apercuRef.current = null;
    setApercu(null);
  }, []);
  const brouillonDeDepartRef = useRef(
    initialSearch != null && initialSearch['id'] === BROUILLON_ID,
  );

  /* ---- Requete ---- */

  /**
   * LES FILTRES DE DEPART, resolus une fois : ils nourrissent la requete ET le
   * tiroir (ouvert des l'arrivee quand un lien pose des tags : le destinataire
   * doit VOIR ce qui filtre la liste qu'on lui a envoyee, pas le deviner).
   *
   * Tout id venu de l'adresse est VERIFIE contre le catalogue : un `f` inconnu
   * ne filtrerait rien tout en s'affichant comme un etat actif, le pire des
   * deux mondes. Avec des portes, le catalogue est celui de la porte de depart
   * et la liste entiere est gardee (multi-filtres additifs, etat vide par
   * defaut). Sans portes, lecture V1 : UNE pastille active en permanence, le
   * premier id connu de la liste, sinon la pastille par defaut.
   */
  const initialFilterIdsRef = useRef<string[] | null>(null);
  if (initialFilterIdsRef.current == null) {
    const asked = splitFilterIds(initialSearch?.['f']);
    if (initialDoor != null) {
      const defs = !doorHasSections(initialDoor)
        ? []
        : (initialDoor.filters ?? options.filters ?? []);
      initialFilterIdsRef.current = asked.filter((id) => defs.some((def) => def.id === id));
    } else {
      const defs = options.filters ?? [];
      const known = asked.find((id) => defs.some((def) => def.id === id));
      const fallback = options.defaultFilterId ?? defs[0]?.id ?? null;
      const single = known ?? fallback;
      initialFilterIdsRef.current = single == null ? [] : [single];
    }
  }
  const initialFilterIds = initialFilterIdsRef.current;

  /*
   * LES DEUX MODES DE DEPART, LUS D'UN SEUL COUP. `readModesParams` tranche
   * l'exclusivite (`arch` gagne sur `mod`) a un seul endroit, partage avec
   * `fromSearch` : deux copies d'un meme arbitrage divergent au premier
   * ajustement, et celui-ci decide QUELLE LISTE une adresse partagee ouvre.
   */
  const initialModes = readModesParams(initialSearch?.['arch'], initialSearch?.['mod']);

  /*
   * LE PORT DES MODELES, lu ICI parce que `useScreen` est un HOOK : le contexte
   * se lit au rendu, jamais dans une closure d'action. `null` quand l'hote n'en
   * fournit pas, et ce null se rend (entree grisee avec sa raison), il ne se
   * cache pas.
   *
   * IL EST LU AVANT L'ETAT, ET C'EST VOLONTAIRE : l'etat de depart doit deja
   * savoir si le mode Modeles est ALLUMABLE (`modelesAllumables`), sinon un
   * `?mod=1` retouche a la main allumerait le mode le temps d'un rendu sur une
   * porte qui ne declare pas les modeles. Un hook lu plus haut reste un hook lu
   * a chaque rendu, dans le meme ordre : rien d'autre ne change.
   */
  const modelesPort = useModelesPort();

  /**
   * LE MODE ARCHIVES DE DEPART, lu dans l'adresse (`arch=1`). Il n'a rien a
   * verifier contre un catalogue (ce n'est pas un filtre) : toute valeur autre
   * que `1` retombe sur "eteint", c'est `readModesParams` qui le tient.
   *
   * L'ADRESSE FAIT FOI, ET ELLE SEULE : aucune reprise de SESSION, aucune
   * PREF, contrairement a la vue active et a la fiche ouverte. Un mode n'est
   * pas une preference, c'est un endroit ou l'on est.
   *
   * Y COMPRIS L'ADRESSE QUE LE DOCK RESTAURE. Le shell garde la derniere
   * adresse de chaque app visitee dans la page (`lastSearchRef`, TakiShell) et
   * y retourne directement : revenir dans une app par le dock rallume donc le
   * mode qu'on y avait laisse. C'est VOULU, c'est deja le comportement de la
   * fiche ouverte (`id`) et des filtres (`f`), restaures par cette meme
   * memoire, et le tag orange du tiroir dit ou l'on est des l'arrivee. Ce
   * n'est pas une reprise d'etat en douce : c'est une adresse, la meme que
   * celle qu'on aurait collee.
   *
   * SANS PORTES, IL EST IGNORE (chantier 2_0, correctif). Un ecran sans portes
   * ne passe pas `tools` au gabarit : il n'a ni outil filtre, ni tag, ni
   * tiroir, ni "tout effacer". Le mode s'y allumerait sans que RIEN a l'ecran
   * ne le montre ni ne puisse l'eteindre, et la liste resterait vide pour
   * toujours. Un mode qu'aucune surface ne peut eteindre ne doit pas pouvoir
   * s'allumer : `arch` est donc lu comme un parametre qui ne nous concerne
   * pas, exactement comme un `f` inconnu.
   */
  const initialArchives =
    hasDoors &&
    // ... NI DERRIERE UNE PORTE QUI A ABSORBE SES OUTILS, meme raison exactement :
    // un Space Canvas sans 3b n'a ni outil filtre, ni tag, ni tiroir. Le mode
    // s'y allumerait par l'adresse (lien retouche, memoire du dock, bouton
    // Retour), la surface melangerait les archives au jeu vivant, et seule une
    // AUTRE porte permettrait d'en sortir.
    !doorAbsorbsTools(initialDoor) &&
    initialModes.archivesMode;
  /*
   * LE MODE MODELES DE DEPART, sous LA MEME GARDE QUE PARTOUT AILLEURS, ecrite
   * une seule fois (`modelesAllumables`) : des portes, une porte qui garde ses
   * outils, ET la configuration complete des modeles sur cette porte. Un mode
   * qu'on ne peut pas quitter est une nasse ouverte par un simple lien ; un
   * mode que la configuration refuse et que l'adresse allume quand meme est
   * pire, il contourne un NON explicite.
   */
  const initialModeles =
    modelesAllumables({
      hasDoors,
      door: initialDoor,
      port: modelesPort,
      /* SAUF SI L'ADRESSE OUVRE UN ENFANT (pivot du 10/09/2026) : il vit dans la
         porte LIEE, qui n'allume pas les modeles et n'a pas a le faire. Sans
         cette derogation, un F5 sur l'adresse d'un enfant (ou un lien colle)
         perdait `mod=1` au montage : la fiche s'affichait quand meme (tout
         s'aiguille sur le CHEMIN, pas sur le mode), mais l'adresse cessait de
         dire ce qu'elle ouvre des la premiere reecriture. */
      enfant:
        initialSearch?.['id'] != null && lireCheminDEnfant(initialSearch['id']) != null,
    }) && initialModes.modelesMode;

  const [query, setQueryState] = useState<ScreenQuery>(() => ({
    search: '',
    filterIds: initialFilterIds,
    // Le miroir V1, aligne des la naissance (meme regle que `nextQuery`).
    filterId: initialFilterIds.length === 1 ? (initialFilterIds[0] ?? null) : null,
    sortStack: initialSortStack,
    /* LE MIROIR V1, aligne des la naissance, exactement comme `filterId` deux
       lignes plus haut : `nextQuery` tient cette regle a chaque patch, l'etat
       initial doit la tenir aussi, sinon le premier rendu se contredit. */
    sort: initialSortStack[0] ?? null,
    groupBy: null,
    limit: limitDefault,
    after: null,
    range: null,
    includeArchived: false,
    archivesMode: initialArchives,
    modelesMode: initialModeles,
    /* LE CHEMIN DE DEPART (`dans`), seulement s'il tient sur ce catalogue et a
       cette place (`resoudreDans`) : une adresse retouchee ne filtre rien. */
    dans:
      resoudreDans(options.doors ?? [], lireDansParam(initialSearch?.['dans']), {
        section: initialDoor?.id ?? null,
        openId: initialSearch?.['id'] ?? null,
      })?.dans ?? null,
    // Avec des portes, la section EST la porte de depart (deja verifiee).
    // Sans, elle ne peut pas etre verifiee ici : son catalogue vit dans les
    // `menuItems` du gabarit, que le core ne voit pas (cf. `defaultSection`).
    section:
      initialDoor != null
        ? initialDoor.id
        : (initialSearch?.['s'] ?? options.defaultSection ?? null),
  }));

  /* ---- La PORTE ACTIVE, resolue a chaque rendu ---- */

  /**
   * La porte suit `query.section` (c'est la meme information : `?s=` porte
   * l'id de porte). Le repli sur la premiere porte couvre l'etat que rien ne
   * devrait produire (une section ecrite a la main via `setQuery` avec un id
   * inconnu) : le moteur reste sain plutot que de rendre un ecran sans
   * catalogue. Une fois la porte connue, ses catalogues PRENNENT LES REFS :
   * tout ce qui lit `loadRef`, `typesRef`, `bindingsRef` ou `filterDefsRef`
   * plus bas travaille pour la porte active sans le savoir.
   */
  const activeDoor: ScreenDoor<T> | null = hasDoors
    ? (doorsRef.current.find((door) => door.id === query.section) ?? doorsRef.current[0] ?? null)
    : null;
  /*
   * LE MEME PORT (lu plus haut, avant l'etat de depart), LISIBLE DEPUIS UN
   * CALLBACK STABLE. L'ecriture d'une case de modele appelle `update()` :
   * passer le port par la closure de `commitWrite` changerait son identite,
   * donc celle de `patchRecord` et `patchColumn`, a chaque rendu du
   * fournisseur. Meme patron que `raisonAccesRef`.
   */
  const modelesPortRef = useRef(modelesPort);
  modelesPortRef.current = modelesPort;
  /*
   * LE MODE MODELES EST-IL ALLUMABLE SUR LA PORTE ACTIVE. Recalcule a chaque
   * rendu, comme `activeDoor` et `raisonAcces` : il depend de la porte, et
   * memoiser figerait la reponse de la porte precedente.
   *
   * LA REF EST CE QUE `nextQuery` LIT. Toute mutation de `query` passe par lui,
   * et il tranche la la question « ce mode a-t-il le droit d'etre allume ? ».
   * Sans la ref, chaque setter devrait recevoir la reponse en parametre : cinq
   * copies de la meme lecture, donc cinq occasions d'en oublier une.
   */
  /**
   * L'ECRAN OUVRE-T-IL UN ENFANT DE MODELE ? Un seul test, lu partout : l'`id`
   * ouvert est-il un CHEMIN (`<modeleId>/<enfantId>`) ?
   *
   * C'EST L'AIGUILLAGE DU PIVOT DU 10/09/2026, et il ne depend PAS du mode : un
   * enfant se reconnait a son adresse, pas a un drapeau d'ecran. La fiche qu'on
   * projette, le document qu'on ecrit, le curseur des freres et le menu « ... »
   * en decoulent tous, y compris si `mod=1` a saute (une adresse retouchee, un
   * F5 sur un lien tronque) : l'ecran reste juste.
   */
  const cheminOuvert = openId == null ? null : lireCheminDEnfant(openId);
  const enfantOuvert = cheminOuvert != null;
  const modelesAutorises = modelesAllumables({
    hasDoors,
    door: activeDoor,
    port: modelesPort,
    /* LA PORTE LIEE N'ALLUME PAS LES MODELES, et elle n'a pas a le faire : elle
       montre UN enfant, pas une liste de modeles (voir `modelesAllumables`). */
    enfant: enfantOuvert,
  });
  const modelesAutorisesRef = useRef(modelesAutorises);
  modelesAutorisesRef.current = modelesAutorises;
  /*
   * L'ACCES SUR LA PORTE AFFICHEE, lu au meme endroit et pour la meme raison :
   * `useScreen` est un HOOK, un contexte se lit au rendu. `null` = le geste
   * d'ecriture reste vivant, et c'est le defaut : sans port, sans fiche membre
   * lue ou sans jeu declare, on ne grise rien (on ne grise jamais sur une
   * absence d'information ; les rules, elles, refusent quand meme).
   *
   * RECALCULE A CHAQUE RENDU, comme `activeDoor` juste au-dessus et
   * `bulkAvailability` plus bas : il depend de la porte ACTIVE, et memoiser
   * figerait la reponse de la porte precedente.
   */
  const accesPort = useAccesPort();
  const raisonAcces = raisonDeLectureSeule(accesPort, activeDoor?.jeu ?? null);
  /*
   * LA MEME REPONSE, LISIBLE DEPUIS UN CALLBACK STABLE. `commitWrite` refuse
   * l'ecriture en lecture seule ; passer `raisonAcces` par sa closure changerait
   * son identite, donc celle de `patchRecord` et `patchColumn`, a chaque
   * changement de porte. Meme patron que `archiveRef` et les autres verbes.
   */
  const raisonAccesRef = useRef<string | null>(raisonAcces);
  raisonAccesRef.current = raisonAcces;
  /*
   * LE COMPTEUR DES MODELES ENREGISTRES DEPUIS CETTE FICHE. Le chassis lit la
   * liste des modeles UNE fois par porte (la fleche de « Ajouter ») ; sans ce
   * compteur, un modele enregistre depuis le menu de la fiche n'apparaissait
   * qu'apres un rechargement de la page. Il ne porte aucune donnee : il DIT
   * qu'il y a du neuf, et le chassis relit le port.
   */
  const [modelesRevision, setModelesRevision] = useState(0);
  /*
   * LES MODELES DE LA PORTE, quand le MODE MODELES est allume. Le socle les lit
   * LUI-MEME, par le port : aucune app n'a une ligne a ecrire, aucun `load` a
   * changer, aucun `taki.screens.json` a retoucher. C'est ce qui rend le mode
   * disponible partout ou une porte declare son jeu et allume `modeles: true`.
   *
   * QUATRE ETATS ET PAS UN, et chacun repond a une question que l'ecran pose :
   *  - LA LISTE elle-meme ;
   *  - EST-ON EN TRAIN DE LA LIRE. Sans lui, l'ecran passerait de la liste des
   *    fiches a un « aucun modele » le temps de l'aller-retour, et l'etat vide
   *    mentirait a chaque entree dans le mode ;
   *  - POUR QUEL JEU la liste en main a ete lue (`modelesCleLue`). C'est ce qui
   *    ferme le CLIGNOTEMENT du premier rendu : `modelesEnLecture` est leve
   *    dans un EFFET, donc au tout premier rendu apres l'allumage du mode on
   *    avait `loading=false` et `empty=true`, et « Aucun modele pour cette
   *    porte » pouvait se peindre une image. Comparee a la clef courante, cette
   *    clef dit « la liste affichee n'est pas encore celle de ce jeu », des le
   *    rendu, sans attendre l'effet ;
   *  - L'ECHEC de la lecture (`modelesErreur`). Le port qui refuse etait
   *    jusqu'ici MUET (une trace en console, une liste vide) : l'ecran disait
   *    « aucun modele » a quelqu'un qui en a.
   */
  const [modeleFiches, setModeleFiches] = useState<ModeleDeFiche[]>([]);
  const [modelesEnLecture, setModelesEnLecture] = useState(false);
  const [modelesCleLue, setModelesCleLue] = useState<string | null>(null);
  const [modelesErreur, setModelesErreur] = useState<Error | null>(null);
  /**
   * LA LISTE, LISIBLE ET A JOUR DEPUIS UN CALLBACK, ENTRE DEUX RENDUS.
   *
   * ELLE N'EST PAS UN MIROIR DE RENDU (`ref.current = etat` a chaque rendu),
   * et c'est le fond de la correction des ecritures qui se chevauchent : un
   * `setState` pose depuis une fonction asynchrone n'est PAS applique quand le
   * `await` suivant reprend. Deux ecritures rapprochees sur le meme modele
   * repartaient donc toutes les deux des memes `fields`, et la seconde
   * effacait la premiere, cote serveur comme a l'ecran, sans un mot.
   *
   * ELLE EST DONC LA SOURCE, et l'etat la SUIT (voir `majModeles`) : toute
   * ecriture lit ici les champs a jour, y compris celle qui part avant que la
   * precedente n'ait provoque un rendu.
   */
  const modeleFichesRef = useRef<ModeleDeFiche[]>(modeleFiches);
  /**
   * POSER LA LISTE : la ref D'ABORD (synchrone, elle fait foi), l'etat ensuite
   * (le rendu suit). Un seul endroit, pour qu'aucun appelant ne mette a jour
   * l'un sans l'autre.
   */
  const majModeles = useCallback(
    (suivant: (liste: readonly ModeleDeFiche[]) => ModeleDeFiche[]): void => {
      const liste = suivant(modeleFichesRef.current);
      modeleFichesRef.current = liste;
      setModeleFiches(liste);
    },
    [],
  );
  /**
   * LE MODELE PARENT D'UN ENFANT OUVERT, lu par son id (pivot du 10/09/2026).
   *
   * POURQUOI UN SECOND ETAT A COTE DE LA LISTE. Un enfant s'ouvre dans la porte
   * LIEE (`s=interventions&id=<modeleId>/<enfantId>`), et cette porte-la ne
   * liste pas les modeles de materiel : `modeleFiches` y porte autre chose, ou
   * rien. Le parent se lit donc SEUL (`ModelesPort.get`), ce qui est aussi ce
   * qui fait marcher F5 et un lien colle.
   *
   * LES DEUX SOURCES COEXISTENT ET NE SE MELANGENT PAS : sur la fiche du modele
   * (porte Materiel), c'est la LISTE qui porte le document ; sur la fiche d'un
   * enfant (porte liee), c'est celui-ci. Les trois ecritures d'enfant cherchent
   * dans l'ordre liste PUIS parent, et mettent a jour CELUI DES DEUX qui le
   * porte (`majEnfants`), sinon un optimisme se poserait dans un etat que
   * personne ne projette.
   */
  const [modeleParent, setModeleParent] = useState<ModeleDeFiche | null>(null);
  const [modeleParentEnLecture, setModeleParentEnLecture] = useState(false);
  const modeleParentRef = useRef<ModeleDeFiche | null>(modeleParent);
  /** Poser le parent : la ref D'ABORD (elle fait foi entre deux rendus), l'etat ensuite. */
  const majModeleParent = useCallback((suivant: ModeleDeFiche | null): void => {
    modeleParentRef.current = suivant;
    setModeleParent(suivant);
  }, []);
  /**
   * LA FILE D'ATTENTE DES ECRITURES, UNE PAR MODELE.
   *
   * POURQUOI ELLE EXISTE. Le port prend les `fields` EN ENTIER, et depuis le
   * chantier `modeles-enfants` le tableau `children` aussi (contrat de
   * `ModelesPort.update`) : deux ecritures qui se chevauchent envoient donc
   * deux maps COMPLETES, et la seconde ecrase la premiere : cote serveur, et
   * localement. Editer la case A puis la case B avant le retour reseau de A est
   * un geste ordinaire, que l'optimisme ENCOURAGE (la coche de A est deja la
   * quand on clique B) : la valeur de A disparaissait de l'ecran sans un mot.
   *
   * SERIALISER PLUTOT QUE FUSIONNER UN DELTA, et c'est un choix : le port ne
   * sait pas fusionner (une clef pointee ne marche que pour `customFields` cote
   * SDK), donc la seule fusion possible est celle du socle, sur des `fields`
   * frais. Attendre la precedente est ce qui garantit qu'ils le sont, sans rien
   * demander a l'hote.
   *
   * PAR MODELE, ET PAS GLOBALE : deux modeles differents n'ont aucune raison de
   * s'attendre, et une file unique ferait ramer la saisie sur une longue liste.
   *
   * LES QUATRE VERBES DU MODE PASSENT PAR ELLE, et pas seulement l'ecriture
   * d'une case : modifier une case du modele (`ecrireSurModele`), modifier une
   * case d'un de ses ENFANTS (`ecrireSurEnfant`), en ajouter un ou en retirer
   * (`majEnfantsDuModele`) ecrivent tous le MEME document. Une file qui n'en
   * couvrirait que trois laisserait le quatrieme partir avec un tableau
   * `children` perime, et effacer la ligne d'a cote.
   *
   * UN ECHEC N'ARRETE PAS LA FILE : la chaine gardee est celle dont l'erreur est
   * RATTRAPEE, sinon une ecriture refusee bloquerait toutes les suivantes sur ce
   * modele. L'appelant, lui, recoit bien le rejet (c'est `commitWrite` qui
   * affiche le cadre rouge et propose « Reessayer »).
   */
  const chainesDeModeleRef = useRef(new Map<string, Promise<void>>());
  const enfilerSurModele = useCallback(
    (id: string, travail: () => Promise<void>): Promise<void> => {
      const precedente = chainesDeModeleRef.current.get(id) ?? Promise.resolve();
      const tache = precedente.then(travail);
      const suite: Promise<void> = tache.then(
        () => undefined,
        () => undefined,
      );
      chainesDeModeleRef.current.set(id, suite);
      /* On retire l'entree quand plus rien n'attend derriere : la Map ne doit
         pas grandir a chaque case modifiee de la session. */
      void suite.then(() => {
        if (chainesDeModeleRef.current.get(id) === suite) chainesDeModeleRef.current.delete(id);
      });
      return tache;
    },
    [],
  );
  /**
   * UN MODELE PAR SON ID, CHERCHE AUX DEUX ENDROITS QUI PEUVENT LE PORTER : la
   * LISTE du mode (la fiche du modele, porte Materiel), puis le PARENT charge
   * seul (la fiche d'un enfant, porte liee). `null` quand il a disparu.
   *
   * L'ORDRE COMPTE : sur la porte Materiel, la liste est la source de verite et
   * c'est elle que la projection lit. Le parent, lui, n'y est meme pas charge.
   */
  const modeleDId = useCallback((id: string): ModeleDeFiche | null => {
    const dansLaListe = modeleFichesRef.current.find((candidat) => candidat.id === id);
    if (dansLaListe !== undefined) return dansLaListe;
    const parent = modeleParentRef.current;
    return parent != null && parent.id === id ? parent : null;
  }, []);
  /**
   * POSER LES ENFANTS D'UN MODELE, DANS CELUI DES DEUX ETATS QUI LE PORTE.
   *
   * SANS CET AIGUILLAGE, UN OPTIMISME SE PERDRAIT EN SILENCE : ecrire une case
   * d'enfant depuis la fiche de cet enfant met a jour `modeleParent` (la liste ne
   * le contient pas), et le meme geste depuis la fiche du modele met a jour la
   * LISTE (le parent n'est pas charge). Toucher un seul des deux ferait
   * clignoter la valeur : posee, puis reprise a l'ancienne au rendu suivant.
   *
   * LES DEUX SONT TOUCHES QUAND LES DEUX LE PORTENT : c'est le cas transitoire
   * du saut (la liste n'a pas encore ete videe par le changement de porte), et
   * laisser l'un des deux en arriere y ferait revenir l'ancienne valeur.
   */
  const majEnfants = useCallback(
    (modeleId: string, children: EnfantDeModele[]): void => {
      majModeles((liste) =>
        liste.map((candidat) => (candidat.id === modeleId ? { ...candidat, children } : candidat)),
      );
      const parent = modeleParentRef.current;
      if (parent != null && parent.id === modeleId) majModeleParent({ ...parent, children });
    },
    [majModeles, majModeleParent],
  );
  /** La porte se tranche-t-elle (filtres + favoris). Toujours vrai sans portes. */
  const doorSections = doorHasSections(activeDoor);
  // UN SPACE CANVAS N'A AUCUNE VUE DU SOCLE : son catalogue est vide, donc le
  // selecteur (3c) ne se rend pas et rien ne retombe sur les vues de l'ecran.
  // Tenu ici plutot que demande a l'app : une porte custom qui heriterait des
  // vues de l'ecran afficherait un selecteur qui ne mene nulle part.
  const activeTypes = typesDePorte(activeDoor, options.types);
  const activeBindings = activeDoor?.bindings ?? options.bindings;
  const doorId = activeDoor?.id ?? IMPLICIT_DOOR_ID;
  /* DANS UNE FICHE, LE CHAMP DU LIEN NE SE FILTRE PAS : toutes les fiches
     designent deja le parent, un filtre « Projet » n'y trierait rien. */
  const dansDesFiltres = resoudreDans(doorsRef.current, query.dans, { section: query.section, openId: null });
  const cheminSansFiltre = dansDesFiltres?.place === 'enfant' ? cheminDuChampLie(dansDesFiltres.lien) : null;
  const fieldColumns = useMemo(
    () =>
      queryColumns(activeBindings).filter(
        (column) => cheminSansFiltre == null || (column.path ?? column.key) !== cheminSansFiltre,
      ),
    [activeBindings, cheminSansFiltre],
  );
  const [conditionsByDoor, setConditionsByDoor] = useState<Record<string, FieldCondition[]>>({});
  const fieldConditions = useMemo(() => doorSections
    ? (conditionsByDoor[doorId] ?? []).filter((condition) => fieldColumns.some((column) => column.key === condition.field))
    : [], [conditionsByDoor, doorId, doorSections, fieldColumns]);
  const clearFieldConditions = useCallback(() => {
    setConditionsByDoor((previous) => ({ ...previous, [doorId]: [] }));
  }, [doorId]);
  const fieldSortOptions = fieldColumns.filter((column) => column.sortable !== false && !(pagination === 'server' && column.triServeur === false))
    .map((column) => ({ key: column.key, label: column.label }));
  const sortKeys = useMemo(() => sortKeysOf(activeBindings, pagination), [activeBindings, pagination]);
  const sortKeysRef = useRef(sortKeys);
  sortKeysRef.current = sortKeys;
  const sortKeysSignature = sortKeys.join('\u0000');
  /*
   * LE FILET HMR ET MUTATION PROGRAMMATIQUE. L'initialiseur ci-dessus ne
   * rejoue pas quand React conserve l'etat du hook pendant un remplacement de
   * module. Si les bindings changent sous la porte, on retranche avant le
   * prochain affichage les niveaux devenus illisibles, sans toucher aux
   * favoris stockes. `useLayoutEffect` evite une image ou le tag rendrait la
   * clef brute entre le nouveau catalogue et cette correction d'etat.
   */
  useLayoutEffect(() => {
    setQueryState((previous) => {
      const kept = keepAvailableSortLevels(previous.sortStack, sortKeys);
      if (kept.length === previous.sortStack.length) return previous;
      return {
        ...previous,
        sortStack: kept,
        sort: kept[0] ?? null,
        ...(paginationRef.current === 'server' ? { after: null } : {}),
      };
    });
  }, [sortKeysSignature]);
  const activeFilterDefs: ScreenFilterDef<T>[] = !doorSections
    ? []
    : (activeDoor?.filters ?? options.filters ?? []);
  if (activeDoor != null) {
    if (activeDoor.load != null) loadRef.current = activeDoor.load;
    if (activeDoor.get != null) getOneRef.current = activeDoor.get;
    if (activeDoor.observe != null) observeRef.current = activeDoor.observe;
    if (activeDoor.bindings != null) bindingsRef.current = activeDoor.bindings;
    // Les trois verbes de la fiche suivent la porte, comme `load` et `get` :
    // une porte peut savoir archiver ce qu'une autre ne sait pas.
    if (activeDoor.archive != null) archiveRef.current = activeDoor.archive;
    if (activeDoor.unarchive != null) unarchiveRef.current = activeDoor.unarchive;
    if (activeDoor.remove != null) removeRef.current = activeDoor.remove;
  }
  typesRef.current = activeTypes;
  filterDefsRef.current = activeFilterDefs;

  /* ---- Le tiroir de la zone 4 (tags + favoris) ---- */

  /**
   * REPLIE par defaut. Il s'ouvre TOUT SEUL au premier tag pose, et seulement
   * la (la transition "aucun tag -> des tags" vit dans `mutateTags`) : si la
   * simple presence de tags forcait l'ouverture, refermer a la main deviendrait
   * impossible et l'etoile serait un bouton mort des qu'un filtre existe.
   * Il nait OUVERT quand l'adresse d'arrivee pose deja des filtres : le
   * destinataire d'un lien doit voir les tags qui expliquent sa liste. Sur un
   * ecran sans portes il reste ferme quoi qu'il arrive : la pastille par
   * defaut d'un ecran V1 est un etat de repos, pas un tag pose (et son
   * `setFilter` remplace toujours un id par un autre, la transition "aucun
   * tag -> des tags" n'y existe pas).
   *
   * LES MODES N'Y COMPTENT PLUS (28/09/2026) : ils ne posent plus de tag, c'est
   * le titre qui les dit (« Archives : Journal »), pas le tiroir.
   */
  const [drawerOpen, setDrawerOpenState] = useState<boolean>(
    () => hasDoors && initialFilterIds.length > 0,
  );
  /**
   * LE BOUTON « NOUVEAUTES » (3b), du meme moteur que le tiroir : un etat de
   * l'ecran, pas de l'adresse. Eteint au montage et a chaque changement de
   * porte (`openDoor`).
   */
  const [nouveautesSeules, setNouveautesSeulesState] = useState<boolean>(false);

  /* ---- Favoris : les presets de la porte, puis ceux gardes par l'utilisateur ---- */

  /**
   * La liste gardee vit en pref locale PAR PORTE (`favoritesPrefKey`), par la
   * MEME mecanique que les prefs de vues : `usePref` ecoute `taki:pref-change`,
   * donc tout ce qui touche la liste DANS LE MEME DOCUMENT (le hub Parametres,
   * un autre ecran monte) la resynchronise ici sans rien cabler. L'event ne
   * franchit pas l'onglet : un second onglet verra le changement a son
   * prochain rechargement. Le changement de porte change la cle, et `usePref`
   * recharge la bonne liste tout seul.
   */
  /**
   * LA PORTE DU MOMENT, lisible depuis un callback STABLE. Meme patron que
   * `typesRef` et `lastCollectionRef`, et pour la meme raison de bord : elle
   * est aussi avancee dans `openDoor`, parce que la suite du MEME geste doit
   * deja raisonner avec la nouvelle porte. Le cas qui l'exige : le bouton
   * Suivant du navigateur vers la fiche d'une AUTRE porte, ou `fromSearch`
   * enchaine `openDoor` puis `openRecord` dans le meme tick. Sans cette ref,
   * `markReadRef.current` fermait sur le `doorId` du rendu courant et marquait
   * la fiche lue sous l'ANCIENNE porte : elle restait nouvelle dans la sienne.
   */
  const doorIdRef = useRef(doorId);
  doorIdRef.current = doorId;
  const [storedFavoritesRaw, setStoredFavorites] = usePref<ScreenFavorite[]>(
    favoritesPrefKey(appId, screenId, doorId),
    [],
  );
  // Une pref est du JSON ecrit par n'importe quelle version passee : relecture
  // defensive, une entree malformee tombe sans bruit.
  const storedFavorites = useMemo(() => sanitizeFavorites(storedFavoritesRaw), [storedFavoritesRaw]);

  /**
   * Presets D'ABORD (l'ordre de la porte est un choix d'app), gardes ensuite
   * (l'ordre d'enregistrement). Un id garde qui entre en collision avec un
   * preset est ignore : deux favoris du meme id rendraient `applyFavorite` et
   * l'allumage de la zone 4c ambigus.
   */
  const presetsSource = doorSections ? (activeDoor?.presets ?? null) : null;
  const favorites = useMemo<ScreenFavorite[]>(() => {
    if (!doorSections) return [];
    const presets = presetsSource ?? [];
    const seen = new Set(presets.map((preset) => preset.id));
    const merged = [...presets];
    for (const favorite of storedFavorites) {
      if (seen.has(favorite.id)) continue;
      seen.add(favorite.id);
      merged.push(favorite);
    }
    return merged;
  }, [doorSections, presetsSource, storedFavorites]);

  /** Le favori qui decrit EXACTEMENT l'etat courant : celui que la 4c allume. */
  const activeFavoriteId = useMemo<string | null>(
    () =>
      fieldConditions.length > 0 ? null : favorites.find((favorite) => matchesFavorite(favorite, query.filterIds, query.sortStack))
        ?.id ?? null,
    [favorites, query.filterIds, query.sortStack, fieldConditions],
  );

  /**
   * "Garder" (4b) n'a de sens que s'il y a un ETAT a garder (au moins un tag)
   * et qu'il n'est pas DEJA un favori : garder deux fois le meme etat ferait
   * deux pastilles indiscernables dont une seule s'allumerait.
   */
  const canKeepFavorite =
    fieldConditions.length === 0 &&
    doorSections &&
    activeFavoriteId == null &&
    (query.filterIds.length > 0 || query.sortStack.length > 0);

  // Lus par les callbacks stables (`keepFavorite`, `applyFavorite`...) : par
  // ref, comme tout ce que l'ecran recree a chaque rendu.
  const favoritesRef = useRef(favorites);
  favoritesRef.current = favorites;
  const storedFavoritesRef = useRef(storedFavorites);
  storedFavoritesRef.current = storedFavorites;
  const setStoredFavoritesRef = useRef(setStoredFavorites);
  setStoredFavoritesRef.current = setStoredFavorites;
  const canKeepFavoriteRef = useRef(canKeepFavorite);
  canKeepFavoriteRef.current = canKeepFavorite;

  /* ---- La pastille "Nouveau" : ce que ce poste a deja vu ---- */

  /**
   * LE REGLAGE, par APP (`app.<appId>.nouveau`), ALLUME PAR DEFAUT : une cle
   * absente vaut "allume", et une valeur abimee aussi (seul un `false` franc
   * eteint). Eteint, le core ne calcule RIEN : pas de date de reference posee,
   * pas d'id memorise, `isNew` faux partout.
   */
  const [newBadgePref] = usePref<boolean>(newBadgePrefKey(appId), true);
  const newBadgeOn = newBadgePref !== false;

  /**
   * LA MEMOIRE DE LA PORTE (date de reference + ids deja ouverts), lue par
   * `readNewMemory` et par lui seul (voir newRecords.ts). Elle vit en etat
   * React, pas en `usePref`, pour cette raison precise : `usePref` lirait le
   * stockage de son cote, ce qui ferait un TROISIEME point de lecture et
   * couterait a la fiche `nouveau-multi-utilisateur` un endroit de plus a
   * reprendre.
   *
   * ELLE PORTE SA CLE AVEC ELLE, et ce n'est pas de la decoration. Entre un
   * changement de porte et la resynchronisation, l'etat parle encore de la
   * porte PRECEDENTE : sans la cle, un effet pouvait decider sur la memoire
   * d'une porte et ECRIRE sur une autre. Il etait juste par coincidence
   * d'ordonnancement ; il l'est maintenant par construction, chaque effet
   * concerne sortant en tete quand la cle rangee ne vaut pas `newMemoryKey`.
   */
  const newMemoryKey = newMemoryPrefKey(appId, screenId, doorId);
  const [newMemoryState, setNewMemoryState] = useState<{ key: string; memory: NewMemory }>(() => ({
    key: newMemoryPrefKey(appId, screenId, doorId),
    memory: readNewMemory(appId, screenId, doorId),
  }));

  /**
   * Le meme etat, lisible depuis un callback STABLE (`markReadRef.current`).
   * Meme patron que `storedFavoritesRef` : la ref suit a chaque rendu.
   */
  const newMemoryRef = useRef(newMemoryState);
  newMemoryRef.current = newMemoryState;

  /**
   * La memoire de CETTE porte, ou `null` tant que l'etat parle encore d'une
   * autre. Un `null` d'une frame ne coute rien a l'ecran : changer de porte
   * vide deja les lignes (`openDoor`), il n'y a rien a marquer pendant ce
   * battement, et mieux vaut ne rien signaler que signaler d'apres la memoire
   * du jeu de donnees precedent.
   */
  const newMemory = newMemoryState.key === newMemoryKey ? newMemoryState.memory : null;

  const syncNewMemory = useCallback((): void => {
    const key = newMemoryPrefKey(appId, screenId, doorId);
    setNewMemoryState((previous) => {
      const next = readNewMemory(appId, screenId, doorId);
      return previous.key === key && sameNewMemory(previous.memory, next)
        ? previous
        : { key, memory: next };
    });
  }, [appId, screenId, doorId]);

  /**
   * Relecture au changement de CLE (changer de porte change la memoire) et a
   * chaque event de pref sur cette cle : "Tout marquer comme lu" repose la
   * date depuis le hub Parametres, dans CE document. Meme mecanique que
   * `usePref`, avec le meme event, donc la meme limite : l'event ne franchit
   * pas le document et aucun ecouteur "storage" n'existe, donc un SECOND
   * ONGLET ne se resynchronise pas ; il verra le changement a son prochain
   * rechargement.
   */
  useEffect(() => {
    syncNewMemory();
    const onChange = (event: Event): void => {
      if ((event as CustomEvent<string>).detail !== newMemoryKey) return;
      syncNewMemory();
    };
    window.addEventListener(PREF_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(PREF_CHANGE_EVENT, onChange);
  }, [newMemoryKey, syncNewMemory]);

  /**
   * LA DATE DE REFERENCE, posee a la PREMIERE VISITE de la porte et jamais
   * rebougee ensuite. C'est elle qui empeche la premiere ouverture d'allumer
   * les 500 lignes deja en base : ce qui existait avant qu'on regarde n'est
   * pas une nouveaute, c'est le fond de la collection.
   *
   * Elle ne se pose PAS tant que le reperage est eteint : sur une app ou le
   * reglage a toujours ete coupe, le rallumer doit repartir de l'instant ou on
   * le rallume, pas d'une date posee a son insu pendant que rien ne
   * s'affichait. Une date DEJA posee, elle, ne rebouge jamais : eteindre puis
   * rallumer retrouve les nouveautes d'avant, et le seul geste qui remet le
   * compteur a zero est "Tout marquer comme lu".
   *
   * C'est le SEUL des deux effets a garder sa garde sur `newBadgeOn` : poser
   * une date est un choix d'affichage, memoriser une lecture est un fait.
   */
  useEffect(() => {
    // La cle d'abord : on ne decide jamais sur la memoire d'une autre porte
    // que celle sur laquelle on ecrit.
    if (newMemoryState.key !== newMemoryKey) return;
    if (!newBadgeOn || newMemoryState.memory.since != null) return;
    const memory: NewMemory = { since: Date.now(), seen: newMemoryState.memory.seen };
    writeNewMemory(appId, screenId, doorId, memory);
    setNewMemoryState({ key: newMemoryKey, memory });
  }, [newBadgeOn, newMemoryState, newMemoryKey, appId, screenId, doorId]);

  /**
   * Marquer une fiche LUE depuis un callback STABLE (`openRecord` ne se recree
   * pas quand la porte change) : la fonction est reassignee a chaque rendu, la
   * ref ne l'est jamais. Meme patron que `setStoredFavoritesRef`.
   *
   * ON NE RELIT JAMAIS LE STOCKAGE POUR UN ETAT QU'ON VIENT DE CALCULER. C'est
   * la regle, et elle a une cause precise : `writePref` retombe en silence
   * quand localStorage est indisponible (mode prive, quota) tout en dispatchant
   * son event ; une relecture rendait alors une memoire VIDE qui ecrasait
   * l'etat React, `since` repassait a null, l'effet de date le reposait a
   * maintenant, et UN SEUL CLIC eteignait toutes les pastilles de l'ecran. On
   * calcule donc l'etat d'apres avec `markSeen` (pur, newRecords.ts), on
   * l'ecrit, et on le pose : la memoire tient meme quand le stockage ne tient
   * pas.
   *
   * IL MARQUE MEME REGLAGE ETEINT. Ce que `seen` enregistre est un FAIT ("on a
   * ouvert cette fiche sur ce poste"), pas un affichage : le fait reste vrai
   * quand l'affichage est coupe, et le memoriser coute une ecriture. Ne pas le
   * faire produisait exactement le bug que ce chantier ne doit pas produire :
   * tout ce qui avait ete lu pendant que le reperage etait eteint se rallumait
   * au rallumage, la fiche redevenant nouvelle toute seule.
   */
  const markReadRef = useRef<(id: string) => void>(() => {});
  markReadRef.current = (id: string): void => {
    if (id === '') return;
    // La porte du MOMENT, jamais celle du rendu : voir `doorIdRef`.
    const currentDoorId = doorIdRef.current;
    const key = newMemoryPrefKey(appId, screenId, currentDoorId);
    const state = newMemoryRef.current;
    // L'etat parle d'une autre porte (le rendu n'a pas encore rattrape) : on
    // ne s'en sert pas, on lit celle-ci, et la resynchronisation suivra.
    const known = state.key === key;
    const memory = known ? state.memory : readNewMemory(appId, screenId, currentDoorId);
    const next = markSeen(memory, id);
    // Identite : `markSeen` rend l'objet recu quand il n'y a rien a faire
    // (rouvrir la meme fiche, revenir dessus a la fleche). Ni ecriture, ni rendu.
    if (next === memory) return;
    writeNewMemory(appId, screenId, currentDoorId, next);
    if (!known) return;
    const updated = { key, memory: next };
    newMemoryRef.current = updated;
    setNewMemoryState(updated);
  };

  /**
   * OUVRIR UNE FICHE L'ETEINT. `openRecord` marque deja au clic, tout de
   * suite, sans attendre le chargement ; cet effet couvre les DEUX entrees qui
   * ne passent pas par lui : l'arrivee A FROID sur un lien `?id=...` (l'etat
   * de depart pose `openId` directement) et le selecteur de vues qui bascule
   * sur la fiche sans element ouvert. Sans lui, une fiche ouverte par un lien
   * partage restait allumee au retour en collection. Marquer deux fois ne
   * coute rien : rien n'est ecrit si l'id est deja le dernier lu.
   */
  useEffect(() => {
    if (openId == null) return;
    markReadRef.current(openId);
  }, [openId, newMemoryKey]);

  /**
   * CE QUE LA PROJECTION RECOIT. `undefined` des que le reperage est eteint ou
   * que la porte n'a pas encore sa date de reference : la projection ne lit
   * meme pas `createdAt` et rend `isNew: false` partout.
   */
  const newRecordsInput = useMemo<NewRecordsInput | undefined>(() => {
    if (!newBadgeOn || newMemory == null || newMemory.since == null) return undefined;
    return { since: newMemory.since, seen: new Set(newMemory.seen) };
  }, [newBadgeOn, newMemory]);

  /**
   * LE FILTRE « NOUVEAUTES SEULES ». Offert quand le reperage est allume et
   * hors mode Modeles ; reglage eteint, un etat reste allume ne filtre plus.
   * `ficheGardee` : la fiche OUVERTE reste dans la liste meme si l'ouvrir l'a
   * rendue lue (donc plus nouvelle), sinon elle disparaitrait sous les yeux et
   * le curseur Precedent / Suivant la perdrait. Elle sort des qu'on la quitte.
   * Derivee ici pour que la projection ne reparte pas a chaque ouverture quand
   * le filtre est eteint.
   */
  const nouveautesOffertes = newBadgeOn && !query.modelesMode;
  const filtreNouveautesActif = nouveautesSeules && nouveautesOffertes;
  const ficheGardee = filtreNouveautesActif ? openId : null;

  /* ---- Donnees ---- */

  const [records, setRecords] = useState<T[]>([]);
  /**
   * Mode SERVEUR : la derniere page rendue etait pleine, donc il en reste
   * probablement. Etat a part et non deduit de `records.length` : une fois les
   * pages empilees, plus rien dans le jeu ne dit si le serveur avait la suite.
   */
  const [hasMore, setHasMore] = useState(false);
  const [inFlight, setInFlight] = useState(true);
  /**
   * Le meme "un chargement est en vol", lisible depuis un callback stable.
   * `loadMore` en a besoin AVANT le rendu suivant : au moment du clic, l'etat
   * `inFlight` de la closure peut dater d'un rendu, la ref ne date jamais.
   */
  const inFlightRef = useRef(true);
  inFlightRef.current = inFlight;
  const [error, setError] = useState<Error | undefined>(undefined);
  const [reloadTick, setReloadTick] = useState(0);

  /**
   * LE TITRE DE LA FICHE PARENTE DU CHEMIN (`query.dans`) : « Projet Test ».
   * Lu par le `get` de la porte PARENTE (son jeu, pas celui qu'on affiche) et
   * projete par son binding, comme une ligne. Relu a chaque rechargement, pour
   * qu'un nom corrige sur la fiche du projet se retrouve dans les boutons.
   * Vide tant qu'il n'est pas lu, ou si la fiche n'existe plus : les libelles
   * retombent alors sur le nom de la porte.
   */
  const cleDuDans = ecrireDansParam(query.dans) ?? '';
  const [ficheDuParent, setFicheDuParent] = useState<{ cle: string; titre: string } | null>(null);
  useEffect(() => {
    const dans = query.dans;
    if (dans == null) return;
    const parente = (options.doors ?? []).find((porte) => porte.id === dans.porte);
    const lire = parente?.get ?? optionsRef.current.get;
    if (parente == null || lire == null) return;
    const bindings = parente.bindings ?? optionsRef.current.bindings;
    const binding =
      bindings.find((candidat) => candidat.roles.title != null && candidat.type === 'core.fiche') ??
      bindings.find((candidat) => candidat.roles.title != null);
    let annule = false;
    void lire(dans.id).then(
      (fiche) => {
        if (annule) return;
        /* UN PARENT QUI N'EXISTE PLUS (lien recopie, fiche supprimee) : on SORT
           du chemin, vers la collection de la porte parente. Rester montrerait
           une porte vide sous un nom generique, et « + Ajouter » y creerait
           une fiche rattachee a rien. */
        if (fiche == null || (fiche as { deletedAt?: unknown }).deletedAt != null) {
          sortirDuCheminRef.current(dans);
          return;
        }
        const titre = binding == null ? '' : projectRecord(fiche, dans.id, binding).title;
        setFicheDuParent({ cle: cleDuDans, titre: titre === dans.id ? '' : titre });
      },
      () => {
        /* ILLISIBLE, C'EST ABSENT POUR CET ECRAN : les rules refusent la
           lecture d'un document qui n'existe pas, et un parent qu'on ne peut
           pas lire ne peut pas non plus recevoir de fiche. */
        if (!annule) sortirDuCheminRef.current(dans);
      },
    );
    return () => {
      annule = true;
    };
    // `query.dans` est lu par sa cle : un objet neuf de meme valeur ne relit rien.
  }, [cleDuDans, query.section, reloadTick]);
  const titreDuParent = ficheDuParent != null && ficheDuParent.cle === cleDuDans ? ficheDuParent.titre : '';
  const titreDuParentRef = useRef(titreDuParent);
  titreDuParentRef.current = titreDuParent;
  /** Sortir du chemin vers la porte parente (pose plus bas, apres `openDoor`). */
  const sortirDuCheminRef = useRef<(dans: DansUneFiche) => void>(() => {});
  /** Enregistrement charge seul (entree a froid, element sorti du filtre). */
  const [solo, setSolo] = useState<{ id: string; record: T } | null>(null);
  const [soloDeleted, setSoloDeleted] = useState<string | null>(null);
  /** Patches optimistes, par id puis par role. Vides a chaque chargement reussi. */
  const [patches, setPatches] = useState<Record<string, Record<string, Cell>>>({});
  /**
   * Patches optimistes des COLONNES, par id puis par cle de colonne. Un SECOND
   * sac, pas une extension du premier : l'espace de cles n'est pas le meme et il
   * peut collisionner (une colonne `status` n'est pas le role `status`).
   */
  const [columnPatches, setColumnPatches] = useState<Record<string, Record<string, Cell>>>({});

  /* ---- Ecriture : etat visible, annulation, jetons de sequence ---- */

  const [saveState, setSaveState] = useState<SaveState>({ mode: 'idle' });
  /**
   * Les piles d'annulation sont du STATE et non des refs : `canUndo` alimente
   * une puce a l'ecran, une ref ne re-rendrait rien.
   */
  const [undoStack, setUndoStack] = useState<WriteStep[]>([]);
  const [redoStack, setRedoStack] = useState<WriteStep[]>([]);
  /** La derniere ecriture echouee, telle quelle : c'est ce que `retrySave` rejoue. */
  const failedRef = useRef<WriteRequest | null>(null);
  /**
   * Le dernier GESTE DE FICHE echoue (archiver, desarchiver, supprimer), tel
   * quel. Un sac a part de `failedRef` parce que ce n'est pas la meme chose a
   * rejouer (un document, pas une case), mais le MEME bouton "Reessayer" :
   * sans lui, un archivage rate affichait "Echec de l'enregistrement" avec une
   * relance qui ne relancait rien, exactement le controle mort que G16
   * interdit. Le plus RECENT des deux gagne : chacun efface l'autre en
   * s'inscrivant.
   */
  const failedRecordActionRef = useRef<{ kind: RecordActionKind; record: T } | null>(null);
  /**
   * Le jeton de sequence, par couple (cible). Une ecriture qui revient alors
   * qu'une plus recente est partie sur la MEME cible ne fait plus rien : ni
   * rollback, ni transition de `saveState`, ni rechargement.
   */
  const sequenceRef = useRef(new Map<string, number>());
  /**
   * LE JETON DE SESSION DE FICHE. Il change a chaque ouverture et a chaque
   * sortie (`resetRecordSession`). Le jeton de sequence ci-dessus ne repond pas
   * a la meme question : lui compare deux ecritures sur la MEME cible, celui-ci
   * dit si l'ecriture qui atterrit appartient encore a la fiche a l'ecran. Sans
   * lui, une ecriture partie juste avant un `Echap` revient 300 ms plus tard
   * reposer `saveState` a `saved` et re-empiler son pas d'annulation dans une
   * session que `resetRecordSession` venait de vider.
   */
  const recordSessionRef = useRef(0);
  /** Ecritures en vol : sert a ne pas effacer un optimisme encore utile. */
  const pendingWritesRef = useRef(0);

  const [selection, setSelection] = useState<string[]>([]);
  /**
   * VIDER LA SELECTION, d'un seul geste et sans rendu inutile.
   *
   * C'est `clearSelection` a la sortie (la croix de la barre de lot, `Echap`),
   * ET le point de passage unique de tout ce qui la fait TOMBER : changer de
   * porte, de recherche, de filtre, de mode Archives, recharger. La forme
   * fonctionnelle est ce qui rend l'appel gratuit quand il n'y a rien a vider
   * (React sort sur l'identite), sans quoi chaque frappe de recherche
   * reposerait un tableau neuf et ferait repartir tout ce qui en depend.
   *
   * Ce qu'il ne fait PAS : quitter le tableau. Ce cas-la ne passe par aucun
   * geste (on peut y arriver par l'adresse, par le bouton Retour, par le
   * selecteur de vues), il est donc tenu par l'invariant lui-meme, plus bas.
   */
  const dropSelection = useCallback((): void => {
    setSelection((current) => (current.length === 0 ? current : NO_SELECTION));
  }, []);
  const [viewStates, setViewStates] = useState<Record<string, unknown>>({});
  /** La liste d'ids GELEE a l'ouverture de la fiche. */
  const [frozenIds, setFrozenIds] = useState<string[] | null>(null);

  /* ---- LE BROUILLON DE FICHE (chantier « creation-en-fiche ») ---- */

  /**
   * LE BROUILLON, TEL QUE LE MOTEUR LE TIENT : l'etat que le chassis lit, PLUS
   * les deux choses qui ne sortent jamais d'ici.
   *
   * `fiche` EST LA SOURCE DE LA PROJECTION, et elle change a chaque saisie :
   * `openRow` la projette par le binding ORDINAIRE de la porte (aucun patch
   * optimiste, aucun sac a rejouer), donc la fiche vide se raconte exactement
   * comme une fiche pleine, avec les memes cartes et les memes cases.
   *
   * `imposees` SURVIT A L'OUVERTURE parce que la case verrouillee doit le
   * rester tant que le brouillon vit (le « + Ajouter » d'une carte liee impose
   * le champ du lien) : c'est le geste qui l'a pose, pas l'utilisateur.
   *
   * `raison` N'EST PAS ICI : elle se calcule au rendu, depuis l'acces et le
   * port du moment. Rangee dans l'etat, elle serait la reponse d'hier (un hote
   * qui monte son port apres l'ecran, un acces qui change) et « Creer »
   * resterait grise sans motif.
   */
  interface BrouillonInterne {
    porte: string;
    touche: boolean;
    enCreation: boolean;
    manquants: readonly string[];
    sortieEnAttente: boolean;
    titre: string;
    fiche: T;
    imposees: readonly ValeurDeDepart[];
    /** Ce brouillon ajoute un ENFANT a ce modele (voir `BrouillonState`). */
    enfantDe?: { modele: string; lien: LienParLeChampLie; libelle: string };
    /** « Creer » remplira ce champ de cette fiche, puis y ramenera (`PendingAppAction.retour`). */
    retour?: RetourDeCreation;
    /**
     * Ce brouillon vient de ce MODELE (voir `BrouillonState`). L'objet ENTIER,
     * et pas seulement son id : `id` pour « Creer », `name` pour la case du
     * titre, `children` pour l'apercu des cartes liees et pour « Enregistrer
     * comme modele », qui emporte ce que la fiche montre.
     */
    depuisModele?: ModeleDeFiche;
  }
  const [brouillon, setBrouillon] = useState<BrouillonInterne | null>(null);
  /** L'id d'une fiche creee dont le curseur attend la liste rechargee (gel differe). */
  const gelApresCreationRef = useRef<string | null>(null);
  /**
   * L'INTENTION D'ORIGINE D'UNE CREATION DEMANDEE DEPUIS UNE FICHE PARENTE (le
   * « Creer » d'un champ de lien, `creerDepuis`), EN ATTENTE que le brouillon
   * s'ouvre dans la porte liee.
   *
   * UNE INTENTION ET PAS UNE ORIGINE : l'adresse de passage (`?s=<porte>`, sans
   * `id`) n'est la fiche de personne, et l'etape d'historique ne se pose qu'au
   * push qui ouvre le brouillon. C'est le sens 1 de l'adresse qui la CONSOMME a
   * cet unique push (`setParams(next, { push: true, depuis })`) ; la puce de
   * sortie du brouillon dit alors le nom du parent et y ramene (fondateur,
   * 09/09/2026 : la meme sortie qu'une fiche arrivee par un lien). Hors shell,
   * `creerDepuis` la range directement dans la memoire de l'ecran sans
   * historique.
   */
  const departDuBrouillonRef = useRef<OrigineDeFiche | null>(null);
  /**
   * LA FICHE VIDE QUI VA S'OUVRIR REMPLACE L'ETAPE D'ARRIVEE au lieu de s'empiler
   * dessus (le « Ajouter » d'un champ de lien, `PendingAppAction.retour`).
   *
   * POURQUOI. Le geste a deja empile UNE etape (la porte visee, dans cet ecran ou
   * dans une autre app) ; le brouillon en empilait une seconde. Revenir a la
   * fiche d'origine, apres « Creer » comme sur « Annuler », DEPILE une etape :
   * on retombait sur la collection de la porte visee, ou l'on n'est jamais
   * passe, et d'une autre app on n'avait aucun moyen de rejoindre la fiche
   * quittee. Le sens 1 de l'adresse lit ce drapeau une fois, a l'ouverture.
   */
  const etapeARemplacerRef = useRef(false);
  /**
   * LE GESTE DE RETOUR A L'ORIGINE, LISIBLE D'EN HAUT.
   *
   * POURQUOI UNE REF. `retournerALOrigine` a besoin de `fromSearch`, qui se
   * definit tout en bas de ce hook ; « Annuler » (`annulerBrouillon`), lui, se
   * definit bien avant. Les deux doivent pourtant sortir par la MEME porte que
   * la puce, sinon le brouillon d'une fiche liee retomberait dans la
   * COLLECTION de la porte d'arrivee, ou l'on n'est jamais passe. Deplacer
   * `fromSearch` remonterait la moitie du fichier ; une ref pose le lien sans
   * rien reordonner, et elle est lue au CLIC, jamais au rendu.
   */
  const retourALOrigineRef = useRef<((geste: 'depiler' | 'naviguer') => void) | null>(null);
  /**
   * LE MIROIR DU BROUILLON, pour les callbacks STABLES (l'ecriture, la sortie,
   * le clavier). Il suit dans le MEME geste que l'etat (`poserBrouillon`), et
   * jamais au rendu : une ecriture qui part avant le rendu suivant doit repartir
   * de la fiche que la precedente a laissee, sinon deux saisies coup sur coup
   * s'ecrasent (le meme piege que les ecritures de modele).
   */
  const brouillonRef = useRef<BrouillonInterne | null>(null);
  /**
   * CE QU'UNE SORTIE ATTEND : le geste qu'on rejouera si l'utilisateur repond
   * « Quitter sans creer ». Une REF et pas un etat : elle ne rend rien a elle
   * seule, c'est `sortieEnAttente` qui ouvre la fenetre.
   */
  const suiteDeSortieRef = useRef<(() => void) | null>(null);
  /** Poser le brouillon : l'etat POUR le rendu, la ref POUR le geste suivant. */
  const poserBrouillon = useCallback((suivant: BrouillonInterne | null): void => {
    brouillonRef.current = suivant;
    if (suivant == null) suiteDeSortieRef.current = null;
    setBrouillon(suivant);
  }, []);
  /** Le port de l'hote (`sdk.createIn`), `null` quand personne ne le fournit. */
  const creationPort = useCreationPort();
  const creationPortRef = useRef<CreationPort | null>(creationPort);
  creationPortRef.current = creationPort;
  /**
   * POURQUOI LA CREATION EST ETEINTE ICI, ou `null`. Deux causes, dans l'ordre
   * du plus general au plus local (`raisonDeGeste`) : l'acces d'abord, le port
   * absent ensuite. Elle grise « + Ajouter » ET « Creer », avec la meme phrase :
   * deux calculs pour les deux bouts d'un meme geste divergeraient.
   */
  const raisonDeCreation = raisonDeGeste(
    raisonAcces,
    creationPort == null ? RAISONS_CREATION.sansPort : null,
  );

  const scrollRef = useRef<HTMLElement>(null);

  const queryRef = useRef(query);
  queryRef.current = query;
  const patchesRef = useRef(patches);
  patchesRef.current = patches;
  const columnPatchesRef = useRef(columnPatches);
  columnPatchesRef.current = columnPatches;
  const undoRef = useRef(undoStack);
  undoRef.current = undoStack;
  const redoRef = useRef(redoStack);
  redoRef.current = redoStack;
  const recordsRef = useRef(records);
  recordsRef.current = records;
  const soloRef = useRef(solo);
  soloRef.current = solo;
  /** La fiche qu'on vient de creer et qu'on regarde encore (voir `fiche-creee.ts`). */
  const creeeIdRef = useRef<string | null>(null);
  /** Sa lecture immediate n'est pas revenue : l'ecran dit « chargement », jamais « introuvable ». */
  const [creeeEnAttente, setCreeeEnAttente] = useState<string | null>(null);
  const openIdRef = useRef(openId);
  openIdRef.current = openId;
  const viewIdRef = useRef(viewId);
  viewIdRef.current = viewId;
  const lastCollectionRef = useRef(lastCollectionViewId);
  lastCollectionRef.current = lastCollectionViewId;
  const frozenRef = useRef(frozenIds);
  frozenRef.current = frozenIds;

  /**
   * POSER LA VUE ACTIVE : l'etat ET sa ref, dans le meme geste. Le SEUL
   * endroit qui ecrit `viewId`.
   *
   * POURQUOI LA REF SUIT TOUT DE SUITE, exactement comme les catalogues et les
   * chargeurs au changement de porte (`openDoor`) : plusieurs chemins ecrivent
   * la vue PUIS raisonnent dessus dans le MEME tick, et une ref n'est
   * rafraichie qu'au rendu suivant.
   *
   * LE CAS QUI L'EXIGE, ET QUI A COUTE : une adresse qui change de porte ET
   * ouvre une fiche d'un coup (clic sur une ligne de carte liee, lien colle,
   * bouton Suivant du navigateur). `fromSearch` y enchaine `openDoor` (qui
   * referme la fiche, donc repose une vue de COLLECTION) puis `openRecord`, qui
   * decide s'il ENTRE dans la fiche en lisant `viewIdRef`. Sur une ref restee
   * au rendu precedent, il lisait encore la vue FICHE de la porte quittee, se
   * croyait donc deja au bon niveau, et ne remontait pas la vue : l'ecran
   * restait sur la collection avec un `openId` pose. Or l'adresse n'ecrit
   * l'`id` que si la vue active est de niveau fiche (`toSearch`) : l'`id`
   * disparaissait de la barre d'adresse dans le meme battement, et le lien
   * partage ne montrait plus la fiche.
   */
  const poserVueActive = useCallback((next: ViewId): void => {
    viewIdRef.current = next;
    setViewIdState(next);
  }, []);

  // Une application du Studio peut retirer la vue pendant que la porte reste ouverte.
  useLayoutEffect(() => {
    if (activeDoor?.canvas != null || openId != null) return;
    const valide = vueDeCollection(activeTypes, bindingsRef.current, viewId, activeDoor?.defaultView);
    if (valide == null || valide === viewId) return;
    poserVueActive(valide);
    setLastCollectionViewId(valide);
    lastCollectionRef.current = valide;
  }, [activeTypes, activeDoor, openId, viewId, poserVueActive]);

  /* ---- Chargement ---- */

  // Seuls les parametres que le CHARGEUR peut exploiter declenchent un appel.
  // La recherche et les pastilles restent CLIENTES dans les deux modes (pas de
  // recherche plein texte cote Firestore) : les mettre ici relancerait une
  // requete a chaque frappe. Le TRI, lui, part au serveur en mode serveur,
  // donc il compte comme parametre de requete la-bas et pas ici.
  const loadKey = [
    query.limit,
    query.after ?? '',
    // La SECTION segmente les donnees a la source : la changer est un
    // rechargement, dans les deux modes. C'est ce qui a permis aux ecrans de
    // supprimer leur `useEffect(() => reload(), [section])` maison.
    query.section ?? '',
    // LE CHEMIN (`dans`) filtre le jeu rendu : y entrer ou en sortir recharge.
    ecrireDansParam(query.dans) ?? '',
    query.range ? `${query.range.from.getTime()}-${query.range.to.getTime()}` : '',
    String(query.includeArchived),
    // LE MODE ARCHIVES est un parametre de REQUETE : le chargeur le relaie au
    // SDK (`list({ archived })`), donc l'allumer recharge, exactement comme
    // changer de porte. Cette liste doit rester alignee sur celle de
    // `nextQuery` : un champ ajoute ici sans l'autre empile les pages de deux
    // requetes differentes, en silence.
    String(query.archivesMode),
    /*
     * ET LE MODE MODELES N'Y EST PAS, alors que son jumeau y est : la
     * consigne juste au-dessus ("cette liste doit rester alignee sur celle de
     * `nextQuery`") vaut aussi pour ce qu'on N'Y MET PAS, et les deux listes
     * restent bien alignees, l'une comme l'autre sans lui.
     *
     * Le mode Archives est un parametre de REQUETE : le chargeur le relaie au
     * SDK. Le mode Modeles n'en est pas un, et aucun chargeur d'app ne le
     * connaitra jamais : les modeles ne vivent pas dans la collection de
     * l'app, ils vivent dans le repository des modeles, que le SOCLE lit
     * lui-meme par `ModelesPort` (l'effet `modeleFiches`, plus bas). L'ajouter
     * ici ferait recharger la porte a chaque aller-retour dans le mode, pour
     * rendre exactement les memes lignes, et obligerait les 55 apps a relayer
     * un drapeau qui ne leur sert a rien.
     */
    // Le tri SERVEUR est le MIROIR (`query.sort` = niveau 1 de la pile, seul
    // `orderBy` que le SDK accepte) : empiler ou inverser un niveau 2 raffine
    // en memoire et ne recharge rien, changer le niveau 1 recharge.
    pagination === 'server' && query.sort != null ? `${query.sort.key}:${query.sort.dir}` : '',
    // La SOURCE a change en dehors de l'ecran (lecture partagee rechargee
    // ailleurs) : meme mecanique que la section, et meme benefice, l'ecran n'a
    // pas d'`useEffect(() => reload())` a ecrire.
    String(options.reloadKey ?? ''),
    String(reloadTick),
  ].join('|');

  useEffect(() => {
    let cancelled = false;
    const server = paginationRef.current === 'server';
    // PAGE SUIVANTE (on empile) ou chargement neuf (on remplace) : la question
    // se tranche sur le curseur, jamais sur "est-ce que records est vide".
    // Un jeu vide peut tres bien etre une page 2 qui n'a rien rendu.
    const append = server && queryRef.current.after != null;
    // La ref est posee ICI, avant tout await : `loadMore` la lit au clic, et
    // attendre le rendu suivant laisserait passer un clic pendant la fenetre.
    inFlightRef.current = true;
    setInFlight(true);
    setError(undefined);
    void (async () => {
      try {
        const charge = await loadRef.current(queryRef.current);
        if (cancelled) return;
        /* LA PORTE OUVERTE DANS UNE FICHE ne garde que les fiches qui designent
           le parent (`porteDans.ts`). Le chargeur recoit `query.dans` et peut
           filtrer avant ; ce filtre-ci tient pour tous. */
        const dansCharge = resoudreDans(doorsRef.current, queryRef.current.dans, {
          section: queryRef.current.section,
          openId: null,
        });
        /* LE SENS DIRECT DEMANDE LA FICHE PARENTE : sa liste dit quelles fiches
           de cette porte sont les siennes. Lue par le `get` de la porte PARENTE,
           comme le titre du chemin ; illisible, la porte reste vide. */
        let parentDuChemin: unknown;
        if (dansCharge?.place === 'enfant' && estLienParListe(dansCharge.lien)) {
          const parente = doorsRef.current.find((porte) => porte.id === dansCharge.dans.porte);
          const lire = parente?.get ?? optionsRef.current.get;
          try {
            parentDuChemin = lire == null ? undefined : await lire(dansCharge.dans.id);
          } catch {
            parentDuChemin = undefined;
          }
          if (cancelled) return;
        }
        const result =
          dansCharge?.place === 'enfant'
            ? fichesDuParent(charge, dansCharge.lien, dansCharge.dans.id, parentDuChemin)
            : charge;
        if (server) {
          // "Il reste des pages" se lit "la page etait pleine". C'est le
          // contrat du SDK (`runList` ne rend pas de curseur, cf. list-query.ts
          // dans @taki/sdk) : une derniere page exactement pleine fait donc
          // proposer un "charger la suite" qui ne rendra rien, et le bandeau
          // disparait au clic suivant. Defaut connu, assume, auto-corrige.
          const full = result.length >= queryRef.current.limit;
          // MAIS : une page pleine dont TOUTES les lignes sont deja connues
          // n'avance pas. Ca arrive pour de vrai avec un tri sur `updatedAt`
          // quand un collegue remonte des lignes deja lues au-dessus du
          // curseur. Sans ce garde-fou, le curseur ne bouge plus, le bandeau
          // reste, et le bouton devient definitivement mort : l'utilisateur
          // clique sans que rien ne se passe et sans un mot d'explication.
          const known = new Set(recordsRef.current.map(getIdRef.current));
          const advanced = !append || result.some((row) => !known.has(getIdRef.current(row)));
          setHasMore(full && advanced);
          if (import.meta.env.DEV && result.length > queryRef.current.limit) {
            // Le chargeur ne respecte pas la taille de page demandee : la
            // deduction "page pleine = il en reste" ne veut plus rien dire, et
            // le symptome (bandeau perpetuel, ou jamais de bandeau) ne pointe
            // pas vers sa cause. Meme esprit qu'`assertBinding`.
            console.warn(
              `[taki/views] ${appId}.${screenId} : le chargeur a rendu ${result.length} lignes ` +
                `alors que la page demandee etait de ${queryRef.current.limit}. En pagination serveur, ` +
                'le chargeur DOIT relayer `query.limit` au SDK, sinon la detection de page suivante est fausse.',
            );
          }
        }
        setRecords((previous) =>
          append ? mergeById(previous, result, getIdRef.current) : result,
        );
        // Les patches optimistes ont fait leur travail : la donnee fraiche prend
        // le relais. SAUF si une ecriture est encore en vol : ce chargement-ci
        // ne la contient pas, vider le sac ferait disparaitre sa valeur sous les
        // doigts jusqu'a ce qu'elle atterrisse. Le sac sera vide au chargement
        // suivant, celui que cette ecriture declenchera en reussissant.
        if (pendingWritesRef.current === 0) {
          setPatches({});
          setColumnPatches({});
        }
      } catch (cause) {
        if (cancelled) return;
        // CURSEUR PERDU : l'element qui servait de curseur a disparu entre deux
        // pages. Ce n'est pas une panne a montrer, c'est un jeu a reprendre :
        // on repart de la premiere page, ce qui relance le chargement puisque
        // le curseur fait partie de `loadKey`.
        //
        // Les DEUX gardes comptent. `server` : le mode client n'a pas de
        // curseur, une erreur portant ce nom y serait une erreur ordinaire et
        // l'avaler afficherait un ecran vide silencieux a la place du bloc
        // d'erreur et de son bouton Reessayer. `after != null` : sans curseur a
        // annuler, remettre `after` a null ne change AUCUNE valeur de `loadKey`,
        // donc aucun chargement ne repartirait et l'ecran resterait vide, sans
        // erreur et sans reprise possible.
        if (server && queryRef.current.after != null && isCursorLost(cause)) {
          setHasMore(false);
          setRecords([]);
          setQueryState((previous) => ({ ...previous, after: null }));
          return;
        }
        setError(cause instanceof Error ? cause : new Error(String(cause)));
      } finally {
        if (!cancelled) {
          inFlightRef.current = false;
          setInFlight(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadKey]);

  /* ---- Lecture des MODELES (mode Modeles) ---- */

  /**
   * UN EFFET A PART DU CHARGEMENT DES FICHES, et c'est le coeur du chantier.
   *
   * Les modeles ne sont pas une TRANCHE de la collection de l'app : ils vivent
   * dans un autre repository, derriere un autre service (`ModelesPort`), et
   * c'est le SOCLE qui les lit. Les faire passer par `load` aurait oblige les
   * 55 apps a relayer un drapeau et a savoir appeler le SDK des modeles ; c'est
   * exactement ce que le mode ne demande a personne. Corollaire assume :
   * `records` ne bouge pas d'un cheveu pendant le mode, donc les stats de la
   * porte et les compteurs de filtres restent VRAIS (meme invariant que le mode
   * Archives, regle G15).
   *
   * MODE ETEINT, PORT ABSENT OU JEU ABSENT : la liste se VIDE. On ne garde pas
   * les modeles de la porte precedente sous la main "au cas ou" : ils
   * s'afficheraient une image entiere a la prochaine entree dans le mode, sous
   * le binding d'une autre porte.
   *
   * LE DRAPEAU `abandonne` N'EST PAS UNE PRECAUTION DE STYLE : on change de
   * porte, la lecture de l'ancienne revient apres celle de la nouvelle, et sans
   * lui la liste affichee serait celle qu'on vient de quitter. Meme patron que
   * le menu de la fleche de « Ajouter » (`components/ScreenLayout.tsx`).
   *
   * UNE LISTE QU'ON NE PEUT PAS LIRE LE DIT, elle ne se fait plus passer pour
   * une liste vide. C'etait le choix du menu du chevron (console + liste vide),
   * et il ne tient pas ici : le menu propose un raccourci qu'on peut ignorer,
   * le MODE est l'ecran entier. « Aucun modele pour cette porte » sur une porte
   * qui en a douze envoie en fabriquer un treizieme.
   */
  const jeuFamille = activeDoor?.jeu?.family ?? '';
  const jeuType = activeDoor?.jeu?.type ?? '';
  /**
   * LA CLEF DU JEU LU : ce qui identifie la liste affichee. L'octet NUL separe
   * deux champs libres sans qu'aucune valeur ne puisse le contenir (`a/b` +
   * `c` et `a` + `b/c` seraient sinon la meme clef).
   *
   * IL S'ECRIT `\u0000`, EN SEQUENCE D'ECHAPPEMENT, ET JAMAIS TAPE EN DUR :
   * meme valeur a l'execution, mais un octet NUL reel fait passer le fichier
   * pour du BINAIRE, et `grep` devient muet sur les 8000 lignes de ce module.
   */
  const modelesCle = `${jeuFamille}\u0000${jeuType}`;
  useEffect(() => {
    /* LA GARDE EST CELLE DE PARTOUT (`modelesAutorises`, calcule sur la porte
       active) : sans elle, une porte sans `jeu` ou sans `modeles: true` faisait
       quand meme `list('', '')`. Le mode ne peut plus s'y allumer, mais l'effet
       reste le dernier a lire la configuration avant d'appeler le port.

       UN ENFANT OUVERT NE DEMANDE AUCUNE LISTE : ce qu'on regarde est UNE fiche
       d'enfant dans la porte liee, et la liste des modeles de CETTE porte-la ne
       dit rien de lui. La lire couterait un aller-retour pour un jeu que rien ne
       montre (le parent, lui, est charge par son id, juste en dessous). */
    if (!query.modelesMode || modelesPort == null || !modelesAutorises || enfantOuvert) {
      // `[]` NEUF SEULEMENT S'IL Y A QUELQUE CHOSE A VIDER : un tableau vide
      // recree a chaque changement de porte reprojetterait la liste pour rien.
      if (modeleFichesRef.current.length > 0) majModeles(() => []);
      setModelesEnLecture(false);
      setModelesCleLue(null);
      setModelesErreur(null);
      return;
    }
    let abandonne = false;
    setModelesEnLecture(true);
    void (async (): Promise<void> => {
      try {
        const trouves = await modelesPort.list(jeuFamille, jeuType);
        if (abandonne) return;
        majModeles(() => [...trouves]);
        setModelesErreur(null);
      } catch (cause) {
        console.error(
          `[taki/views] La lecture des modeles de ${jeuFamille}/${jeuType} a echoue :`,
          cause,
        );
        if (abandonne) return;
        majModeles(() => []);
        /* LA PHRASE S'ADRESSE A QUI VIENT DE CLIQUER, pas a qui debogue : la
           cause technique reste en console. Le « Reessayer » du ViewHost, lui,
           marche vraiment ici (`reload()` monte `reloadTick`, qui est dans les
           dependances de cet effet). */
        setModelesErreur(new Error("La liste des modèles n'a pas pu être lue."));
      } finally {
        /* LA CLEF LUE SE POSE DANS LES DEUX CAS, succes comme echec : elle dit
           « la lecture de ce jeu a eu lieu », pas « elle a reussi ». Sans ca,
           un echec laisserait l'ecran en chargement pour toujours. */
        if (!abandonne) {
          setModelesCleLue(modelesCle);
          setModelesEnLecture(false);
        }
      }
    })();
    return () => {
      abandonne = true;
    };
    // `modelesRevision` : un modele enregistre, modifie ou retire ailleurs dans
    // l'ecran remet la liste a jour sans rechargement de page. `reloadTick` :
    // « Reessayer » et le rechargement du socle valent pour les modeles aussi,
    // sinon le seul bouton de reprise a l'ecran ne reprendrait rien ici.
  }, [
    query.modelesMode,
    modelesPort,
    modelesAutorises,
    enfantOuvert,
    jeuFamille,
    jeuType,
    modelesCle,
    modelesRevision,
    reloadTick,
    majModeles,
  ]);

  /* ---- Lecture du MODELE PARENT (la fiche d'un enfant, pivot du 10/09/2026) ---- */

  /**
   * L'ID DU MODELE DONT L'ECRAN A BESOIN EN ENTIER, ou `null`.
   *
   * DEUX CHEMINS Y MENENT, et un seul id en sort : la FICHE d'un enfant
   * (l'adresse le porte) et le BROUILLON qui en ajoute un (l'intention le
   * porte). Dans les deux cas on est dans la porte LIEE, ou la liste des modeles
   * ne contient pas ce document.
   */
  const modeleParentId = cheminOuvert?.modeleId ?? brouillon?.enfantDe?.modele ?? null;
  useEffect(() => {
    if (modeleParentId == null || modelesPort == null) {
      /* RIEN A TENIR : on relache, sinon le parent d'hier resterait sous la main
         et une ecriture d'enfant pourrait le viser depuis une autre porte. */
      if (modeleParentRef.current != null) majModeleParent(null);
      setModeleParentEnLecture(false);
      return;
    }
    if (modeleParentRef.current?.id === modeleParentId) return;
    /* DEJA DANS LA LISTE : c'est le cas du CLIC (on vient de la fiche du modele,
       la liste de la porte precedente est encore en main). On le prend tel quel,
       sans aller-retour : sans ca, la fiche de l'enfant clignoterait en
       « chargement » a chaque clic sur une ligne. */
    const dejaLu = modeleFichesRef.current.find((candidat) => candidat.id === modeleParentId);
    if (dejaLu !== undefined) {
      majModeleParent(dejaLu);
      setModeleParentEnLecture(false);
      return;
    }
    let abandonne = false;
    setModeleParentEnLecture(true);
    void (async (): Promise<void> => {
      try {
        const lu = await modelesPort.get(modeleParentId);
        if (!abandonne) majModeleParent(lu);
      } catch (cause) {
        console.error(`[taki/views] La lecture du modele ${modeleParentId} a echoue :`, cause);
        if (!abandonne) majModeleParent(null);
      } finally {
        /* LA LECTURE A EU LIEU, reussie ou non : sans ce drapeau, l'ecran
           resterait en « chargement » pour toujours au lieu de dire « Fiche
           introuvable » sur un modele retire ailleurs. */
        if (!abandonne) setModeleParentEnLecture(false);
      }
    })();
    return () => {
      abandonne = true;
    };
    /* `modelesRevision` N'EST PAS UNE DEPENDANCE, ET C'EST VOULU : il monte
       apres CHAQUE ecriture reussie, donc apres chaque case modifiee. Relire le
       parent la-dessus ferait un aller-retour par frappe validee, et une lecture
       partie avant une seconde ecriture atterrirait avec des champs perimes,
       par-dessus l'optimisme. L'etat suit les ecritures par `majEnfants`. */
  }, [modeleParentId, modelesPort, majModeleParent]);

  /**
   * LES MODELES, VUS COMME DES FICHES. Une pseudo-fiche par modele, projetee
   * ensuite par le binding ORDINAIRE de la porte : c'est ce qui donne au mode
   * les memes colonnes, les memes cellules et les memes editeurs que la liste
   * de travail, sans une ligne de vue dediee.
   *
   * LE DOUBLE `as` EST LA PARCE QUE `T` N'EST PAS CONTRAINT : le moteur ne LIT
   * pas ces documents, il les fait lire au binding, qui les lit par clef comme
   * il lirait la fiche d'origine (c'est la meme forme : le SDK a copie ses
   * champs depuis une fiche). Une pseudo-fiche a laquelle il manque un champ
   * rend une cellule vide, jamais une exception.
   */
  const modelesProjetes = useMemo<T[]>(
    () => modeleFiches.map((modele) => pseudoFicheDeModele(modele) as unknown as T),
    [modeleFiches],
  );

  /** Le NOM de chaque modele, par id : ce qui ecrase le titre projete. */
  const nomsDesModeles = useMemo<Map<string, string>>(
    () => new Map(modeleFiches.map((modele) => [modele.id, modele.name])),
    [modeleFiches],
  );

  /* ---- Verification des bindings (developpement uniquement) ---- */

  // Chaque porte a ses catalogues : on re-verifie au passage d'une porte, pas
  // seulement au montage (les refs portent deja ceux de la porte active).
  const activeDoorId = activeDoor?.id ?? null;
  useEffect(() => {
    assertBinding(sessionKey, typesRef.current, bindingsRef.current);
  }, [sessionKey, activeDoorId]);

  /*
   * Le champ `demand` des vues cesse d'etre decoratif ici. Une vue qui declare
   * `demand: 'all'` (le kanban : "une carte mal placee est un mensonge, il doit
   * voir tout le jeu") montee sur une pagination serveur incomplete regroupe la
   * PAGE COURANTE : ses colonnes et leurs compteurs decrivent un sous-ensemble
   * arbitraire, sans que rien ne le signale a l'ecran. On ne peut pas l'en
   * empecher (l'utilisateur a le droit de choisir sa vue), mais on peut refuser
   * que ce soit silencieux pendant la construction de l'app.
   */
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (pagination !== 'server' || !hasMore) return;
    const active = typesRef.current.find((type) => type.id === viewIdRef.current);
    if (active?.demand !== 'all') return;
    console.warn(
      `[taki/views] ${sessionKey} : la vue ${viewIdRef.current} demande tout le jeu ` +
        "(demand: 'all') alors que la pagination serveur n'a charge qu'une partie des lignes. " +
        'Elle ne décrit donc que les pages chargées. Chargez la suite, ou réservez cette vue ' +
        'aux écrans en pagination cliente.',
    );
  }, [sessionKey, pagination, hasMore, viewId]);

  /* ---- Projection ---- */

  const activeType = useMemo<ViewType | null>(
    () => activeTypes.find((type) => type.id === viewId) ?? null,
    [activeTypes, viewId],
  );
  const level: ViewLevel = activeType?.level ?? 'collection';
  /* L'APERCU VIT AVEC LA FICHE : la quitter le referme, sans quoi la prochaine
     fiche ouverte montrerait encore un brouillon qu'on ne regarde plus. */
  useEffect(() => {
    if (level !== 'record' && apercuRef.current != null) fermerApercu();
  }, [level, fermerApercu]);

  /* ---- Les annuaires de la porte : le NOM des fiches qu'elle designe ---- */

  /**
   * LES JEUX QUE CETTE PORTE DESIGNE, pris sur TOUS ses bindings et pas sur le
   * seul binding actif, roles et colonnes compris (`referencesDeBindings`).
   *
   * POURQUOI TOUS. Un annuaire nourrit trois choses qui ne regardent pas la
   * meme vue : la cellule d'une colonne de reference, le TRI (qui appartient a
   * l'ECRAN et peut porter sur une clef que la vue active ne rend pas), et le
   * selecteur de la FICHE. Charger le seul annuaire de la vue courante ferait
   * trier des ids en liste et des noms en tableau, sur les memes donnees.
   */
  const references = useMemo<ColumnReference[]>(() => {
    return referencesDeBindings(bindingsRef.current);
    /* LA PORTE decide : c'est elle qui change les bindings (`bindingsRef` est
       avancee au meme rendu, plus haut). Les bindings eux-memes sont lus par
       ref, comme partout dans ce fichier. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doorId]);

  /* SANS PORT, AUCUN ANNUAIRE, ET RIEN NE CASSE : les cellules de reference
     gardent leurs ids, exactement comme les ecrans d'avant ce lot. */
  const liensPort = useLiensPort();
  /*
   * LE MEME PORT, LISIBLE DEPUIS UN CALLBACK STABLE. Meme patron et meme raison
   * que `modelesPortRef` : les primitives de LOT sont des `useCallback` que la
   * barre de lot gele au clic ; les passer par closure changerait leur identite
   * a chaque rendu du fournisseur, et le lot en vol travaillerait avec une
   * primitive remplacee sous ses pieds.
   */
  const liensPortRef = useRef(liensPort);
  liensPortRef.current = liensPort;
  /*
   * LES LIENS DE LA PORTE ACTIVE, meme patron que `loadRef` et les trois verbes
   * de fiche : la porte les reassigne a chaque passage, et ce qui les lit
   * (le menu « ... », le lot) travaille pour la porte affichee sans le savoir.
   */
  const liensRef = useRef<LinkedDoor[]>([]);
  liensRef.current = activeDoor?.links ?? [];
  /**
   * LE LIBELLE D'UNE PORTE LIEE, tel que le menu des portes l'affiche.
   *
   * REPLI SUR L'ID, jamais sur un blanc : un refus qui dirait
   * « 3 fiches liees () » ferait chercher un defaut d'affichage la ou il n'y
   * en a pas. Un id est moche et vrai ; un vide est muet.
   *
   * IDENTITE STABLE (`useCallback` sans dependance, lecture par ref) : elle
   * voyage dans les fabriques de confirmation, qui sont recomposees a chaque
   * rendu et ne doivent pas l'etre pour elle.
   */
  const libelleDePorte = useCallback(
    (door: string): string => doorsRef.current.find((porte) => porte.id === door)?.label ?? door,
    [],
  );
  /**
   * CE QUI POINTE UNE FICHE, PORTE PAR PORTE, DEMANDE AU SERVEUR.
   *
   * REND UNE LISTE VIDE quand la porte ne declare aucun lien ou que l'hote ne
   * fournit pas de port : « rien n'est accroche » et « on ne peut pas savoir »
   * ne se confondent pas ici, parce que l'absence de lien est un FAIT (la porte
   * n'en declare pas) et l'absence de port un ecran monte sans hote, ou aucune
   * carte liee ne s'affiche non plus. Les deux appelants (le menu de la fiche,
   * le lot) traitent donc les deux de la meme facon : rien a refuser.
   *
   * IL NE RATTRAPE PAS L'ECHEC D'UN COMPTE : c'est l'appelant qui decide quoi
   * en faire, et les deux decident la meme chose (refuser). Le rattraper ici en
   * rendant zero ferait exactement l'inverse.
   */
  const compterLesLiees = useCallback(
    async (id: string): Promise<CompteLie[]> => {
      const port = liensPortRef.current;
      const liens = liensRef.current;
      if (port == null || liens.length === 0) return [];
      return await comptesLies(port, liens, id, libelleDePorte);
    },
    [libelleDePorte],
  );
  /* LE PORT DES PERSONNES, et sa ref pour les callbacks stables (le brouillon
     lit la personne connectee au moment ou il s'ouvre, pas au montage). */
  const personnesPort = usePersonnesPort();
  const personnesPortRef = useRef(personnesPort);
  personnesPortRef.current = personnesPort;
  /* LE FUSEAU DE L'ORG, lu a l'ouverture du brouillon (`socle:aujourdhui`) :
     l'option de l'hote d'abord, puis le reglage de l'installation pour une app
     ecrite a la main, puis le fuseau par defaut du modele de donnees. */
  const fuseauDeLInstallation = useInstallationPort()?.fuseau ?? null;
  const fuseauRef = useRef(FUSEAU_PAR_DEFAUT);
  fuseauRef.current = options.fuseau ?? fuseauDeLInstallation ?? FUSEAU_PAR_DEFAUT;
  /* LES IDS QUE LA PORTE MONTRE, pour que l'annuaire nomme aussi les fiches
     hors de sa fenetre (`souhaiterTitres`) : lus sur les fiches chargees, par
     les memes colonnes et roles que la projection, tous bindings confondus. */
  const voulus = useMemo(
    () => idsDesReferences(solo === null ? records : [...records, solo.record], bindingsRef.current),
    // Les bindings sont lus par ref, comme pour `references` plus haut : la porte decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [records, solo, doorId],
  );
  const annuaires = useAnnuaires(liensPort, references, personnesPort, voulus);
  /* LES CHAMPS FILTRABLES : une colonne qui designe une fiche ou une personne
     propose ses fiches (l'annuaire de la porte), jamais une zone de texte. */
  const filterFields = useMemo<QueryField[]>(() => fieldColumns.map((column) => {
    const reference = column.reference;
    const annuaire = reference == null ? undefined : annuaires.get(cleDeJeu(reference.family, reference.type));
    const personne = reference != null && estReferenceDePersonnes(reference);
    const moi = personne
      ? (reference.type === TYPE_ANNUAIRE_PAR_UID ? personnesPort?.moi?.userId : personnesPort?.moi?.personId) ?? undefined
      : undefined;
    const fiches = annuaire == null ? [] : [...annuaire.fiches.entries()]
      .filter(([id]) => id !== moi)
      .sort(([, a], [, b]) => a.title.localeCompare(b.title, 'fr'))
      .map(([id, fiche]) => ({
        value: id, label: fiche.title, tone: 'neutral' as const,
        ...(fiche.archive ? { archive: true } : {}),
        ...(personne ? { avatar: { src: fiche.photo ?? null } } : {}),
      }));
    const choix = moi === undefined ? fiches : [{ value: VALEUR_MOI, label: 'Moi', tone: 'neutral' as const, avatar: { src: personnesPort?.moi?.photoURL ?? null } }, ...fiches];
    return {
      key: column.key, label: column.label,
      /* LE TYPE DU CHAMP choisit les conditions (un montant se compare comme un
         nombre, une periode « en cours le », des etiquettes « contiennent »),
         y compris sur une colonne sans editeur (un calcul, un numero). */
      ...(column.valueType === undefined ? {} : { type: column.valueType }),
      /* ET SES REGLAGES : le format d'un nombre (une duree se saisit
         « 2 h 30 ») et les liens multiples (« contient la fiche »). */
      ...(column.reglages === undefined ? {} : { reglages: column.reglages }),
      ...(column.valueType === 'boolean' ? { kind: 'boolean' as const } : column.kind == null ? {} : { kind: column.kind }),
      ...(column.valueType === 'boolean' ? { options: [
        { value: 'true', label: 'Oui', tone: 'neutral' as const },
        { value: 'false', label: 'Non', tone: 'neutral' as const },
      ] } : column.valueType === 'checklist' ? { options: [...ETATS_DE_CHECKLIST] }
        : reference != null ? (annuaire != null || choix.length > 0 ? { options: choix } : {}) : column.options == null ? {} : { options: column.options() }),
      ...(reference == null ? {} : { lien: true, inconnu: reference.repli ?? FICHE_LIEE_INTROUVABLE }),
      ...(personne ? { personne: true } : {}),
      ...(moi === undefined ? {} : { moi }),
    };
  }), [fieldColumns, annuaires, personnesPort]);

  /*
   * LA RECHERCHE EST DIFFEREE (chantier `ecran-fluide`) : la frappe s'affiche
   * dans le champ tout de suite, et la projection qui en depend passe ensuite,
   * en priorite basse. Une frappe suivante qui arrive avant interrompt le calcul
   * de la precedente au lieu de s'empiler derriere lui.
   */
  const rechercheDifferee = useDeferredValue(query.search);
  /* LA PROJECTION DE CHAQUE FICHE, GARDEE d'une passe a l'autre
     (`projectRecordsEnCache`) : une frappe ne la refait plus sur tout le jeu. */
  const cacheDeProjectionRef = useRef<CacheDeProjection | null>(null);
  /*
   * UNE VUE VIRTUELLE N'A PAS DE PLAFOND D'AFFICHAGE (`ViewType.virtuelle`) :
   * elle ne dessine que les lignes visibles, donc le core lui passe tout le jeu
   * et il n'y a rien a « afficher de plus ». Les autres vues (le kanban, le
   * calendrier) gardent leur fenetre et leur bandeau.
   */
  const sansPlafond = pagination !== 'server' && activeType?.virtuelle === true;

  // Une seule passe : archives exclus, pastille appliquee, projection, patches
  // optimistes, recherche, tri, fenetre. Memoisee sur ce qui la fait changer ;
  // les bindings sont lus par ref (leurs closures sont stables en pratique).
  const projected = useMemo(() => {
    const binding = bindingsRef.current.find((candidate) => candidate.type === viewId);
    const getId = getIdRef.current;

    // Les archives sortent du jeu AVANT tout le reste : une vue n'en voit
    // jamais. `live` est le jeu VIVANT de la porte, et il ne bouge PAS avec le
    // mode Archives : c'est lui qui alimente les stats (2c) et les compteurs
    // du popover de filtres, que la regle G15 veut sur la porte BRUTE, donc
    // sur les actifs. Un compteur qui change de sens selon un mode est un
    // compteur auquel on ne peut plus se fier.
    const live = query.includeArchived
      ? records
      : records.filter((record) => !isArchived(record));
    /*
     * LE JEU DES STATS (2c). C'est `live`, sauf quand « Nouveautes » est allume :
     * les stats comptent alors les nouveautes, la MEME regle que la liste
     * (fondateur, 03/11/2026 : « le compte du haut suit »). Seul ce bouton les
     * fait bouger. Les filtres, la recherche et les compteurs du popover restent
     * sur `live` (G15).
     */
    const liveDesStats = filtreNouveautesActif
      ? live.filter((record) => {
          const id = getId(record);
          return id === ficheGardee || isRecordNew(record, id, newRecordsInput);
        })
      : live;
    /*
     * LE JEU QUE LES VUES RENDENT (chantier 2_0). Le MODE ARCHIVES decide
     * SEUL : allume, on voit les archivees et RIEN d'autre ; eteint, on n'en
     * voit aucune. Son test passe AVANT les filtres, et c'est tout le point du
     * chantier : ce n'est pas un filtre de plus, c'est un ETAT A PART de
     * l'ecran.
     *
     * `records` peut contenir les deux etats (un chargeur en pagination
     * cliente relaie `archived: 'all'` pour garder ses compteurs vrais) ou les
     * archives seules (`'only'`, pagination serveur) : le partage se fait ici,
     * dans les deux cas, et le moteur n'a pas a savoir lequel des deux
     * l'ecran a choisi.
     */
    /*
     * ET LE MODE MODELES PASSE ENCORE AVANT, pour la meme raison qu'il n'est pas
     * un filtre : il decide SEUL du jeu. Les lignes ne viennent alors plus du
     * tout de `records` (qui reste le jeu de l'app, intact) mais du port des
     * modeles, projete par le MEME binding : meme colonnes, memes cellules,
     * memes editeurs, donc rien a apprendre pour qui sait deja lire la liste.
     *
     * LA LISTE VIDE EST UN ETAT LEGITIME du mode, pas un cas a eviter : une
     * porte sans un seul modele affiche son etat vide, qui dit comment en
     * fabriquer un premier. Rien ici ne retombe sur les fiches.
     */
    const pool = query.modelesMode
      ? modelesProjetes
      : query.archivesMode
        ? records.filter((record) => isArchived(record))
        : live;
    // MULTI-FILTRES ADDITIFS : une ligne passe si AU MOINS UN filtre actif la
    // retient (un OU, jamais un ET : poser "Camera" puis "Optique" AGRANDIT la
    // selection, c'est le contrat des tags de la zone 4). Aucun matcher = pas
    // de filtrage : c'est a la fois l'etat vide du gabarit v2 ("Tout le parc")
    // et la lecture V1 d'une pastille active sans `match` ("Tous"), qui garde
    // ainsi exactement son comportement d'avant les portes.
    // LES FILTRES S'APPLIQUENT AUSSI DANS LES MODES (decision du fondateur du
    // 28/09/2026, chantier `modes-menu-trois-points`). Le mode a choisi le JEU
    // (les archives, les modeles), les filtres en montrent une TRANCHE, comme
    // sur le travail courant : chercher « les cameras archivees » est un geste
    // legitime. Sur un modele, le `match` lit la pseudo-fiche `{ ...fields, id,
    // type }` : un filtre qui teste un champ STANDARD (`createdAt`,
    // `archivedAt`) n'y mord pas, puisqu'un modele n'en garde pas.
    const matchers = query.filterIds
      .map((id) => filterDefsRef.current.find((candidate) => candidate.id === id)?.match)
      .filter((match): match is (record: T) => boolean => match != null);
    const shortcutPool =
      matchers.length === 0
        ? pool
        : pool.filter((record) => matchers.some((match) => match(record)));

    const kept = fieldConditions.length === 0 ? shortcutPool : shortcutPool.filter((record) =>
      fieldConditions.every((condition) => {
        const column = fieldColumns.find((candidate) => candidate.key === condition.field);
        const field = filterFields.find((candidate) => candidate.key === condition.field);
        if (!column || !field) return true;
        const raw = column.value(record);
        const value = column.reference && field.options === undefined ? (libelleDeReference(column.reference, raw, annuaires) ?? raw) : raw;
        return matchesField(value, condition, field);
      }),
    );

    const roles = rolesOf(binding);
    const catalogs = columnCatalogs(binding);
    const referencesParClef = columnReferences(binding);
    const rendusParClef = columnRendus(binding);
    // La pastille "Nouveau" est posee ICI, par le core : une vue lit
    // `row.isNew`, elle ne le calcule jamais (elle ne lit aucun contexte).
    // `newRecordsInput` est `undefined` quand le reglage est eteint, et la
    // projection retombe alors exactement sur son comportement d'avant.
    // LES ANNUAIRES donnent aux cellules de REFERENCE leur libelle : c'est lui
    // que la recherche et le tri liront (`label ?? value`), jamais l'id.
    const projection = projectRecordsEnCache(cacheDeProjectionRef.current, kept, getId, binding, newRecordsInput, annuaires);
    cacheDeProjectionRef.current = projection.cache;
    let rows = projection.rows;
    // « Nouveautes seules » : la MEME regle que la pastille (`row.isNew`), avant
    // recherche, tri et fenetre. Les stats et les compteurs lisent `records` et
    // `pool`, pas `rows` : rien d'autre ne bouge. En mode serveur, il porte sur
    // les fiches chargees, comme les conditions de champ.
    if (filtreNouveautesActif) rows = rows.filter((row) => row.isNew || row.id === ficheGardee);
    /* DANS UNE FICHE, LE SOUS-TITRE QUI NOMME LE PARENT SE TAIT : toutes les
       cartes diraient « Projet Test », que le titre de la zone 2 dit deja. */
    const dansVue = resoudreDans(doorsRef.current, query.dans, { section: query.section, openId: null });
    if (dansVue?.place === 'enfant' && roles['subtitle']?.path === cheminDuChampLie(dansVue.lien)) {
      rows = rows.map(({ subtitle: _parent, ...ligne }) => ligne);
    }

    /*
     * LE TITRE D'UNE LIGNE DE MODELE EST LE NOM DU MODELE, pas le titre projete
     * depuis ses champs : c'est le nom qu'on a tape en l'enregistrant, et c'est
     * lui qui distingue deux modeles partis de la meme fiche. L'ecrasement se
     * fait ICI, apres la projection, parce que `projectRecord` calcule
     * `row.title` par une closure de role qui appartient a l'app : le socle ne
     * peut ni la lire ni la remplacer. Meme geste exactement qu'`applyRolePatch`
     * pour l'optimisme.
     */
    if (query.modelesMode) rows = nommerLignesDeModeles(rows, nomsDesModeles);

    // Ecriture optimiste : la vue voit le changement avant le serveur. Les deux
    // sacs s'appliquent AVANT la recherche, le tri et la fenetre : une valeur
    // fraichement saisie pese donc tout de suite sur ce qu'on voit.
    if (Object.keys(patches).length > 0) {
      rows = rows.map((row) => {
        const rowPatches = patches[row.id];
        if (!rowPatches) return row;
        let patched = row;
        for (const [role, value] of Object.entries(rowPatches)) {
          patched = applyRolePatch(patched, role, value, roles[role], annuaires);
        }
        return patched;
      });
    }
    if (Object.keys(columnPatches).length > 0) {
      rows = rows.map((row) => {
        const rowPatches = columnPatches[row.id];
        if (!rowPatches) return row;
        let patched = row;
        for (const [key, value] of Object.entries(rowPatches)) {
          /* LE LIBELLE DE LA FICHE CHOISIE, pour une colonne de REFERENCE : la
             valeur ecrite est un ID, et sans ce mot la case afficherait cet id
             nu le temps de l'aller-retour serveur, puis le nom au
             rechargement. C'est le meme clignotement qu'une colonne a catalogue
             sans son libelle, et il se corrige au meme endroit. */
          patched = applyColumnPatch(
            patched,
            key,
            value,
            catalogs[key],
            libelleDeReference(referencesParClef[key], value, annuaires),
            rendusParClef[key],
          );
        }
        return patched;
      });
    }

    rows = rows.filter((row) => matchesSearch(row, rechercheDifferee));

    // LA PILE DE TRI, niveau par niveau : le niveau 1 commande, les suivants
    // departagent ses egalites. Egalite sur TOUS les niveaux : on ne tranche
    // pas, `Array.prototype.sort` est stable (ES2019), donc l'ordre du
    // chargeur fait foi et deux rendus du meme etat sont identiques. En mode
    // serveur, seul le niveau 1 est parti dans la requete (le SDK ne prend
    // qu'un `orderBy`) : les niveaux suivants raffinent ICI, sur les pages
    // chargees, ce qui est exactement la promesse du bandeau du ViewHost.
    const stack = query.sortStack;
    if (stack.length > 0) {
      /*
       * LE TRI APPARTIENT A L'ECRAN, PAS A LA VUE ACTIVE. `sortValueOf` lit
       * `row.cells[key]`, or seules les colonnes du binding ACTIF produisent
       * des cellules : en liste ou en kanban (aucune colonne declaree), une
       * cle de colonne ("reference", "dailyRate") ne resoudrait RIEN et le tag
       * de tri se poserait sans rien trier, en silence. Pour chaque cle de la
       * pile que la vue active ne sait pas resoudre (`resolvesSortKey`), on
       * precalcule donc un SAC id -> valeur en evaluant la colonne sur l'UNION
       * des bindings de la porte (premiere declaration gagnante), sur les
       * enregistrements retenus, et le comparateur le consulte a la place de
       * la ligne. Valeur BRUTE de la colonne, sans patch optimiste : la vue
       * active n'affiche rien pour ces cles, et le rechargement d'apres
       * ecriture reconcilie.
       */
      const fallbacks = new Map<string, Map<string, Cell>>();
      for (const { key } of stack) {
        if (fallbacks.has(key) || resolvesSortKey(binding, key)) continue;
        let source: Column<T> | undefined;
        for (const candidate of bindingsRef.current) {
          source = (candidate.fieldColumns ?? candidate.columns)?.find((column) => column.key === key);
          if (source != null) break;
        }
        if (source == null) continue;
        const bag = new Map<string, Cell>();
        /* UNE COLONNE DE REFERENCE SE TRIE SUR LE NOM, pas sur l'id, y compris
           quand la vue active ne la rend pas : sans cette ligne, trier par
           « Materiel » depuis la liste rangerait les interventions par
           identifiant Firestore, sous des libelles alphabetiques, et rien ne le
           signalerait. Meme regle que `sortValueOf` sur une cellule. */
        const reference = source.reference;
        /* UNE COLONNE A CATALOGUE SE TRIE SUR SON LIBELLE, meme raison exactement
           et meme regle que la cellule projetee (`ProjectedCell.label`) : le
           favori « Par secteur » de la LISTE ne declare aucune colonne, donc il
           passe par ce sac, et sans ces deux lignes il rangeait « Commerce »
           apres « Institution », dans l'ordre de `retail` et `institution`.
           Le catalogue se resout UNE fois, pas une fois par ligne. */
        const catalogue = reference === undefined ? source.options?.() : undefined;
        for (const record of kept) {
          const brute = source.value(record);
          /* LA CASE VIDE RESTE VIDE : `compareCells` range les vides en bas quel
             que soit le sens, et un catalogue qui porterait une option sans
             valeur les remonterait au milieu des autres. */
          const libelle =
            brute === null || brute === undefined || brute === ''
              ? undefined
              : (libelleDeReference(reference, brute, annuaires) ??
                catalogue?.find((option) => option.value === String(brute))?.label);
          bag.set(getId(record), libelle ?? brute);
        }
        fallbacks.set(key, bag);
      }
      rows = [...rows].sort((a, b) => {
        for (const levelSort of stack) {
          const bag = fallbacks.get(levelSort.key);
          const compared = bag
            ? compareCells(bag.get(a.id), bag.get(b.id))
            : compareCells(sortValueOf(a, levelSort.key), sortValueOf(b, levelSort.key));
          if (compared !== 0) return levelSort.dir === 'desc' ? -compared : compared;
        }
        return 0;
      });
    }

    /* LES METADONNEES passent la frontiere, jamais les fonctions : le catalogue
       d'un `select` se demande a `optionsOf(key)`, il n'est pas resolu ici (ce
       memo tourne a chaque frappe, il n'a pas a appeler le `options()` de l'app
       pour des colonnes que personne n'edite).
       LA TRADUCTION VIT DANS `colonnesDeVue` (`lib/liens.ts`), et une seule
       fois : la CARTE LIEE monte les memes vues avec les memes colonnes, et les
       deux copies « identiques au champ pres » ont tenu jusqu'au jour ou la
       REFERENCE d'une colonne a du traverser. */
    const columns: ViewColumn[] = colonnesDeVue(binding, pagination);

    return {
      binding,
      roles,
      catalogs,
      rows,
      // Le jeu VIVANT de la porte (archives exclues, avant recherche et
      // filtres) : ce sur quoi les stats de la porte et les compteurs du
      // popover de filtres se calculent (regle "un compteur ne bouge pas
      // quand on filtre"). Insensible au mode Archives, par construction.
      live,
      // Le jeu des stats : `live`, ou ses seules nouveautes quand le bouton
      // « Nouveautes » est allume.
      liveDesStats,
      // Le jeu du MODE : ce a quoi repond "y a-t-il des donnees du tout ?"
      // (l'etat vide), question distincte de "la recherche ne rend rien". En
      // mode archives, un zero ici veut dire "aucune fiche archivee", et le
      // ViewHost doit le dire avec ses mots a lui.
      poolCount: pool.length,
      // LE JEU DU MODE LUI-MEME, avant recherche et filtres : c'est lui que les
      // compteurs du popover de filtres comptent (28/09/2026). Hors mode, c'est
      // `live` a l'identique ; en archives, « Camera 3 » dit qu'il y a trois
      // cameras ARCHIVEES, le nombre de lignes que le filtre va montrer.
      pool,
      orderedIds: rows.map((row) => row.id),
      // Mode SERVEUR : la fenetre a deja ete decidee par le serveur, page par
      // page. Re-couper a `query.limit` ici ne montrerait que la premiere page
      // quoi qu'on charge, et "Afficher plus" n'afficherait jamais rien.
      windowRows: pagination === 'server' || sansPlafond ? rows : rows.slice(0, query.limit),
      truncated: pagination === 'server' || sansPlafond ? false : rows.length > query.limit,
      columns,
    };
  }, [
    pagination,
    records,
    viewId,
    patches,
    // LE CHEMIN fait taire le sous-titre qui nomme le parent.
    query.dans,
    // Oublier ce terme donne un optimisme qui ne s'affiche JAMAIS : la ligne ne
    // bougerait qu'au rechargement, et le symptome ("ca finit par marcher")
    // detourne du vrai coupable.
    columnPatches,
    rechercheDifferee,
    sansPlafond,
    // Les identites de `filterIds` et `sortStack` ne changent QUE quand leur
    // contenu change (`nextQuery` recopie la reference telle quelle sur les
    // patchs qui ne les touchent pas) : la projection ne repart donc pas pour
    // une frappe de recherche ou un rechargement sans rapport.
    query.filterIds,
    // UN FILTRE « EST MOI » change de verdict quand la personne connectee est
    // lue (apres l'arrivee) : sans ces termes, la liste resterait vide.
    personnesPort?.moi?.personId,
    personnesPort?.moi?.userId,
    fieldConditions,
    fieldColumns,
    filterFields,
    query.sortStack,
    query.limit,
    query.includeArchived,
    // Le MODE ARCHIVES change le jeu rendu ET desactive les filtres : oublier
    // ce terme laisserait la liste des actifs a l'ecran jusqu'au retour du
    // chargeur, donc un battement ou l'ecran ment.
    query.archivesMode,
    // Le MODE MODELES fait les deux aussi, et remplace la SOURCE des lignes :
    // les trois termes qui suivent sont ce dont il depend, et rien d'autre du
    // memo n'en depend (les stats et les compteurs restent sur `records`).
    query.modelesMode,
    modelesProjetes,
    nomsDesModeles,
    // La memoire du "nouveau" : ouvrir une fiche eteint sa pastille tout de
    // suite, sans attendre un rechargement.
    newRecordsInput,
    // Le filtre « Nouveautes seules » et la fiche qu'il garde (nulle tant qu'il
    // est eteint : ouvrir une fiche ne relance alors rien).
    filtreNouveautesActif,
    ficheGardee,
    // LES ANNUAIRES : un nom qui arrive doit apparaitre dans les cellules, et
    // faire retrier la liste si le tri porte sur cette colonne. Leur identite ne
    // change qu'au chargement d'un annuaire, pas a chaque rendu.
    annuaires,
  ]);

  /**
   * LES PSEUDO-FICHES, LISIBLES DEPUIS UN CALLBACK STABLE. `recordById` sert les
   * trois primitives de lot et le chemin d'ecriture : son identite ne doit pas
   * changer sous leurs pieds, elle passe donc par une ref, comme `recordsRef`.
   */
  const modelesProjetesRef = useRef(modelesProjetes);
  modelesProjetesRef.current = modelesProjetes;

  const orderedIdsRef = useRef(projected.orderedIds);
  orderedIdsRef.current = projected.orderedIds;
  /**
   * Le binding de la vue ACTIVE, lisible depuis un callback stable. Les trois
   * primitives de lot en ont besoin pour retrouver le `set()` d'un champ, et
   * elles sont appelees en boucle par le moteur de lot : leur identite ne doit
   * pas changer sous ses pieds au milieu de douze ecritures.
   */
  const bindingRef = useRef(projected.binding);
  bindingRef.current = projected.binding;

  /* ---- La fiche : ligne ouverte, chargement solo, curseur ---- */

  const openIsLoaded = openId != null && projected.orderedIds.includes(openId);

  // Element absent du jeu courant (entree a froid, ou sorti du filtre) : on le
  // charge seul plutot que d'aller chercher toute la collection.
  useEffect(() => {
    /*
     * EN MODE MODELES, ON NE VA JAMAIS CHERCHER UNE FICHE A FROID. Le `get()` de
     * la porte lit la collection de l'APP : lui passer un id de MODELE le ferait
     * chercher dans la mauvaise famille, il rendrait `null`, et une entree
     * directe par `?mod=1&id=<templateId>` ferait clignoter « fiche
     * introuvable » avant meme que la liste des modeles n'arrive. Le mode a sa
     * propre source, COMPLETE par construction (le port rend tous les modeles du
     * jeu) : ce qui n'y est pas n'existe pas, et il n'y a rien a aller chercher
     * ailleurs. L'element solo tombe en meme temps, sinon la fiche d'avant le
     * mode resterait a portee des replis qui la cherchent par id.
     */
    /* La fiche creee ne survit qu'ouverte (`fiche-creee.ts`). */
    creeeIdRef.current = creeeApresOuverture(creeeIdRef.current, openId, BROUILLON_ID);
    /*
     * Une fiche CREEE garde sa valeur lue meme quand la liste la contient : un
     * second rechargement peut la retirer un instant, et la relecture qui
     * comblerait le trou attend la fin de tout rechargement. Une fiche ouverte
     * par l'adresse, elle, libere `solo` des que la liste la porte, comme avant.
     */
    const decision = decisionSolo({
      modelesMode: query.modelesMode === true,
      openId,
      brouillonId: BROUILLON_ID,
      openIsLoaded,
      inFlight,
      soloId: soloRef.current?.id ?? null,
      creeeId: creeeIdRef.current,
    });
    if (decision === 'liberer') {
      if (soloRef.current != null) setSolo(null);
      return;
    }
    if (decision !== 'lire' || openId == null) return;
    const getOne = getOneRef.current;
    if (getOne == null) return;
    let cancelled = false;
    void (async () => {
      try {
        const record = await getOne(openId);
        // Une fiche supprimee ne se rouvre jamais comme une archivee : la vue le dit.
        const deleted = record != null && (record as { deletedAt?: unknown }).deletedAt != null;
        if (!cancelled) {
          setSoloDeleted(deleted ? openId : null);
          setSolo(record == null || deleted ? null : { id: openId, record });
          /* Une lecture qui dit « absente » : la fiche creee n'est plus couverte. */
          if (record == null || deleted) creeeIdRef.current = null;
        }
      } catch {
        if (!cancelled) setSolo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openId, openIsLoaded, inFlight, query.modelesMode]);

  // Gel differe : entree a froid, la collection n'etait pas la a l'ouverture.
  // UNIQUEMENT au niveau fiche : de retour en collection, `setView` a vide le
  // gel expres, pour que la PROCHAINE ouverture gele sur le jeu courant. Sans
  // cette garde, l'effet regelerait aussitot sur le jeu d'avant la recherche.
  useEffect(() => {
    if (level !== 'record') return;
    if (openId == null || frozenIds != null) return;
    /* UN BROUILLON N'A PAS DE RANG : il n'est pas dans la liste, et le geler
       sur elle ferait dire « 1 / 47 » a une fiche qui n'existe pas. Son
       curseur est neutre (voir `cursor`), et le gel se fera a la creation. */
    if (openId === BROUILLON_ID) return;
    /* UN ENFANT SE GELE SUR SES FRERES, jamais sur le jeu de la porte : les
       fleches y passent d'une intervention du modele a l'autre, et le jeu de la
       porte liee (ses vraies fiches, ou ses modeles) n'a rien a voir avec elles.
       Le gel attend le PARENT : il arrive par une lecture, comme une collection
       arrive par la sienne, et c'est exactement l'entree a froid. */
    if (cheminOuvert != null) {
      const parent =
        modeleParentRef.current?.id === cheminOuvert.modeleId ? modeleParentRef.current : null;
      if (parent == null) return;
      const freres = cheminsDesFreres(cheminOuvert.modeleId, parent.children, cheminOuvert.enfantId);
      if (freres.length === 0) return;
      setFrozenIds(freres);
      return;
    }
    if (orderedIdsRef.current.length === 0) return;
    /* LA FICHE QU'ON VIENT DE CREER N'EST PAS ENCORE DANS LA LISTE : le
       rechargement est en vol, et geler maintenant figerait un jeu sans elle,
       donc « .. / .. » pour toujours (un gel pose ne se refait pas). On attend
       la liste qui la contient ; c'est `creerBrouillon` qui pose l'attente. */
    if (gelApresCreationRef.current === openId) {
      if (!orderedIdsRef.current.includes(openId)) return;
      gelApresCreationRef.current = null;
    }
    setFrozenIds(freezeIds(orderedIdsRef.current));
  }, [level, openId, frozenIds, projected.orderedIds, cheminOuvert, modeleParent]);

  const openRow = useMemo<ProjectedRow | null>(() => {
    if (openId == null) return null;
    /*
     * LE BROUILLON PASSE AVANT TOUS LES REPLIS, et il ne partage aucun d'eux :
     * sa fiche n'est ni dans le jeu affiche, ni dans `records`, ni dans `solo`,
     * et elle n'y sera jamais. Elle se projette par le binding ORDINAIRE de la
     * porte, comme une fiche existante : memes cartes, memes cases, memes
     * editeurs. AUCUN PATCH OPTIMISTE a rejouer non plus, et c'est le fond de
     * la creation en fiche : la fiche du brouillon EST la valeur, elle change a
     * chaque saisie, et il n'y a pas de serveur derriere elle qui pourrait la
     * contredire.
     */
    if (openId === BROUILLON_ID) {
      if (brouillon == null) return null;
      const vide = projectRecord(brouillon.fiche, BROUILLON_ID, projected.binding, false, annuaires);
      /*
       * LE LIBELLE D'UNE VALEUR IMPOSEE, POSE SUR SA CASE VERROUILLEE.
       *
       * POURQUOI L'ANNUAIRE NE SUFFIT PAS (voir `ValeurDeDepart.libelle`) :
       * depuis la carte d'un MODELE, l'id impose est celui d'un modele, qu'aucun
       * annuaire ne contient. La case afficherait l'id nu, attenue, avec
       * « Supprimee ou introuvable » au survol, sur une valeur que le geste
       * vient de poser exprès. Le mot voyage donc avec l'intention, et il gagne
       * sur ce que la projection en a fait (pour une VRAIE fiche parente c'est
       * le meme mot, une image plus tot).
       *
       * `applyColumnPatch` ET PAS UNE RECOPIE : c'est deja lui qui pose un
       * libelle sur une cellule de reference (l'optimisme d'un choix de fiche
       * liee), et deux recettes du meme rendu divergeraient.
       */
      let nomme = vide;
      for (const imposee of brouillon.imposees) {
        if (imposee.libelle === undefined) continue;
        const colonne = colonneDuChemin(projected.binding?.columns, imposee.chemin);
        if (colonne === undefined) continue;
        nomme = applyColumnPatch(
          nomme,
          colonne.key,
          /* UNE LISTE IMPOSEE (l'appartenance) S'ECRIT EN CELLULE, un id par ligne. */
          Array.isArray(imposee.valeur) ? celluleDeListe(imposee.valeur.map(String)) : (imposee.valeur as Cell),
          projected.catalogs[colonne.key],
          imposee.libelle,
        );
      }
      return nomme;
    }
    /*
     * UN ENFANT DE MODELE PASSE JUSTE APRES, et il ne partage aucun repli non
     * plus : il n'est ni dans `records`, ni dans `solo`, ni dans les
     * pseudo-fiches de modeles de cette porte. Il vit dans le tableau `children`
     * du modele PARENT, charge par son id (`modeleParent`), et il se projette
     * par le binding ORDINAIRE de la porte LIEE : memes cartes, memes cases,
     * memes editeurs qu'une vraie intervention.
     *
     * LE TITRE VIENT DU BINDING, PAS DE `nommerLignesDeModeles` : cette regle-la
     * ecrase le titre par le NOM DU MODELE, et elle ne vaut que pour les lignes
     * de MODELES. Appliquee ici, chaque enfant s'appellerait « Camera de
     * tournage » au lieu de porter son objet.
     *
     * AUCUN PATCH OPTIMISTE A REJOUER : l'ecriture d'une case d'enfant pose son
     * optimisme dans le document du modele (`majEnfants`), donc la relecture
     * ci-dessous le voit deja. Les sacs de patchs du moteur ne le concernent pas.
     */
    if (cheminOuvert != null) {
      const parent = modeleParent?.id === cheminOuvert.modeleId ? modeleParent : null;
      const enfant = (parent?.children ?? []).find(
        (candidat) => candidat.id === cheminOuvert.enfantId,
      );
      if (enfant === undefined || parent == null) return null;
      const ligne = projectRecord(
        pseudoFicheDEnfant(enfant) as unknown as T,
        openId,
        projected.binding,
        false,
        annuaires,
      );
      /*
       * LA CASE DU LIEN D'UN ENFANT PORTE LE NOM DE SON MODELE, et c'est le
       * moteur qui l'y pose.
       *
       * POURQUOI ELLE EST VIDE SANS CA. Un modele est un PATRON : il ne retient
       * AUCUN rattachement, donc `enfant.fields` ne porte pas `linkKey`
       * (`templateChildFromRecord` l'a retire, et `instantiate` le repose sur la
       * fiche creee). La carte « Rattachement » de l'enfant disait donc
       * « Materiel : Non renseigne », c'est-a-dire un MANQUE, sur une fiche qui
       * n'a rien perdu : elle appartient a « Camera de tournage », et c'est ce
       * mot qu'il faut lire.
       *
       * LE MEME MECANISME QUE LE BROUILLON D'ENFANT, exactement : la valeur est
       * l'id du modele, le mot affiche est son nom (`applyColumnPatch`), et la
       * case est VERROUILLEE plus bas (`viewProps.verrouillees`). Deux recettes
       * du meme rendu divergeraient, et celle-ci se verrait la premiere.
       *
       * L'ID NE PART JAMAIS AU SERVEUR : il ne sert qu'a la cellule, et la case
       * n'a pas d'editeur. « Un lien stocke un id, jamais un libelle recopie »
       * vaut ici a l'envers : rien n'est stocke du tout.
       */
      const colonne = colonneDuChemin(projected.binding?.columns, enfant.linkKey);
      if (colonne === undefined) return ligne;
      return applyColumnPatch(
        ligne,
        colonne.key,
        parent.id,
        projected.catalogs[colonne.key],
        parent.name,
      );
    }
    const found = projected.rows.find((row) => row.id === openId);
    if (found) return found;
    // Sorti du jeu affiche (filtre, recherche, tri) mais DEJA EN MEMOIRE : on le
    // projette directement. Passer par `solo` ferait un aller-retour SDK inutile,
    // et sans `get()` la fiche s'afficherait "introuvable" alors que la donnee
    // est la. Le curseur, lui, dit "hors du filtre courant" (`outOfScope`).
    //
    // CES DEUX REPLIS REPROJETTENT DEPUIS LE RECORD BRUT : il faut leur rejouer
    // les patches a la main, sinon la valeur qu'on vient de saisir sur une fiche
    // hors filtre (ou ouverte a froid) revient en arriere sous les doigts.
    const getId = getIdRef.current;
    const rolePatches = patches[openId];
    const cellPatches = columnPatches[openId];
    /*
     * LES REPLIS SUIVENT LE MODE. En mode Modeles, le jeu en memoire est celui
     * des MODELES : chercher dans `records` y trouverait, au mieux, une fiche
     * ordinaire qui partage l'id d'un modele, et l'afficherait a sa place. Le
     * titre est reecrit comme dans la projection : la fiche d'un modele porte
     * son nom, ou qu'elle soit lue.
     */
    if (query.modelesMode) {
      const modele = modelesProjetes.find((candidate) => getId(candidate) === openId);
      if (modele === undefined) return null;
      const row = projectRecord(modele, openId, projected.binding, false, annuaires);
      const nomme = nommerLignesDeModeles([row], nomsDesModeles)[0] ?? row;
      return replayPatches(nomme, rolePatches, cellPatches, projected.roles, projected.catalogs, annuaires, columnRendus(projected.binding));
    }
    /* LES ANNUAIRES SUIVENT, comme dans la fenetre : une fiche ouverte a froid
       ou hors filtre doit nommer ce qu'elle designe comme la liste le fait. */
    const inMemory = records.find((candidate) => getId(candidate) === openId);
    if (inMemory !== undefined) {
      const row = projectRecord(inMemory, openId, projected.binding, false, annuaires);
      return replayPatches(row, rolePatches, cellPatches, projected.roles, projected.catalogs, annuaires, columnRendus(projected.binding));
    }
    if (solo?.id === openId) {
      const row = projectRecord(solo.record, openId, projected.binding, false, annuaires);
      return replayPatches(row, rolePatches, cellPatches, projected.roles, projected.catalogs, annuaires, columnRendus(projected.binding));
    }
    return null;
  }, [
    openId,
    cheminOuvert,
    modeleParent,
    projected,
    records,
    solo,
    patches,
    columnPatches,
    query.modelesMode,
    modelesProjetes,
    nomsDesModeles,
    brouillon,
    annuaires,
  ]);

  /**
   * LA LIGNE DE LA FICHE OUVERTE, lisible depuis un callback STABLE. Un seul
   * appelant : `openLinked`, qui a besoin du TITRE de la fiche qu'on quitte
   * pour le poser dans l'origine du saut. Le mettre en dependance changerait
   * l'identite d'`openLinked` a chaque projection, donc a chaque frappe dans la
   * barre de recherche, et la carte liee se remonterait avec.
   */
  const openRowRef = useRef(openRow);
  openRowRef.current = openRow;

  /**
   * L'ENREGISTREMENT OUVERT, BRUT (pas sa ligne projetee). Les trois gestes de
   * fiche s'appliquent au DOCUMENT, pas a ses cellules : ils ont besoin du
   * `T`, exactement comme `commitWrite`, et le cherchent aux memes deux
   * endroits (le jeu en memoire, puis l'element charge seul). `null` quand
   * rien n'est ouvert, ou quand la fiche est introuvable : il n'y a alors rien
   * a archiver, et le menu de la rangee 3c reste vide.
   */
  const openRecordValue = useMemo<T | null>(() => {
    if (openId == null) return null;
    const getId = getIdRef.current;
    /* MEME REGLE QUE `openRow` : un ENFANT vient du modele PARENT, et de nulle
       part ailleurs. C'est ce que le menu « ... » de l'enfant lit pour savoir
       qu'il y a quelque chose sur quoi agir. */
    if (cheminOuvert != null) {
      const parent = modeleParent?.id === cheminOuvert.modeleId ? modeleParent : null;
      const enfant = (parent?.children ?? []).find(
        (candidat) => candidat.id === cheminOuvert.enfantId,
      );
      return enfant === undefined ? null : (pseudoFicheDEnfant(enfant) as unknown as T);
    }
    // MEME REGLE QUE `openRow` : en mode Modeles, la seule source est le port.
    if (query.modelesMode) {
      return modelesProjetes.find((candidate) => getId(candidate) === openId) ?? null;
    }
    const inMemory = records.find((candidate) => getId(candidate) === openId);
    if (inMemory !== undefined) return inMemory;
    return solo?.id === openId ? solo.record : null;
  }, [openId, cheminOuvert, modeleParent, records, solo, query.modelesMode, modelesProjetes]);

  /** La fiche ouverte est-elle archivee : ce que le bandeau de tete rend. */
  const openArchived = openRecordValue != null && isArchived(openRecordValue);

  const cursor = useMemo(
    () =>
      /*
       * UN BROUILLON N'A PAS DE RANG, ET LE CURSEUR LE DIT AU LIEU DE MENTIR :
       * la fiche n'est pas dans la liste, donc ni total, ni « precedent », ni
       * « suivant ». Le chassis rend « nouveau » a la place du rang et grise les
       * deux fleches avec leur raison. Un « 1 / 47 » sur une fiche qui n'existe
       * pas designerait une AUTRE fiche a chaque fleche.
       */
      openId === BROUILLON_ID
        ? { ...EMPTY_CURSOR, openId: BROUILLON_ID }
        : /*
           * LE CURSEUR D'UN ENFANT EST CELUI DE SES FRERES, et son jeu COURANT
           * est le meme que son jeu GELE (`cheminsDesFreres`, pose par l'effet
           * de gel). Lui passer `projected.orderedIds` (les modeles de la porte
           * liee, souvent vides, jamais les bons) ferait lever `outOfScope` sur
           * une fiche parfaitement a sa place : la bande « hors du filtre
           * courant » s'afficherait sur chaque enfant.
           */
          enfantOuvert
          ? buildCursor(frozenIds, frozenIds ?? [], openId)
          : buildCursor(frozenIds, projected.orderedIds, openId),
    [frozenIds, projected.orderedIds, openId, enfantOuvert],
  );

  /**
   * L'ORIGINE DE LA FICHE OUVERTE (`OrigineDEtape`), LUE OU ELLE VIT : dans
   * l'ETAT DE L'ETAPE D'HISTORIQUE, par le port d'adresse (`urlPort.origine`).
   *
   * UN SEUL MECANISME. L'origine appartient a l'etape que le navigateur garde :
   * elle survit au F5, a Precedent et Suivant, et au demontage de l'ecran quand
   * le shell change d'app (il remonte l'ecran). Un changement de vue, de
   * section ou de pastille REMPLACE l'etape et la garde ; une fleche suivant ou
   * precedent la remplace aussi, et la puce se tait (`sortieDeFiche` juge que
   * l'etape ne montre plus la fiche visee) ; si la fleche ramene a la fiche
   * visee, la puce revient, parce que l'etape derriere est toujours celle du
   * parent : c'est voulu. Plus rien n'est efface a la main.
   *
   * `origineSansHistoriqueRef` N'EST PAS UN SECOND MECANISME : c'est le repli de
   * l'ecran SANS HISTORIQUE (`urlPort == null` : banc d'essai, Studio sans
   * adresse), qui n'a pas d'etape ou la ranger. Il tient le meme role en
   * memoire, ecrit aux memes points que l'etape, et ne sert jamais quand un port
   * existe. Une REF et pas un etat : elle ne pilote aucun rendu a elle seule
   * (la fiche ouverte et le niveau de la vue rendent deja).
   */
  const origineSansHistoriqueRef = useRef<OrigineDEtape | null>(null);
  const origineDEtape: OrigineDEtape | null =
    urlPort != null ? (urlPort.origine ?? null) : origineSansHistoriqueRef.current;

  /**
   * L'ORIGINE D'UN ENFANT SE REFAIT DEPUIS SON MODELE PARENT (pivot du
   * 10/09/2026, finition du 10/09/2026).
   *
   * LE CAS QUI L'EXIGE : L'ENTREE A FROID. Un lien colle ou un nouvel onglet sur
   * la fiche d'un enfant : l'adresse suffit a rouvrir la fiche (elle porte les
   * deux ids), mais aucune etape d'historique ne porte la provenance. La puce
   * disait alors « Liste » et ramenait a la liste des MODELES de la porte liee,
   * qui ne contient rien : un enfant n'a AUCUNE autre sortie que son parent.
   *
   * ELLE SE REFAIT AUSSI APRES LES FLECHES DU CURSEUR, et c'est la meme regle,
   * pas une seconde : les freres d'un enfant sont les enfants du MEME modele,
   * donc le parent ne change pas quand on passe de l'un a l'autre, alors que
   * la fleche a remplace l'etape et fait taire l'origine de l'etape.
   *
   * `rang: null` : ON N'A RIEN EMPILE. Le retour est donc une NAVIGATION
   * (remplacer l'etape courante par celle du parent), jamais un depilage, qui
   * renverrait a la page d'avant, c'est-a-dire n'importe ou.
   *
   * ELLE NE S'ECRASE JAMAIS SUR UNE ORIGINE D'ETAPE : le clic depuis la carte
   * du modele a deja rangee la sienne (avec son rang, donc son depilage), et
   * `origineEffective` ne retient celle-ci qu'a defaut. Un CALCUL, pas une
   * memoire : elle n'ecrit rien.
   */
  const origineRefaite = useMemo<OrigineDEtape | null>(() => {
    if (cheminOuvert == null || openId == null) return null;
    const parent = modeleParent?.id === cheminOuvert.modeleId ? modeleParent : null;
    if (parent == null || parent.name === '') return null;
    const enfant = (parent.children ?? []).find(
      (candidat) => candidat.id === cheminOuvert.enfantId,
    );
    if (enfant === undefined) return null;
    const porteParente = porteParenteDUnEnfant(doorsRef.current, parent, enfant, doorId);
    /* SANS PORTE CANDIDATE, ON NE DIT RIEN : la puce garde « Liste », qui est
       faux mais honnete, plutot qu'un nom vers une porte qui n'a pas ce modele. */
    if (porteParente == null) return null;
    return {
      porte: doorId,
      id: openId,
      titre: parent.name,
      /* LA MEME ADRESSE QUE LE SAUT AURAIT ECRITE, par la MEME fonction : la
         fiche du modele, dans SA porte, en mode Modeles (`mod=1`), avec la vue
         FICHE de cette porte-la. */
      adresse: adresseDeFicheLiee(
        porteParente.id,
        parent.id,
        typesDePorte(porteParente, optionsRef.current.types),
        true,
      ),
      app: appId,
      rang: null,
      org: null,
      parent: null,
    };
  }, [cheminOuvert, openId, modeleParent, doorId, appId]);

  /**
   * L'ORIGINE QUE LA PUCE UTILISE : celle de l'etape quand elle vise LA fiche
   * ouverte (meme porte, meme id), sinon l'origine refaite d'un enfant, sinon
   * rien.
   */
  const origineEffective: OrigineDEtape | null =
    origineDEtape != null && origineDEtape.porte === doorId && origineDEtape.id === openId
      ? origineDEtape
      : origineRefaite;
  /** MIROIR DE LECTURE pour les callbacks (une ref a jour a chaque rendu), pas une memoire. */
  const origineEffectiveRef = useRef<OrigineDEtape | null>(null);
  origineEffectiveRef.current = origineEffective;

  /* ---- Persistance de session ---- */

  useEffect(() => {
    if (activeDoor?.canvas != null) return;
    writeSession(viewMemoryKey(appId, screenId, activeDoor?.id), { viewId, openId, lastCollectionViewId });
    // Une vue arrivee par adresse compte aussi comme derniere vue utilisee.
    if (activeDoor != null && activeTypes.find((type) => type.id === viewId)?.level === 'collection') {
      const key = lastViewPrefKey(appId, screenId, activeDoor.id);
      if (readPref<string | null>(key, null) !== viewId) writePref(key, viewId);
    }
  }, [appId, screenId, activeDoor?.id, activeDoor?.canvas, viewId, openId, lastCollectionViewId]);

  /* ---- Actions ---- */

  /* ---- Filtres, tri : les mutations qui posent des tags ---- */

  /**
   * LE CHEMIN UNIQUE des mutations de filtres et de tri. Deux raisons d'exister :
   * - passer par `nextQuery`, qui tient les miroirs V1 et la peremption des
   *   pages serveur (aucune mutation n'ecrit `filterIds` ou `sortStack` a la
   *   main ailleurs) ;
   * - tenir la regle du TIROIR : il s'ouvre tout seul quand l'etat passe de
   *   "aucun tag" a "des tags", et JAMAIS sur les tags suivants (refermer a la
   *   main reste respecte). C'est une transition, pas une presence : la
   *   detecter ici, au seul endroit qui voit l'avant et l'apres, evite a
   *   chaque surface de la recoder en comparant des compteurs.
   * `compute` rend null quand il n'y a rien a faire : aucun setState, aucun
   * rendu, aucune fausse transition.
   *
   * La transition se juge sur l'etat NORMALISE (`nextQuery`), jamais sur le
   * patch brut : un patch peut porter les MIROIRS V1 (`sort`, `filterId`,
   * l'en-tete du tableau patche encore `sort`) et compter les seuls champs
   * canoniques du patch raterait leur effet.
   */
  const mutateTags = useCallback(
    (compute: (previous: ScreenQuery) => Partial<ScreenQuery> | null): void => {
      const previous = queryRef.current;
      const patch = compute(previous);
      if (patch == null) return;
      const server = paginationRef.current === 'server';
      const next = nextQuery(previous, patch, server, modelesAutorisesRef.current);
      // LES MODES NE COMPTENT PAS (28/09/2026) : ils ne posent plus de tag en
      // zone 4, c'est le titre qui les dit. Le tiroir ne s'ouvre donc qu'au
      // premier FILTRE ou TRI pose, jamais a l'entree d'un mode.
      const countTags = (state: ScreenQuery): number =>
        state.filterIds.length + state.sortStack.length;
      const before = countTags(previous);
      const after = countTags(next);
      /*
       * LA SELECTION TOMBE quand la TRANCHE change (chantier 3_0) : un filtre
       * pose ou retire, une entree ou une sortie du mode Archives. On agit sur
       * ce qu'on voit, et ces deux gestes changent ce qu'on voit.
       *
       * LE TRI N'EST PAS DANS CETTE LISTE, et la nuance vaut d'etre lue en
       * entier, parce qu'elle n'est pas la meme dans les deux modes de
       * pagination. En pagination CLIENTE, trier ne retire aucune ligne du jeu,
       * il les remet dans un autre ordre : vider la selection la-dessus serait
       * une punition sans cause. En pagination SERVEUR, changer le NIVEAU 1
       * perime les pages deja empilees (`nextQuery` remet `after` a null) et
       * recharge une page 1 REORDONNEE : les lignes cochees qui n'y sont plus
       * ont quitte l'ecran, donc l'invariant (`visibleSelection`) les retire
       * tout seul, et le compte suit. Ce ne sont pas deux regles, c'est la meme,
       * tenue au meme endroit : on garde ce qu'on voit encore.
       *
       * La comparaison porte sur l'IDENTITE de `filterIds` : `nextQuery`
       * recopie la reference telle quelle quand le patch ne la touche pas
       * (poser un tri ne fabrique pas un tableau de filtres neuf).
       */
      if (
        next.filterIds !== previous.filterIds ||
        next.archivesMode !== previous.archivesMode ||
        // Entrer dans les modeles ou en sortir remplace la liste ENTIERE : les
        // lignes cochees n'y sont plus, et un lot lance dessus ecrirait sur des
        // documents qu'on ne voit plus.
        next.modelesMode !== previous.modelesMode
      ) {
        dropSelection();
      }
      setQueryState((current) => nextQuery(current, patch, server, modelesAutorisesRef.current));
      if (before === 0 && after > 0) setDrawerOpenState(true);
    },
    [dropSelection],
  );

  const setQuery = useCallback(
    (patch: Partial<ScreenQuery>): void => {
      /*
       * LES CHAMPS A TAGS PASSENT PAR `mutateTags` : une vue qui patche le tri
       * ou le filtre (contrat `onQueryChange`, l'en-tete du tableau patche
       * `sort`) pose un tag en zone 4a, et la regle du tiroir ("il s'ouvre au
       * PREMIER tag pose, quel que soit le geste") vaut pour ce chemin aussi.
       * Sans ce detour, le premier clic d'en-tete posait son tag dans un
       * tiroir ferme : seul le point d'encre de l'outil tri bougeait, et rien
       * ne montrait ce que le clic venait de creer. Ecran sans portes :
       * ecriture directe, le tiroir n'y existe pas.
       */
      if (
        doorsRef.current.length > 0 &&
        ('sort' in patch ||
          'sortStack' in patch ||
          'filterId' in patch ||
          'filterIds' in patch ||
          // Les deux modes passent par le meme chemin (selection, adresse).
          'archivesMode' in patch ||
          'modelesMode' in patch)
      ) {
        mutateTags(() => patch);
        return;
      }
      /*
       * ECRAN SANS PORTES : le detour par `mutateTags` n'existe pas, donc la
       * chute de la selection se pose ici, sur les MEMES champs (la tranche et
       * le jeu), jamais sur le tri. Les deux chemins tiennent la meme regle ;
       * celui du haut la tient par `mutateTags`.
       */
      if (
        'search' in patch ||
        'filterId' in patch ||
        'filterIds' in patch ||
        'archivesMode' in patch ||
        'modelesMode' in patch ||
        'section' in patch
      ) {
        dropSelection();
      }
      setQueryState((previous) =>
        nextQuery(
          previous,
          patch,
          paginationRef.current === 'server',
          modelesAutorisesRef.current,
        ),
      );
    },
    [mutateTags, dropSelection],
  );

  const setSearch = useCallback(
    (value: string): void => {
      // CHERCHER FAIT TOMBER LA SELECTION, entierement : la recherche change
      // ce qu'on voit, et on agit sur ce qu'on voit. Gratuit quand il n'y a
      // rien a vider (voir `dropSelection`), donc sans effet sur la frappe.
      dropSelection();
      setQueryState((previous) => ({ ...previous, search: value }));
    },
    [dropSelection],
  );

  /** Lecture V1 : la pastille a choix unique REMPLACE l'etat. */
  const setFilter = useCallback(
    (id: string): void => {
      mutateTags(() => ({ filterIds: [id] }));
    },
    [mutateTags],
  );

  const saveFieldCondition = useCallback((condition: FieldCondition): void => {
    if (!filterFields.some((field) => field.key === condition.field)) return;
    setConditionsByDoor((previous) => {
      const current = previous[doorId] ?? [];
      return { ...previous, [doorId]: [...current.filter((item) => item.id !== condition.id), condition] };
    });
    dropSelection();
    setDrawerOpenState(true);
  }, [doorId, filterFields, dropSelection]);

  const toggleFilter = useCallback(
    (id: string): void => {
      // Verifie contre le catalogue de la porte : un id inconnu poserait un
      // tag qui ne filtre rien, l'etat actif menteur qu'on refuse deja aux
      // adresses.
      if (!filterDefsRef.current.some((def) => def.id === id)) {
        if (import.meta.env.DEV) {
          console.warn(
            `[taki/views] toggleFilter("${id}") : cet id n'est pas dans le catalogue ` +
              'de filtres de la porte active. Rien n\'est pose.',
          );
        }
        return;
      }
      mutateTags((previous) => ({
        filterIds: previous.filterIds.includes(id)
          ? previous.filterIds.filter((other) => other !== id)
          : [...previous.filterIds, id],
      }));
    },
    [mutateTags],
  );

  const removeFilter = useCallback(
    (id: string): void => {
      if (id.startsWith('~field:')) {
        setConditionsByDoor((previous) => ({ ...previous, [doorId]: (previous[doorId] ?? []).filter((condition) => condition.id !== id) }));
        dropSelection();
        return;
      }
      mutateTags((previous) =>
        previous.filterIds.includes(id)
          ? { filterIds: previous.filterIds.filter((other) => other !== id) }
          : null,
      );
    },
    [mutateTags, doorId, dropSelection],
  );

  const clearFilters = useCallback((): void => {
    clearFieldConditions();
    mutateTags((previous) => (previous.filterIds.length === 0 ? null : { filterIds: [] }));
  }, [mutateTags, clearFieldConditions]);

  /** Popover de tri : ajoute un niveau ASCENDANT en bout de pile, ou le retire. */
  const toggleSort = useCallback(
    (key: string): void => {
      mutateTags((previous) => ({
        sortStack: previous.sortStack.some((levelSort) => levelSort.key === key)
          ? previous.sortStack.filter((levelSort) => levelSort.key !== key)
          : [...previous.sortStack, { key, dir: 'asc' as const }],
      }));
    },
    [mutateTags],
  );

  /** Le corps d'un tag de tri, et le second clic d'un en-tete de colonne. */
  const invertSort = useCallback(
    (key: string): void => {
      mutateTags((previous) => {
        if (!previous.sortStack.some((levelSort) => levelSort.key === key)) return null;
        return {
          sortStack: previous.sortStack.map((levelSort) =>
            levelSort.key === key
              ? { key, dir: levelSort.dir === 'asc' ? ('desc' as const) : ('asc' as const) }
              : levelSort,
          ),
        };
      });
    },
    [mutateTags],
  );

  const removeSort = useCallback(
    (key: string): void => {
      mutateTags((previous) =>
        previous.sortStack.some((levelSort) => levelSort.key === key)
          ? { sortStack: previous.sortStack.filter((levelSort) => levelSort.key !== key) }
          : null,
      );
    },
    [mutateTags],
  );

  const clearSort = useCallback((): void => {
    mutateTags((previous) => (previous.sortStack.length === 0 ? null : { sortStack: [] }));
  }, [mutateTags]);

  /**
   * "Effacer filtres" (zone 4a) : les filtres ET la pile de tri d'un coup.
   *
   * UN SEUL patch, donc UN SEUL rendu et UNE SEULE ecriture d'adresse. Enchainer
   * `clearFilters()` puis `clearSort()` ferait deux passages dans `mutateTags`,
   * donc deux setState en cascade : la liste se reprojette deux fois (elle
   * clignote, l'etat intermediaire "plus de filtres mais encore un tri" est
   * visible) et le parametre `f` de l'adresse est reecrit deux fois. C'est le
   * meme patron qu'`applyFavorite`, qui pose lui aussi les deux champs en une
   * passe.
   *
   * `null` quand il n'y a rien a vider : aucun rendu, et surtout aucune fausse
   * transition de tiroir. Rien d'autre ne bouge : ni la recherche, ni la porte,
   * ni la fiche ouverte.
   */
  const clearAll = useCallback((): void => {
    clearFieldConditions();
    // LES MODES RESTENT (28/09/2026) : « Reinitialiser » vide la TRANCHE, pas
    // le JEU. En mode Archives, on retrouve toutes les archives.
    mutateTags((previous) =>
      previous.filterIds.length === 0 && previous.sortStack.length === 0
        ? null
        : { filterIds: [], sortStack: [] },
    );
  }, [mutateTags, clearFieldConditions]);

  /**
   * LE MODE ARCHIVES (chantier 2_0). Il passe par `mutateTags` comme les
   * filtres et le tri, et pas par `setQueryState` : c'est ce qui lui donne
   * gratuitement la chute de la selection, la peremption des pages en
   * pagination serveur et l'ecriture de l'adresse.
   *
   * Il ne touche a RIEN d'autre : ni la recherche, ni la porte, ni la fiche
   * ouverte, ni les filtres poses. Les filtres survivent a l'aller-retour, et
   * tranchent les archives comme ils tranchent le travail courant.
   */
  const setArchivesMode = useCallback(
    (on: boolean): void => {
      /* L'AUTRE MODE S'ETEINT DANS LE MEME PATCH, mais PAS ICI : `nextQuery` le
         fait pour tous les chemins a la fois (un patch qui ALLUME un mode
         eteint l'autre). L'ecrire aussi dans ce setter en ferait un fait ecrit
         deux fois, et surtout ca laisserait `setQuery({ archivesMode: true })`
         allumer les DEUX, ce qui produisait deux tags et une adresse
         `arch=1&mod=1` que la relecture interprete en « archives » : l'ecran
         affiche une liste, le lien qu'on partage en ouvre une autre. */
      mutateTags((previous) => (previous.archivesMode === on ? null : { archivesMode: on }));
    },
    [mutateTags],
  );

  /**
   * LE MODE MODELES, JUMEAU EXACT DU PRECEDENT. Meme chemin (`mutateTags`),
   * donc memes comportements gratuits : la selection qui tombe, l'adresse qui
   * s'ecrit. Il ne touche a rien d'autre :
   * ni la recherche, ni la porte, ni les filtres poses, qui survivent a
   * l'aller-retour comme ils survivent aux archives.
   *
   * IL N'ATTEND PAS DE SAVOIR S'IL Y A DES MODELES : le mode s'allume, la liste
   * se lit, et zero modele affiche l'etat vide qui dit comment en creer un. Le
   * conditionner aux DONNEES ferait un bouton qui, une fois sur deux, ne fait
   * rien de visible. Il attend en revanche la CONFIGURATION, et ce n'est pas la
   * meme chose : `nextQuery` refuse de l'allumer sur une porte qui ne declare
   * pas les modeles, quel que soit l'appelant.
   */
  const setModelesMode = useCallback(
    (on: boolean): void => {
      /* Symetrie exacte de `setArchivesMode` : l'exclusivite est tenue par
         `nextQuery`, pas ici. */
      mutateTags((previous) => (previous.modelesMode === on ? null : { modelesMode: on }));
    },
    [mutateTags],
  );

  /*
   * LE FILET DE LA GARDE DE CONFIGURATION. `nextQuery` empeche le mode de
   * s'ALLUMER sans configuration complete, sur tous les chemins de mutation ;
   * il ne peut rien pour un mode DEJA allume dont la configuration disparait
   * sous les pieds (l'hote demonte son `ModelesProvider`, une porte cesse de
   * declarer `modeles: true` au rechargement de sa declaration, une app force
   * `setQuery({ section })` vers une porte qui n'en veut pas). On en sort alors
   * comme on sortirait par la croix du tag : c'est la MEME regle
   * (`modelesAutorises`), lue au seul moment ou elle peut changer sans qu'une
   * mutation de `query` ne passe.
   */
  useEffect(() => {
    if (query.modelesMode && !modelesAutorises) setModelesMode(false);
  }, [query.modelesMode, modelesAutorises, setModelesMode]);

  /** L'etoile (3b) : le tiroir a la demande. */
  const setDrawerOpen = useCallback((open: boolean): void => {
    setDrawerOpenState(open);
  }, []);

  /** Le bouton « Nouveautes » (3b). */
  const setNouveautesSeules = useCallback((on: boolean): void => {
    setNouveautesSeulesState(on);
  }, []);

  /* ---- Favoris : appliquer, garder, oublier ---- */

  const applyFavorite = useCallback(
    (id: string): void => {
      const favorite = favoritesRef.current.find((candidate) => candidate.id === id);
      if (favorite == null) return;
      clearFieldConditions();
      // Les filtres d'un favori garde hier peuvent citer un id retire du
      // catalogue depuis : on ne pose que ce qui existe. L'etat obtenu ne
      // correspondra alors plus exactement au favori, qui ne s'allumera pas :
      // honnete, et visible, plutot qu'un tag mort.
      const defs = filterDefsRef.current;
      const filterIds = favorite.filterIds.filter((filterId) =>
        defs.some((def) => def.id === filterId),
      );
      mutateTags(() => ({
        filterIds,
        // Le favori reste stocke ENTIER. Seul son rappel sur cette porte
        // retranche les champs que ses bindings ne savent pas trier.
        sortStack: keepAvailableSortLevels(favorite.sortStack, sortKeysRef.current),
      }));
    },
    [mutateTags, clearFieldConditions],
  );

  const keepFavorite = useCallback((): void => {
    if (!canKeepFavoriteRef.current) return;
    const current = queryRef.current;
    // Nom auto SIMPLE : "Favori <n>", premier numero libre. L'id suit le meme
    // numero, prefixe pour ne jamais croiser un id de preset.
    const existing = favoritesRef.current;
    let number = 1;
    while (existing.some((favorite) => favorite.id === `user-${number}`)) number += 1;
    const kept: ScreenFavorite = {
      id: `user-${number}`,
      label: `Favori ${number}`,
      // Copies defensives : l'etat continue de vivre, pas le favori.
      filterIds: [...current.filterIds],
      sortStack: current.sortStack.map((levelSort) => ({ ...levelSort })),
    };
    setStoredFavoritesRef.current([...storedFavoritesRef.current, kept]);
  }, []);

  const forgetFavorite = useCallback((id: string): void => {
    const stored = storedFavoritesRef.current;
    const next = stored.filter((favorite) => favorite.id !== id);
    // Un id de preset (ou inconnu) ne change rien : les presets appartiennent
    // a la porte, seule la liste gardee s'oublie.
    if (next.length !== stored.length) setStoredFavoritesRef.current(next);
  }, []);

  /**
   * LES DEUX BORDS DE LA SESSION DE FICHE. La pile d'annulation et
   * `saveState` vivent le temps d'UNE fiche ouverte : ils survivent aux fleches
   * precedent / suivant (on continue de travailler sur le meme jeu), et ils
   * tombent aux deux bouts.
   *
   * A LA SORTIE : "Enregistre" n'a aucun sens de retour en collection,
   * et `Cmd+Z` ne doit pas defaire depuis la collection une modification
   * invisible a l'ecran.
   *
   * A L'OUVERTURE : une fiche qu'on vient d'ouvrir n'a rien enregistre. Le
   * kanban ecrit au niveau COLLECTION (`onPatchRecord(id, 'groupKey', ...)`) :
   * sans ce nettoyage, deposer une carte puis ouvrir la fiche d'un AUTRE
   * element afficherait aussitot "Enregistre" (la spec exige `idle`,
   * rien du tout, avant la premiere ecriture), et `Cmd+Z` depilerait le pas du
   * kanban, teleporterait sur la carte deplacee et n'annulerait rien (le role
   * `groupKey` n'est pas declare dans le binding de la fiche).
   *
   * Le jeton de session change ici : ce qui atterrit apres ne parle plus.
   */
  const resetRecordSession = useCallback((): void => {
    recordSessionRef.current += 1;
    setUndoStack([]);
    setRedoStack([]);
    undoRef.current = [];
    redoRef.current = [];
    failedRef.current = null;
    // Le geste de fiche rate meurt avec la session, comme l'ecriture ratee :
    // proposer de rejouer un archivage sur la fiche SUIVANTE serait pire que
    // de ne rien proposer.
    failedRecordActionRef.current = null;
    setSaveState({ mode: 'idle' });
  }, []);

  /**
   * TOUTE SORTIE D'UN BROUILLON PASSE PAR ICI, ET C'EST LA REGLE DU CHANTIER :
   * la puce de sortie, `Echap`, « Annuler », une autre fiche, un changement de
   * porte, de vue, un saut par lien, l'entree en Archives ou en Modeles. Un seul
   * endroit decide, donc un seul comportement.
   *
   * TROIS CAS, ET LE PREMIER EST LE PLUS FREQUENT :
   *  - pas de brouillon : la suite se fait, rien ne s'interpose ;
   *  - brouillon INTACT (rien de saisi) : il se ferme SANS RIEN DEMANDER, et la
   *    suite se fait. Demander « es-tu sur ? » pour une fiche vide, c'est faire
   *    payer un clic a quelqu'un qui n'a rien a perdre ;
   *  - brouillon TOUCHE : on garde le geste en attente et on leve la fenetre.
   *    Ce que l'utilisateur a tape ne disparait jamais sans qu'on le lui dise.
   *
   * LA SUITE EST GARDEE EN REF, telle quelle : c'est le MEME geste qui se
   * rejoue apres la reponse, pas une reconstitution. Reconstruire « ou l'on
   * allait » a partir d'un etat serait la seule facon d'y aller ailleurs.
   */
  const demanderSortie = useCallback(
    (suite: () => void): void => {
      const courant = brouillonRef.current;
      if (courant == null) {
        suite();
        return;
      }
      if (!courant.touche) {
        poserBrouillon(null);
        suite();
        return;
      }
      suiteDeSortieRef.current = suite;
      poserBrouillon({ ...courant, sortieEnAttente: true });
    },
    [poserBrouillon],
  );

  const openRecord = useCallback((id: string | null): void => {
    /* QUITTER LA FICHE PARENTE, C'EST SORTIR DU CHEMIN : il n'y a rien a
       filtrer sur la porte parente, et la fiche suivante n'est plus le parent. */
    const dansCourant = queryRef.current.dans;
    if (dansCourant != null && queryRef.current.section === dansCourant.porte && id !== dansCourant.id) {
      setQueryState((previous) =>
        nextQuery(previous, { dans: null }, paginationRef.current === 'server', modelesAutorisesRef.current),
      );
    }
    if (id == null) {
      setOpenId(null);
      setFrozenIds(null);
      resetRecordSession();
      // Sortie de la fiche : retour a la derniere vue collection.
      poserVueActive(lastCollectionRef.current);
      return;
    }
    // OUVRIR, C'EST LIRE : la pastille "Nouveau" s'eteint IMMEDIATEMENT, sans
    // attendre le chargement de la fiche. Attendre la donnee laisserait la
    // ligne allumee pendant l'aller-retour, et une ouverture qui echoue
    // laisserait croire que le geste n'a pas compte. `openRecord(null)` est la
    // FERMETURE de la fiche : elle est deja repartie plus haut, elle ne marque
    // rien (sans quoi refermer eteindrait une ligne qu'on n'a pas ouverte).
    markReadRef.current(id);
    // On ENTRE dans la fiche quand la vue active n'est pas encore de niveau
    // `record` : c'est une session NEUVE. Naviguer au curseur (on y est deja)
    // reste la MEME session, sinon les fleches videraient la pile a chaque pas.
    const current = typesRef.current.find((type) => type.id === viewIdRef.current);
    const entering = current?.level !== 'record';
    if (entering) resetRecordSession();
    else if (openIdRef.current !== id) {
      /*
       * CHANGER DE FICHE AU CURSEUR DESARME LA RELANCE, meme si la SESSION,
       * elle, continue (les fleches gardent volontairement la pile
       * d'annulation : on travaille sur le meme jeu).
       *
       * Un geste de fiche et une ecriture de cellule portent sur UN document
       * NOMME, jamais sur celui qu'on regarde : `retrySave` rejoue la cible
       * memorisee. Sans cette remise a zero, le bandeau rouge et son
       * "Reessayer" survivaient au passage a la fiche SUIVANTE, et cliquer
       * dessus en croyant relancer la fiche affichee rejouait le `remove` de
       * la PRECEDENTE : supprimee definitivement, sans fenetre, sans que rien
       * a l'ecran ne l'ait designee.
       *
       * La relance n'existe donc que tant que la fiche fautive est celle qu'on
       * a sous les yeux. La trace console, elle, reste : l'echec n'est pas
       * efface, il cesse d'etre rejouable a l'aveugle.
       */
      failedRecordActionRef.current = null;
      failedRef.current = null;
      setSaveState({ mode: 'idle' });
    }
    // Le curseur ne se gele qu'a l'OUVERTURE, jamais en naviguant dedans.
    // SAUF pour une fiche qu'on vient de CREER (`gelApresCreationRef`) : le jeu
    // courant ne la contient pas encore, et le gel differe attend la liste
    // rechargee, comme apres « Creer » sur un brouillon.
    if (
      gelApresCreationRef.current !== id &&
      (openIdRef.current == null || frozenRef.current == null)
    ) {
      /* ET UN ENFANT NE SE GELE PAS ICI : son jeu est celui de ses FRERES, que
         seul le modele parent connait (l'effet de gel differe le pose des qu'il
         est charge). Le geler sur le jeu de la porte liee lui donnerait le rang
         d'une fiche qui n'a rien a voir. */
      const ids = lireCheminDEnfant(id) != null ? [] : orderedIdsRef.current;
      setFrozenIds(ids.length > 0 ? freezeIds(ids) : null);
    }
    setOpenId(id);
    if (entering) {
      /* LA VUE DE FICHE D'ARRIVEE (chantier `fiche-suivi`) : le choix de chacun,
         puis le defaut de la porte, puis `core.fiche`. */
      const porte = doorsRef.current.find((candidate) => candidate.id === doorIdRef.current);
      const recordType = vueDeFicheChoisie(
        typesRef.current,
        porte,
        appId,
        screenId,
        doorsRef.current.length > 0 ? doorIdRef.current : undefined,
      );
      if (recordType) poserVueActive(recordType);
    }
  }, [appId, screenId, poserVueActive, resetRecordSession]);

  /**
   * La MEME marque que celle d'`openRecord`, offerte a l'ecran : une fiche
   * qu'on vient de creer soi-meme n'est pas une nouveaute pour soi. Callback
   * STABLE, comme tout ce que l'ecran met en dependance : la ref porte la
   * porte et la memoire du moment, cette fonction ne fait que la relayer.
   */
  const markRead = useCallback((id: string): void => {
    markReadRef.current(id);
  }, []);

  /**
   * CHANGER DE SECTION (zone 1). Deux effets, et le second n'est pas cosmetique :
   * la fiche ouverte se REFERME. Une section segmente les donnees, donc
   * l'element ouvert n'appartient pas forcement a celle qu'on vient de choisir ;
   * le garder affiche montrerait une fiche que la liste d'a cote ne contient
   * pas, et le curseur ("3 / 47") compterait dans un jeu auquel elle n'est plus.
   */
  const applySection = useCallback((id: string): void => {
    if (queryRef.current.section === id) return;
    // Une section segmente les donnees : les lignes choisies appartiennent a
    // celle qu'on quitte. Meme raison qu'au changement de porte, ou la
    // selection tombe deja (`openDoor`).
    dropSelection();
    setQueryState((previous) =>
      // LE MODE ARCHIVES S'ETEINT ICI AUSSI, comme il s'eteint au changement de
      // porte (`openDoor`) : changer de section, c'est changer de jeu de
      // donnees, et rien de l'etat de tranche ne survit a ce geste. C'est le
      // SECOND verrou de la nasse du chantier 2_0 : sur un ecran sans portes,
      // aucune surface ne montre ni n'eteint le mode, donc si quoi que ce soit
      // parvenait a l'y allumer (une app qui force `setQuery`), un changement
      // de section reste une sortie. Le premier verrou, lui, l'empeche de
      // s'allumer par l'adresse (voir `initialArchives`).
      // LES DEUX MODES S'ETEIGNENT ICI, pour la meme raison : changer de section,
      // c'est changer de jeu de donnees, et rien de l'etat de tranche n'y
      // survit.
      nextQuery(
        previous,
        { section: id, archivesMode: false, modelesMode: false },
        paginationRef.current === 'server',
        modelesAutorisesRef.current,
      ),
    );
  }, [dropSelection]);

  /**
   * CHANGER DE PORTE (gabarit v2). Un AUTRE JEU DE DONNEES : rien de l'etat de
   * tranche ne survit. Dans l'ordre :
   * - la fiche se referme (elle appartient a l'ancien jeu), et la vue
   *   d'atterrissage est REVALIDEE contre le catalogue de la nouvelle porte
   *   (deux portes n'ont pas forcement les memes vues) ;
   * - la selection, les lignes chargees et l'element solo tombent : laisser
   *   les lignes de l'ancienne porte a l'ecran pendant le rechargement les
   *   ferait projeter par les bindings de la nouvelle, un melange qu'aucun
   *   message n'explique ;
   * - recherche, filtres et MODE ARCHIVES sont REMIS A ZERO ; la pile de tri
   *   ne garde que les clefs encore comprises par la porte cible (ce que
   *   l'adresse impose, lui, s'applique par-dessus : c'est le cas du lien
   *   partage et du bouton Retour) ;
   * - le tiroir se replie, les actions, stats et placeholder suivent la porte
   *   par construction (`state.door` se derive de `query.section`).
   * Le rechargement, lui, est automatique : la section fait partie de la cle
   * de chargement, exactement comme avant les portes.
   */
  const openDoor = useCallback(
    (
      id: string,
      nextFilterIds: string[],
      nextArchives: boolean,
      nextModeles: boolean,
      /**
       * CE QUE LA PORTE VA OUVRIR, quand l'appelant le sait deja (l'adresse, un
       * saut par lien). Un CHEMIN D'ENFANT y autorise le mode Modeles sur une
       * porte qui ne l'allume pas : voir `modelesAllumables`. Absent partout
       * ailleurs, et le mode retombe alors sur la configuration de la porte.
       */
      ouvre?: string | null,
      /**
       * LE CHEMIN QU'ON EMPORTE (`ScreenQuery.dans`) et la vue d'arrivee que le
       * geste impose (`ScreenDoor.auClic.view`). Absent : changer de porte
       * sort du chemin, comme tout le reste de l'etat de tranche.
       */
      entree?: { dans: DansUneFiche | null; vue?: ViewId },
    ): void => {
      const door = doorsRef.current.find((candidate) => candidate.id === id);
      // Le menu n'offre que le catalogue, et l'adresse inconnue est retombee
      // plus tot : un id inconnu ici est un appel programmatique errone, on ne
      // demonte pas l'ecran pour lui.
      if (door == null) return;
      // LE MODE ARCHIVES NE SUIT PAS DERRIERE UNE PORTE QUI A ABSORBE SES
      // OUTILS, meme quand l'adresse le demande : rien ne pourrait l'y montrer
      // ni l'y eteindre (meme regle qu'a l'initialisation, `initialArchives`).
      const archives = nextArchives && !doorAbsorbsTools(door);
      /* LA GARDE DU MODE MODELES SE POSE SUR LA PORTE QU'ON OUVRE, jamais sur
         celle qu'on quitte, et c'est pour ca qu'elle se calcule ICI : la porte
         d'arrivee peut ne pas declarer les modeles quand la precedente les
         declarait. Elle couvre les trois manques de configuration en plus des
         outils absorbes (`modelesAllumables`). L'exclusivite, elle, est tenue
         par `nextQuery` plus bas, comme sur tous les autres chemins. */
      const permisIci = modelesAllumables({
        hasDoors: true,
        door,
        port: modelesPortRef.current,
        enfant: ouvre != null && lireCheminDEnfant(ouvre) != null,
      });
      const modeles = nextModeles && permisIci;
      const cameFromRecord = openIdRef.current != null;
      if (cameFromRecord) openRecord(null);
      // Les refs de FICHE suivent tout de suite (les etats, eux, attendent le
      // rendu) : quand l'adresse enchaine "changer de porte" et "ouvrir une
      // fiche" dans le meme tick (lien partage, bouton Suivant), la reouverture
      // doit repartir d'une fiche FERMEE et d'un jeu VIDE. Sans ca, elle se
      // croyait deja ouverte (meme id via deux portes : la fiche restait
      // fermee) ou gelait son curseur sur le jeu ordonne de l'ANCIENNE porte ;
      // avec un jeu vide, le gel differe attend les donnees de la nouvelle,
      // exactement le chemin de l'entree a froid.
      openIdRef.current = null;
      frozenRef.current = null;
      orderedIdsRef.current = [];
      // L'arrivee se resout sur la porte cible : epinglage, sa derniere vue,
      // puis son defaut. La vue de la porte quittee ne sert plus de repli.
      const base = optionsRef.current;
      const nextTypes = typesDePorte(door, base.types);
      const nextBindings = door.bindings ?? base.bindings;
      const allowed = (value: string | null): ViewId | undefined => {
        const candidate = asViewId(value);
        const type = nextTypes.find((item) => item.id === candidate && item.level === 'collection');
        return type != null && unmetRequirement(type, nextBindings.find((binding) => binding.type === type.id)) == null
          ? type.id : undefined;
      };
      const pinned = allowed(readPref<string | null>(pinnedViewPrefKey(appId, screenId, id), null));
      const last = allowed(readPref<string | null>(lastViewPrefKey(appId, screenId, id), null));
      const fallback = vueDeCollection(nextTypes, nextBindings, entree?.vue ?? pinned ?? last, door.defaultView)
        ?? nextTypes[0]?.id ?? FALLBACK_VIEW_ID;
      if (door.canvas == null) {
        poserVueActive(fallback);
        setLastCollectionViewId(fallback);
        lastCollectionRef.current = fallback;
      }
      // LES REFS DE CATALOGUE SUIVENT TOUT DE SUITE, comme `lastCollectionRef` :
      // la suite du MEME geste raisonne deja avec la nouvelle porte. Le cas qui
      // l'exige : le bouton Suivant du navigateur vers la fiche d'une autre
      // porte, ou `fromSearch` enchaine openDoor puis openRecord dans le meme
      // tick ; openRecord choisit la vue fiche dans `typesRef`, qui pointerait
      // sinon l'ancien catalogue jusqu'au rendu suivant. Le rendu re-assigne
      // ensuite les memes valeurs, sans rien y changer.
      typesRef.current = nextTypes;
      // La PORTE elle-meme suit ici : `markReadRef.current` la lit pour savoir
      // sous quelle memoire ranger la fiche qu'on ouvre. Sans cette ligne, le
      // bouton Suivant du navigateur vers la fiche d'une AUTRE porte la
      // marquait lue sous l'ancienne, et elle restait nouvelle dans la sienne.
      doorIdRef.current = id;
      bindingsRef.current = door.bindings ?? base.bindings;
      loadRef.current = door.load ?? base.load;
      getOneRef.current = door.get ?? base.get;
      observeRef.current = door.observe ?? base.observe;
      filterDefsRef.current = !doorHasSections(door)
        ? []
        : (door.filters ?? base.filters ?? []);
      // Les trois verbes de la fiche suivent la porte cible dans le MEME tick,
      // au meme titre que le chargeur : une fiche rouverte par l'adresse
      // derriere la nouvelle porte doit deja lire les gestes de celle-la.
      archiveRef.current = door.archive ?? base.archive;
      unarchiveRef.current = door.unarchive ?? base.unarchive;
      removeRef.current = door.remove ?? base.remove;
      setSelection([]);
      setRecords([]);
      setHasMore(false);
      setSolo(null);
      // Le chargement neuf part au prochain commit (la section est dans la cle
      // de chargement), mais `inFlight` passe a vrai TOUT DE SUITE : sans ca,
      // la frame entre ce rendu et l'effet montrerait "Rien pour l'instant"
      // (lignes vides, aucun chargement en vol), un etat vide mensonger le
      // temps d'un battement. Meme raison pour l'erreur : celle de l'ancienne
      // porte ne doit pas s'afficher une frame au-dessus de la nouvelle.
      setError(undefined);
      inFlightRef.current = true;
      setInFlight(true);
      setQueryState((previous) =>
        nextQuery(
          previous,
          {
            section: id,
            search: '',
            filterIds: nextFilterIds,
            // Un tri partage par les deux portes garde sa priorite et son
            // sens. Seuls les niveaux propres au jeu quitte tombent.
            sortStack: keepAvailableSortLevels(
              previous.sortStack,
              sortKeysOf(door.bindings ?? base.bindings, paginationRef.current),
            ),
            // La FENETRE ("Afficher plus") et l'INTERVALLE du calendrier
            // retombent aussi : un autre jeu de donnees ne garde rien de
            // l'etat de tranche precedent. `nextQuery` marque la peremption
            // serveur tout seul (`limit` et `range` sont dans sa liste stale).
            limit:
              optionsRef.current.limit ??
              (paginationRef.current === 'server' ? DEFAULT_PAGE_SIZE : DEFAULT_LIMIT),
            range: null,
            // LE MODE ARCHIVES S'ETEINT AU CHANGEMENT DE PORTE, comme tout le
            // reste de l'etat de tranche : un autre jeu de donnees ne garde
            // rien du precedent. Il ne reste allume que si l'ADRESSE le
            // demande (lien partage, bouton Retour), et il est alors pose ici
            // dans le meme patch, sans rechargement de plus.
            archivesMode: archives,
            // Meme chose pour les modeles : ils repartent eteints derriere une
            // porte neuve, sauf si l'adresse les demande.
            modelesMode: modeles,
            // LE CHEMIN ne survit au changement de porte que si on l'emporte.
            dans: entree?.dans ?? null,
          },
          paginationRef.current === 'server',
          // La permission de la porte CIBLE, pas celle de la porte quittee :
          // `modelesAutorisesRef` ne suivra qu'au prochain rendu.
          permisIci,
        ),
      );
      // Replie par defaut derriere une porte neuve ; OUVERT quand l'adresse
      // pose deja des filtres, pour que le destinataire d'un lien voie ce qui
      // explique sa liste. Un mode, lui, se lit dans le titre.
      setDrawerOpenState(nextFilterIds.length > 0);
      // Et « Nouveautes » repart ETEINT : le reperage est celui d'une porte.
      setNouveautesSeulesState(false);
    },
    [appId, screenId, openRecord, poserVueActive],
  );

  sortirDuCheminRef.current = (dans: DansUneFiche): void => {
    if (!memeDans(queryRef.current.dans, dans)) return;
    openDoor(dans.porte, [], false, false);
  };

  const setSection = useCallback(
    (id: string): void => {
      /* LA MEME PORTE, MAIS DANS UNE FICHE : le menu en SORT (toutes les
         taches, et plus celles d'un projet). */
      if (queryRef.current.section === id && queryRef.current.dans == null) return;
      // Avec des portes, changer de section EST changer de porte. Sans, le
      // comportement historique ne bouge pas d'un cheveu : les sections
      // restent des tranches du meme jeu, recherche et filtres survivent.
      if (doorsRef.current.length > 0) {
        // Un clic de menu n'apporte aucun etat : ni filtre, ni l'un des modes.
        openDoor(id, [], false, false);
        return;
      }
      if (openIdRef.current != null) openRecord(null);
      applySection(id);
    },
    [applySection, openRecord, openDoor],
  );

  /**
   * OUVRIR UNE FICHE D'UNE AUTRE PORTE, depuis une CARTE LIEE de la fiche
   * courante (`ViewProps.onOpenLinked`).
   *
   * ON CHANGE DE PORTE, on n'empile pas une fiche dans une fiche : la fiche
   * liee s'ouvre DANS SA porte, avec ses colonnes, ses gestes et ses droits
   * (decision du fondateur, 08/09/2026). Une fiche empilee n'a pas d'adresse,
   * et une adresse est ce qui se partage et ce qui se recharge.
   *
   * IL PASSE PAR L'ADRESSE, ET C'EST LE POINT. Le chemin « changer de porte
   * puis ouvrir » existe deja : c'est celui d'un lien colle et du bouton
   * Suivant du navigateur (`fromSearch`, qui enchaine `openDoor` puis
   * `openRecord` dans le meme tick). En ECRIVANT l'adresse ici, on l'emprunte
   * tel quel au lieu d'en ouvrir un second, et on gagne la seule chose que
   * l'etat ne sait pas faire : une VRAIE etape d'historique (`push`). Sans
   * elle, le sens 1 REMPLACERAIT l'etape (on entre dans une fiche depuis une
   * fiche : ni `entering` ni `leaving`), et le retour navigateur ne ramenerait
   * pas au parent, alors que c'est exactement ce qui a ete decide.
   *
   * SANS ADRESSE (hors shell, ou second etat d'ecran d'une meme route), on
   * retombe sur les memes deux gestes, en memoire : la fiche s'ouvre, sans
   * lien partageable et sans retour navigateur. Un ecran sans routeur n'a
   * jamais eu ni l'un ni l'autre.
   *
   * IL REND VRAI QUAND LA NAVIGATION EST ACCEPTEE, et faux sur le seul refus
   * possible : une porte que cet ecran ne declare pas. L'appelant en a besoin :
   * le « + Ajouter » d'une carte liee POSE une intention de creation, et la
   * poser sans arriver la laisserait en attente dans une porte ou l'on ne va
   * pas, prete a rouvrir une fenetre au passage suivant.
   */
  const openLinked = useCallback(
    (doorId: string, id?: string, options?: OptionsDeSaut): boolean => {
      /* SANS ID, ON PASSE DANS LA PORTE, PAS DANS UNE FICHE : c'est le
         « + Ajouter » d'une carte liee, qui va CREER la-bas. L'adresse ne porte
         alors que la porte (`?s=<porte>`), toujours en PUSH : l'etape de la
         fiche parente doit rester derriere, sinon le retour navigateur, qui
         doit y ramener, sortirait de l'app. */
      const door = doorsRef.current.find((candidate) => candidate.id === doorId);
      if (door == null) {
        // La carte a ete assemblee contre les portes de l'ecran : une porte
        // inconnue ici veut dire que ce `useScreen` n'a pas les memes. On ne
        // demonte pas l'ecran pour ca, on le dit.
        console.warn(
          `[taki/views] Lien vers la porte "${doorId}", que cet ecran ne declare pas : ` +
            'la fiche liée ne peut pas s\'ouvrir.',
        );
        return false;
      }
      /* LA CIBLE, NORMALISEE UNE SEULE FOIS : `undefined` et la chaine vide
         disent la meme chose, « on passe dans la porte, on n'ouvre pas de
         fiche ». */
      const cible = id === undefined || id === '' ? null : id;
      /* L'ADRESSE SE FABRIQUE A PART (`adresseDeFicheLiee` et sa jumelle sans
         fiche, deux fonctions pures et testees) : le catalogue passe est celui
         de la porte CIBLE, son repli d'ecran resolu ici, car c'est ici qu'on a
         les deux. */
      /* LE MODE MODELES SUIT LE SAUT, ET C'EST TOUT CE QUI CHANGE DE CE COTE :
         une ligne de carte liee d'un MODELE ouvre un ENFANT dans la porte liee,
         et le « + Ajouter » de la meme carte y ouvre le brouillon qui en
         ajoutera un. Dans les deux cas l'adresse doit dire `mod=1` : la porte
         d'arrivee ne l'allume pas d'elle-meme, et la puce de sortie ramene a la
         fiche du modele PARENT, qui n'existe que dans ce mode.
         SAUF QUAND L'APPELANT LE FIXE (`options.modeles`, chantier
         `plan-sur-modele`) : la fleche du modele d'un plan l'ouvre EN mode
         Modeles depuis une fiche ordinaire, et le plan cree depuis un modele
         s'ouvre HORS de ce mode, depuis un modele ouvert. */
      const enModeles = options?.modeles ?? queryRef.current.modelesMode;
      const adresseCible =
        cible == null
          ? adresseDePorteLiee(doorId, enModeles)
          : adresseDeFicheLiee(
              doorId,
              cible,
              typesDePorte(door, optionsRef.current.types),
              enModeles,
              vueDeFicheChoisie(typesDePorte(door, optionsRef.current.types), door, appId, screenId, doorId),
            );
      /*
       * L'ORIGINE DU SAUT SE LIT MAINTENANT, ET NULLE PART AILLEURS : le titre
       * et l'adresse sont ceux de la fiche PARENTE, celle qu'on regarde encore.
       * Un tick plus tard la porte a change, la fiche parente est refermee, et
       * il n'y a plus rien a nommer.
       *
       * LE TITRE VIENT DE LA LIGNE PROJETEE de la fiche ouverte : elle couvre a
       * elle seule les trois sources (le jeu affiche, le jeu en memoire, la
       * fiche chargee seule a froid). Repli sur le libelle de la porte
       * courante : la puce DIT ou l'on retombe, elle ne peut pas etre muette.
       *
       * SANS FICHE VISEE, PAS D'ORIGINE, et l'ancienne s'efface : le
       * « + Ajouter » d'une carte liee PASSE dans la porte pour y creer, il
       * n'ouvre aucune fiche, donc aucune puce n'a de retour a proposer.
       */
      const titreDuParent =
        options?.origine ??
        openRowRef.current?.title ??
        doorsRef.current.find((candidate) => candidate.id === doorIdRef.current)?.label ??
        '';
      /* SANS TITRE, PAS D'ORIGINE : une puce muette n'existe pas (meme regle que
         le defaut « Retour » de `recordExitLabel`), et le gabarit a deja une
         sortie qui, elle, sait toujours se nommer. Le cas est theorique (une
         ligne projetee a toujours un titre, une porte a toujours un libelle),
         mais la consequence ne l'est pas : une puce vide au bord de la rangee 3
         ne se lit meme plus comme un bouton.
         UN SEUL CHEMIN POUR LA FICHE VISEE ET POUR LE « + AJOUTER » : sans
         fiche visee, la fiche vide qui s'ouvrira (`BROUILLON_ID`) doit pouvoir
         dire d'ou l'on vient et y ramener, exactement comme une fiche arrivee
         par un lien. */
      const depuis: OrigineDeFiche | null =
        titreDuParent === ''
          ? null
          : {
              porte: doorId,
              id: cible ?? BROUILLON_ID,
              titre: titreDuParent,
              adresse: pickScreenSearch(urlPort?.params ?? toSearchRef.current()),
            };
      if (urlPort != null) {
        /* L'ORIGINE SE RANGE DANS L'ETAPE POUSSEE : le port la complete (app,
           rang, org, parent) et la garde avec l'etape, pour le F5, Precedent
           et les sauts entre apps. */
        urlPort.setParams(adresseCible, { push: true, ...(depuis == null ? {} : { depuis }) });
        return true;
      }
      /* SANS ADRESSE (hors shell), les memes deux gestes, en memoire.
         `openDoor` referme deja la fiche ouverte : il n'y a rien a fermer de
         plus quand on ne va pas en ouvrir une autre. */
      /* LE MODE COMPTE AUTANT QUE LA PORTE : un saut dans la MEME porte mais
         dans l'autre mode (le modele d'un plan, le plan d'un modele) doit
         changer de jeu, sinon l'id se chercherait dans le mauvais. */
      if (queryRef.current.section !== doorId || queryRef.current.modelesMode !== enModeles) {
        openDoor(doorId, [], false, enModeles, cible);
      }
      openRecord(cible);
      /* HORS SHELL, L'ORIGINE VA DANS LA MEMOIRE DE L'ECRAN SANS HISTORIQUE
         (`origineSansHistoriqueRef`), APRES LES GESTES : ils sont synchrones et
         `ouvrirBrouillon` relit cette memoire pour le « + Ajouter ». */
      origineSansHistoriqueRef.current =
        depuis == null ? null : { ...depuis, app: appId, rang: null, org: null, parent: null };
      return true;
    },
    [urlPort, openDoor, openRecord, appId, screenId],
  );

  const setView = useCallback((id: ViewId): void => {
    const type = typesRef.current.find((candidate) => candidate.id === id);
    if (type == null) return;
    if (unmetRequirement(type, bindingsRef.current.find((binding) => binding.type === id)) != null) return;
    if (type.level === 'record') {
      // Fiche sans element ouvert : on ouvre le premier du jeu ordonne
      // courant. Entree legitime, pas un accident.
      if (openIdRef.current == null) {
        const first = firstOf(orderedIdsRef.current);
        if (first == null) return; // Collection vide : l'entree est grisee.
        setFrozenIds(freezeIds(orderedIdsRef.current));
        setOpenId(first);
      }
      // Le SELECTEUR est la seconde ENTREE dans la fiche : session NEUVE, au
      // meme titre qu'un clic sur une ligne (cf. `resetRecordSession`). Rien a
      // faire si on y est deja : on ne coupe pas la session en cours.
      const current = typesRef.current.find((candidate) => candidate.id === viewIdRef.current);
      if (current?.level !== 'record') resetRecordSession();
      // LA DERNIERE VUE DE FICHE UTILISEE (`auClic: { derniereVue }`) : celle que
      // la personne choisit ici, par le selecteur. Une vue qui n'est jamais
      // d'arrivee (`arrivee: false`) ne l'ecrase pas : la derniere vue
      // utilisable reste la memoire.
      if (type.arrivee !== false && viewIdRef.current !== id) {
        writePref(lastRecordViewPrefKey(appId, screenId, doorsRef.current.length > 0 ? doorIdRef.current : undefined), id);
      }
    } else {
      // `lastCollectionViewId` n'est ecrit QUE par une vue collection : la
      // fiche ne peut pas corrompre la memoire de sortie. La vue collection
      // choisie devient aussi la vue par defaut au prochain passage.
      setLastCollectionViewId(id);
      lastCollectionRef.current = id;
      writePref(lastViewPrefKey(appId, screenId, doorsRef.current.length > 0 ? doorIdRef.current : undefined), id);
      // Retour au niveau COLLECTION : le gel du curseur tombe. Sans ca, la
      // prochaine ouverture rejouerait la liste d'ids de la premiere fiche de
      // la session, et "3 / 47" designerait un rang de l'ancien jeu (ou
      // resterait a ".. / .."). Le gel se refait a l'ouverture suivante, sur le
      // jeu courant. La ref suit tout de suite : `openRecord` la lit.
      frozenRef.current = null;
      setFrozenIds(null);
      // Le SELECTEUR est la seconde sortie de la fiche, et elle ne passe pas
      // par `openRecord(null)` : `openId` reste pose (voir le commentaire
      // au-dessus). La session de fiche se termine quand meme, sinon la pile
      // d'annulation survivrait a une fiche qu'on ne voit plus, et `Cmd+Z`
      // defairait depuis la collection une modification invisible.
      resetRecordSession();
    }
    poserVueActive(id);
  }, [appId, screenId, poserVueActive, resetRecordSession]);

  /**
   * LE RECHARGEMENT NU : il relit la porte et ne touche a rien d'autre.
   *
   * Il existe a part parce qu'un lot termine recharge la porte SANS vider la
   * selection : les fiches modifiees ont peut-etre quitte la vue (le lot vient
   * peut-etre de changer le champ sur lequel l'ecran filtre ou trie), et c'est
   * l'INVARIANT qui doit alors les retirer du lot, une par une, en laissant
   * celles qui sont encore la. La barre de lot se referme ainsi d'elle-meme
   * quand elle se vide, au lieu de se refermer a tous les coups.
   */
  const reloadPort = useCallback((): void => {
    /* L'INVALIDATION DES ANNUAIRES, C'EST LE RECHARGEMENT DE LA PORTE, ET RIEN
       D'AUTRE. Elle vivait dans `reload()` jusqu'a la relecture du 09/09/2026,
       et un LOT passait donc a cote : `endBulkWrite` recharge par ICI, sans
       vider la selection, et les noms des fiches liees restaient ceux d'avant la
       modification groupee. Il n'y a pas de peremption au temps, et il ne faut
       pas en ajouter une : un cache qui se rafraichit tout seul rend le meme
       ecran different d'une seconde a l'autre sans que rien ne l'explique. */
    invaliderAnnuaires();
    // Mode SERVEUR : recharger, c'est repartir de la premiere page. Garder le
    // curseur relirait la DERNIERE page seulement, et une modification faite
    // page 1 resterait invisible. Les deux setState sont groupes par React :
    // un seul chargement part.
    if (paginationRef.current === 'server') {
      setQueryState((previous) => (previous.after == null ? previous : { ...previous, after: null }));
    }
    setReloadTick((tick) => tick + 1);
  }, []);

  useEffect(() => {
    let generation = 0;
    const unsubscribe = onExternalRecordMutation(({ recordId }) => {
      // Le même jeu peut être affiché dans plusieurs apps. Seul le chargeur de
      // chaque écran sait si la mutation le concerne ; tous les écrans montés
      // relisent donc leur collection, y compris leurs compteurs et annuaires.
      reloadPort();
      const soloId = soloRef.current?.id;
      if (soloId == null || (recordId != null && soloId !== recordId) || getOneRef.current == null) return;
      const current = ++generation;
      const door = doorIdRef.current;
      const getOne = getOneRef.current;
      void getOne(soloId).then((fresh) => {
        if (fresh != null && current === generation && doorIdRef.current === door && soloRef.current?.id === soloId) {
          setSolo({ id: soloId, record: fresh });
        }
      }).catch(() => {
        // La collection se recharge déjà ; on garde la fiche visible si sa
        // lecture isolée échoue, sans afficher un faux état introuvable.
      });
    });
    return () => { generation += 1; unsubscribe(); };
  }, [reloadPort]);

  /**
   * RECHARGER, le geste offert a l'ecran et au bouton "Reessayer" du ViewHost.
   * Il fait tomber la selection, comme changer de porte ou de filtre : on
   * redemande le jeu au serveur, donc plus rien ne garantit que les lignes
   * qu'on avait cochees soient encore la ni encore les memes.
   */
  const reload = useCallback((): void => {
    dropSelection();
    /* LES ANNUAIRES SONT PERIMES PAR `reloadPort`, ET PAR LUI SEUL : tous les
       chemins de rechargement y passent, celui d'un lot compris. */
    reloadPort();
  }, [dropSelection, reloadPort]);

  /**
   * Rafraichir UN element dans le jeu, sans toucher a la pagination.
   *
   * C'est ce qui remplace le rechargement complet apres une ecriture en mode
   * serveur : relire toute la liste ferait retomber l'utilisateur en page 1
   * apres chaque champ modifie, ce qui est intenable des qu'on travaille au
   * fond d'une longue liste. Une lecture d'un document, la ligne est a jour.
   * Sans `get()` fourni par l'ecran, on n'invente rien : l'optimisme reste
   * affiche et la prochaine navigation fera foi.
   */
  /**
   * POSER UNE FICHE RELUE dans le jeu charge, a sa place. C'est la moitie de
   * `refreshRecord` qui ne lit rien, separee pour servir aussi a la fiche
   * suivie (`observe`, plus bas) : un instantane du serveur EST une fiche
   * relue, la reposer par un second chemin serait se garantir deux
   * comportements qui divergent (les annuaires, d'abord).
   */
  const poserFicheRelue = useCallback((id: string, fresh: T): boolean => {
    let applied = false;
    setRecords((previous) => {
      const index = previous.findIndex((candidate) => getIdRef.current(candidate) === id);
      if (index < 0) return previous;
      const next = [...previous];
      next[index] = fresh;
      applied = true;
      return next;
    });
    /* LES ANNUAIRES SONT PERIMES ICI AUSSI, et l'oubli etait invisible. En
       pagination SERVEUR, une ecriture ne recharge PAS la porte : elle relit
       la seule ligne touchee (c'est tout l'objet de `refreshRecord`). Renommer
       un materiel depuis sa fiche ne perimait donc rien, et les interventions
       continuaient d'afficher l'ANCIEN nom jusqu'a un changement de porte. */
    if (applied) invaliderAnnuaires();
    return applied;
  }, []);

  const refreshRecord = useCallback(async (id: string): Promise<boolean> => {
    const getOne = getOneRef.current;
    if (getOne == null) return false;
    try {
      const fresh = await getOne(id);
      if (fresh == null) return false;
      return poserFicheRelue(id, fresh);
    } catch {
      /* Relecture ratee : l'ecriture, elle, est passee. On garde l'optimisme. */
      return false;
    }
  }, [poserFicheRelue]);

  /**
   * RELIRE UNE FICHE apres une ecriture du serveur que l'ecran n'a pas faite
   * (un flux lance par un smart bouton), par les chemins de relecture d'une
   * ecriture de case : la fiche chargee seule se relit et se repose, puis, en
   * pagination serveur, la seule ligne touchee ; en pagination client (ou sans
   * lecture a l'unite), la porte se recharge. Jamais de `setSolo(null)` : la
   * fiche afficherait « introuvable » le temps de la relecture.
   */
  const relireLaFiche = useCallback(async (id: string): Promise<boolean> => {
    const getOne = getOneRef.current;
    let relue = false;
    if (soloRef.current?.id === id && getOne != null) {
      try {
        const fresh = await getOne(id);
        if (fresh != null) {
          setSolo({ id, record: fresh });
          relue = true;
        }
      } catch {
        /* Relecture ratee : la fiche garde ce qu'elle a, l'ecoute (`observe`) rattrapera. */
      }
    }
    if (paginationRef.current === 'server' && getOne != null) return (await refreshRecord(id)) || relue;
    reload();
    return true;
  }, [reload, refreshRecord]);

  /*
   * LA FICHE OUVERTE SUIT LE SERVEUR (`observe` de la porte active). Le choix
   * de chaque instantane et ses raisons vivent dans `views/ficheSuivie.ts` ; ici
   * il n'y a que l'abonnement, et la pose par les deux chemins qui existent
   * deja : `setSolo` pour une fiche chargee seule (comme apres une ecriture),
   * `poserFicheRelue` pour une fiche du jeu.
   *
   * ON S'ABONNE UNE FOIS LA FICHE EN MEMOIRE, pas avant : le premier instantane
   * d'une ecoute est l'etat courant du serveur, il rattrape donc ce qui a ete
   * ecrit pendant le chargement (le numero pose entre « Creer » et la relecture
   * du jeu). Abonne plus tot, il arriverait sur une fiche absente et serait
   * perdu. L'id suivi est la seule dependance qui compte : changer de fiche, la
   * fermer, ouvrir un brouillon ou passer en mode Modeles le fait tomber, donc
   * desabonne.
   */
  const ficheEnMemoire = openIsLoaded || (openId != null && solo?.id === openId);
  const idSuivi =
    openId != null &&
    openId !== BROUILLON_ID &&
    !openId.includes('/') &&
    !query.modelesMode &&
    ficheEnMemoire &&
    observeRef.current != null
      ? openId
      : null;
  useEffect(() => {
    const observe = observeRef.current;
    if (idSuivi == null || observe == null) return;
    return observe(
      idSuivi,
      (record, { local }) => {
        const seule = soloRef.current?.id === idSuivi ? soloRef.current.record : undefined;
        const enMemoire =
          recordsRef.current.find((candidate) => getIdRef.current(candidate) === idSuivi) ?? seule;
        const instantane = { id: idSuivi, record, local };
        const etat = { ouverteId: openIdRef.current, enMemoire, ecrituresEnVol: pendingWritesRef.current };
        if (!instantaneRemplaceLaFiche(instantane, etat)) return;
        if (seule !== undefined) setSolo({ id: idSuivi, record: instantane.record });
        poserFicheRelue(idSuivi, instantane.record);
      },
      (cause) => {
        /* Une ecoute refusee ou coupee ne casse pas la fiche : elle garde ce
           qu'elle a, et les relectures de l'ecran continuent de faire foi. */
        console.warn(`[taki/views] Suivi de la fiche ${idSuivi} interrompu :`, cause);
      },
    );
  }, [idSuivi, poserFicheRelue]);

  /* ---- LA CREATION EN FICHE : ouvrir, ecrire, creer, sortir ---- */

  /**
   * OUVRIR LA FICHE VIDE. C'est le geste de « + Ajouter » depuis le
   * 09/09/2026 : plus une fenetre ecrite par l'app, une FICHE, en zone 5, avec
   * les memes cartes et les memes cases que celle d'une fiche existante.
   *
   * IL REFUSE PLUTOT QUE DE MENTIR, et chaque refus a sa cause :
   *  - LECTURE SEULE : la creation est une ecriture, et l'acces passe devant
   *    tout le reste (`raisonDeGeste`, meme ordre que partout) ;
   *  - MODE ARCHIVES : il montre autre chose que le jeu de la porte, et on ne
   *    cree pas une fiche dans une liste de fiches rangees ;
   *  - PORTE SANS JEU, ou sans action d'ajout tenue par le socle : on ne sait
   *    pas ou creer, ou l'app tient encore sa propre creation. Dans les deux
   *    cas, l'action visible est deja grisee avec sa raison (voir `door`).
   * Un brouillon DEJA ouvert rend `true` sans rien refaire : le geste est
   * satisfait, la fiche est la, et la rouvrir effacerait ce qui vient d'y etre
   * saisi.
   *
   * LE MODE MODELES N'EST PLUS UN REFUS (pivot du 10/09/2026) : un MODELE se
   * cree desormais par une fiche vide, exactement comme une fiche (regle G6), et
   * un ENFANT de modele aussi. La fiche du BROUILLON est la meme des trois
   * cotes ; seul ce que « Creer » ecrit au bout change (`creerBrouillon`).
   *
   * CHANGER DE PORTE PUIS OUVRIR, DANS LE MEME TICK : c'est le chemin d'un lien
   * colle (`fromSearch` enchaine `openDoor` puis `openRecord`), et il marche
   * parce qu'`openDoor` fait suivre ses REFS tout de suite. Le cas qui l'exige :
   * le raccourci du dock, qui vise une porte sans regarder laquelle est
   * affichee.
   *
   * DEPUIS UN MODELE (fondateur, 23/09/2026) : choisir un modele dans le menu
   * de « Ajouter » ouvrait la fiche EN BASE, fiches liees comprises, sans rien a
   * relire ni a annuler. Il ouvre desormais CE brouillon, pre-rempli : les
   * champs du modele se posent entre les defauts de la porte et les valeurs
   * imposees (`ficheVide`), son NOM devient le titre quand ses champs n'en
   * portent pas (`avecTitreDuModele`, le miroir du SDK), et ses fiches liees se
   * voient en apercu. Le brouillon reste INTACT (`touche: false`) : le fermer
   * sans rien changer ne demande rien, le modele existe toujours et rien n'est
   * perdu. Deux refus de plus : en mode Modeles (la fleche y est grisee, mais ce
   * verbe est public, et « Creer » y fabriquerait une FICHE) et avec `enfantDe`
   * (deux destinations qui se contredisent).
   */
  const ouvrirBrouillon = useCallback(
    (contexte?: {
      porte?: string;
      defaults?: ValeurImposee[];
      enfantDe?: { modele: string; lien: LienParLeChampLie; libelle: string };
      depuisModele?: ModeleDeFiche;
      retour?: RetourDeCreation;
    }): boolean => {
      const refus = (pourquoi: string): false => {
        if (import.meta.env.DEV) {
          console.warn(`[taki/views] Creation en fiche refusee : ${pourquoi}`);
        }
        return false;
      };
      if (brouillonRef.current != null) return true;
      if (raisonAccesRef.current != null) return refus(raisonAccesRef.current);
      const requete = queryRef.current;
      if (requete.archivesMode) {
        return refus("le mode Archives est allumé : il ne montre pas le jeu de la porte.");
      }
      const modele = contexte?.depuisModele;
      if (modele !== undefined && requete.modelesMode) {
        return refus('le mode Modèles est allumé : on ne crée pas de fiche depuis un modèle dans ce mode.');
      }
      if (modele !== undefined && contexte?.enfantDe !== undefined) {
        return refus("un brouillon ne vient pas d'un modèle ET n'ajoute pas un enfant à un modèle.");
      }
      if (contexte?.retour !== undefined && (modele !== undefined || contexte.enfantDe !== undefined)) {
        return refus("un brouillon qui remplit un champ ne vient pas d'un modèle et n'ajoute pas un enfant.");
      }
      const visee = contexte?.porte ?? doorIdRef.current;
      const porte = doorsRef.current.find((candidate) => candidate.id === visee);
      if (porte == null) return refus(`la porte "${visee}" n'est pas déclarée par cet écran.`);
      if (porte.jeu == null) return refus(RAISONS_CREATION.sansJeu);
      const tenuParLeSocle = (porte.actions ?? []).some(
        (action) => action.kind === 'add' && action.brouillon === true,
      );
      if (!tenuParLeSocle) return refus(RAISONS_CREATION.sansAjout);
      /* LA PORTE CHANGE D'ABORD, ET SES REFS AVEC : la fiche vide se projette
         par le binding de la porte VISEE, pas de celle qu'on quittait. */
      if (visee !== doorIdRef.current) openDoor(visee, [], false, false);
      /* LA VUE DE FICHE ET SON BINDING, LUS APRES `openDoor` : ses refs suivent
         dans le meme tick, donc c'est bien le binding de la porte VISEE (le
         titre d'un brouillon issu d'un modele se pose par SON role). */
      const recordType = typesRef.current.find((type) => type.level === 'record');
      const bindingFiche = bindingsRef.current.find(
        (candidate) => candidate.type === recordType?.id,
      );
      /* DANS UNE FICHE, LA FICHE CREEE LUI EST RATTACHEE : le champ du lien est
         impose et verrouille sur le parent, comme depuis une carte liee. */
      const dansIci = resoudreDans(doorsRef.current, queryRef.current.dans, {
        section: visee,
        openId: null,
      });
      const dansLaPorte =
        dansIci?.place === 'enfant' && queryRef.current.section === visee && contexte?.enfantDe === undefined
          ? dansIci
          : null;
      /* LE LIEN DU CHEMIN DIT CE QUE LA FICHE CREEE PORTE :
         - EGALITE : l'id du parent, a son champ ;
         - APPARTENANCE : une liste d'UN element, l'id du parent ;
         - SENS DIRECT : rien sur la fiche creee (la liste vit sur le PARENT), qui
           recoit l'id cree par le retour de la creation (voir plus bas). */
      const cheminDuLien = dansLaPorte === null ? null : cheminDuChampLie(dansLaPorte.lien);
      const rattachement: ValeurDeDepart[] =
        dansLaPorte !== null &&
        cheminDuLien !== null &&
        !(contexte?.defaults ?? []).some((valeur) => valeur.chemin === cheminDuLien)
          ? [
              {
                chemin: cheminDuLien,
                valeur:
                  !estLienParListe(dansLaPorte.lien) && dansLaPorte.lien.multiple === true
                    ? [dansLaPorte.dans.id]
                    : dansLaPorte.dans.id,
                ...(titreDuParentRef.current === '' ? {} : { libelle: titreDuParentRef.current }),
              },
            ]
          : [];
      /* LE SENS DIRECT, DEPUIS LA PORTE OUVERTE DANS UNE FICHE : la fiche creee
         n'apparaitrait dans aucune liste sans cet ajout. Il se fait au retour de
         la creation (`rendreAuChamp`), comme le « Ajouter » d'un champ de lien. */
      const parenteDuChemin =
        dansLaPorte !== null && estLienParListe(dansLaPorte.lien)
          ? doorsRef.current.find((candidate) => candidate.id === dansLaPorte.dans.porte)
          : undefined;
      const retourDuChemin: RetourDeCreation | undefined =
        dansLaPorte !== null &&
        estLienParListe(dansLaPorte.lien) &&
        parenteDuChemin?.jeu != null &&
        contexte?.retour === undefined &&
        modele === undefined
          ? {
              family: parenteDuChemin.jeu.family,
              ...(parenteDuChemin.jeu.type === undefined ? {} : { type: parenteDuChemin.jeu.type }),
              id: dansLaPorte.dans.id,
              chemin: dansLaPorte.lien.liste,
              titre: titreDuParentRef.current,
              libelleDuChamp: dansLaPorte.lien.id,
              liste: true,
            }
          : undefined;
      const imposees: readonly ValeurDeDepart[] = [...(contexte?.defaults ?? []), ...rattachement];
      let champs = ficheVide({
        type: porte.jeu.type,
        /* LES JETONS DU SOCLE SE RESOLVENT ICI, a l'ouverture : c'est le seul
           instant ou l'on sait QUI cree. Un jeton sans reponse laisse la case
           vide, et « Creer » dit ce qui manque. */
        defauts: resoudreDefauts(porte.creation?.defauts ?? [], {
          personneConnectee: personnesPortRef.current?.moi?.personId ?? null,
          maintenant: new Date(),
          fuseau: fuseauRef.current,
        }),
        ...(modele === undefined ? {} : { modele: modele.fields ?? {} }),
        imposees,
      });
      if (modele !== undefined) {
        /* LE NOM DU MODELE, au CHEMIN du role titre, seulement si ses champs
           n'en portent pas : c'est ce que le SDK fait a l'instanciation
           (`avecNomDeSecours`), et le brouillon montre ce que la fiche portera. */
        const roleTitre = rolesOf(bindingFiche)['title'];
        champs = avecTitreDuModele(champs, roleTitre?.path, modele.name, titreEstTexte(bindingFiche));
      }
      /* UN BROUILLON TIRE D'UN MODELE (ou d'un defaut) MONTRE DEJA SES CALCULS. */
      const fiche = ficheAvecCalculs(champs, bindingFiche?.columns) as unknown as T;
      /* LE CURSEUR TOMBE : le brouillon n'a pas de rang, et le gel de la fiche
         precedente designerait un jeu auquel il n'appartient pas. */
      frozenRef.current = null;
      setFrozenIds(null);
      /* SESSION NEUVE, comme a l'entree dans une fiche : ni pile d'annulation
         heritee, ni « Enregistre » d'une autre fiche au-dessus d'une
         fiche qui n'existe pas encore. */
      resetRecordSession();
      poserBrouillon({
        porte: visee,
        touche: false,
        enCreation: false,
        manquants: [],
        sortieEnAttente: false,
        /* LE TITRE SE LIT DES L'OUVERTURE : un brouillon issu d'un modele en
           porte un, et la fenetre « Fiche non creee » doit le nommer meme si
           l'on n'a touche qu'une autre case. Vide sur une fiche vide. */
        titre: titreDuBrouillon(fiche, bindingFiche) ?? '',
        fiche,
        imposees,
        /* CE QUE « CREER » FERA AU BOUT : un ENFANT du modele nomme, pas une
           fiche reelle. Seule la CARTE le savait au moment du clic, et
           l'intention l'a porte jusqu'ici (`PendingAppAction.enfantDe`). */
        ...(contexte?.enfantDe === undefined ? {} : { enfantDe: contexte.enfantDe }),
        /* OU LA FICHE ET SES FICHES LIEES, D'UN SEUL GESTE, PAR LE PORT DES
           MODELES (`creerBrouillon`, destination (d)). */
        ...(modele === undefined ? {} : { depuisModele: modele }),
        /* ET CE QUE LA FICHE CREEE REMPLIRA : le champ de lien d'ou l'on vient. */
        ...(contexte?.retour === undefined && retourDuChemin === undefined
          ? {}
          : { retour: contexte?.retour ?? retourDuChemin }),
      });
      etapeARemplacerRef.current = contexte?.retour !== undefined;
      openIdRef.current = BROUILLON_ID;
      setOpenId(BROUILLON_ID);
      /* L'ORIGINE D'UNE CREATION DEMANDEE DEPUIS UN PARENT devient celle du
         brouillon : la puce de sortie dit le nom du parent et y ramene. AVEC UN
         PORT, il n'y a rien a faire ici : le « + Ajouter » d'une carte a range
         son origine sur l'etape de la porte (`openLinked`), une surface qui
         n'est pas une vue l'a posee en intention (`creerDepuis`,
         `departDuBrouillonRef`), et le sens 1 de l'adresse les range sur l'etape du brouillon. SANS PORT, la
         memoire de l'ecran sans historique ne vaut que pour la porte visee par
         le geste ; un « + Ajouter » de la porte elle-meme n'en a pas, et
         efface celle qui attendait. */
      if (urlPort == null) {
        const attente = origineSansHistoriqueRef.current;
        if (attente == null || attente.id !== BROUILLON_ID || attente.porte !== visee) {
          origineSansHistoriqueRef.current = null;
        }
      }
      if (recordType) poserVueActive(recordType.id);
      return true;
    },
    [urlPort, openDoor, poserBrouillon, poserVueActive, resetRecordSession],
  );

  /**
   * ECRIRE UNE CASE DU BROUILLON : le dernier metre de `commitWrite` quand la
   * fiche n'existe pas encore. Elle ne quitte jamais le moteur.
   *
   * OU LA VALEUR SE RANGE NE SE DEVINE PAS, CA SE PROUVE : la MEME regle que le
   * mode Modeles (`champsApresEcriture`), et pour la meme raison. Le socle voit
   * la clef d'une case, pas l'etage ou l'app la lit : il POSE la valeur, RELIT
   * la fiche par le chemin de la PROJECTION, et ne garde que le rangement qui
   * rend ce qu'on vient de taper. Deviner « les champs declares vont dans le
   * sac » creerait des fiches invisibles dans toutes les cartes liees. La
   * FORME, elle, suit l'etage : jamais de `Date` dans le sac (`valeursPourModele`),
   * sans quoi le SDK refuse la creation.
   *
   * LA CLEF EST LE CHEMIN, PRIVE DE SON SAC : `field.path` pour un role (son nom
   * n'est pas celui d'un champ), la clef pour une colonne. `customFields.cout`
   * devient `cout`, parce que c'est la PREUVE qui choisit l'etage, et lui passer
   * un chemin pointe l'ecrirait sous une clef qui contient un point.
   *
   * `saveState` NE BOUGE PAS, ET C'EST LE FOND DE LA CREATION EN FICHE : rien
   * n'est enregistre. Une coche verte ou un « Enregistre » sur une fiche
   * qui n'existe pas ferait croire le contraire, et c'est exactement ce qu'on
   * apprendrait a l'utilisateur juste avant de perdre sa saisie.
   */
  const ecrireSurBrouillon = useCallback(
    (request: WriteRequest, history: 'push' | 'skip'): void => {
      const courant = brouillonRef.current;
      if (courant == null) return;
      const { kind, key, value } = request;
      const target: SaveTarget = { kind, id: BROUILLON_ID, key };
      const binding = bindingsRef.current.find((candidate) => candidate.type === viewIdRef.current);
      const field = kind === 'role' ? rolesOf(binding)[key] : undefined;
      const column: Column<T> | undefined = kind === 'column' ? colonneDEcriture(binding, key) : undefined;
      if (field == null && column == null) {
        console.warn(`[taki/views] Ecriture refusee : ${key} n'existe pas dans ce binding.`);
        return;
      }
      /* LA MEME LECTURE QUE LA PROJECTION, et c'est ce qui fait tenir la preuve
         (voir `champsApresEcriture`). */
      const lireCase = (cible: T): Cell =>
        field != null ? field.get(cible) : (column?.value(cible) ?? null);
      const stored = lireCase(courant.fiche);
      if (sameCell(stored, value)) return;
      const editeurDeclare: EditorKind | undefined =
        kind === 'column'
          ? column?.kind
          : key === 'dateStart' || key === 'dateEnd'
            ? 'date'
            : undefined;
      const editeur = editorKindOf(stored, editeurDeclare);
      const chemin = kind === 'role' ? (field?.path ?? key) : (column?.path ?? key);
      const clef = chemin.startsWith(SAC_SUR_MESURE)
        ? chemin.slice(SAC_SUR_MESURE.length)
        : chemin;
      const ecrits = champsApresEcriture({
        champs: courant.fiche as unknown as Record<string, unknown>,
        key: clef,
        editeur,
        valeur: value,
        relire: (essai) => lireCase(essai as unknown as T),
      });
      /* CHANGER DE PARENT VIDE LE DEPENDANT devenu incompatible (`videAussi`),
         sur le brouillon comme sur une fiche reelle : un Job de Sales ne
         survit pas au passage de la fiche dans le pole MyTaki. */
      const aVider = column?.videAussi?.(courant.fiche, value) ?? {};
      const sansDependants =
        ecrits == null || Object.keys(aVider).length === 0
          ? ecrits
          : sacSansDependants(ecrits, aVider);
      /* LES CALCULS SUIVENT LA SAISIE (`ficheAvecCalculs`) : changer le prix
         montre tout de suite le total qui le cite, comme sur une fiche reelle. */
      const champs = sansDependants == null ? null : ficheAvecCalculs(sansDependants, binding?.columns);
      if (champs == null) {
        /* AUCUN DES DEUX ETAGES NE SE RELIT : une colonne calculee, un role
           compose. On le DIT au lieu d'avaler la saisie, exactement comme en
           mode Modeles : une case qui dit non est honnete. */
        console.warn(`[taki/views] La case ${key} ne sait pas se ranger dans la fiche a creer.`);
        setSaveState({
          mode: 'error',
          target,
          reason: 'Cette case ne sait pas se ranger dans la fiche.',
        });
        return;
      }
      poserBrouillon({
        ...courant,
        fiche: champs as unknown as T,
        touche: true,
        /* LA CASE CESSE D'ETRE RECLAMEE DES QU'ELLE EST REMPLIE : on ne garde
           pas un anneau rouge sur une case qu'on vient de saisir. */
        manquants: courant.manquants.filter(
          (clefManquante) => clefManquante !== fieldKeyOf(kind, key),
        ),
        /* LE TITRE SE RELIT PAR LE ROLE, jamais par la clef ecrite : sur une
           porte dont le titre est un champ declare (`objet`), la case se
           saisit comme une COLONNE, et la fenetre « Fiche non creee » doit
           quand meme nommer la fiche. */
        titre: titreDuBrouillon(champs as unknown as T, binding) ?? courant.titre,
      });
      /* LA PASTILLE REDEVIENT MUETTE : rien ne s'enregistre ici (pas de coche,
         pas de « Enregistre a »), et « Il manque : Objet. » ne doit pas rester
         affiche sous une case qu'on vient de remplir. Le prochain « Creer »
         redira ce qui manque encore, s'il manque encore quelque chose. */
      setSaveState({ mode: 'idle' });
      if (history === 'push') {
        /* LA PILE D'ANNULATION MARCHE COMME AILLEURS : `undo` rappellera
           `commitWrite` avec le meme id, qui repassera par ici. */
        const step: WriteStep = { ...request, before: stored };
        const grown = [...undoRef.current, step];
        const capped = grown.length > UNDO_LIMIT ? grown.slice(grown.length - UNDO_LIMIT) : grown;
        undoRef.current = capped;
        setUndoStack(capped);
        redoRef.current = [];
        setRedoStack([]);
      }
    },
    [poserBrouillon],
  );

  /**
   * ECRIRE POUR DE BON : le bouton « Creer » de la rangee 3.
   *
   * L'ORDRE N'EST PAS NEGOCIABLE, et chaque etape ferme une panne vecue :
   *  1. CE QUI MANQUE se dit AVANT d'appeler qui que ce soit (le titre, puis les
   *     colonnes obligatoires) : une phrase dans la pastille et les cases
   *     cerclees, jamais un refus du serveur qu'on traduirait mal ;
   *  2. LA FICHE RESTE OUVERTE apres la creation, et devient une fiche
   *     ordinaire : c'est la promesse du chantier (« comme dans Odoo »).
   *     `markRead` d'abord (on ne se signale pas a soi-meme une nouveaute qu'on
   *     vient d'ecrire), puis le rechargement de la porte, puis l'id ;
   *  3. PAS DE `resetRecordSession` : c'est la MEME session. La pile
   *     d'annulation du brouillon, elle, est morte avec lui (ses pas ne
   *     designent plus rien), mais l'indicateur doit pouvoir dire
   *     « Enregistre » sur la fiche qu'on vient de creer.
   *
   * PAS DE « REESSAYER » SUR UN ECHEC : `failedRef` reste vide. `retrySave` ne
   * sait rejouer que des ecritures de cellule ; un bouton qui ne peut pas tenir
   * sa promesse est pire qu'une explication.
   */
  const creerBrouillon = useCallback(async (): Promise<void> => {
    const courant = brouillonRef.current;
    if (courant == null || courant.enCreation) return;
    /*
     * QUATRE DESTINATIONS, UNE SEULE FICHE VIDE (pivot du 10/09/2026, puis
     * 23/09/2026) : une FICHE reelle, une fiche DEPUIS UN MODELE (avec ses
     * fiches liees), un MODELE, ou un ENFANT de modele. Le geste, la surface, les
     * cases obligatoires et la garde de sortie sont les memes partout ; seul le
     * dernier metre change, exactement comme `commitWrite`.
     *
     * L'AIGUILLAGE SE LIT SUR LE BROUILLON, PUIS SUR LE MODE, dans cet ordre :
     * l'intention d'ENFANT est plus precise que le mode (elle nomme un modele
     * PARENT et un lien), et elle survit meme si `mod=1` est tombe de l'adresse
     * au passage dans la porte liee. L'origine d'un MODELE aussi, et elle ne
     * coexiste ni avec le mode Modeles ni avec un enfant (`ouvrirBrouillon` les
     * refuse ensemble).
     */
    const versEnfant = courant.enfantDe;
    const versFicheDeModele = courant.depuisModele;
    const versModele =
      versEnfant === undefined && versFicheDeModele === undefined && queryRef.current.modelesMode;
    /* LE PORT EXIGE N'EST PAS LE MEME : creer une fiche demande le port de
       CREATION ; creer un modele, un enfant ou une fiche depuis un modele (qui
       relit le modele pour en recreer les fiches liees) demande celui des
       MODELES. Griser « Creer » au nom du mauvais port enverrait chercher le
       mauvais fournisseur. */
    const portManquant =
      versEnfant !== undefined || versFicheDeModele !== undefined || versModele
        ? modelesPortRef.current == null
          ? MODELE_WRITE_REASON.sansPort
          : null
        : creationPortRef.current == null
          ? RAISONS_CREATION.sansPort
          : null;
    const raison = raisonDeGeste(raisonAccesRef.current, portManquant);
    if (raison != null) {
      setSaveState({ mode: 'error', reason: raison });
      return;
    }
    const porte = doorsRef.current.find((candidate) => candidate.id === courant.porte);
    const jeu = porte?.jeu ?? null;
    if (jeu == null) {
      setSaveState({ mode: 'error', reason: RAISONS_CREATION.sansJeu });
      return;
    }
    const binding = bindingsRef.current.find((candidate) => candidate.type === viewIdRef.current);
    const roleTitre = rolesOf(binding)['title'];
    /* LE MOT DU TITRE EST CELUI DE LA FICHE (« Nom », « Objet ») : c'est ce que
       l'utilisateur a sous les yeux, et la phrase du refus doit nommer la case
       qu'il doit remplir, pas le role qui la porte. */
    const motDuTitre = binding?.options?.['titleLabel'];
    /* UN TITRE QUI REPREND UNE FICHE LIEE, OU QUI EST UN CHOIX, NE SE RECLAME
       PAS (`titreAReclamer`, decision du 02/11/2026) : c'est un texte calcule.
       Son champ, dans sa carte, garde son `required` et se reclame par les
       colonnes ci-dessous. */
    const titreReclame = titreAReclamer(binding, queryRef.current.modelesMode);
    const { cles, libelles } = manquantsDuBrouillon({
      /* UN TITRE QU'ON NE PEUT PAS SAISIR NE SE RECLAME PAS : un role compose
         ou calcule n'a pas de `set`, donc aucune case a remplir. */
      titre:
        roleTitre == null || titreReclame === undefined
          ? undefined
          : {
              valeur: roleTitre.get(courant.fiche),
              libelle:
                titreReclame.libelle ??
                roleTitre.label ??
                (typeof motDuTitre === 'string' ? motDuTitre : 'Titre'),
            },
      colonnes: (binding?.columns ?? [])
        .filter((colonne) => colonne.required === true)
        .map((colonne) => ({
          key: colonne.key,
          label: colonne.label,
          required: true,
          valeur: colonne.value(courant.fiche),
        })),
    });
    if (cles.length > 0) {
      poserBrouillon({ ...courant, manquants: cles });
      setSaveState({ mode: 'error', reason: phraseDesManquants(libelles) });
      return;
    }
    /* UN CHOIX HORS DE SA LISTE (un Job d'un autre pole, venu d'un modele) se
       dit avant d'appeler le port, comme une case manquante. */
    const horsListe = (binding?.columns ?? []).find((colonne) => {
      if (colonne.optionsPour === undefined) return false;
      const valeur = colonne.value(courant.fiche);
      if (valeur === null || valeur === '') return false;
      const permises = colonne.optionsPour(courant.fiche).map((option) => option.value);
      return clefsDuChoixMultiple(valeur).some((clef) => !permises.includes(clef));
    });
    if (horsListe !== undefined) {
      poserBrouillon({ ...courant, manquants: [fieldKeyOf('column', horsListe.key)] });
      setSaveState({ mode: 'error', reason: `Choisis « ${horsListe.label} » dans la liste proposée.` });
      return;
    }
    const champsSaisis = champsDuBrouillon(courant.fiche as unknown as Record<string, unknown>);
    poserBrouillon({ ...courant, enCreation: true, manquants: [] });
    setSaveState({ mode: 'saving' });
    /* CE QUI RATE SE DIT AU MEME ENDROIT POUR LES TROIS : la fiche vide reste
       ouverte avec sa saisie, et la pastille de la rangee 3 porte la raison. Pas
       de « Reessayer » (`failedRef` reste vide) : `retrySave` ne sait rejouer
       qu'une ecriture de cellule, et un bouton qui ne tient pas sa promesse est
       pire qu'une phrase. */
    const echec = (cause: unknown, defaut: string): void => {
      console.error(`[taki/views] ${defaut}`, cause);
      const encore = brouillonRef.current;
      if (encore != null) poserBrouillon({ ...encore, enCreation: false });
      failedRef.current = null;
      /*
       * LA PHRASE D'UN REFUS, SINON CELLE DU SOCLE, ET JAMAIS LE MESSAGE BRUT
       * D'UNE PANNE. Le `message` de n'importe quelle erreur atterrissait ici :
       * une adresse mal formee remontait donc le texte de Zod (une liste JSON
       * d'issues, en anglais) sur la pastille d'un ecran francais. Seule une
       * phrase que l'app a ECRITE POUR ETRE LUE (`refuser`, `views/refus.ts`)
       * passe ; le reste garde la phrase de repli, et la cause reste en console.
       */
      setSaveState({ mode: 'error', reason: raisonDeRefus(cause) ?? defaut });
    };

    /*
     * LA SEQUENCE D'APRES CREATION D'UNE FICHE REELLE, ecrite UNE fois pour les
     * deux destinations qui en creent une ((c) et (d)) : c'est la promesse de la
     * regle G6, et deux copies divergeraient au premier retouche.
     */
    const terminerCreation = (id: string): void => {
      markReadRef.current(id);
      /* LA FICHE CREEE SE LIT TOUT DE SUITE, sans attendre le rechargement de la
         liste. Le rechargement demarre au rendu suivant, et l'effet qui va
         chercher une fiche absente de la liste (`solo`) se range pendant qu'il
         tourne : jusque-la, la fiche n'etait nulle part et l'ecran disait
         « introuvable » sur une fiche qu'on venait de creer (28/10/2026). */
      const lireSeule = getOneRef.current;
      /* ELLE RESTE AFFICHEE tant qu'elle est ouverte (`fiche-creee.ts`) : la
         liste rechargee peut ne pas la contenir encore, ou la perdre un
         instant au rechargement suivant (fiches liees du modele). */
      creeeIdRef.current = id;
      if (lireSeule != null) {
        const porte = doorIdRef.current;
        setCreeeEnAttente(id);
        void lireSeule(id)
          .then((record) => {
            if (record != null && doorIdRef.current === porte && openIdRef.current === id) {
              setSolo({ id, record });
            }
          })
          .catch(() => {
            /* La liste rechargee ou l'effet de lecture seule prendront le relais. */
          })
          .finally(() => {
            setCreeeEnAttente((avant) => (avant === id ? null : avant));
          });
      }
      reload();
      poserBrouillon(null);
      /* LA PILE D'ANNULATION DU BROUILLON MEURT AVEC LUI : ses pas designent
         `~nouveau`, un id qui n'existe plus. Les laisser laisserait `Cmd+Z` actif
         sur une fiche ou il ne ferait rien, silencieusement. La SESSION, elle,
         continue : c'est la meme, et l'indicateur doit pouvoir dire « Enregistre
         a 14:32 » juste en dessous. */
      undoRef.current = [];
      redoRef.current = [];
      setUndoStack([]);
      setRedoStack([]);
      /* LE CURSEUR N'EST PAS GELE ICI, ET C'EST VOULU : le jeu ordonne courant
         ne contient pas encore la fiche creee (le rechargement est en vol), et
         geler dessus ferait dire « .. / .. » pour toujours, puisque le gel
         differe ne rattrape qu'un gel ABSENT. On laisse donc le gel vide : la
         collection rechargee arrive, et le gel differe se pose sur elle, avec la
         fiche dedans, exactement comme une entree a froid. */
      setFrozenIds(null);
      gelApresCreationRef.current = id;
      /* L'ORIGINE SUIT LA FICHE CREEE : elle vient du parent, elle y ramene
         encore. Le brouillon a change d'id, pas de provenance. AVEC UN PORT,
         c'est le sens 1 de l'adresse qui renomme l'origine de l'etape, au
         remplacement `BROUILLON_ID` -> id reel ; la memoire de l'ecran sans
         historique, elle, se renomme ici. */
      const origine = origineSansHistoriqueRef.current;
      if (origine != null && origine.id === BROUILLON_ID) {
        origineSansHistoriqueRef.current = { ...origine, id };
      }
      openIdRef.current = id;
      setOpenId(id);
      setSaveState({ mode: 'saved', at: new Date() });
    };

    /*
     * LA FICHE CREEE DEPUIS UN CHAMP DE LIEN (`courant.retour`, chantier
     * `champ-lie-deux-gestes`) : son id REMPLIT ce champ, puis on REVIENT a la
     * fiche d'origine, le champ rempli. C'est la tache qu'on faisait : on
     * choisissait un client, il n'existait pas, on l'a cree. Sans retour, on
     * resterait sur la fiche creee (regle G6, celle du « + Ajouter » ordinaire).
     *
     * L'ECRITURE EST LE VERBE DU PORT DES LIENS (`rattacher`), le meme que la
     * carte : il range le chemin natif ou sur-mesure (`patchDeRattachement`) et
     * le SDK fusionne le sac clef par clef. Ici il ecrit la fiche D'ORIGINE.
     *
     * L'ANNUAIRE DU JEU CREE EST PERIME : la fiche neuve n'y est pas, et le champ
     * d'origine afficherait son id nu. On l'invalide avant de repartir.
     *
     * LE RETOUR EST CELUI DE LA PUCE ET D'« Annuler » (`sortieDeFiche`) : depiler
     * l'etape d'ou l'on vient, dans cet ecran ou dans l'app quittee. On NE passe
     * PAS par `terminerCreation`, qui ouvrirait la fiche creee dans l'adresse au
     * moment meme ou le retour la depile.
     *
     * L'ECHEC NE PERD RIEN : la fiche est creee, on RESTE dessus, et la pastille
     * nomme le champ et la fiche qui ne l'ont pas recue.
     */
    const rendreAuChamp = async (id: string): Promise<void> => {
      const retour = courant.retour;
      if (retour === undefined) {
        terminerCreation(id);
        return;
      }
      try {
        const liens = liensPortRef.current;
        if (liens == null) throw new Error('Aucun port des liens : le champ ne peut pas être écrit.');
        /* UNE LISTE S'AJOUTE, UN CHAMP SIMPLE SE REMPLACE : ecraser la liste
           d'une fiche pour y poser l'id cree effacerait les fiches deja citees. */
        if (retour.liste === true) await liens.ajouterALaListe(retour.family, retour.id, retour.chemin, id);
        else await liens.rattacher(retour.family, retour.id, retour.chemin, id);
      } catch (cause) {
        console.error('[taki/views] Fiche creee, champ d\'origine non rempli.', cause);
        terminerCreation(id);
        setSaveState({
          mode: 'error',
          reason: `Fiche créée, mais « ${retour.libelleDuChamp} » de ${retour.titre} n'a pas pu être rempli.`,
        });
        return;
      }
      invaliderAnnuaires();
      const sortie = sortieDeFiche(origineEffectiveRef.current, {
        niveau: 'record',
        porte: doorIdRef.current,
        id: openIdRef.current,
        rang: urlPort?.rang ?? null,
      });
      if (sortie == null) {
        terminerCreation(id);
        return;
      }
      markReadRef.current(id);
      reload();
      poserBrouillon(null);
      undoRef.current = [];
      redoRef.current = [];
      setUndoStack([]);
      setRedoStack([]);
      setSaveState({ mode: 'saved', at: new Date() });
      retourALOrigineRef.current?.(sortie.geste);
    };

    /* ---- (a) UN ENFANT DE MODELE : rien n'est cree dans aucune collection ---- */
    if (versEnfant !== undefined) {
      const portDesModeles = modelesPortRef.current;
      if (portDesModeles == null) return;
      const modele = modeleDId(versEnfant.modele);
      if (modele == null) {
        echec(new Error(MODELE_WRITE_REASON.disparu), MODELE_WRITE_REASON.disparu);
        return;
      }
      let enfantNeuf: EnfantDeModele;
      try {
        /* LE CHEMIN DU LIEN QUITTE LES CHAMPS : la case etait VERROUILLEE sur le
           nom du modele parent, et sa valeur est l'id d'un MODELE. La garder
           ferait naitre chaque fiche creee depuis ce patron accrochee a un
           rattachement qui n'existe dans aucune collection. Le lien, lui, est
           repose a l'instanciation par le SDK (`linkKey`). */
        enfantNeuf = nouvelEnfant(
          versEnfant.lien,
          sansChampsDeLien(champsSaisis, [versEnfant.lien.by]),
        );
      } catch (cause) {
        echec(cause, "La fiche liée n'a pas pu être ajoutée au modèle.");
        return;
      }
      try {
        await enfilerSurModele(modele.id, async () => {
          /* LE TABLEAU SE RELIT DANS LA FILE : le capturer au clic ferait
             repartir ce geste des enfants d'avant l'ecriture precedente. */
          const frais = modeleDId(modele.id);
          if (frais == null) throw new Error(MODELE_WRITE_REASON.disparu);
          const children = avecEnfant(frais.children, enfantNeuf);
          await portDesModeles.update(modele.id, { children });
          majEnfants(modele.id, children);
        });
      } catch (cause) {
        echec(cause, "La fiche liée n'a pas pu être ajoutée au modèle.");
        return;
      }
      setModelesRevision((avant) => avant + 1);
      poserBrouillon(null);
      undoRef.current = [];
      redoRef.current = [];
      setUndoStack([]);
      setRedoStack([]);
      const chemin = cheminDEnfant(modele.id, enfantNeuf.id);
      /* LE GEL TOMBE ET NE SE REPOSE PAS ICI : les freres viennent du modele
         parent, et l'effet de gel differe les pose des qu'il est en main. */
      frozenRef.current = null;
      setFrozenIds(null);
      /* L'ORIGINE SUIT L'ENFANT CREE : elle vient du modele parent, elle y ramene
         encore. Le brouillon a change d'id, pas de provenance. Avec un port,
         c'est le sens 1 de l'adresse qui renomme l'origine de l'etape. */
      const origineDuParent = origineSansHistoriqueRef.current;
      if (origineDuParent != null && origineDuParent.id === BROUILLON_ID) {
        origineSansHistoriqueRef.current = { ...origineDuParent, id: chemin };
      }
      /* LE MODE MODELES REVIENT AVEC L'ENFANT, et il faut le dire ici : le
         passage dans la porte liee l'avait eteint (elle n'allume pas les
         modeles, et rien n'ouvrait encore d'enfant a ce moment-la). L'adresse de
         l'enfant le porte (`mod=1`), et c'est elle qui se partage et se
         recharge. La permission est passee en dur parce qu'on la CONNAIT : on
         ouvre un enfant dans le meme geste. */
      setQueryState((previous) =>
        nextQuery(previous, { modelesMode: true }, paginationRef.current === 'server', true),
      );
      openIdRef.current = chemin;
      setOpenId(chemin);
      setSaveState({ mode: 'saved', at: new Date() });
      return;
    }

    /* ---- (d) UNE FICHE DEPUIS UN MODELE : la racine ET ses fiches liees ---- */
    if (versFicheDeModele !== undefined) {
      const portDesModeles = modelesPortRef.current;
      if (portDesModeles == null) return;
      let creeDepuisModele: { id: string };
      try {
        /* LA FICHE ENTIERE PART, JAMAIS UN DELTA : l'instanciation pose ces
           champs APRES ceux du modele et fusionne le sac par REMPLACEMENT. Le
           brouillon porte deja la fusion (defauts, modele, saisies), et une case
           VIDEE doit rester vide : le SDK retire les chaines vides APRES avoir
           pose ce qu'on lui envoie, donc la valeur du modele ne revient pas. */
        creeDepuisModele = await portDesModeles.createFrom({
          templateId: versFicheDeModele.id,
          type: jeu.type ?? '',
          fields: champsSaisis,
        });
      } catch (cause) {
        echec(cause, "La fiche n'a pas pu être créée depuis le modèle.");
        return;
      }
      await rendreAuChamp(creeDepuisModele.id);
      return;
    }

    /* ---- (b) UN MODELE : le titre saisi devient son NOM ---- */
    if (versModele) {
      const portDesModeles = modelesPortRef.current;
      if (portDesModeles == null) return;
      /* LE CHEMIN DU TITRE, ET NON LE NOM DU ROLE : c'est lui que
         `modeleDuBrouillon` retire de `fields`, pour qu'un renommage ne laisse
         jamais un vieux nom dans les champs du modele. */
      const { name, fields } = modeleDuBrouillon({
        fiche: champsSaisis,
        ...(roleTitre?.path === undefined ? {} : { cheminDuTitre: roleTitre.path }),
      });
      if (name === '') {
        echec(new Error(MODELE_WRITE_REASON.nomVide), MODELE_WRITE_REASON.nomVide);
        return;
      }
      let creeModele: { id: string };
      try {
        creeModele = await portDesModeles.create({
          family: jeu.family,
          type: jeu.type ?? '',
          name,
          fields,
        });
      } catch (cause) {
        echec(cause, "Le modèle n'a pas pu être créé.");
        return;
      }
      /* LA LISTE DU MODE SE RELIT : c'est elle que la surface projette, et le
         menu de la fleche de « Ajouter » compte dessus lui aussi. */
      setModelesRevision((avant) => avant + 1);
      poserBrouillon(null);
      undoRef.current = [];
      redoRef.current = [];
      setUndoStack([]);
      setRedoStack([]);
      /* MEME GEL DIFFERE QU'APRES UNE FICHE CREEE : la liste des modeles est en
         vol, geler maintenant figerait un jeu sans lui, donc « .. / .. » pour
         toujours. */
      frozenRef.current = null;
      setFrozenIds(null);
      gelApresCreationRef.current = creeModele.id;
      openIdRef.current = creeModele.id;
      setOpenId(creeModele.id);
      setSaveState({ mode: 'saved', at: new Date() });
      return;
    }

    /* ---- (c) UNE FICHE REELLE : le chemin d'avant, inchange ---- */
    const port = creationPortRef.current;
    if (port == null) return;
    let cree: { id: string };
    try {
      cree = await port.create({
        family: jeu.family,
        type: jeu.type ?? '',
        fields: champsSaisis,
      });
    } catch (cause) {
      echec(cause, "La fiche n'a pas pu être créée.");
      return;
    }
    await rendreAuChamp(cree.id);
  }, [poserBrouillon, reload, enfilerSurModele, majEnfants, modeleDId, urlPort]);

  /* ---- Les trois verbes de la fenetre « Fiche non creee » ---- */

  /**
   * « Annuler » et `Echap` : LE MEME GESTE QUE LA PUCE DE SORTIE, au pied de la
   * lettre depuis le 10/09/2026.
   *
   * UN BROUILLON QUI A UNE ORIGINE REVIENT A CETTE ORIGINE. Il a ete ouvert
   * depuis la carte d'une fiche PARENTE (« + Ajouter »), reelle ou modele :
   * `openLinked` a mis le parent de cote et l'a range dans l'etape d'historique
   * du brouillon (`OrigineDEtape`), ce qui fait deja dire son nom a la puce. « Annuler »
   * doit donc y ramener aussi, sinon deux sorties d'une meme fiche partent a
   * deux endroits : la puce chez le parent, « Annuler » dans la COLLECTION de
   * la porte d'arrivee, ou l'on n'est jamais passe et ou la fiche parente n'est
   * meme pas visible. Depuis un MODELE c'est pire encore : on retombe sur la
   * liste des modeles de la porte liee, qui est vide.
   *
   * LA DECISION EST CELLE DE LA PUCE, prise par la MEME regle pure
   * (`sortieDeFiche`) : deux calculs de « d'ou vient-on » divergeraient au
   * premier Retour navigateur. `openIdRef` vaut ici `~nouveau`, c'est-a-dire
   * exactement l'`id` que `ouvrirBrouillon` a pose dans l'origine.
   *
   * SANS ORIGINE, RIEN NE CHANGE : « + Ajouter » de la rangee 3a ferme la fiche
   * et rend la collection, comme avant.
   */
  const annulerBrouillon = useCallback((): void => {
    demanderSortie(() => {
      const retour = sortieDeFiche(origineEffectiveRef.current, {
        niveau: 'record',
        porte: doorIdRef.current,
        id: openIdRef.current,
        rang: urlPort?.rang ?? null,
      });
      if (retour == null) {
        openRecord(null);
        return;
      }
      retourALOrigineRef.current?.(retour.geste);
    });
  }, [demanderSortie, openRecord, urlPort]);

  /** « Quitter sans creer » : le brouillon meurt, le geste en attente se fait. */
  const confirmerSortieDuBrouillon = useCallback((): void => {
    const suite = suiteDeSortieRef.current;
    poserBrouillon(null);
    suite?.();
  }, [poserBrouillon]);

  /** « Revenir » : la fenetre se referme, rien d'autre ne bouge. */
  const reprendreBrouillon = useCallback((): void => {
    const courant = brouillonRef.current;
    if (courant == null || !courant.sortieEnAttente) return;
    suiteDeSortieRef.current = null;
    poserBrouillon({ ...courant, sortieEnAttente: false });
  }, [poserBrouillon]);

  /* ---- Les sorties, toutes passees par la fenetre ---- */

  /**
   * LES SIX GESTES QUI QUITTENT UNE FICHE, ENVELOPPES UNE FOIS. Ce sont les
   * memes fonctions qu'avant, precedees de la question « et le brouillon ? ».
   *
   * POURQUOI DES ENVELOPPES ET PAS LA QUESTION DANS LES FONCTIONS ELLES-MEMES :
   * le moteur s'appelle lui-meme (le changement de porte referme la fiche,
   * l'adresse enchaine `openDoor` puis `openRecord`), et poser la question au
   * fond ferait s'interposer une fenetre au MILIEU d'un geste qui a deja ete
   * decide. Ce qui traverse ces enveloppes, c'est ce que l'UTILISATEUR
   * declenche ; ce qui passe dessous, c'est le moteur qui se deplace.
   */
  const openRecordDemande = useCallback(
    (id: string | null): void => {
      demanderSortie(() => {
        openRecord(id);
      });
    },
    [demanderSortie, openRecord],
  );

  /**
   * ENTRER DANS UNE FICHE (`ScreenDoor.auClic`) : la porte liee, filtree sur
   * la fiche `id` de la porte `parente`, dans la vue que le reglage nomme.
   * L'adresse porte `dans` et le changement EMPILE (sens 1) : Retour ramene a
   * la collection d'ou l'on a clique.
   */
  const entrerDans = useCallback(
    (parente: string, id: string, vue?: ViewId): void => {
      const porte = doorsRef.current.find((candidate) => candidate.id === parente);
      const auClic = porte?.auClic;
      if (auClic == null) return;
      openDoor(auClic.link.door, [], false, false, null, {
        dans: { porte: parente, lien: auClic.link.id, id },
        ...((vue ?? auClic.view) === undefined ? {} : { vue: vue ?? auClic.view }),
      });
    },
    [openDoor],
  );

  /**
   * LE CLIC SUR UNE FICHE DANS UNE VUE DE COLLECTION. Sur une porte qui declare
   * `auClic`, il ENTRE dans la fiche au lieu de l'ouvrir ; partout ailleurs,
   * et en mode Archives ou Modeles (on y regarde des fiches, pas leur
   * contenu), il ouvre la fiche comme avant. Les autres chemins d'ouverture
   * (adresse, fleches du curseur, lien, recherche) ne passent pas ici.
   */
  const navigationDuClic = useNavigationPort();
  const ouvrirDepuisLaVue = useCallback(
    (id: string | null): void => {
      const porte = doorsRef.current.find((candidate) => candidate.id === doorIdRef.current);
      const vueActive = typesRef.current.find((type) => type.id === viewIdRef.current);
      const niveau = vueActive?.level;
      const requete = queryRef.current;
      const ordinaire =
        id == null ||
        id === BROUILLON_ID ||
        niveau === 'record' ||
        requete.archivesMode ||
        requete.modelesMode;
      /*
       * LA FICHE S'OUVRE DANS UNE AUTRE PORTE (`ScreenDoor.ouvreDans`, chantier
       * `fiche-suivi`). Meme app : le saut d'une carte liee (`openLinked`),
       * qui change de porte avec la fiche et dont la puce ramene ICI, en un
       * seul Retour ; son mot dit la collection quittee (« Kanban : Projets »).
       * Autre app : le saut du shell (`NavigationPort.ouvrir`), meme adresse,
       * meme puce. Sans shell, la fiche s'ouvre ici : un clic qui ne mene nulle
       * part serait un clic qui ment.
       *
       * L'APP VISEE DOIT ETRE OUVRABLE (audit du 27/10, I16) : une app que
       * l'org n'a pas installee, ou que ce membre ne peut pas ouvrir, menait a
       * une route morte. Le shell le dit EXACTEMENT, pour cette app et cette
       * porte (`NavigationPort.peutOuvrir`). Un hote qui ne le fournit pas
       * garde le repli d'avant : la porte visee montre le MEME jeu que
       * celle-ci (la declaration le refuse autrement), donc sans aucune
       * destination pour ce jeu (`destination`, parmi les apps ouvrables),
       * l'app visee ne l'est pas. Non ouvrable : la fiche s'ouvre ici, en le
       * disant a la console.
       */
      const ailleurs = porte?.ouvreDans;
      if (!ordinaire && id != null && ailleurs !== undefined) {
        const depuis = [vueActive?.label, porte?.label].filter((mot) => mot != null && mot !== '').join(' : ');
        if (ailleurs.app !== undefined) {
          const jeu = porte?.jeu;
          const ouvrable =
            navigationDuClic !== null &&
            (navigationDuClic.peutOuvrir !== undefined
              ? navigationDuClic.peutOuvrir(ailleurs.app, ailleurs.door)
              : jeu === undefined || navigationDuClic.destination(jeu.family, jeu.type) !== null);
          if (navigationDuClic !== null && !ouvrable) {
            console.warn(
              `[taki/views] La porte "${porte?.id ?? ''}" ouvre ses fiches dans l'app "${ailleurs.app}", ` +
                "qui n'est pas ouvrable ici (pas installée, ou fermée à ce compte) : la fiche s'ouvre sur place.",
            );
          }
          if (navigationDuClic !== null && ouvrable) {
            const cible = ailleurs.app;
            demanderSortie(() => {
              navigationDuClic.ouvrir({ app: cible, appLabel: '', porte: ailleurs.door }, id, depuis === '' ? null : depuis);
            });
            return;
          }
        } else if (doorsRef.current.some((candidate) => candidate.id === ailleurs.door)) {
          demanderSortie(() => {
            openLinked(ailleurs.door, id, depuis === '' ? {} : { origine: depuis });
          });
          return;
        }
      }
      if (ordinaire || porte?.auClic == null) {
        openRecordDemande(id);
        return;
      }
      const parente = porte.id;
      demanderSortie(() => {
        entrerDans(parente, id);
      });
    },
    [demanderSortie, entrerDans, openRecordDemande, openLinked, navigationDuClic],
  );
  const setSectionDemande = useCallback(
    (id: string): void => {
      demanderSortie(() => {
        setSection(id);
      });
    },
    [demanderSortie, setSection],
  );
  const setViewDemande = useCallback(
    (id: ViewId): void => {
      demanderSortie(() => {
        setView(id);
      });
    },
    [demanderSortie, setView],
  );
  const setArchivesModeDemande = useCallback(
    (on: boolean): void => {
      /* ON NE DEMANDE QU'A L'ENTREE : eteindre un mode ne quitte aucune fiche,
         et un brouillon ne peut de toute facon pas vivre dans un mode. */
      if (!on) {
        setArchivesMode(false);
        return;
      }
      demanderSortie(() => {
        setArchivesMode(true);
      });
    },
    [demanderSortie, setArchivesMode],
  );
  const setModelesModeDemande = useCallback(
    (on: boolean): void => {
      if (!on) {
        setModelesMode(false);
        return;
      }
      demanderSortie(() => {
        setModelesMode(true);
      });
    },
    [demanderSortie, setModelesMode],
  );
  /**
   * LE SAUT PAR LIEN, MEME REGLE. Il rend `false` quand la fenetre s'interpose,
   * et c'est la verite : rien n'a encore bouge. L'appelant (le « + Ajouter »
   * d'une carte liee) ne pose donc pas d'intention dans une porte ou l'on n'est
   * pas encore alle.
   */
  const openLinkedDemande = useCallback(
    (doorId: string, id?: string, options?: OptionsDeSaut): boolean => {
      if (brouillonRef.current == null) return openLinked(doorId, id, options);
      let parti = false;
      demanderSortie(() => {
        parti = openLinked(doorId, id, options);
      });
      return parti;
    },
    [demanderSortie, openLinked],
  );
  /**
   * CREER UNE FICHE DANS UNE PORTE DE L'ECRAN DEPUIS UNE SURFACE QUI N'EST PAS
   * UNE VUE (une porte Canvas, chantier `fiche-suivi`) : la fiche vide de la
   * porte visee, dont la puce, « Annuler » et la fiche creee ramenent ici, en
   * un seul Retour (l'etape poussee est celle du saut, voir le sens 1). Le mot
   * de la puce est `origine`. Rend faux sur un refus (porte inconnue, lecture
   * seule), comme `ouvrirBrouillon`.
   */
  const creerDepuis = useCallback(
    (doorId: string, origine: string): boolean => {
      if (!doorsRef.current.some((candidate) => candidate.id === doorId) || origine === '') return false;
      const depart: OrigineDeFiche = {
        porte: doorId,
        id: BROUILLON_ID,
        titre: origine,
        adresse: pickScreenSearch(urlPort?.params ?? toSearchRef.current()),
      };
      /* AVEC UN PORT, UNE INTENTION que le sens 1 de l'adresse consomme au push
         qui ouvre le brouillon ; SANS PORT, directement dans la memoire de
         l'ecran sans historique, que `ouvrirBrouillon` relit. */
      if (urlPort == null) {
        origineSansHistoriqueRef.current = { ...depart, app: appId, rang: null, org: null, parent: null };
      } else {
        departDuBrouillonRef.current = depart;
      }
      const ouvert = ouvrirBrouillon({ porte: doorId });
      if (!ouvert) {
        departDuBrouillonRef.current = null;
        if (urlPort == null) origineSansHistoriqueRef.current = null;
      }
      return ouvert;
    },
    [urlPort, ouvrirBrouillon, appId],
  );
  /**
   * LE SAUT VERS UNE AUTRE APP, pour une reference SANS porte dans cet ecran.
   *
   * Le shell dit quelle app montre la famille visee, parmi celles que ce
   * membre peut ouvrir ; sans shell (vue montee seule) ou sans app, `null`, et
   * la fiche ne rend pas de fleche. Le depart passe par `demanderSortie` comme
   * `openLinkedDemande` : un brouillon en cours a le droit de retenir.
   *
   * LE TYPE QU'ON TRANSMET. Celui de la reference quand elle en fixe un ; sinon
   * celui de la FICHE VISEE, lu dans l'annuaire (`FicheDAnnuaire.type`). Sans
   * lui, « un client : `contacts` » n'ouvrirait rien dans une app qui n'a que
   * des portes typees (Contact : personnes, societes), alors que la fiche, elle,
   * sait tres bien ce qu'elle est.
   */
  const navigationPort = useNavigationPort();
  const destinationDeReference = useCallback(
    (cible: { family: string; type?: string; id: string }): { libelle: string; ouvrir(): void } | null => {
      if (navigationPort === null) return null;
      const type = cible.type ?? annuaires.get(cleDeJeu(cible.family))?.fiches.get(cible.id)?.type;
      const destination = navigationPort.destination(cible.family, type);
      if (destination === null) return null;
      /* CE QU'ON EMPORTE : LE TITRE DE LA FICHE QU'ON QUITTE, pour que la puce
         de sortie de l'autre app le dise et y ramene, exactement comme un saut
         par carte liee a l'interieur d'un ecran. Le titre vient de la ligne
         projetee de la fiche ouverte, la meme source que `openLinked` ; vide,
         on n'emporte rien : une puce muette n'existe pas, et l'arrivee garde sa
         sortie ordinaire. */
      const titreQuOnQuitte = openRowRef.current?.title ?? '';
      const partir = (): void =>
        navigationPort.ouvrir(destination, cible.id, titreQuOnQuitte === '' ? null : titreQuOnQuitte);
      return {
        libelle: destination.appLabel,
        ouvrir: () => {
          if (brouillonRef.current == null) partir();
          else demanderSortie(partir);
        },
      };
    },
    [navigationPort, annuaires, demanderSortie],
  );


  /**
   * ECRIRE UNE CASE SUR UN MODELE, et pas sur une fiche. LE COEUR DU MODE.
   *
   * CE QUI CHANGE PAR RAPPORT A UNE ECRITURE ORDINAIRE : le DERNIER METRE, et
   * lui seul. Tout ce qui precede (la case cliquee, l'editeur monte, la valeur
   * relue, l'optimisme, le jeton de sequence, la coche ou le cadre rouge de la
   * rangee 3) est identique, parce que c'est la meme surface et le meme geste.
   * Seul `source.set(record, value)` ne peut pas servir : la closure d'une app
   * ecrit dans SA collection, et le document a modifier est un MODELE, dans un
   * autre repository, derriere un autre service.
   *
   * ET LE SOCLE PEUT LE FAIRE, parce qu'il connait la CLEF METIER : une demande
   * d'ecriture porte `{ kind, id, key, value }`, pas seulement une closure. La
   * clef suffit a dire quoi ecrire, le port sait ou.
   *
   * DEUX CHEMINS, ET LE PREMIER N'EST PAS UN CAS PARTICULIER. Le titre d'une
   * ligne de modele est le NOM du modele (voir `nommerLignesDeModeles`) : le
   * modifier, c'est RENOMMER le modele, pas ecrire un champ qui s'appellerait
   * "titre". C'est aussi pourquoi le menu de la fiche n'offre pas de
   * « Renommer » : il ferait deux gestes pour une seule chose.
   *
   * ELLE ECRIT LES `fields` DU MODELE, ET RIEN D'AUTRE. Une case d'une FICHE
   * LIEE emportee par le modele passe par `ecrireSurEnfant`, juste en dessous :
   * meme document, mais le tableau `children` au lieu de la map `fields`, et un
   * binding qui appartient a la PORTE LIEE.
   *
   * LES `fields` PARTENT EN ENTIER, refusionnes ICI. Le port prend la map
   * complete (voir `ModelesPort.update`) : envoyer `{ [clef]: valeur }` seul
   * effacerait tous les autres champs du modele. On ne compte pas sur l'hote
   * pour eclater une clef en chemin pointe non plus : cote SDK, seuls les
   * `customFields` se traitent ainsi, et un champ standard s'y perdrait.
   *
   * OU LA VALEUR SE RANGE DANS CES `fields` NE SE DEVINE PAS, CA SE PROUVE :
   * `champsApresEcriture` pose la valeur, RELIT la pseudo-fiche par le meme
   * chemin que la projection, et ne garde que le rangement qui rend ce qu'on
   * vient d'ecrire. Sans cette preuve, le socle ecrivait a plat pendant que la
   * porte relisait `customFields`, et la case revenait a son ancienne valeur
   * apres la coche verte. Voir le detail la-bas.
   *
   * LES ECRITURES D'UN MEME MODELE SE SUIVENT A LA QUEUE LEU LEU
   * (`enfilerSurModele`), elles ne se croisent pas : deux cases modifiees coup
   * sur coup partaient toutes les deux des memes `fields` (un `setState`
   * asynchrone n'est pas applique quand le `await` suivant reprend), et la
   * seconde effacait la premiere, cote serveur comme a l'ecran.
   */
  const ecrireSurModele = useCallback(
    (
      kind: WriteKind,
      id: string,
      key: string,
      value: Cell,
      editeur: EditorKind,
      /**
       * LA LECTURE DE LA CASE, telle que la projection la fait : `role.get` ou
       * `column.value`, la closure de l'app. C'est elle qui dit ou la valeur se
       * range, parce que c'est elle qui devra la relire.
       */
      lire: (fiche: T) => Cell,
    ): Promise<void> =>
      enfilerSurModele(id, async () => {
        const port = modelesPortRef.current;
        if (port == null) throw new Error(MODELE_WRITE_REASON.sansPort);
        const modele = modeleFichesRef.current.find((candidat) => candidat.id === id);
        if (modele == null) throw new Error(MODELE_WRITE_REASON.disparu);

        if (kind === 'role' && key === 'title') {
          const nom = String(value ?? '').trim();
          if (nom === '') throw new Error(MODELE_WRITE_REASON.nomVide);
          await port.update(id, { name: nom });
          majModeles((avant) =>
            avant.map((candidat) => (candidat.id === id ? { ...candidat, name: nom } : candidat)),
          );
        } else {
          const champs = champsApresEcriture({
            champs: modele.fields ?? {},
            key,
            editeur,
            valeur: value,
            /* LA PSEUDO-FICHE D'ESSAI EST FABRIQUEE COMME LA VRAIE, par la
               MEME fonction : une seconde recette ici ferait relire une forme
               que la projection ne rend pas, et la preuve ne prouverait rien. */
            relire: (essai) =>
              lire(pseudoFicheDeModele({ ...modele, fields: essai }) as unknown as T),
          });
          if (champs == null) throw new Error(MODELE_WRITE_REASON.champIntrouvable);
          await port.update(id, { fields: champs });
          /* LA LISTE SUIT TOUT DE SUITE, sans attendre la relecture : c'est elle
             que la projection lit, et c'est elle que la PROCHAINE ecriture
             refusionnera (par la ref, donc sans attendre un rendu). */
          majModeles((avant) =>
            avant.map((candidat) =>
              candidat.id === id ? { ...candidat, fields: champs } : candidat,
            ),
          );
        }
        /* LE SIGNAL, pour tout ce qui montre les modeles ailleurs dans l'ecran :
           le menu de la fleche de « Ajouter » relit sa liste dessus, et la lecture
           du mode aussi. Il monte APRES l'ecriture reussie, jamais avant. */
        setModelesRevision((avant) => avant + 1);
      }),
    [enfilerSurModele, majModeles],
  );

  /**
   * ECRIRE UNE CASE D'UN ENFANT DE MODELE : le dernier metre de `commitWrite`
   * quand la fiche ouverte EST un enfant (adresse `<modeleId>/<enfantId>`).
   *
   * CE QUI CHANGE PAR RAPPORT A `ecrireSurModele`, ET C'EST TOUT : la valeur ne
   * va pas dans les `fields` du modele, mais dans les `fields` d'UN de ses
   * `children`, et le document ecrit reste le MODELE (le port prend le tableau
   * ENTIER, comme pour `fields`). Un enfant n'est une fiche NULLE PART : il n'y
   * a ni `set()` d'app a appeler, ni collection a recharger, ni ligne a relire.
   *
   * LE BINDING EST CELUI DE LA PORTE AFFICHEE, et c'est ce que le pivot du
   * 10/09/2026 a simplifie. La version d'avant recevait la relecture et
   * l'editeur en DONNEE, dans la demande d'ecriture (`WriteRequest.enfant`) :
   * la case vivait alors dans une carte du PARENT, montee avec le binding d'une
   * AUTRE porte que celle de l'ecran. Elle vit maintenant dans une FICHE, dans
   * SA porte, donc le moteur lit `field.get` et `column.value` sur son propre
   * binding, exactement comme pour une fiche ou un brouillon.
   *
   * LA CLEF EST LE CHEMIN, PRIVE DE SON SAC, la MEME regle que
   * `ecrireSurBrouillon` : `customFields.cout` se range sous `cout` (c'est la
   * PREUVE qui choisit l'etage), et lui passer un chemin pointe l'ecrirait sous
   * une clef qui contient un point, invisible pour toujours.
   *
   * CE QUI NE CHANGE PAS : le refus d'acces en amont, la preuve du rangement
   * (`champsApresEcriture`), le jeton de sequence par cible, le `saveState`
   * cible sur l'ENFANT, « Reessayer » (`failedRef`) et la pile d'annulation.
   *
   * IL NE REJETTE JAMAIS, comme le reste de `commitWrite` : ce qui rate se DIT
   * dans la rangee 3, avec sa raison.
   */
  const ecrireSurEnfant = useCallback(
    async (request: WriteRequest, history: 'push' | 'skip'): Promise<void> => {
      const { kind, id, key, value } = request;
      const chemin = lireCheminDEnfant(id);
      if (chemin == null) return;
      const target: SaveTarget = { kind, id, key };
      /*
       * L'ACCES PASSE DEVANT TOUTE ECRITURE, comme partout (`raisonDeGeste`) :
       * les cases de la fiche sont deja fermees en amont (`lignesEnLectureSeule`),
       * ce chemin est le FILET. Pas de « Reessayer » : rejouer donnerait le meme
       * refus.
       */
      const refus = raisonAccesRef.current;
      if (refus != null) {
        console.warn(`[taki/views] Ecriture refusee sur l'enfant ${id}.${key} : ${refus}`);
        failedRef.current = null;
        failedRecordActionRef.current = null;
        setSaveState({ mode: 'error', target });
        return;
      }
      const port = modelesPortRef.current;
      if (port == null) {
        setSaveState({ mode: 'error', target, reason: MODELE_WRITE_REASON.sansPort });
        return;
      }
      /*
       * LE DOCUMENT A ECRIRE VIENT DE L'ADRESSE, PAS DE L'ECRAN. `chemin.modeleId`
       * est ce que l'`id` de la demande porte, donc un `Cmd+Z` sur une case
       * d'enfant faite dans un AUTRE modele vise bien celui-la, meme si
       * `openIdRef` n'a pas encore rattrape (`openRecord` ne fait qu'appeler
       * `setOpenId`, la ref suit au rendu suivant). C'est exactement le meme
       * choix que le chemin ordinaire, qui cherche par `request.id`.
       */
      const modele = modeleDId(chemin.modeleId);
      const cible = (modele?.children ?? []).find((candidat) => candidat.id === chemin.enfantId);
      if (modele == null || cible == null) {
        console.warn(`[taki/views] Ecriture impossible : l'enfant ${id} n'est plus dans le modele.`);
        setSaveState({ mode: 'error', target, reason: MODELE_WRITE_REASON.disparu });
        return;
      }
      const binding = bindingsRef.current.find((candidate) => candidate.type === viewIdRef.current);
      const field = kind === 'role' ? rolesOf(binding)[key] : undefined;
      const column: Column<T> | undefined = kind === 'column' ? colonneDEcriture(binding, key) : undefined;
      if (field == null && column == null) {
        console.warn(`[taki/views] Ecriture refusee : ${key} n'existe pas dans ce binding.`);
        return;
      }
      /* LA MEME LECTURE QUE LA PROJECTION (`openRow` projette la pseudo-fiche de
         l'enfant par ce binding), et c'est ce qui fait tenir la preuve. */
      const lireCase = (fiche: Record<string, unknown>): Cell =>
        field != null
          ? field.get(fiche as unknown as T)
          : (column?.value(fiche as unknown as T) ?? null);
      const before = lireCase(pseudoFicheDEnfant(cible));
      // Rien n'a change : on referme sans ecrire, donc sans coche (point 1 de
      // `commitWrite`). Avant la preuve de rangement : une case CALCULEE qu'on
      // re-valide sans la toucher n'a aucune raison de rougir.
      if (sameCell(before, value)) return;
      const editeurDeclare: EditorKind | undefined =
        kind === 'column'
          ? column?.kind
          : key === 'dateStart' || key === 'dateEnd'
            ? 'date'
            : undefined;
      const editeur = editorKindOf(before, editeurDeclare);
      const cheminDeCase = kind === 'role' ? (field?.path ?? key) : (column?.path ?? key);
      const clef = cheminDeCase.startsWith(SAC_SUR_MESURE)
        ? cheminDeCase.slice(SAC_SUR_MESURE.length)
        : cheminDeCase;
      const champs = champsApresEcriture({
        champs: cible.fields,
        key: clef,
        editeur,
        valeur: value,
        /* LA PSEUDO-FICHE D'ESSAI EST FABRIQUEE COMME LA VRAIE, par la MEME
           fonction que la projection : une seconde recette ici ferait relire une
           forme que l'ecran ne rend pas, et la preuve ne prouverait rien. */
        relire: (essai) => lireCase(pseudoFicheDEnfant({ ...cible, fields: essai })),
      });
      if (champs == null) {
        console.warn(`[taki/views] La case ${key} ne sait pas se ranger dans cet enfant.`);
        setSaveState({
          mode: 'error',
          target,
          reason: MODELE_WRITE_REASON.champIntrouvable,
        });
        return;
      }

      // La session au moment du DEPART : ce qui atterrit apres la fermeture de
      // la fiche ne parle plus a la fiche suivante (point 7 de `commitWrite`).
      const session = recordSessionRef.current;
      const seqKey = writeKeyOf(kind, id, key);
      const token = (sequenceRef.current.get(seqKey) ?? 0) + 1;
      sequenceRef.current.set(seqKey, token);

      /* L'OPTIMISME EST DANS LE DOCUMENT DU MODELE, ET NULLE PART AILLEURS : la
         fiche de l'enfant projette `modeleParent` (ou la liste, selon la porte)
         a chaque rendu, sans passer par les sacs de patchs du moteur. Il se pose
         AVANT l'ecriture, comme partout, et se defait a l'echec. */
      majEnfants(chemin.modeleId, enfantModifie(modele.children, chemin.enfantId, champs));
      setSaveState({ mode: 'saving', target });

      try {
        await enfilerSurModele(chemin.modeleId, async () => {
          /* LE TABLEAU RELU DANS LA FILE, pas celui capture au clic : la file
             serialise les ecritures d'un meme modele justement pour que chacune
             reparte des `children` a jour (`enfilerSurModele`). */
          const courant = modeleDId(chemin.modeleId);
          if (courant == null) throw new Error(MODELE_WRITE_REASON.disparu);
          await port.update(chemin.modeleId, { children: [...(courant.children ?? [])] });
        });
      } catch (cause) {
        if (sequenceRef.current.get(seqKey) !== token) return;
        /*
         * ON REMET LA SEULE CLEF ECRITE, SUR LES CHAMPS COURANTS DE L'ENFANT.
         *
         * TROIS PRECAUTIONS EN UNE LIGNE, et elles ne disent pas la meme chose.
         * On ne repose pas le TABLEAU capture au depart : la ligne voisine qu'on
         * vient d'ajouter disparaitrait, ou celle qu'on vient de retirer
         * ressusciterait. On ne repose pas non plus la MAP `fields` du clic :
         * une seconde ecriture partie entre temps sur le meme enfant serait
         * effacee de l'ecran alors que sa tache va reussir, coche verte
         * comprise. On relit donc les champs COURANTS et on n'y defait que cette
         * case, par la meme preuve que l'ecriture (`champsApresRollback`).
         *
         * L'ENFANT DISPARU N'A RIEN A RESTAURER : un « Retirer du modele » parti
         * pendant l'aller-retour a deja emporte la ligne.
         */
        const frais = modeleDId(chemin.modeleId);
        const courant = (frais?.children ?? []).find(
          (candidat) => candidat.id === chemin.enfantId,
        );
        if (frais != null && courant != null) {
          const rollback = champsApresRollback({
            champs: courant.fields,
            key: clef,
            editeur,
            avant: before,
            relire: (essai) => lireCase(pseudoFicheDEnfant({ ...courant, fields: essai })),
          });
          if (!rollback.prouve) {
            /* LA VALEUR RESTE A L'ECRAN, ET PERSONNE NE PEUT LE DEVINER : la
               case affiche ce que le serveur n'a pas pris. Le cadre rouge le
               dit a l'utilisateur, cette ligne le dit a qui code. */
            console.warn(
              `[taki/views] Rollback impossible sur l'enfant ${id}.${key} : la valeur d'avant ` +
                "ne se relit à aucun rangement connu. Les champs courants sont laissés en place.",
            );
          }
          majEnfants(
            chemin.modeleId,
            enfantModifie(frais.children, chemin.enfantId, rollback.champs),
          );
        }
        console.error(`[taki/views] Ecriture echouee sur l'enfant ${id}.${key} :`, cause);
        if (recordSessionRef.current !== session) return;
        // UN REFUS DIT SA RAISON ET NE SE REJOUE PAS, comme dans `commitWrite` :
        // la fiche d'un enfant de modele est une fiche comme une autre, et son
        // `set()` est celui de l'app.
        const refus = raisonDeRefus(cause);
        failedRef.current = refus == null ? request : null;
        // Le plus RECENT des deux echecs gagne, comme dans `commitWrite`.
        failedRecordActionRef.current = null;
        setSaveState({ mode: 'error', target, ...(refus == null ? {} : { reason: refus }) });
        return;
      }

      if (sequenceRef.current.get(seqKey) !== token) return;
      /* LE SIGNAL, APRES L'ECRITURE REUSSIE : la carte du modele PARENT relit
         ses enfants au retour, et le menu de la fleche de « Ajouter » compte les
         fiches liees d'un modele. */
      setModelesRevision((precedent) => precedent + 1);
      if (recordSessionRef.current !== session) return;
      failedRef.current = null;
      setSaveState({ mode: 'saved', at: new Date(), target });
      if (history === 'push') {
        /* LE PAS PORTE LE CHEMIN DE L'ENFANT DANS SON `id` : `undo` rouvre donc
           la bonne fiche, et `commitWrite` y reconnait de nouveau une ecriture
           d'enfant, sans champ de plus. */
        const step: WriteStep = { ...request, before };
        const grown = [...undoRef.current, step];
        const capped = grown.length > UNDO_LIMIT ? grown.slice(grown.length - UNDO_LIMIT) : grown;
        undoRef.current = capped;
        setUndoStack(capped);
        // Une ecriture neuve coupe la branche du retablissement, comme partout.
        redoRef.current = [];
        setRedoStack([]);
      }
    },
    [enfilerSurModele, majEnfants, modeleDId],
  );

  /**
   * LE CHEMIN D'ECRITURE UNIQUE, roles et colonnes confondus.
   *
   * `patchRecord` et `patchColumn` ne sont que deux portes vers ici : dupliquer
   * ce corps, c'est se garantir deux comportements qui divergent au premier
   * ajustement (le jeton de sequence d'un cote, pas de l'autre).
   *
   * L'ordre des gestes n'est pas negociable :
   * 1. valeur INCHANGEE : on sort sans rien ecrire et sans coche (une fausse
   *    coche serait un mensonge) ;
   * 2. jeton de sequence PAR CIBLE : une ecriture qui revient alors qu'une plus
   *    recente est partie sur la meme cible ne fait plus rien du tout ;
   * 3. echec : on remet la valeur en place ET on le DIT (`saveState.error` +
   *    `retrySave`), au lieu de la faire disparaitre dans la console ;
   * 4. succes : `saveState.saved` D'ABORD, `reload()` ENSUITE. L'inverse
   *    remettrait la zone 5 en chargement pendant que la coche s'affiche ;
   * 5. JAMAIS de rechargement sur echec : il ramenerait l'ancienne valeur,
   *    ecraserait le rollback, et l'utilisateur ne verrait pas son erreur ;
   * 6. `saveState` porte la CIBLE (`SaveTarget`) a chaque transition : c'est
   *    ce qui permet a la fiche de rendre la coche ou le cadre rouge sur la
   *    bonne case quand deux ecritures se chevauchent ;
   * 7. ce qui atterrit HORS de la session de fiche qui l'a demande ne touche ni
   *    `saveState`, ni la pile, ni l'echec rejouable. Le rollback et le
   *    rechargement, eux, valent toujours : ce sont des faits sur la DONNEE,
   *    pas des messages a l'utilisateur.
   * 8. LECTURE SEULE : on refuse AVANT tout le reste, sans optimisme, sans
   *    rollback a defaire et sans aller deranger le serveur pour un refus
   *    connu d'avance. Voir juste en dessous.
   */
  const commitWrite = useCallback(
    async (request: WriteRequest, history: 'push' | 'skip'): Promise<void> => {
      const { kind, id, key, value } = request;
      const target: SaveTarget = { kind, id, key };
      /*
       * (0) LE BROUILLON PASSE AVANT TOUT, MEME AVANT LE REFUS D'ACCES : sa
       * fiche n'existe pas, donc rien de ce qui suit ne s'applique. Il n'y a ni
       * document a chercher dans `records`, ni `set()` d'app a appeler, ni
       * serveur a deranger, ni ligne a recharger. Le refus d'acces, lui, a deja
       * ete pose a l'OUVERTURE (`ouvrirBrouillon` refuse en lecture seule) : le
       * repasser ici afficherait un echec d'ecriture sur une fiche que personne
       * n'ecrit.
       */
      if (id === BROUILLON_ID) {
        ecrireSurBrouillon(request, history);
        return;
      }
      /*
       * (0 bis) UNE CASE D'ENFANT DE MODELE, ET ELLE PART TOUT DE SUITE, pour la
       * meme raison que le brouillon : `id` designe un ENFANT, pas une fiche.
       * Rien de ce qui suit ne s'applique. Le chercher dans `records` ou dans
       * les pseudo-fiches de modeles ne trouverait rien (au mieux), et
       * l'optimisme se pose dans le document du MODELE, pas dans les sacs de
       * patchs.
       *
       * L'ADRESSE SUFFIT A LE DIRE, ET LE MODE NE COMPTE PLUS (pivot du
       * 10/09/2026) : un chemin `<modeleId>/<enfantId>` ne designe rien d'autre
       * qu'un enfant, dans aucun mode. Le gater sur `mod=1` aurait fait ecrire
       * une case dans la collection de l'app le jour ou la clef tombe de
       * l'adresse (un lien tronque, un retour navigateur). Le refus d'acces,
       * lui, est repose la-bas (`ecrireSurEnfant`), il n'est pas saute.
       */
      if (lireCheminDEnfant(id) != null) {
        await ecrireSurEnfant(request, history);
        return;
      }
      /*
       * (8) LE REFUS D'ECRITURE EN LECTURE SEULE, EN AMONT DE TOUT.
       *
       * Les vues ne posent deja plus d'affordance (`lignesEnLectureSeule` a
       * eteint `editable` sur chaque ligne) : ce chemin est un FILET, pour une
       * vue custom qui appellerait `onPatchRecord` sans regarder, ou pour un
       * acces qui se referme entre l'affichage et le clic. Il ne se traverse
       * donc pas en temps normal, et c'est voulu.
       *
       * IL DIT LE REFUS AU LIEU DE L'AVALER : la rangee 3 passe en erreur et
       * affiche `raisonAcces` (le chassis lit `tools.raisonAcces`). Une saisie
       * qui disparait sans un mot est le pire des deux.
       *
       * PAS DE « REESSAYER » : `failedRef` reste vide, parce qu'il n'y a rien
       * a rejouer. Rejouer la meme ecriture avec le meme acces donnerait le
       * meme refus, et un bouton qui ne peut pas tenir sa promesse est pire
       * qu'une explication.
       */
      const refus = raisonAccesRef.current;
      if (refus != null) {
        console.warn(`[taki/views] Ecriture refusee sur ${id}.${key} : ${refus}`);
        failedRef.current = null;
        failedRecordActionRef.current = null;
        setSaveState({ mode: 'error', target });
        return;
      }
      // La session au moment du DEPART : on la compare au retour (point 7).
      const session = recordSessionRef.current;
      const sameSession = (): boolean => recordSessionRef.current === session;
      const binding = bindingsRef.current.find((candidate) => candidate.type === viewIdRef.current);
      const field = kind === 'role' ? rolesOf(binding)[key] : undefined;
      const column: Column<T> | undefined = kind === 'column' ? colonneDEcriture(binding, key) : undefined;

      /*
       * La cible : un ROLE (`FieldMap`) ou une COLONNE (`Column`). Les deux
       * portent un `set()` de signature identique, c'est exactement ce qui
       * permet aux deux entrees de partager ce chemin. On garde l'OBJET plutot
       * que d'extraire la fonction : un binding qui ecrit `set()` en methode
       * garderait ainsi son `this`.
       */
      const source: FieldMap<T> | Column<T> | undefined = field ?? column;
      if (source?.set == null) {
        // Pas de `set()` : pas d'ecriture. Sans affordance mensongere en amont,
        // ce cas ne devrait jamais arriver ; il reste sur, pas silencieux.
        const what = kind === 'role' ? 'le rôle' : 'la colonne';
        console.warn(`[taki/views] Ecriture refusee : ${what} ${key} n'expose pas set().`);
        return;
      }

      /*
       * OU L'ON CHERCHE LE DOCUMENT : EN MODE MODELES, DANS LES MODELES. Les
       * pseudo-fiches sont la seule source du mode (voir `recordById`) ;
       * fouiller `records` y trouverait, au mieux, une fiche qui partage l'id
       * d'un modele, et l'ecriture repartirait de SES valeurs.
       */
      const enModeles = queryRef.current.modelesMode;
      const record = enModeles
        ? modelesProjetesRef.current.find((candidate) => getIdRef.current(candidate) === id)
        : (recordsRef.current.find((candidate) => getIdRef.current(candidate) === id) ??
          (soloRef.current?.id === id ? soloRef.current.record : undefined));
      if (record === undefined) {
        // Arrive quand on annule un pas fait sur un element qui a quitte la
        // memoire (fiche ouverte a froid, puis navigation ailleurs).
        console.warn(`[taki/views] Ecriture impossible : ${id} n'est plus en memoire.`);
        return;
      }

      const bag = kind === 'role' ? patchesRef.current : columnPatchesRef.current;
      const setBag = kind === 'role' ? setPatches : setColumnPatches;
      /**
       * LA LECTURE DE LA CASE, isolee parce qu'elle sert DEUX fois : ici pour
       * la valeur d'avant, et en mode Modeles pour PROUVER ou l'ecriture doit
       * se ranger (`champsApresEcriture`). Les deux doivent etre le meme
       * chemin, sinon la preuve porte sur autre chose que ce qu'on affiche.
       */
      const lireCase = (cible: T): Cell =>
        field != null ? field.get(cible) : (column?.value(cible) ?? null);
      /*
       * LA VALEUR D'AVANT, ET EN MODE MODELES LE TITRE N'EST PAS CELUI DE LA
       * FICHE. Le titre d'une ligne de modele est le NOM DU MODELE
       * (`nommerLignesDeModeles`), c'est lui qu'on voit, et c'est lui que
       * l'ecriture modifie (`ecrireSurModele` appelle `update({ name })`). Le
       * lire par le role prendrait le titre du CONTENU du modele, et les deux
       * pannes qui suivent sont silencieuses :
       *  - renommer « Kit tournage » en « Camera FX6 » alors que son contenu
       *    s'appelle deja « Camera FX6 » : `sameCell` conclut « rien n'a
       *    change » et le renommage est ignore sans un mot ;
       *  - la pile d'annulation empile ce mauvais avant, donc un `Cmd+Z`
       *    renomme le modele avec le titre de son contenu.
       */
      const stored =
        enModeles && kind === 'role' && key === 'title'
          ? (modeleFichesRef.current.find((candidat) => candidat.id === id)?.name ?? null)
          : lireCase(record);
      const before = currentValueOf(bag, id, key, stored);
      /*
       * LE TYPE DE LA CASE, RESOLU ICI PARCE QU'IL N'EST ATTEIGNABLE QUE D'ICI.
       * Une ecriture de modele range la valeur BRUTE dans ses `fields` : il faut
       * donc savoir sous quelles formes la chaine `YYYY-MM-DD` qui sort de
       * l'editeur de date peut s'y ranger (`valeursPourModele`), et dans quel
       * espace comparer la relecture (`champsApresEcriture`). La reponse est la
       * meme que celle de la fenetre de lot, par la MEME fonction : le type
       * DECLARE par l'ecran gagne (`Column.kind`, que `colonnesDe` pose depuis
       * le fichier de champs), sinon la valeur stockee decide.
       *
       * LES DEUX ROLES DE DATE SONT DES DATES PAR LEUR NOM : le contrat des
       * vues les definit comme "le placement dans le temps", et aucune valeur
       * declaree ne le dit a leur place.
       */
      const editeurDeclare: EditorKind | undefined =
        kind === 'column'
          ? column?.kind
          : key === 'dateStart' || key === 'dateEnd'
            ? 'date'
            : undefined;
      const editeur = editorKindOf(stored, editeurDeclare);

      // (1) Rien n'a change : on referme sans ecrire, donc sans coche.
      if (sameCell(before, value)) return;

      // (2) Le jeton se pose AVANT le depart : c'est lui qui dira, au retour, si
      // une ecriture plus recente est passee devant sur la MEME cible.
      const seqKey = writeKeyOf(kind, id, key);
      const token = (sequenceRef.current.get(seqKey) ?? 0) + 1;
      sequenceRef.current.set(seqKey, token);

      // On ne retient QUE la valeur de CETTE clef, jamais le sac entier de la
      // ligne : deux champs d'une meme fiche s'ecrivent couramment coup sur
      // coup, et restaurer le sac complet ferait disparaitre de l'ecran la
      // saisie voisine dont l'ecriture est encore en vol.
      const previousCell = bag[id]?.[key];
      setBag((current) => ({ ...current, [id]: { ...current[id], [key]: value } }));
      setSaveState({ mode: 'saving', target });
      pendingWritesRef.current += 1;

      try {
        /* LE DERNIER METRE, ET LUI SEUL, CHANGE AVEC LE MODE. Le `set()` de
           l'app ecrit dans SA collection : en mode Modeles, le document a
           modifier n'y est pas. Tout le reste de ce chemin (optimisme, jeton,
           rollback, coche, pile d'annulation) est deja le bon, et le
           dedoubler serait se garantir deux comportements qui divergent. */
        if (enModeles) await ecrireSurModele(kind, id, key, value, editeur, lireCase);
        else await source.set(record, value);
      } catch (cause) {
        if (sequenceRef.current.get(seqKey) !== token) return;
        // (3) Echec : on remet la ligne en place. Une carte qui revient toute
        // seule dans sa colonne est le comportement voulu, un mensonge ne l'est
        // pas. La difference avec avant : ca se VOIT dans la rangee 3.
        setBag((current) => {
          const row = current[id];
          if (row === undefined) return current;
          const nextRow = { ...row };
          if (previousCell === undefined) delete nextRow[key];
          else nextRow[key] = previousCell;
          const next = { ...current };
          // Sac de ligne vide : on retire l'entree, sinon la projection
          // repasserait sur une ligne qui n'a plus rien a patcher.
          if (Object.keys(nextRow).length === 0) delete next[id];
          else next[id] = nextRow;
          return next;
        });
        console.error(`[taki/views] Ecriture echouee sur ${id}.${key} :`, cause);
        // (7) Session terminee : afficher "Echec de l'enregistrement" et un
        // "Reessayer" sur la fiche suivante designerait une ecriture qui ne la
        // concerne pas. La trace console, elle, reste.
        if (!sameSession()) return;
        /*
         * (8) UN REFUS DE L'APP N'EST PAS UNE PANNE. L'app a lance `refuser()`
         * avec une phrase ecrite pour etre lue (un email mal forme, un nom
         * qu'on ne peut pas vider) : elle REMPLACE « Echec de l'enregistrement »
         * et RETIRE « Reessayer », qui rejouerait le meme refus mot pour mot.
         * Rien a rejouer, donc rien dans `failedRef` : le raccourci clavier ne
         * doit pas ressusciter un geste que le bouton ne propose plus.
         */
        const refus = raisonDeRefus(cause);
        failedRef.current = refus == null ? request : null;
        // Le plus RECENT des deux echecs gagne : un geste de fiche rate avant
        // celui-ci ne doit pas voler le bouton "Reessayer" a cette saisie-la.
        failedRecordActionRef.current = null;
        setSaveState({ mode: 'error', target, ...(refus == null ? {} : { reason: refus }) });
        return;
      } finally {
        pendingWritesRef.current -= 1;
      }

      if (sequenceRef.current.get(seqKey) !== token) return;

      // (4) Succes. Tout ce qui s'ADRESSE a l'utilisateur (indicateur, pile
      // d'annulation, echec rejouable) appartient a la session qui a demande
      // l'ecriture, et a elle seule (point 7).
      if (sameSession()) {
        failedRef.current = null;
        setSaveState({ mode: 'saved', at: new Date(), target });

        if (history === 'push') {
          // On empile APRES coup : on ne propose jamais d'annuler ce qui n'a
          // pas ete ecrit. Les refs suivent tout de suite, le clavier les lit
          // avant le prochain rendu.
          const step: WriteStep = { ...request, before };
          const grown = [...undoRef.current, step];
          const capped =
            grown.length > UNDO_LIMIT ? grown.slice(grown.length - UNDO_LIMIT) : grown;
          undoRef.current = capped;
          setUndoStack(capped);
          // Une ecriture neuve coupe la branche du retablissement, comme partout.
          redoRef.current = [];
          setRedoStack([]);
        }
      }

      /*
       * MODE MODELES : ON S'ARRETE ICI, et les trois chemins qui suivent
       * seraient tous les trois FAUX.
       *
       * `getOne` (les deux blocs suivants) lit la collection de l'APP : lui
       * passer l'id d'un MODELE le ferait chercher dans la mauvaise famille. Au
       * mieux il rend `null` (et la fiche clignote "introuvable"), au pire il
       * rend une fiche homonyme et l'affiche a la place du modele.
       *
       * `reload()` relit la porte : il ne ramenerait aucun modele, puisque les
       * modeles ne viennent pas du chargeur.
       *
       * CE QUI REMPLACE LES TROIS est deja fait : `ecrireSurModele` a mis la
       * liste a jour et incremente `modelesRevision`, qui fait relire le port.
       * Il ne reste qu'a retirer l'optimisme, comme le fait le mode serveur
       * apres sa relecture : la donnee locale a pris le relais, et un patch qui
       * lui survit masquerait la prochaine valeur venue du port. Meme jeton que
       * le reste du chemin : une ecriture plus recente sur la MEME cible garde
       * le sien.
       */
      if (enModeles) {
        if (sequenceRef.current.get(seqKey) === token) {
          setBag((previous) => dropPatch(previous, id, key));
        }
        return;
      }

      // Un element ouvert a FROID ne revient jamais par `reload()` : celui-ci
      // recharge la LISTE, et la garde `soloRef.current?.id === openId` empeche
      // tout refetch. On le rafraichit donc a la main. Surtout pas de
      // `setSolo(null)` : la fiche afficherait "introuvable" le temps du
      // rechargement.
      if (soloRef.current?.id === id) {
        const getOne = getOneRef.current;
        if (getOne != null) {
          try {
            const fresh = await getOne(id);
            if (fresh != null) setSolo({ id, record: fresh });
          } catch {
            /* Refetch rate : l'ecriture, elle, est passee. La fiche garde son optimisme. */
          }
        }
      }

      // MODE SERVEUR : relire toute la liste ferait retomber l'utilisateur en
      // page 1 a chaque champ modifie, intenable des qu'on travaille au fond
      // d'une longue liste. On relit la SEULE ligne touchee, puis on retire son
      // patch optimiste : la donnee fraiche a pris le relais.
      if (paginationRef.current === 'server') {
        // SANS `get()`, il n'y a pas de relecture d'une ligne possible. On ne
        // fait alors PAS semblant : on retombe sur le rechargement complet du
        // mode client. La liste revient en page 1, ce qui est desagreable, mais
        // c'est infiniment moins grave que l'alternative silencieuse : des
        // stats de zone 3 fausses pour toujours et les modifications des
        // collegues qui ne remontent plus jamais.
        if (getOneRef.current == null) {
          reload();
          return;
        }
        const applied = await refreshRecord(id);
        // Le patch ne tombe QUE si la relecture a vraiment eu lieu ET qu'aucune
        // ecriture plus recente n'est partie sur la meme cible entre-temps.
        // Le cas vecu que ca protege : ecrire un champ puis faire `Cmd+Z` tout
        // de suite. L'annulation pose son propre patch pendant que la relecture
        // de la premiere ecriture est en vol ; sans ce jeton, elle effacerait le
        // patch de l'annulation et la valeur sauterait sous les doigts avant de
        // revenir. Meme jeton que le reste du chemin d'ecriture, pas un
        // mecanisme parallele.
        const current = sequenceRef.current.get(seqKey);
        if (applied && current === token) {
          const setBag = kind === 'role' ? setPatches : setColumnPatches;
          setBag((previous) => dropPatch(previous, id, key));
        }
        return;
      }

      // MODE CLIENT : la seule parade au multi-utilisateur de ce lot, on relit.
      // Ca ramene les modifications des autres et ca reconcilie les stats de la
      // zone 3, que le core calcule sur les records BRUTS et que l'optimisme ne
      // touche pas.
      reload();
    },
    [reload, refreshRecord, ecrireSurModele, ecrireSurEnfant, ecrireSurBrouillon],
  );

  const patchRecord = useCallback(
    (id: string, role: string, value: Cell): Promise<void> =>
      commitWrite({ kind: 'role', id, key: role, value }, 'push'),
    [commitWrite],
  );

  const patchColumn = useCallback(
    (id: string, key: string, value: Cell): Promise<void> =>
      commitWrite({ kind: 'column', id, key, value }, 'push'),
    [commitWrite],
  );

  /* La lecture du sac optimiste, par la meme regle que le `before` d'un pas
     d'annulation (`currentValueOf`). Elle change d'identite avec le sac : ce
     qui la lit au rendu suit l'ecriture. */
  const valeurEnCours = useCallback(
    (kind: WriteKind, id: string, key: string, stockee: Cell): Cell =>
      currentValueOf(kind === 'role' ? patches : columnPatches, id, key, stockee),
    [patches, columnPatches],
  );

  /**
   * CHANGER LA LISTE DES ENFANTS D'UN MODELE : le « Retirer du modele » du menu
   * « ... » d'un enfant ouvert, et l'ajout d'un enfant depuis une fiche vide.
   *
   * UN SEUL CORPS POUR LES DEUX, parce que c'est le meme geste a une fonction
   * pure pres (`avecEnfant`, `sansEnfants`) : le port prend le tableau ENTIER
   * (contrat de `ModelesPort.update`), donc les deux envoient la meme chose et
   * ne different que par ce qu'elles en ont fait.
   *
   * LE MODELE EST NOMME PAR L'APPELANT, ET PLUS LU SUR `openIdRef` (pivot du
   * 10/09/2026) : ce qui est ouvert est l'ENFANT, pas le modele, et son adresse
   * porte les deux ids. Lire la fiche affichee viserait un chemin comme s'il
   * etait un id de modele, donc « Ce modele n'est plus dans la liste » a chaque
   * retrait.
   *
   * PAS D'OPTIMISME ICI, CONTRAIREMENT A L'ECRITURE D'UNE CASE : ces gestes
   * ferment la fiche (« Retirer ») ou en ouvrent une (« Creer »), donc rien n'a
   * besoin d'apparaitre avant la reponse, et une ligne posee puis reprise a
   * l'echec se lirait comme une donnee perdue.
   *
   * ELLE REJETTE, ET C'EST LA DIFFERENCE AVEC `commitWrite` : l'appelant
   * enchaine (fermer la fiche, ouvrir l'enfant cree) et ne doit pas le faire sur
   * un echec. L'indicateur, lui, REDEVIENT MUET (`idle`) : un `saveState: error`
   * allumerait un « Reessayer » que `retrySave` ne sait pas rejouer.
   *
   * L'ACCES PASSE DEVANT, comme partout : on refuse avant de deranger le port.
   */
  const majEnfantsDuModele = useCallback(
    async (
      modeleId: string,
      suivant: (enfants: readonly EnfantDeModele[] | undefined) => EnfantDeModele[],
    ): Promise<void> => {
      const refus = raisonAccesRef.current;
      if (refus != null) throw new Error(refus);
      const port = modelesPortRef.current;
      if (port == null) throw new Error(MODELE_WRITE_REASON.sansPort);
      // La session au moment du DEPART : ce qui atterrit apres la fermeture de
      // la fiche ne parle plus a la fiche suivante.
      const session = recordSessionRef.current;
      setSaveState({ mode: 'saving' });
      try {
        await enfilerSurModele(modeleId, async () => {
          /* LE TABLEAU SE RELIT DANS LA FILE : c'est tout l'objet de
             `enfilerSurModele`. Le capturer au clic ferait repartir ce geste des
             enfants d'avant l'ecriture precedente, qui les effacerait. */
          const modele = modeleDId(modeleId);
          if (modele == null) throw new Error(MODELE_WRITE_REASON.disparu);
          const children = suivant(modele.children);
          await port.update(modeleId, { children });
          /* LA DONNEE SUIT L'ECRITURE REUSSIE, dans CELUI DES DEUX ETATS qui
             porte ce modele (la liste du mode, ou le parent charge seul). */
          majEnfants(modeleId, children);
        });
      } catch (cause) {
        console.error(
          `[taki/views] Les fiches liees du modele ${modeleId} n'ont pas pu etre ecrites :`,
          cause,
        );
        if (recordSessionRef.current === session) setSaveState({ mode: 'idle' });
        throw cause;
      }
      /* LE SIGNAL MONTE AVANT LA GARDE DE SESSION : le modele a change, quelle
         que soit la fiche affichee a l'arrivee de la reponse, et le menu de la
         fleche de « Ajouter » compte les fiches liees. */
      setModelesRevision((avant) => avant + 1);
      if (recordSessionRef.current !== session) return;
      setSaveState({ mode: 'saved', at: new Date() });
    },
    [enfilerSurModele, majEnfants, modeleDId],
  );

  const retirerEnfants = useCallback(
    async (modeleId: string, ids: readonly string[]): Promise<void> => {
      await majEnfantsDuModele(modeleId, (enfants) => sansEnfants(enfants, ids));
    },
    [majEnfantsDuModele],
  );

  /**
   * LES TROIS GESTES DE FICHE (chantier 2_0) : archiver, desarchiver,
   * supprimer. Un chemin A PART de `commitWrite`, et pour une raison de fond :
   * ceux-la portent sur le DOCUMENT entier, pas sur une case. Il n'y a donc
   * ni valeur d'avant a retenir, ni optimisme a poser (la fiche quitte
   * l'ecran), ni pas d'annulation a empiler (`Cmd+Z` est fait pour les
   * cellules ; annuler une suppression n'est pas une ecriture inverse).
   *
   * Ce que ce chemin garantit, et qu'aucune app n'a donc a recoder :
   * 1. la fiche se REFERME apres coup. Elle vient de quitter le jeu (ou de le
   *    rejoindre) : la garder ouverte laisserait un curseur qui compte "3 / 47"
   *    dans un jeu auquel elle n'appartient plus ;
   * 2. la porte se RECHARGE (`reload`), et c'est le socle qui le fait, jamais
   *    l'app (regle G19 : pas de bouton "Rafraichir", pas de rechargement a la
   *    main) ;
   * 3. l'ecriture REND COMPTE par `saveState` : le rond pendant, l'echec en
   *    rouge avec sa relance. Le succes, lui, n'a pas de coche a montrer : la
   *    fiche s'est refermee, et la ligne qui disparait de la liste est le
   *    meilleur accuse de reception qui soit ;
   * 4. ce qui atterrit HORS de la session de fiche qui l'a demande ne touche
   *    ni l'indicateur ni la fiche a l'ecran (meme regle que `commitWrite`,
   *    point 7). Le rechargement, lui, vaut toujours : c'est un fait sur la
   *    DONNEE, pas un message a l'utilisateur.
   */
  const runRecordAction = useCallback(
    async (kind: RecordActionKind, record: T): Promise<void> => {
      const verb =
        kind === 'archive'
          ? archiveRef.current
          : kind === 'unarchive'
            ? unarchiveRef.current
            : removeRef.current;
      if (verb == null) {
        // L'entree du menu est grisee quand le verbe manque : ce chemin ne
        // devrait pas exister. Il reste sur, pas silencieux. C'est ICI que vit
        // la consigne TECHNIQUE, pas dans l'infobulle du bouton : celle-la
        // s'adresse a qui survole, celle-ci a qui code.
        console.warn(
          `[taki/views] Geste de fiche refuse : la porte active ne declare pas ${kind}(). ` +
            `Pour l'offrir, declare ${kind}(record) sur la porte (ScreenDoor) ou, pour ` +
            "toutes les portes a la fois, sur l'ecran (option de meme nom de useScreen).",
        );
        return;
      }
      const session = recordSessionRef.current;
      const sameSession = (): boolean => recordSessionRef.current === session;
      setSaveState({ mode: 'saving' });
      try {
        await verb(record);
      } catch (cause) {
        console.error(
          `[taki/views] ${kind} a echoue sur ${getIdRef.current(record)} :`,
          cause,
        );
        // Session terminee : afficher l'echec et un "Reessayer" sur la fiche
        // suivante designerait un geste qui ne la concerne pas.
        if (!sameSession()) return;
        failedRef.current = null;
        /* UN REFUS DE L'APP DIT SA RAISON ET NE SE REJOUE PAS, comme pour une
           case (`commitWrite`) : un `remove()` qui refuse parce que la fiche
           tient des fiches liees n'a pas de seconde chance. */
        const refus = raisonDeRefus(cause);
        failedRecordActionRef.current = refus == null ? { kind, record } : null;
        setSaveState({ mode: 'error', ...(refus == null ? {} : { reason: refus }) });
        return;
      }
      failedRecordActionRef.current = null;
      // La fermeture remet `saveState` a `idle` (elle passe par
      // `resetRecordSession`) : l'indicateur ne vit qu'au niveau fiche, il
      // n'a plus rien a dire une fois de retour en collection.
      if (sameSession()) openRecord(null);
      reload();
    },
    [openRecord, reload],
  );

  /* ------------------------------------------------------------------ */
  /* LES TROIS PRIMITIVES DE LOT (chantier 3_0), a DOCUMENT UNIQUE       */
  /* ------------------------------------------------------------------ */

  /**
   * L'enregistrement BRUT d'un id, cherche aux deux MEMES endroits que la
   * fiche ouverte : le jeu en memoire, puis l'element charge seul. Les trois
   * primitives ecrivent sur le DOCUMENT, elles ont donc besoin du `T` et pas
   * de sa ligne projetee.
   */
  const recordById = useCallback((id: string): T | null => {
    const getId = getIdRef.current;
    // ET IL SUIT LE MODE, comme `openRow` et `openRecordValue` : ce que designe
    // un id en mode Modeles est un MODELE, jamais une fiche de la collection.
    if (queryRef.current.modelesMode) {
      return modelesProjetesRef.current.find((candidate) => getId(candidate) === id) ?? null;
    }
    const inMemory = recordsRef.current.find((candidate) => getId(candidate) === id);
    if (inMemory !== undefined) return inMemory;
    const soloValue = soloRef.current;
    return soloValue?.id === id ? soloValue.record : null;
  }, []);

  /**
   * LE COMPTEUR D'ECRITURES DE LOT EN VOL, et le rechargement qu'il declenche.
   *
   * Un lot ecrit un document a la fois, donc le compteur repasse par zero
   * entre chaque fiche : recharger a ce moment-la rechargerait la porte douze
   * fois pour douze fiches. On attend donc un souffle apres la DERNIERE
   * ecriture, et toute ecriture qui repart dans cette fenetre annule
   * l'attente.
   *
   * POURQUOI LE CORE LE FAIT LUI-MEME, plutot que de laisser la fenetre de lot
   * appeler `reload()` : un rechargement oublie ne rougit aucun build. La
   * derniere ecriture d'un lot est le seul moment ou l'on SAIT qu'il faut
   * relire, et ce moment est ici.
   */
  const bulkInFlightRef = useRef(0);
  const bulkReloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const beginBulkWrite = useCallback((): void => {
    bulkInFlightRef.current += 1;
    if (bulkReloadTimerRef.current != null) {
      clearTimeout(bulkReloadTimerRef.current);
      bulkReloadTimerRef.current = null;
    }
  }, []);

  const endBulkWrite = useCallback((): void => {
    bulkInFlightRef.current = Math.max(0, bulkInFlightRef.current - 1);
    if (bulkInFlightRef.current > 0) return;
    if (bulkReloadTimerRef.current != null) clearTimeout(bulkReloadTimerRef.current);
    bulkReloadTimerRef.current = setTimeout(() => {
      bulkReloadTimerRef.current = null;
      if (bulkInFlightRef.current > 0) return;
      // Le rechargement NU : l'invariant retire du lot les fiches qui ont
      // quitte la vue, et laisse les autres. Voir `reloadPort`.
      reloadPort();
    }, BULK_RELOAD_DELAY_MS);
  }, [reloadPort]);

  // Un lot en vol au demontage de l'ecran ne doit pas laisser un rechargement
  // programme derriere lui.
  useEffect(
    () => () => {
      if (bulkReloadTimerRef.current != null) clearTimeout(bulkReloadTimerRef.current);
    },
    [],
  );

  /**
   * ARCHIVER UNE fiche, et rien d'autre. Pas de boucle, pas de progression,
   * pas de fermeture de fiche, pas de `saveState` : ce chemin sert un LOT, et
   * c'est le moteur de lot qui rend compte.
   *
   * L'ERREUR REMONTE TELLE QUELLE. C'est la seule facon pour l'appelant de
   * NOMMER la fiche tombee, et un lot a moitie ecrit ne se voit dans aucun
   * build vert.
   */
  const archiveOne = useCallback(
    async (id: string): Promise<void> => {
      const verb = archiveRef.current;
      if (verb == null) {
        // Le bouton est grise avec sa raison (`bulkAvailability`) : ce chemin
        // ne devrait pas exister. Il reste sur, pas silencieux. Le message
        // JETE s'adresse a qui lit le compte rendu du lot ; la consigne
        // technique va en console, la ou un developpeur regarde.
        console.warn(
          "[taki/views] Archivage en lot refuse : la porte active ne declare pas archive(). " +
            'Pour l\'offrir, declare archive(record) sur la porte (ScreenDoor) ou, pour toutes ' +
            "les portes a la fois, sur l'ecran (option de meme nom de useScreen).",
        );
        throw new Error(RECORD_ACTION_REASON.archive);
      }
      const record = recordById(id);
      if (record == null) throw new Error("Cette fiche n'est plus dans la liste.");
      beginBulkWrite();
      try {
        await verb(record);
      } finally {
        endBulkWrite();
      }
    },
    [recordById, beginBulkWrite, endBulkWrite],
  );

  /**
   * DESARCHIVER UNE fiche : jumelle exacte d'`archiveOne`, avec le verbe
   * `unarchive` de la porte. Meme silence, meme remontee d'erreur.
   */
  const unarchiveOne = useCallback(
    async (id: string): Promise<void> => {
      const verb = unarchiveRef.current;
      if (verb == null) {
        console.warn(
          "[taki/views] Desarchivage en lot refuse : la porte active ne declare pas unarchive(). " +
            'Pour l\'offrir, declare unarchive(record) sur la porte (ScreenDoor) ou, pour toutes ' +
            "les portes a la fois, sur l'ecran (option de meme nom de useScreen).",
        );
        throw new Error(RECORD_ACTION_REASON.unarchive);
      }
      const record = recordById(id);
      if (record == null) throw new Error("Cette fiche n'est plus dans la liste.");
      beginBulkWrite();
      try {
        await verb(record);
      } finally {
        endBulkWrite();
      }
    },
    [recordById, beginBulkWrite, endBulkWrite],
  );

  /**
   * SUPPRIMER UNE fiche. Meme patron, meme silence, meme remontee d'erreur.
   *
   * ELLE COMPTE SES FICHES LIEES AVANT D'ECRIRE, et jette le refus au lieu de
   * supprimer (« a la Odoo », fondateur 09/09/2026). Trois choses expliquent
   * pourquoi ce controle est ICI et pas dans la confirmation du lot :
   *
   * 1. UN LOT ECRIT SEQUENTIELLEMENT, un document a la fois, sans transaction
   *    (G22). Il n'existe aucun moment ou l'on connaitrait l'etat des douze
   *    fiches a la fois ; et le compter d'avance pour toutes ne prouverait rien
   *    de plus, une fiche pouvant recevoir un lien pendant que le lot tourne.
   *    Le seul instant ou le compte vaut, c'est juste avant d'ecrire CETTE
   *    fiche ;
   * 2. LE BILAN NOMME DEJA LES TOMBEES (G22). Une fiche refusee devient un
   *    echec NOMME, avec sa phrase, a cote de celles qui sont passees : c'est
   *    exactement la forme que le lot sait rendre, sans une ligne de plus ;
   * 3. LE REJEU NE REJOUE QUE LES ECHECS (G22). Rattacher les interventions
   *    ailleurs puis relancer rejoue la seule fiche refusee, pas les onze
   *    autres, deja supprimees.
   *
   * ET LE COMPTE EST DANS LE MEME `beginBulkWrite` que l'ecriture : c'est lui
   * qui retient le rechargement de la porte, et une fiche refusee ne doit pas
   * en declencher un a elle seule au milieu du lot.
   *
   * `archiveOne` NE COMPTE RIEN, et c'est la meme regle qu'a l'unite :
   * archiver ne casse aucun lien, la fiche liee reste visible.
   */
  const removeOne = useCallback(
    async (id: string): Promise<void> => {
      const verb = removeRef.current;
      if (verb == null) {
        console.warn(
          "[taki/views] Suppression en lot refusee : la porte active ne declare pas remove(). " +
            'Pour l\'offrir, declare remove(record) sur la porte (ScreenDoor) ou, pour toutes ' +
            "les portes a la fois, sur l'ecran (option de meme nom de useScreen).",
        );
        throw new Error(RECORD_ACTION_REASON.remove);
      }
      const record = recordById(id);
      if (record == null) throw new Error("Cette fiche n'est plus dans la liste.");
      beginBulkWrite();
      try {
        let comptes: CompteLie[];
        try {
          comptes = await compterLesLiees(id);
        } catch (cause) {
          // On ne prouve pas une absence avec une lecture qui a echoue : la
          // fiche tombe, NOMMEE, et le lot continue.
          console.error(`[taki/views] Comptage des fiches liees de ${id} echoue :`, cause);
          throw new Error(LIEES_ILLISIBLES);
        }
        const refus = refusDeSuppression(comptes);
        if (refus != null) throw new Error(refus);
        await verb(record);
      } finally {
        endBulkWrite();
      }
    },
    [recordById, beginBulkWrite, endBulkWrite, compterLesLiees],
  );

  /**
   * ECRIRE UN CHAMP SUR UNE fiche. `fieldKey` est la cle OPAQUE d'un
   * `BulkEditField` : elle porte son espace de cles, ce qui empeche une
   * colonne `status` d'aller ecrire le role `status`.
   *
   * CE CHEMIN NE PASSE PAS PAR `commitWrite`, et ce n'est pas un oubli :
   * `commitWrite` avale l'echec (son contrat est de ne jamais rejeter, pour
   * que le kanban puisse faire `void onPatchRecord(...)`), il empile un pas
   * d'annulation par fiche et il recharge la porte a chaque ecriture. Trois
   * comportements justes pour UNE case, ruineux pour douze : `Cmd+Z` defairait
   * un lot une fiche a la fois, le "Reessayer" de la rangee 3 rejouerait une
   * fiche prise au hasard dans le lot, et la porte se rechargerait douze fois.
   */
  const writeCell = useCallback(
    async (id: string, fieldKey: string, value: unknown): Promise<void> => {
      if (raisonAccesRef.current != null) throw new Error(raisonAccesRef.current);
      const record = recordById(id);
      if (record == null) throw new Error("Cette fiche n'est plus dans la liste.");
      const cell = asCell(value);
      const binding = bindingRef.current;
      const target = parseBulkFieldKey(fieldKey);
      const role = target.kind === 'column' ? undefined : rolesOf(binding)[target.key];
      const column =
        target.kind === 'role'
          ? undefined
          : (binding?.fieldColumns ?? binding?.columns)?.find((candidate) => candidate.key === target.key);
      // Clef non prefixee : la COLONNE d'abord, le role ensuite (meme priorite
      // que `sortValueOf`). Une clef prefixee, elle, n'a qu'un seul candidat.
      const write = column?.set ?? role?.set;
      if (write == null) {
        throw new Error("Ce champ ne se modifie pas sur cet écran.");
      }
      beginBulkWrite();
      try {
        /*
         * MEME REROUTAGE QUE `commitWrite`, ET IL N'EST PAS DECORATIF. Les trois
         * verbes du lot sont GRISES en mode Modeles (`bulkAvailability`), donc
         * ce chemin ne devrait pas se traverser ; mais une fenetre de lot restee
         * ouverte pendant qu'on entre dans le mode appellerait quand meme, et le
         * `set()` de l'app irait alors ecrire dans la COLLECTION avec l'id d'un
         * MODELE. Selon l'app, ca cree un document ou ca en modifie un autre.
         * Un grisage est une affordance, pas une garantie : la garantie est ici.
         */
        if (queryRef.current.modelesMode) {
          const declare: EditorKind | undefined =
            target.kind === 'column'
              ? column?.kind
              : target.key === 'dateStart' || target.key === 'dateEnd'
                ? 'date'
                : undefined;
          /* LA MEME LECTURE QUE CELLE DE `commitWrite`, et pour la meme raison :
             elle donne le type d'editeur ICI, et elle sert LA-BAS a prouver ou
             la valeur se range (`champsApresEcriture`). */
          const lireCase = (cible: T): Cell =>
            column != null ? column.value(cible) : (role?.get(cible) ?? null);
          const stored = lireCase(record);
          await ecrireSurModele(
            column != null ? 'column' : 'role',
            id,
            target.key,
            cell,
            editorKindOf(stored, declare),
            lireCase,
          );
        } else {
          await write(record, cell);
        }
      } finally {
        endBulkWrite();
      }
    },
    [recordById, beginBulkWrite, endBulkWrite, ecrireSurModele],
  );

  const retrySave = useCallback((): void => {
    // LE GESTE DE FICHE D'ABORD : c'est le plus recent des deux par
    // construction (chacun efface l'autre en s'inscrivant), et c'est lui que
    // l'echec affiche a l'ecran designe.
    const failedAction = failedRecordActionRef.current;
    if (failedAction != null) {
      void runRecordAction(failedAction.kind, failedAction.record);
      return;
    }
    const request = failedRef.current;
    if (request == null) return;
    // On rejoue la MEME ecriture, telle quelle : l'optimisme ayant ete remis en
    // place a l'echec, `commitWrite` reverra bien une valeur differente.
    void commitWrite(request, 'push');
  }, [commitWrite, runRecordAction]);

  /**
   * Annuler, c'est REECRIRE la valeur d'avant par le meme chemin : meme
   * optimisme, meme rollback, meme indicateur. Jamais un mecanisme parallele qui
   * remettrait une valeur sans passer par le serveur.
   */
  const undo = useCallback((): void => {
    const step = undoRef.current[undoRef.current.length - 1];
    if (step == null) return;
    const remaining = undoRef.current.slice(0, -1);
    undoRef.current = remaining;
    setUndoStack(remaining);
    const grown = [...redoRef.current, step];
    redoRef.current = grown;
    setRedoStack(grown);
    // Le pas vient d'une AUTRE fiche : on y ramene dans le MEME geste, pour
    // qu'on voie ce qu'on defait. Exiger une seconde frappe se lirait comme
    // "Cmd+Z n'a rien fait".
    /* LE PAS VIENT D'UNE AUTRE FICHE : on y ramene dans le MEME geste. `step.id`
       suffit dans les trois cas (fiche, modele, CHEMIN d'enfant) : c'est
       exactement ce qu'`openRecord` sait ouvrir. */
    if (openIdRef.current !== step.id) openRecord(step.id);
    void commitWrite({ kind: step.kind, id: step.id, key: step.key, value: step.before }, 'skip');
  }, [commitWrite, openRecord]);

  const redo = useCallback((): void => {
    const step = redoRef.current[redoRef.current.length - 1];
    if (step == null) return;
    const remaining = redoRef.current.slice(0, -1);
    redoRef.current = remaining;
    setRedoStack(remaining);
    const grown = [...undoRef.current, step];
    undoRef.current = grown;
    setUndoStack(grown);
    if (openIdRef.current !== step.id) openRecord(step.id);
    void commitWrite({ kind: step.kind, id: step.id, key: step.key, value: step.value }, 'skip');
  }, [commitWrite, openRecord]);

  const setViewState = useCallback((state: unknown): void => {
    setViewStates((current) => ({ ...current, [viewIdRef.current]: state }));
  }, []);

  /* ---- Le clavier de la fiche ---- */

  /*
   * LE CLAVIER EST ECOUTE ICI, JAMAIS PAR UNE VUE. Fermer, naviguer, annuler
   * sont des gestes de NAVIGATION et d'ETAT, et une vue ne navigue jamais
   * (c'est la regle absolue du contrat). L'ecoute n'est posee qu'au niveau
   * `record`, le seul ou ces trois gestes ont un sens.
   */
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;

  useEffect(() => {
    if (level !== 'record') return;

    const onKeyDown = (event: KeyboardEvent): void => {
      /*
       * PREMIERE GARDE, celle du contrat : le focus est dans un editeur, on ne
       * fait RIEN. C'est elle qui rend possibles les deux gestes d'`Echap`
       * (dans un champ il annule la saisie, hors champ il ferme la fiche), et
       * elle qui laisse l'annulation NATIVE du navigateur gagner sur `Cmd+Z`
       * pendant qu'on tape.
       */
      const active = document.activeElement;
      const tag = active?.tagName.toLowerCase();
      if (tag === 'input' || tag === 'select' || tag === 'textarea') return;
      if (active instanceof HTMLElement && active.isContentEditable) return;

      /*
       * SECONDE GARDE : un OVERLAY est ouvert par-dessus la fiche (le `Modal`
       * de creation, le lanceur rapide ou Ask AI du shell). Le `Modal` ecoute
       * deja `Escape` sur `document` sans stopper la propagation
       * (components/Modal.tsx) : sans cette garde, une seule frappe fermerait
       * l'overlay ET la fiche derriere lui. Tous portent `role="dialog"`, c'est
       * le seul signal commun disponible depuis le socle.
       */
      if (document.querySelector('[role="dialog"]') != null) return;

      /*
       * TROISIEME GARDE : la touche est deja traitee (`preventDefault`), ou elle
       * vient d'une surface du shell posee AU-DESSUS de l'ecran (la fenetre de
       * communication, `data-taki-hors-ecran`). Un Echap qui ferme un menu, une
       * liste ou la fenetre elle-meme ne fait jamais reculer la fiche derriere.
       */
      if (event.defaultPrevented) return;
      if (event.target instanceof Element && event.target.closest('[data-taki-hors-ecran]') != null) return;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        /* SUR UN BROUILLON, `Echap` ANNULE LA CREATION : c'est le meme geste que
           la puce de sortie et que « Annuler », donc la meme question posee
           (intact, on sort ; touche, la fenetre s'ouvre). La garde du dialogue
           est deja passee plus haut : tant que la fenetre est ouverte, c'est
           ELLE qu'Echap ferme, jamais la fiche derriere. */
        if (brouillonRef.current != null) {
          annulerBrouillon();
          return;
        }
        openRecord(null);
        return;
      }

      // Le curseur au clavier : exactement le geste des fleches de la fiche, on
      // relit les ids que le core a deja calcules.
      if (event.altKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        const target =
          event.key === 'ArrowUp' ? cursorRef.current.prevId : cursorRef.current.nextId;
        if (target == null) return;
        event.preventDefault();
        openRecord(target);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [level, openRecord, undo, redo, annulerBrouillon]);

  const loadMore = useCallback((): void => {
    if (paginationRef.current === 'server') {
      // UN CHARGEMENT A LA FOIS. Sans cette garde, cliquer "Charger la suite"
      // pendant qu'un chargement est en vol (changement de tri, reprise apres
      // erreur, bascule d'archives) posait un curseur pris sur le jeu de
      // l'ANCIENNE requete : la page 1 de la nouvelle etait annulee, et la
      // liste finissait par montrer les pages d'un tri sous celles d'un autre.
      // Le bandeau reste affiche pendant ce temps, il n'y a rien a masquer :
      // le clic est simplement sans effet, comme un second clic sur un bouton
      // deja parti.
      if (inFlightRef.current) return;
      // Le curseur est le dernier element du JEU BRUT, dans l'ordre rendu par
      // le serveur, jamais la derniere LIGNE affichee : celle-la a traverse la
      // recherche, les pastilles et le tri client, elle n'est pas a la meme
      // place dans l'ordre du serveur, et repartir de la ferait sauter des
      // pages entieres sans que personne ne le voie.
      const last = recordsRef.current[recordsRef.current.length - 1];
      if (last == null) return;
      const cursor = getIdRef.current(last);
      setQueryState((previous) =>
        previous.after === cursor ? previous : { ...previous, after: cursor },
      );
      return;
    }
    // Mode client : agrandir la fenetre en memoire. Le chargeur est rappele
    // avec la nouvelle limite, au cas ou il sache en tirer parti, et c'est
    // justement pourquoi elle est PLAFONNEE : un ecran qui relaie `query.limit`
    // vers `sdk.<famille>.list({ limit })` verrait le SDK lever au-dela de son
    // propre plafond (1000), et l'utilisateur passerait d'une liste qui marche
    // a un ecran d'erreur au clic suivant.
    setQueryState((previous) => ({
      ...previous,
      limit: Math.min(previous.limit + limitDefault, MAX_CLIENT_WINDOW),
    }));
  }, [limitDefault]);

  const setRange = useCallback((from: Date, to: Date): void => {
    // Par `nextQuery` et jamais en ecrivant directement : en mode serveur,
    // changer de mois doit REMPLACER les pages, pas empiler le nouveau mois
    // sous l'ancien. Le calendrier appelle ceci depuis un effet de montage,
    // donc ce chemin est emprunte des la premiere bascule de vue.
    setQueryState((previous) =>
      nextQuery(
        previous,
        { range: { from, to } },
        paginationRef.current === 'server',
        modelesAutorisesRef.current,
      ),
    );
  }, []);

  /**
   * Le catalogue d'une cle : les ROLES d'abord, les COLONNES ensuite. La regle
   * vit dans `optionsOfBinding` (views/project.ts) depuis que la CARTE LIEE la
   * pose elle aussi, sur le binding de la porte LIEE : deux copies de l'ordre
   * de priorite auraient diverge, et rien a l'ecran ne l'aurait dit.
   */
  /* UN CATALOGUE PAR CLE ET PAR PROJECTION, calcule une fois : une vue le
     demande pour CHAQUE ligne dessinee (`StatusEditor`), et il parcourt tout le
     jeu (les valeurs hors catalogue). Sur 5 000 fiches, le recalculer ligne par
     ligne coutait une seconde par passe de defilement (chantier `ecran-fluide`). */
  const optionsOf = useMemo(() => {
    const deja = new Map<string, Option[]>();
    return (role: string): Option[] => {
      let options = deja.get(role);
      if (options === undefined) {
        options = optionsOfBinding(projected.binding, projected.rows, role);
        deja.set(role, options);
      }
      return options;
    };
  }, [projected]);

  /* ---- Les champs modifiables en LOT (chantier 3_0) ---- */

  /**
   * Le catalogue des champs modifiables de la porte, indépendamment des
   * colonnes visibles dans la vue active.
   *
   * IL NE FABRIQUE RIEN. Un champ n'entre ici que si l'ecran a declare son
   * `set()` : ce qui ne s'ecrit pas une fiche a la fois ne s'ecrit pas non
   * plus douze d'un coup, et l'inverse ferait promettre au lot un pouvoir que
   * la fiche n'a pas.
   *
   * LES ROLES D'ABORD, LES COLONNES ENSUITE : c'est l'ordre de lecture du
   * tableau (le titre, le statut, puis les colonnes declarees), donc la liste
   * deroulante range les champs comme l'oeil les a rencontres.
   */
  const bulkEditFields = useMemo<BulkEditField[]>(() => {
    const fields: BulkEditField[] = [];
    const sack = projected.binding?.options ?? {};
    const columns = projected.binding?.fieldColumns ?? projected.binding?.columns ?? [];
    // Les deux libelles que le TABLEAU accepte de renommer par son sac libre
    // (`titleLabel`, `statusLabel`) : la fenetre de lot doit nommer le champ
    // exactement comme la colonne qu'on voit, sinon on croit modifier autre
    // chose. Le `label` du role sert de second recours, le vocabulaire du core
    // de dernier.
    const sackLabel = (key: string): string | undefined => {
      const value = sack[key];
      return typeof value === 'string' && value !== '' ? value : undefined;
    };
    const roleLabelOf = (name: string, field: FieldMap<T>): string => {
      const fromSack =
        name === 'title' ? sackLabel('titleLabel') : name === 'status' ? sackLabel('statusLabel') : undefined;
      return fromSack ?? field.label ?? BULK_ROLE_LABEL[name] ?? name;
    };

    const push = (kind: WriteKind, key: string, label: string, editor: EditorKind, options?: Option[], reglages?: ReglagesDeColonne): void => {
      // La regle vit dans `editeurModifiableEnLot`, et elle est posee ICI, sur
      // le seul chemin par lequel un champ entre au catalogue : roles et
      // colonnes d'un coup, plutot qu'un test recopie dans chaque boucle.
      if (!editeurModifiableEnLot(editor)) return;
      fields.push({
        key: fieldKeyOf(kind, key),
        label,
        editor,
        // Le type s'annonce DANS l'option, jamais dans une colonne a part.
        editorLabel: `${label}, ${libelleDEditeur(editor)}`,
        ...(['select', 'multiselect', 'lien', 'liens'].includes(editor) ? { options: options ?? [] } : {}),
        ...(reglages === undefined ? {} : { reglages }),
      });
    };

    for (const [name, field] of Object.entries(projected.roles)) {
      if (field == null || typeof field.set !== 'function') continue;
      if (field.path != null && columns.some(column => column.path === field.path && column.set != null)) continue;
      // Multi-values : voir `BULK_EXCLUDED_ROLES`. Une seule valeur ecrite sur
      // un role qui en porte plusieurs efface les autres, en silence.
      if (BULK_EXCLUDED_ROLES.has(name)) continue;
      const label = roleLabelOf(name, field);
      // Un role a CATALOGUE est une liste de choix, quoi qu'il arrive : c'est
      // la presence d'`options()` qui le dit, exactement comme pour la fiche.
      if (typeof field.options === 'function') {
        push('role', name, label, 'select', optionsOf(name));
        continue;
      }
      if (name === 'dateStart' || name === 'dateEnd') {
        push('role', name, label, 'date');
        continue;
      }
      // Aucune valeur declaree ne dit le type d'un role de texte : on prend la
      // premiere valeur RENCONTREE dans les lignes affichees, sinon `text`.
      // Meme deduction que la fiche, parce que c'est LA MEME FONCTION.
      let sample: Cell = null;
      for (const row of projected.rows) {
        const raw = row.extra[name];
        if (raw !== undefined && raw !== null && raw !== '') {
          sample = raw;
          break;
        }
      }
      push('role', name, label, editorKindOf(sample, undefined));
    }

    for (const column of columns) {
      if (typeof column.set !== 'function') continue;
      const sample = records[0] == null ? null : column.value(records[0]);
      const editor = editorKindOf(sample, column.kind);
      const options = (editor === 'lien' || editor === 'liens') && column.reference != null
        ? [...(annuaires.get(cleDeJeu(column.reference.family, column.reference.type))?.fiches.entries() ?? [])]
          .filter(([, fiche]) => !fiche.archive)
          /* LA CONDITION DES CHOIX DU CHAMP (`ColumnReference.choixPermis`) :
             pas de valeur en place dans un lot, donc aucune exception. */
          .filter(([, fiche]) => fiche.document === undefined || column.reference?.choixPermis?.(fiche.document) !== false)
          .map(([id, fiche]) => ({ value: id, label: fiche.title, tone: 'neutral' as const }))
        : column.options?.();
      push('column', column.key, column.label, editor, options, column.reglages);
    }

    return fields;
  }, [projected, optionsOf, records, annuaires]);

  /* ---- Selecteur de vues ---- */

  const views = useMemo<ViewOption[]>(() => {
    const collectionEmpty = projected.orderedIds.length === 0;
    return activeTypes.map((type) => {
      const binding = bindingsRef.current.find((candidate) => candidate.type === type.id);
      let reason = unmetRequirement(type, binding);
      if (reason == null && type.level === 'record' && collectionEmpty && openId == null) {
        reason = 'Aucun élément';
      }
      return {
        id: type.id,
        label: type.label,
        icon: type.icon,
        level: type.level,
        disabled: reason != null,
        ...(reason != null ? { reason } : {}),
      };
    });
  }, [activeTypes, projected.orderedIds, openId]);

  /* ---- L'URL : l'etat d'ecran devient une adresse, et l'inverse ---- */

  /**
   * L'etat d'ecran, vu comme une adresse. Que des IDENTIFIANTS.
   *
   * LE TEXTE DE RECHERCHE N'Y EST PAS, et c'est volontaire (decision du
   * fondateur, 30/07/2026) : on tape un nom ou un email dans cette barre, et
   * une adresse se copie dans un mail, un ticket, l'historique d'un navigateur
   * et les journaux d'un serveur. Le chantier l'exigeait en toutes lettres :
   * des ids, jamais un nom. Cout assume : un lien partage ne rejoue pas la
   * recherche de celui qui l'a envoye.
   */
  const toSearch = useCallback((): Record<string, string> => {
    const params: Record<string, string> = { view: viewIdRef.current };
    // L'`id` ne sort QUE si la vue active est de niveau fiche. `openId` reste
    // volontairement pose quand on quitte la fiche par le SELECTEUR de vues
    // (voir `setView`) : l'ecrire quand meme mettait dans l'adresse un couple
    // que la relecture tranche a l'envers ("l'id fait foi sur le niveau").
    // L'utilisateur regardait un tableau, F5 le posait sur une fiche, et le
    // lien qu'il copiait ouvrait chez son collegue autre chose que ce qu'il
    // avait sous les yeux.
    const active = typesRef.current.find((type) => type.id === viewIdRef.current);
    const porteActive = doorsRef.current.find((door) => door.id === doorIdRef.current);
    if (idDansLAdresse(active?.level, porteActive?.canvas != null, openIdRef.current)) {
      params['id'] = openIdRef.current as string;
    }
    // `apercu` : la vue HTML en brouillon qu'on regarde sur la fiche ouverte
    // (chantier `fiche-suivi`). Il ne vit qu'au niveau fiche.
    if (active?.level === 'record' && apercuRef.current != null) params['apercu'] = apercuRef.current;
    const current = queryRef.current;
    // `s` : l'id de porte, ou la section libre d'un ecran d'avant les portes.
    // Meme parametre, meme sens.
    if (current.section != null) params['s'] = current.section;
    // `f` : les filtres actifs, en liste separee par des virgules (contrat
    // etendu du gabarit v2, voir screenUrl.tsx). Un ecran V1 n'a jamais plus
    // d'un id actif : son adresse est OCTET POUR OCTET celle d'avant.
    const filterParam = joinFilterIds(current.filterIds);
    if (filterParam != null) params['f'] = filterParam;
    // `arch` : le MODE ARCHIVES, et lui seul des etats de tranche, parce qu'il
    // change QUELS enregistrements existent a l'ecran (le tri, qui ne change
    // que leur ordre, reste hors de l'adresse). Ecrit uniquement quand il est
    // allume : une adresse ordinaire ne change pas d'un octet.
    const archParam = writeArchivesParam(current.archivesMode);
    if (archParam != null) params['arch'] = archParam;
    // `mod` : le MODE MODELES, meme regle exactement. Les deux etant exclusifs,
    // une adresse produite ici ne porte JAMAIS les deux clefs a la fois.
    const modParam = writeModelesParam(current.modelesMode);
    if (modParam != null) params['mod'] = modParam;
    // `dans` : la porte ouverte dans une fiche parente. Absente hors du chemin.
    const dansParam = ecrireDansParam(current.dans);
    if (dansParam != null) params['dans'] = dansParam;
    return params;
  }, []);
  /**
   * L'ADRESSE COURANTE, lisible depuis un callback declare PLUS HAUT. Un seul
   * appelant : `openLinked`, qui note l'adresse de la fiche parente hors shell
   * (sans routeur, `urlPort.params` n'existe pas). Le mettre en dependance
   * la-bas leverait une TDZ : la liste de dependances s'evalue AU RENDU, et
   * `toSearch` n'est pas encore declare a cet endroit du fichier.
   */
  const toSearchRef = useRef(toSearch);
  toSearchRef.current = toSearch;

  const fromSearch = useCallback(
    (params: Record<string, string | undefined>): void => {
      const id = params['id'] != null && params['id'] !== '' ? params['id'] : null;
      const view = asViewId(params['view']);
      const apercuDemande = params['apercu'] != null && params['apercu'] !== '' ? params['apercu'] : null;
      if (apercuDemande !== apercuRef.current) {
        apercuRef.current = apercuDemande;
        setApercu(apercuDemande);
      }
      const section = params['s'];
      const filter = params['f'];
      /*
       * LE MODE ARCHIVES, comme les filtres : l'adresse fait foi, y compris
       * par son ABSENCE. Un `arch` disparu veut dire "mode eteint" (le Retour
       * du navigateur depuis un etat archives vers un etat nu doit bien
       * eteindre le mode ; nos adresses ecrivent toujours `arch` quand il est
       * allume, son absence est donc une information, pas un oubli). Une
       * valeur inconnue vaut eteint elle aussi, meme regle que `?s=` inconnu.
       *
       * Il n'est lu QUE par la branche a portes, plus bas : sans portes, aucune
       * surface ne peut montrer ni eteindre le mode (le detail y est ecrit).
       */
      const modes = readModesParams(params['arch'], params['mod']);
      const archives = modes.archivesMode;
      /*
       * ET LE MODE MODELES AVEC LUI, LU AU MEME ENDROIT : `readModesParams`
       * tranche l'exclusivite (`arch` gagne) une fois pour toutes, ici comme a
       * l'initialisation. Les deux modes sont ensuite poses dans le MEME patch,
       * plus bas : les separer ferait passer l'ecran par un etat ou les deux
       * sont allumes, et reprojetterait la liste deux fois.
       */
      const modeles = modes.modelesMode;

      if (doorsRef.current.length > 0) {
        /*
         * ECRAN A PORTES. `s` designe une porte, `f` la liste des filtres, et
         * l'adresse fait foi sur les DEUX, y compris leur absence : un `f`
         * disparu veut dire "aucun filtre" (le Retour du navigateur depuis un
         * etat filtre vers un etat nu doit bien retirer les tags ; nos
         * adresses ecrivent toujours `f` quand des filtres sont actifs, son
         * absence est donc une information, pas un oubli). Un `s` inconnu ou
         * absent laisse la porte courante : une adresse retouchee a la main ne
         * demonte pas l'ecran.
         */
        const doors = doorsRef.current;
        const target =
          section != null && doors.some((door) => door.id === section)
            ? section
            : queryRef.current.section;
        if (target != null && target !== queryRef.current.section) {
          // Changement de porte par l'adresse : MEME remise a zero qu'un clic
          // de menu, avec les filtres de l'adresse par-dessus, verifies contre
          // le catalogue de la porte CIBLE (les refs portent encore l'ancienne
          // porte a cet instant : on lit le catalogue a la source).
          const door = doors.find((candidate) => candidate.id === target);
          const defs = !doorHasSections(door)
            ? []
            : (door?.filters ?? optionsRef.current.filters ?? []);
          const asked = splitFilterIds(filter).filter((filterId) =>
            defs.some((def) => def.id === filterId),
          );
          /* L'`id` DE L'ADRESSE PART AVEC : quand c'est un CHEMIN D'ENFANT, il
             autorise `mod=1` sur une porte liee qui n'allume pas les modeles
             (voir `modelesAllumables`). Sans lui, le saut vers un enfant perdait
             son mode a l'arrivee, et l'adresse cessait de dire ce qu'elle ouvre. */
          openDoor(target, asked, archives, modeles, id, {
            dans: resoudreDans(doors, lireDansParam(params['dans']), { section: target, openId: id })?.dans ?? null,
          });
        } else {
          // Meme porte : seuls les filtres et le mode bougent. LES DEUX DANS
          // LE MEME PATCH, jamais deux appels : deux passages dans
          // `mutateTags` recalculeraient la liste deux fois et reecriraient
          // l'adresse deux fois, exactement ce que `clearAll` evite deja.
          const asked = splitFilterIds(filter).filter((filterId) =>
            filterDefsRef.current.some((def) => def.id === filterId),
          );
          mutateTags((previous) =>
            sameIdList(previous.filterIds, asked) &&
            previous.archivesMode === archives &&
            previous.modelesMode === modeles
              ? null
              : { filterIds: asked, archivesMode: archives, modelesMode: modeles },
          );
          /* LE CHEMIN, sur la meme porte : Retour et Suivant du navigateur
             entre deux projets, ou hors du chemin. */
          const dans = resoudreDans(doors, lireDansParam(params['dans']), {
            section: queryRef.current.section,
            openId: id,
          })?.dans ?? null;
          if (!memeDans(queryRef.current.dans, dans)) {
            setQueryState((previous) =>
              nextQuery(previous, { dans }, paginationRef.current === 'server', modelesAutorisesRef.current),
            );
          }
        }
      } else {
        // ECRAN SANS PORTES : comportement historique, au cheveu pres.
        //
        // La section passe par `applySection`, PAS par `setSection` : ce
        // dernier referme la fiche, or une adresse peut tres bien porter une
        // section ET un element (un lien vers la fiche d'un materiel de la
        // section Camera). L'appeler ici cassait ce lien sans un mot :
        // `openRecord` lit des refs que le rendu n'a pas encore rafraichies,
        // donc la fermeture demandee juste avant lui faisait croire qu'on
        // etait deja au bon niveau, et la fiche ne s'ouvrait pas. Ici
        // l'adresse fait foi : elle dit deja si un element est ouvert,
        // personne n'a a le deduire.
        if (section != null) applySection(section);
        // Une pastille venue de l'adresse est verifiee, comme a
        // l'initialisation : un `f` inconnu (faute de frappe, pastille masquee
        // depuis dans les reglages) laisserait la rangee 3 sans aucune pastille
        // allumee et sans filtrage, un etat que rien a l'ecran n'explique. On
        // tolere la forme liste (une adresse v2 collee sur un ecran V1) en
        // prenant le premier id connu : la pastille reste a choix unique.
        const askedFilter = splitFilterIds(filter).find((filterId) =>
          filterDefsRef.current.some((def) => def.id === filterId),
        );
        if (askedFilter != null) setFilter(askedFilter);
        /*
         * AUCUN DES DEUX MODES N'EST RELU ICI, et c'est le meme verrou qu'a
         * l'initialisation (voir `initialArchives` et `initialModeles`) : un
         * ecran sans portes ne
         * passe pas `tools` au gabarit, donc il n'a ni outil filtre, ni tag,
         * ni tiroir, ni "tout effacer". Un mode s'y allumerait sans qu'aucune
         * surface ne le montre ni ne puisse l'eteindre, et la liste resterait
         * vide (ou remplie de modeles) pour toujours : une nasse, ouverte par
         * un simple lien.
         *
         * Le jour ou l'un de ces ecrans passera `tools={screen}`, il aura des
         * portes (c'est le meme passage au gabarit v2) et ce verrou tombera
         * tout seul.
         */
      }

      /*
       * `id=~nouveau` VENU DE L'ADRESSE : on ROUVRE UN BROUILLON, VIDE. Un lien
       * copie promet une CREATION, pas une saisie : l'adresse ne porte que
       * l'id, jamais les valeurs (on y tape des noms, et une adresse se copie).
       *
       * DEJA OUVERT : rien a faire, c'est nous qui avons ecrit cette adresse.
       * REFUSE (porte sans jeu, sans action socle, lecture seule) : on retombe
       * en collection, et le sens 1 reecrit l'adresse SANS `id` au rendu
       * suivant. Une adresse qui promet une creation impossible ne doit pas
       * rester dans la barre.
       */
      if (id === BROUILLON_ID) {
        if (brouillonRef.current != null) return;
        if (!ouvrirBrouillon() && openIdRef.current != null) openRecord(null);
        return;
      }
      /*
       * UN BROUILLON NE SURVIT PAS A UNE ADRESSE QUI PARLE D'AUTRE CHOSE
       * (Retour du navigateur, lien colle, bouton Suivant) : il se ferme SANS
       * fenetre. C'est la limite assumee du chantier, et elle est ecrite dans
       * `docs/VUES-CONTRAT.md` : le navigateur a deja navigue quand on
       * l'apprend, et une fenetre qui demanderait de confirmer un geste DEJA
       * fait ne pourrait plus le defaire.
       */
      if (brouillonRef.current != null) poserBrouillon(null);
      if (id == null) {
        // Adresse sans element : la fiche se ferme. C'est ce chemin qui donne
        // son sens au bouton Retour du navigateur, et il n'existait pas dans
        // la version "prete pour l'URL" de ce hook.
        if (openIdRef.current != null) openRecord(null);
        if (view != null && view !== viewIdRef.current) setView(view);
        return;
      }
      // L'`id` fait foi sur le niveau : `view=core.fiche` n'est qu'un
      // raccourci, et une vue de collection en presence d'un `id` est ignoree
      // (l'ecriture reecrira l'adresse avec la vue reellement active).
      if (id !== openIdRef.current) openRecord(id);
      const asked = typesRef.current.find((type) => type.id === view);
      if (asked?.level === 'record' && asked.id !== viewIdRef.current) setView(asked.id);
    },
    [applySection, mutateTags, openDoor, openRecord, ouvrirBrouillon, poserBrouillon, setFilter, setView],
  );

  /**
   * LA SYNCHRONISATION, dans les deux sens, avec UNE seule empreinte partagee.
   *
   * `urlSyncedRef` porte l'adresse que l'etat et le routeur sont censes dire
   * tous les deux. Chaque sens ne parle que si ce qu'il voit en differe : sans
   * ce garde-fou, l'ecriture declencherait la relecture, qui declencherait
   * l'ecriture, et l'ecran tournerait en rond a chaque clic.
   *
   * Elle est initialisee avec l'adresse du PREMIER rendu, dont l'etat de depart
   * est deja issu (plus haut). ATTENTION, ca ne suffit PAS a proteger le
   * montage : les deux effets tournent dans le meme flush, le sens 1 peut avoir
   * bouge l'empreinte avant que le sens 2 ne la compare, et le sens 2 lit alors
   * une adresse que le routeur n'a pas encore eu le temps de mettre a jour.
   * C'est `urlFirstReadRef` qui tient ce bord, pas cette empreinte.
   */
  const urlSyncedRef = useRef<string>(screenSearchKey(initialSearchRef.current));
  const urlLastRef = useRef<ScreenSearch>(initialSearchRef.current);
  /**
   * L'ADRESSE DEPUIS LAQUELLE ON A EMPILE LA FICHE, ou `null` si l'etape de
   * fiche courante ne nous appartient pas (entree a froid par un lien partage,
   * F5 dans une fiche : il n'y a rien a depiler derriere, l'etape d'avant peut
   * meme appartenir a un autre site).
   *
   * Elle sert a trancher la sortie de fiche : DEPILER (le Retour du navigateur
   * et la puce de sortie de la rangee 3 font alors la meme chose, sans laisser
   * une etape morte qui rouvrirait la fiche), ou REMPLACER. On ne peut pas se
   * contenter de "l'`id` a disparu" : changer de section depuis une fiche
   * referme la fiche ET change la section, et depiler ramenerait l'ANCIENNE
   * section, donc annulerait le clic qu'on vient de faire. On ne depile donc
   * que si ce qu'on s'apprete a ecrire est EXACTEMENT l'adresse d'ou l'on vient.
   */
  const urlRecordOriginRef = useRef<ScreenSearch | null>(null);
  /**
   * L'ADRESSE D'OU L'ON EST ENTRE DANS UNE FICHE (`dans`) : la collection de la
   * porte parente. Meme regle que `urlRecordOriginRef` : en sortir vers cette
   * meme adresse DEPILE, pour que Retour ne ramene pas aux taches du projet
   * qu'on vient de quitter par « Liste : Projets ».
   */
  const urlDansOriginRef = useRef<ScreenSearch | null>(null);
  /**
   * Le tout premier passage du sens 2 ne doit RIEN appliquer : l'etat de depart
   * est deja issu de cette adresse (plus haut). Les deux effets tournent dans
   * le meme flush au montage, et le sens 1 a pu ecrire entre-temps : le sens 2
   * lirait alors l'adresse d'AVANT et defairait ce que l'etat vient de
   * resoudre. Symptome vecu : on revient dans une app, la reprise de session
   * rouvre la fiche, et le sens 2 la referme aussitot en relisant une adresse
   * vide.
   */
  const urlFirstReadRef = useRef(true);
  /**
   * Le montage n'empile jamais : il remplace l'etape courante. Une reprise (de
   * session ou de lien) n'est pas un geste de l'utilisateur, et l'etape
   * parasite se paierait en un appui de Retour qui ne fait rien.
   */
  const urlFirstWriteRef = useRef(true);

  /*
   * Sens 1 : l'etat ecrit l'adresse.
   *
   * EFFET DE MISE EN PAGE, et pas un effet ordinaire : un effet ordinaire
   * tourne APRES la peinture, donc l'adresse arrivait une image en retard. En
   * changeant d'app, la barre d'adresse affichait d'abord `/app/<id>` tout nu
   * (le routeur a navigue), puis les parametres apparaissaient : ca CLIGNOTAIT,
   * avec un etat intermediaire bien visible. Ici l'ecriture se fait dans le
   * meme commit que le rendu qui l'a provoquee, avant que l'ecran ne soit
   * peint : l'adresse et l'image changent ensemble.
   *
   * Le sens 2, lui, reste un effet ordinaire : relire l'adresse n'a aucune
   * raison de bloquer la peinture, et l'ordre entre les deux ne change pas (le
   * garde-fou d'empreinte est le meme des deux cotes).
   */
  useLayoutEffect(() => {
    if (urlPort == null) return;
    // Le drapeau se consomme AU PREMIER PASSAGE, pas a la premiere ecriture.
    // Sinon une arrivee par lien (ou l'adresse dit deja tout, donc rien a
    // ecrire) laisserait le drapeau leve, et la premiere fiche ouverte ensuite
    // serait prise pour le montage : elle n'empilerait pas son etape, et le
    // Retour du navigateur quitterait la page au lieu de refermer la fiche.
    const first = urlFirstWriteRef.current;
    urlFirstWriteRef.current = false;
    const next = pickScreenSearch(toSearch());
    const key = screenSearchKey(next);
    if (key === urlSyncedRef.current) return;
    const before = urlLastRef.current;
    // Empiler une etape est reserve a l'OUVERTURE d'une fiche, et jamais au
    // montage : au montage il n'y a pas eu de geste, seulement une reprise (de
    // session ou de lien), et l'etape parasite se paierait en un appui de
    // Retour qui ne fait rien.
    const entering = !first && before['id'] == null && next['id'] != null;
    const leaving = before['id'] != null && next['id'] == null;
    const origin = urlRecordOriginRef.current;
    /* ENTRER DANS UNE FICHE (le clic sur un projet) EMPILE, comme ouvrir une
       fiche : Retour ramene aux projets. En SORTIR depuis la collection
       filtree vers l'adresse d'ou l'on venait DEPILE. */
    const entreDans = !first && before['dans'] == null && next['dans'] != null && next['id'] == null;
    const sortDans = before['dans'] != null && next['dans'] == null && before['id'] == null;
    const dansOrigin = urlDansOriginRef.current;
    urlSyncedRef.current = key;
    urlLastRef.current = next;
    if (sortDans) {
      urlDansOriginRef.current = null;
      if (dansOrigin != null && screenSearchKey(dansOrigin) === key) {
        urlPort.back();
        return;
      }
    }
    if (entreDans) {
      urlDansOriginRef.current = before;
      urlPort.setParams(next, { push: true });
      return;
    }
    if (leaving) {
      urlRecordOriginRef.current = null;
      if (origin != null && screenSearchKey(origin) === key) {
        urlPort.back();
        return;
      }
    }
    if (entering) urlRecordOriginRef.current = before;
    /* LA FICHE VIDE D'UN CHAMP DE LIEN PREND LA PLACE DE L'ETAPE D'ARRIVEE (voir
       `etapeARemplacerRef`). Un REMPLACEMENT garde l'origine de l'etape, et son
       rang : la sortie depile, comme la puce d'une fiche arrivee par un lien. */
    const remplacer = entering && etapeARemplacerRef.current && next['id'] === BROUILLON_ID;
    if (entering) etapeARemplacerRef.current = false;
    /* L'ORIGINE D'UNE FICHE VIDE, RANGEE DANS L'ETAPE QUE CE SENS ECRIT. Deux
       provenances, et rien d'autre :
        - L'INTENTION de `creerDepuis` (une surface qui n'est pas une vue, une
          porte Canvas) : l'etape qu'on pousse ICI est celle du saut, `depuis`
          dit au port qu'elle vient de la fiche courante, et la sortie la
          depile en un seul Retour. Elle se consomme a cet unique passage.
        - L'ORIGINE DE L'ETAPE DE LA PORTE, quand le « + Ajouter » d'une carte
          l'y a rangee (`openLinked`, sans fiche visee) : le brouillon est une
          nouvelle etape, il en recopie l'origine telle quelle. */
    let depuis: OrigineDeFiche | undefined;
    let origineRecopiee: OrigineDEtape | undefined;
    if (entering && next['id'] === BROUILLON_ID) {
      const intention = departDuBrouillonRef.current;
      departDuBrouillonRef.current = null;
      if (!remplacer) {
        if (intention != null) depuis = intention;
        else if (
          urlPort.origine != null &&
          urlPort.origine.id === BROUILLON_ID &&
          urlPort.origine.porte === next['s']
        ) {
          origineRecopiee = urlPort.origine;
        }
      }
    }
    /* LA CREATION GARDE SA PROVENANCE : l'adresse passe de `~nouveau` a l'id
       reel (la fiche creee, ou l'enfant `<modele>/<enfant>`) en REMPLACANT
       l'etape, qui garderait l'origine mais pour l'ancien id, donc muette. On
       lui donne l'id neuf. */
    const creation =
      before['id'] === BROUILLON_ID &&
      next['id'] != null &&
      next['id'] !== BROUILLON_ID &&
      urlPort.origine?.id === BROUILLON_ID
        ? urlPort.origine
        : null;
    urlPort.setParams(next, {
      push: entering && !remplacer,
      ...(depuis === undefined ? {} : { depuis }),
      ...(origineRecopiee === undefined ? {} : { origine: origineRecopiee }),
      ...(creation === null ? {} : { origine: { ...creation, id: next['id'] as string } }),
    });
    // `query.filterIds` et non le miroir `filterId` : le miroir s'annule des
    // qu'un second filtre se pose, l'adresse doit suivre CHAQUE changement de
    // la liste (son identite ne change que quand son contenu change).
    // `query.archivesMode` y est au meme titre : sans lui, la barre d'adresse
    // garderait `arch=1` apres la sortie du mode, et le lien qu'on copie
    // ouvrirait une autre liste que celle qu'on a sous les yeux.
    // `query.modelesMode` y est pour la meme raison que `query.archivesMode` :
    // sans lui, la barre d'adresse garderait `mod=1` apres la sortie du mode.
  }, [
    urlPort,
    toSearch,
    viewId,
    openId,
    query.section,
    query.filterIds,
    query.archivesMode,
    query.modelesMode,
    query.dans,
    apercu,
  ]);

  // Sens 2 : l'adresse ecrit l'etat (retour navigateur, lien colle, F5).
  useEffect(() => {
    if (urlPort == null) return;
    const incoming = pickScreenSearch(urlPort.params);
    if (urlFirstReadRef.current) {
      urlFirstReadRef.current = false;
      // Surtout ne pas toucher a `urlSyncedRef` : le sens 1 y a peut-etre deja
      // pose l'empreinte qu'il vient d'ecrire, et c'est celle-la qui fait foi.
      return;
    }
    const key = screenSearchKey(incoming);
    if (key === urlSyncedRef.current) return;
    const hadId = urlLastRef.current['id'] != null;
    urlSyncedRef.current = key;
    urlLastRef.current = incoming;
    /* RIEN A EFFACER DE L'ORIGINE ICI : elle appartient a l'etape, et le
       navigateur rend a Retour et a Suivant celle de l'etape ou ils ramenent. */
    if (incoming['id'] == null) {
      // L'etape de fiche qu'on avait empilee vient d'etre depilee.
      urlRecordOriginRef.current = null;
    } else if (!hadId) {
      // Une fiche retrouvee par le bouton SUIVANT du navigateur : son etape
      // existe bel et bien dans l'historique, et la collection est derriere.
      // Sans ca, la sortie suivante remplacait au lieu de depiler et laissait
      // deux etapes identiques, donc un appui de Retour sans aucun effet.
      // L'origine se reconstruit telle qu'on l'ECRIRA en sortant : meme section,
      // meme pastille, sans element, et la derniere vue collection (c'est celle
      // ou `openRecord(null)` ramene).
      urlRecordOriginRef.current = pickScreenSearch({
        ...incoming,
        id: undefined,
        view: lastCollectionRef.current,
      });
    }
    fromSearch(incoming);
  }, [urlPort, fromSearch]);

  /*
   * L'ARRIVEE SUR `id=~nouveau` (F5 dans un brouillon, lien colle). Le sens 2
   * n'applique RIEN a son premier tour (l'etat de depart est deja issu de cette
   * adresse), donc personne ne rouvrirait le brouillon sans cet effet.
   *
   * IL SE CONSOMME UNE FOIS. Refuse (porte sans jeu, sans action socle, lecture
   * seule), on retombe sur la derniere vue de collection : `openId` est deja
   * nul, et le sens 1 reecrira l'adresse sans `id` au meme commit. Sans ce
   * repli, l'ecran resterait sur une vue de niveau fiche sans fiche, c'est-a-dire
   * « Fiche introuvable » sur une adresse qui ne demandait aucune fiche.
   */
  useEffect(() => {
    if (!brouillonDeDepartRef.current) return;
    brouillonDeDepartRef.current = false;
    if (!ouvrirBrouillon()) poserVueActive(lastCollectionRef.current);
  }, [ouvrirBrouillon, poserVueActive]);

  /**
   * LE GESTE DE LA PUCE DE SORTIE D'UNE FICHE ARRIVEE PAR UN LIEN. La DECISION
   * (« depiler » ou « naviguer ») est prise par `sortieDeFiche`, la fonction
   * pure ; ici on ne fait que l'APPLIQUER. C'est le meme partage que partout
   * dans ce fichier : la regle se teste hors de React, le hook la joue.
   *
   * DEPILER, c'est le Retour du navigateur : l'etape de la fiche liee dispa-
   * rait, et il ne reste pas derriere nous une etape morte qui la rouvrirait au
   * premier appui sur Retour. NAVIGUER remplace l'etape courante par l'adresse
   * du parent : le sens 2 y ouvre la porte et la fiche, exactement comme sur un
   * lien colle.
   *
   * SANS ROUTEUR, le meme trajet en memoire : `fromSearch` enchaine `openDoor`
   * puis `openRecord`, c'est le chemin du lien colle, emprunte tel quel.
   *
   * LE PORT SAIT REVENIR (`revenir`) : il recule, ou remplace l'etape par
   * l'adresse de l'origine DANS SON APP (un saut venu d'une autre app se rejoue
   * la-bas, jamais ici), et rend a cette etape sa propre origine (`parent`). A
   * defaut, l'ecran depile, ou rejoue l'adresse lui-meme quand l'origine est de
   * son app.
   *
   * AVEC UN PORT, L'ORIGINE NE MEURT PAS AVEC LE GESTE : elle appartient a
   * l'etape qu'on quitte, qui disparait ou est remplacee. Sans historique, la
   * memoire de l'ecran est effacee, pour ne pas revenir deux fois au meme parent.
   */
  const retournerALOrigine = useCallback(
    (geste: 'depiler' | 'naviguer'): void => {
      const origine = origineEffectiveRef.current;
      if (origine == null) return;
      if (urlPort == null) {
        origineSansHistoriqueRef.current = null;
        fromSearch(origine.adresse);
        return;
      }
      if (urlPort.revenir != null) {
        urlPort.revenir(origine, geste);
        return;
      }
      if (geste === 'naviguer' && origine.app === appId) {
        urlPort.setParams(origine.adresse, { origine: origine.parent });
        return;
      }
      urlPort.back();
    },
    [urlPort, fromSearch, appId],
  );
  /* LA MEME SORTIE, LISIBLE PAR « ANNULER » (voir `retourALOrigineRef`). */
  retourALOrigineRef.current = retournerALOrigine;

  /* ---- Sortie ---- */

  /*
   * QUELLE LECTURE L'ECRAN ATTEND. En mode Modeles, ce n'est plus celle du
   * chargeur de l'app : c'est celle du port. Lire `inFlight` la-bas ferait
   * annoncer un chargement termine pendant que les modeles arrivent (donc un
   * « aucun modele » qui clignote), ou l'inverse, un squelette perpetuel sur un
   * chargeur d'app reste en vol pour une liste deja affichee.
   *
   * LE SECOND TERME FERME LE CLIGNOTEMENT DU PREMIER RENDU. `modelesEnLecture`
   * est leve dans un EFFET : au tout premier rendu qui suit l'allumage du mode,
   * il valait encore faux et la liste etait deja vide, donc « Aucun modele pour
   * cette porte » (ou « Fiche introuvable » sur une entree a froid
   * `?mod=1&id=...`) pouvait se peindre une image entiere. La clef LUE, elle,
   * se compare des le rendu : tant qu'elle n'est pas celle du jeu courant, la
   * liste affichee n'est pas encore celle qu'on regarde.
   */
  /*
   * ET LA FICHE D'UN ENFANT ATTEND SON PARENT, PAS UNE LISTE (pivot du
   * 10/09/2026). Ce qu'on regarde alors n'est ni la collection de la porte
   * liee, ni ses modeles : c'est UN document, charge par son id
   * (`ModelesPort.get`). Sans ces deux lignes, la fiche affichait « Fiche
   * introuvable » le temps de l'aller-retour, c'est-a-dire exactement le mot
   * qu'il ne faut pas dire pendant un chargement (`ViewHost` fait passer
   * « introuvable » avant « vide », mais APRES « chargement »).
   */
  const lectureEnCours = enfantOuvert
    ? modeleParentEnLecture
    : query.modelesMode
      ? modelesEnLecture || modelesCleLue !== modelesCle
      : inFlight;
  const jeuVide = enfantOuvert
    ? modeleParent == null
    : query.modelesMode
      ? modeleFiches.length === 0
      : records.length === 0;
  /*
   * L'ERREUR AFFICHEE EST CELLE DU MODE. `error` est l'echec du `load()` de
   * l'APP, que le mode Modeles n'utilise pas : une porte dont le chargement a
   * echoue affichait son ecran d'erreur (avec un « Reessayer ») PAR-DESSUS une
   * liste de modeles parfaitement lue. Symetriquement, l'echec du PORT etait
   * muet : une console et une liste vide, donc « aucun modele » a quelqu'un qui
   * en a douze. Chaque mode montre l'echec de la lecture QU'IL FAIT.
   */
  /* ET LA FICHE D'UN ENFANT N'AFFICHE NI L'UNE NI L'AUTRE : la lecture du
     chargeur de l'app ne la concerne pas, et celle de la LISTE des modeles
     n'a pas eu lieu (l'effet la saute). Un parent illisible se rend en « Fiche
     introuvable », comme une fiche supprimee. */
  const erreurAffichee = enfantOuvert ? undefined : query.modelesMode ? modelesErreur : error;
  const status: ViewStatus = {
    // Doctrine du gabarit : `loading` est reserve au PREMIER chargement, sinon
    // l'ecran clignote a chaque rafraichissement.
    loading: (lectureEnCours && jeuVide) || creationEnLecture(creeeEnAttente, openId, openRow),
    ...(erreurAffichee != null ? { error: erreurAffichee } : {}),
    // "Aucune donnee n'existe", jamais "aucun resultat de recherche" : la
    // recherche et les pastilles s'appliquent APRES, et leur absence de
    // resultat se rend dans la vue, pas dans l'etat vide.
    //
    // JAMAIS SUR LA FICHE D'UN ENFANT, ET LE DEFAUT A ETE VU A L'ECRAN : le
    // JEU de la porte liee (ses modeles a elle) est vide, donc `poolCount` vaut
    // zero, et le `ViewHost` fait passer l'etat VIDE avant le contenu. La fiche
    // parfaitement chargee d'un enfant s'affichait « Aucun modele pour cette
    // porte ». Un enfant n'appartient a aucun de ces jeux : ce qui manque, s'il
    // manque, se dit « Fiche introuvable » (le `ViewHost` y passe deja avant,
    // sur `rows.length === 0`).
    empty:
      !enfantOuvert && !lectureEnCours && erreurAffichee == null && projected.poolCount === 0,
    // Mode serveur : le serveur seul sait s'il reste des pages. Mode client :
    // la projection le sait, elle a tout le jeu sous la main.
    // EN MODE MODELES, JAMAIS : le port rend la liste ENTIERE des modeles du
    // jeu, il n'y a pas de page suivante. Un « Afficher plus » y serait un
    // bouton qui ne peut rien ramener.
    /* AU NIVEAU FICHE, JAMAIS : la fiche montre UNE fiche, et « seules les 500
       premieres lignes sont affichees » n'y decrit rien de ce qu'on voit
       (constate sur une fiche de la Supply, 02/11). */
    truncated: query.modelesMode || level === 'record'
      ? false
      : pagination === 'server'
        ? hasMore
        : projected.truncated,
  };

  /**
   * Le catalogue de filtres pour l'UI. Les COMPTEURS se calculent sur le jeu
   * du MODE (le travail courant, ou les archives, ou les modeles), jamais sur
   * le resultat courant (regle "un compteur ne
   * bouge pas quand on filtre") : cliquer un filtre ne fait bouger aucun
   * nombre du popover. Un filtre sans `match` n'a pas de compteur : le core ne
   * sait pas l'evaluer, et un zero serait un mensonge.
   */
  const filters = useMemo<ScreenFilterOption[]>(
    () =>
      activeFilterDefs.map((def) => {
        const match = def.match;
        if (match == null) return { id: def.id, label: def.label };
        return { id: def.id, label: def.label, count: projected.pool.filter(match).length };
      }),
    // La dependance est l'IDENTITE du catalogue : un ecran V1 qui memoise ses
    // defs ne recalcule qu'a bon escient, un ecran a portes qui les recree a
    // chaque rendu recompte a chaque rendu (quelques `filter` sur le jeu
    // vivant, negligeable, et surtout jamais faux).
    [activeFilterDefs, projected.pool],
  );

  // Niveau fiche : la vue ne recoit QUE la ligne ouverte (une, ou aucune).
  const rowsProjetees = level === 'record' ? (openRow ? [openRow] : []) : projected.windowRows;
  /*
   * L'EDITION EN PLACE S'ETEINT AVEC L'ACCES, A LA SOURCE. La fiche (`core.fiche`)
   * s'edite sans formulaire, case par case : sa seule affordance est le
   * marqueur `editable` que chaque ligne porte. En lecture seule, on le retire
   * ICI, une fois, pour toutes les vues a la fois : le kanban ne rend plus ses
   * cartes glissables, la fiche ne pose plus de curseur sur ses cases, et
   * aucune vue n'a une ligne a ajouter pour ca.
   *
   * MEMOISE SUR LA RAISON : sans ca, la copie repartirait a chaque rendu et
   * ferait recalculer tout ce qui depend de l'identite des lignes (la
   * selection visible, la fenetre de lot) pour une frappe de recherche.
   */
  const rows = useMemo(
    () => (raisonAcces == null ? rowsProjetees : lignesEnLectureSeule(rowsProjetees)),
    [raisonAcces, rowsProjetees],
  );

  /* ---- LA SELECTION : sa surface, et son INVARIANT (chantier 3_0) ---- */

  /**
   * QUELLE SURFACE PORTE LA SELECTION. Le tableau, et lui seul : agir sur un
   * lot demande de VOIR les valeurs sur lesquelles on agit. Le test se fait
   * sur le descripteur RESOLU, pas sur `viewId` seul : un id de vue qui ne
   * correspond a aucun type monte (catalogue de porte reduit) ne monte rien du
   * tout, et une selection sur une vue absente n'aurait aucune surface.
   */
  const selectionLevel: 'table' | null =
    activeType != null && activeType.id === SELECTION_VIEW_ID && activeType.level === 'collection'
      ? 'table'
      : null;

  /**
   * L'INVARIANT, LE PLUS IMPORTANT DE CE CHANTIER : une ligne qui sort de
   * l'ecran sort de la selection.
   *
   * Il est DERIVE, dans le rendu meme, et pas seulement range par un effet :
   * un effet s'execute apres la peinture, et il resterait une image ou la
   * barre annonce "12 selectionnes" sur une liste qui n'en montre plus trois.
   * Une selection invisible est une bombe : on croit agir sur trois fiches et
   * on en modifie douze.
   *
   * C'est aussi ce qui tient QUITTER LE TABLEAU sans un seul appel a la
   * sortie : hors tableau, il n'y a plus de surface, donc plus de selection.
   * On y arrive par le selecteur de vues, par l'adresse, par le bouton Retour
   * du navigateur ou en ouvrant une fiche : aucun de ces chemins ne partage un
   * geste ou l'on aurait pu poser la remise a zero.
   *
   * ET UNE LIGNE SORT DE L'ECRAN AUSSI QUAND L'ECRAN ENTIER EST REMPLACE PAR UN
   * BLOC D'ETAT. Sortir de `rows` n'est pas la seule facon de ne plus rien
   * montrer : en ERREUR, le `ViewHost` remplace la vue par son bloc d'alerte,
   * alors que le chargeur qui a echoue n'efface RIEN (`setRecords` n'est jamais
   * appele dans la branche d'echec). Les lignes restent donc en memoire, et sans
   * cette ligne-ci la barre de lot continuerait d'annoncer "3 elements
   * selectionnes" avec ses trois boutons VIVANTS au-dessus d'un ecran qui ne
   * montre plus une seule ligne : Archiver ou Supprimer ecriraient sur des
   * fiches que l'utilisateur ne peut plus ni voir ni verifier. C'est ici, dans
   * le MOTEUR, que ca se ferme : le chassis ne recoit pas les etats, c'est le
   * ViewHost qui tient leur priorite. Les autres etats du ViewHost (chargement,
   * delai depasse, vide, fiche introuvable) sont deja couverts : tous supposent
   * `rows` VIDE, donc l'invariant les traite par sa regle ordinaire.
   *
   * La liste d'origine est RENDUE TELLE QUELLE quand rien n'a disparu :
   * fabriquer un tableau neuf a chaque rendu ferait repartir tout ce qui en
   * depend, pour rien.
   */
  const visibleSelection = useMemo<string[]>(() => {
    if (selectionLevel == null) return NO_SELECTION;
    if (error != null) return NO_SELECTION;
    if (selection.length === 0) return NO_SELECTION;
    const shown = new Set(rows.map((row) => row.id));
    const kept = selection.filter((id) => shown.has(id));
    return kept.length === selection.length ? selection : kept;
  }, [selectionLevel, error, selection, rows]);

  /*
   * ET LE MEME INVARIANT, RANGE DANS L'ETAT. Sans cette remise en place, une
   * ligne masquee par une recherche puis RAMENEE par son effacement
   * reviendrait cochee : l'utilisateur l'a vue quitter la selection, elle
   * n'avait aucune raison d'y revenir. La comparaison porte sur la longueur :
   * `visibleSelection` est toujours un sous-ensemble ordonne de `selection`,
   * donc a longueur egale les deux disent la meme chose.
   */
  useEffect(() => {
    if (visibleSelection.length === selection.length) return;
    setSelection(visibleSelection);
  }, [visibleSelection, selection]);

  /**
   * CE QUE LA PORTE SAIT FAIRE, geste par geste. `null` = disponible ; une
   * chaine = la RAISON, ecrite pour celui qui survole le bouton (regle G16 :
   * on grise avec sa raison, on ne cache pas).
   *
   * Recompose a chaque rendu, comme `recordMenuActions` : les verbes vivent
   * dans des refs que la porte active reassigne, memoiser figerait le geste de
   * la porte precedente.
   *
   * L'ACCES PRIME SUR LES QUATRE (`raisonDeGeste`) : un lot est une ecriture, et
   * « ces fiches sont deja archivees » n'est pas la reponse a donner a
   * quelqu'un qui n'ecrit nulle part sur cette porte.
   */
  const bulkAvailability = {
    // EN MODE ARCHIVES, la barre ne montre pas « Archiver » : elles y sont deja,
    // et le bouton qui prend sa place est « Désarchiver » (`unarchive`). La cle
    // `archive` y reste celle de la porte, jamais lue par la barre.
    // EN MODE MODELES, les quatre se grisent avec la MEME phrase : les lignes ne
    // sont pas des fiches, et les trois verbes du lot ne savent ecrire que des
    // fiches. Le mode passe DEVANT les causes ordinaires (verbe absent, aucun
    // champ modifiable) : c'est lui qu'on quitte d'un clic, et le nommer envoie
    // au bon endroit.
    archive: raisonDeGeste(
      raisonAcces,
      query.modelesMode
        ? BULK_REASON.modelesMode
        : archiveRef.current == null
          ? RECORD_ACTION_REASON.archive
          : null,
    ),
    unarchive: raisonDeGeste(
      raisonAcces,
      query.modelesMode
        ? BULK_REASON.modelesMode
        : unarchiveRef.current == null
          ? RECORD_ACTION_REASON.unarchive
          : null,
    ),
    remove: raisonDeGeste(
      raisonAcces,
      query.modelesMode
        ? BULK_REASON.modelesMode
        : removeRef.current == null
          ? RECORD_ACTION_REASON.remove
          : null,
    ),
    edit: raisonDeGeste(
      raisonAcces,
      query.modelesMode
        ? BULK_REASON.modelesMode
        : bulkEditFields.length === 0
          ? BULK_REASON.noEditableField
          : null,
    ),
  };

  /* ---- Ce que la VUE recoit, assemble a UN seul endroit ---- */

  /**
   * CE QUE LE TITRE D'UN BROUILLON MONTRE TANT QU'IL EST VIDE, lu sur le role
   * `title` du binding actif (« Sony FX9 n°3 »). `undefined` quand la
   * declaration n'en donne pas : la fiche retombe sur son defaut, elle ne rend
   * JAMAIS l'id (`~nouveau` n'est le nom de rien).
   */
  const placeholderDuTitre = rolesOf(projected.binding)['title']?.placeholder;

  /**
   * LE MODELE DONT LA FICHE EST OUVERTE, en mode Modeles. `undefined` partout
   * ailleurs, et c'est ce qui rend la question sans reponse hors du mode.
   *
   * IL SE CALCULE ICI, UNE FOIS, POUR DEUX CONSOMMATEURS : ce que la FICHE
   * recoit (`ViewProps.modele`, ses enfants et les trois verbes qui les
   * changent) et l'entree « Retirer le modele » du menu « ... ». Deux lectures
   * separees finiraient par diverger sur ce que veut dire « ouvert ».
   */
  const modeleOuvert = query.modelesMode
    ? modeleFiches.find((candidat) => candidat.id === openId)
    : undefined;

  /**
   * LES CASES QUE LE GESTE A FERMEES (`ViewProps.verrouillees`), calculees ICI
   * pour les DEUX fiches qui en ont, parce que c'est la MEME case et le MEME
   * mot : le champ du LIEN.
   *
   *  - LE BROUILLON ouvert depuis une carte liee : le « + Ajouter » a impose
   *    l'id du parent (`brouillon.imposees`), et la case affiche son nom
   *    (`ValeurDeDepart.libelle`, pose sur la cellule par `openRow`).
   *  - LA FICHE D'UN ENFANT DE MODELE : le champ du lien n'est stocke NULLE
   *    PART (un patron ne retient aucun rattachement), et la case affiche le
   *    nom du modele parent, pose par `openRow` de la meme facon. Sans ce
   *    verrou, elle disait « Materiel : Non renseigne » et ouvrait un choix de
   *    fiche : on aurait pu y designer une camera precise, c'est-a-dire
   *    exactement ce qu'un modele refuse de retenir.
   *
   * UNE SEULE LISTE, PAS DEUX MECANISMES : la fiche ne connait qu'un mot,
   * « fermee », et c'est le moteur qui sait pourquoi.
   */
  const casesVerrouillees = ((): string[] => {
    if (brouillon != null) {
      return (projected.binding?.columns ?? [])
        .filter((colonne) =>
          brouillon.imposees.some((valeur) => valeur.chemin === (colonne.path ?? colonne.key)),
        )
        .map((colonne) => fieldKeyOf('column', colonne.key));
    }
    if (cheminOuvert == null) return [];
    const parent = modeleParent?.id === cheminOuvert.modeleId ? modeleParent : null;
    const enfant = (parent?.children ?? []).find(
      (candidat) => candidat.id === cheminOuvert.enfantId,
    );
    if (enfant === undefined) return [];
    const colonne = colonneDuChemin(projected.binding?.columns, enfant.linkKey);
    return colonne === undefined ? [] : [fieldKeyOf('column', colonne.key)];
  })();

  const viewProps: ViewProps = {
    rows,
    columns: projected.columns,
    optionsOf,
    status,
    query,
    onQueryChange: setQuery,
    cursor,
    /* MEME REGLE QU'A LA SORTIE : ouvrir une autre fiche depuis une vue
       demande d'abord ce qu'on fait du brouillon en cours. */
    onOpenRecord: ouvrirDepuisLaVue,
    onPatchRecord: patchRecord,
    onPatchColumn: patchColumn,
    saveState,
    // `onLoadMore` n'est fourni que s'il reste vraiment des lignes a montrer :
    // une vue ne doit pas proposer un bouton qui ne fait rien.
    ...(status.truncated ? { onLoadMore: loadMore } : {}),
    onRangeChange: setRange,
    viewState: viewStates[viewId],
    setViewState,
    // La cle se compose ICI : la vue ne connait ni l'app, ni l'ecran, ni la
    // porte, et une cle fabriquee a la main dans un composant divergerait du
    // moule des trois autres helpers.
    columnWidthsKey: columnWidthsPrefKey(appId, screenId, doorId, viewId),
    scrollRef,
    options: projected.binding?.options ?? {},
    /* LES ANNUAIRES CHARGES : la FICHE en a besoin pour proposer les fiches
       liees dans son editeur `lien`. Les lignes, elles, portent deja leur
       libelle (la projection l'a pose) : c'est le CHOIX, et lui seul, qui
       demande la liste entiere. */
    annuaires,
    /*
     * LA SELECTION (chantier 3_0), SEULEMENT EN TABLEAU. Les deux props sont
     * ABSENTES partout ailleurs, et c'est ce qui met la decision hors de
     * portee d'une vue : la liste et le kanban ne recoivent rien a ignorer, ils
     * ne recoivent RIEN. Une vue qui les recoit sait, par leur seule presence,
     * qu'elle a le droit de dessiner sa colonne de choix.
     */
    ...(selectionLevel === 'table'
      ? { selection: visibleSelection, onSelectionChange: setSelection }
      : {}),
    /*
     * LE BANDEAU D'ARCHIVE (chantier 2_0), au niveau fiche uniquement : une
     * collection n'a pas d'etat d'archive, et poser `archived: false` sur
     * toutes les vues ferait croire l'inverse. La sortie ne part QUE si la
     * porte declare le verbe : un bandeau qui porterait un bouton mort serait
     * pire que pas de bouton du tout.
     */
    /*
     * LE SAUT VERS UNE AUTRE PORTE (chantier « liens entre portes »), au niveau
     * FICHE uniquement : seule une carte liee l'emploie, et une carte liee ne
     * vit que sur une fiche. Une vue de collection qui la recevrait aurait de
     * quoi changer de porte sans que rien a l'ecran ne l'annonce.
     */
    ...(level === 'record' ? { onOpenLinked: openLinkedDemande, destinationDeReference } : {}),
    /* LA LECTURE SEULE DE L'ECRAN, ET SA RAISON. Servie a toutes les vues, mais
       une seule s'en sert : la carte liee, qui retire son « + Ajouter » (il
       irait CREER dans une autre porte). Les CASES, elles, sont deja fermees en
       amont (`lignesEnLectureSeule`), et une vue n'a rien a en deduire. */
    ...(raisonAcces == null ? {} : { lectureSeule: raisonAcces }),
    /* LE TABLEAU S'EDITE EN PLACE : seule la porte le dit, et seul le `true`. */
    ...(activeDoor?.modifierDansLeTableau === true ? { modifierDansLeTableau: true } : {}),
    /*
     * LA FICHE EST UN BROUILLON, ET ELLE EST LA SEULE A LE SAVOIR. Sa presence
     * suffit : les cases vides invitent a ecrire au lieu de dire « Non
     * renseigne », le titre montre son placeholder, les cartes liees se taisent.
     * ABSENTE hors creation, pour que rien ne change sur une fiche ordinaire.
     */
    ...(brouillon == null
      ? {}
      : {
          brouillon: {
            manquants: brouillon.manquants,
            ...(placeholderDuTitre === undefined ? {} : { placeholderTitre: placeholderDuTitre }),
            /* LE BROUILLON VIENT D'UN MODELE : ses fiches liees, pour l'APERCU
               des cartes liees et les champs de total. JAMAIS dans
               `ViewProps.modele` : celui-la eteint les fleches des references et
               donne a la carte les gestes d'un enfant de modele (l'ouvrir, en
               ajouter un), c'est-a-dire modifier le MODELE depuis une fiche qui
               n'en est pas un. ABSENT QUAND LE MODELE N'EMPORTE RIEN : la fiche
               est alors exactement celle de « + Ajouter », cartes et totaux
               compris, sans un tiret de total a la place de « 0 ». */
            ...(brouillon.depuisModele === undefined ||
            (brouillon.depuisModele.children ?? []).length === 0
              ? {}
              : {
                  modele: {
                    id: brouillon.depuisModele.id,
                    name: brouillon.depuisModele.name,
                    enfants: brouillon.depuisModele.children ?? [],
                  } satisfies ModeleOuvert,
                }),
          },
        }),
    /* LES CASES FERMEES PAR LE GESTE, au PREMIER niveau depuis le 10/09/2026 :
       un brouillon n'est plus la seule fiche a en avoir (voir
       `casesVerrouillees`). Absentes quand il n'y en a aucune, et absentes hors
       niveau fiche : une vue de collection n'a pas de case a fermer. */
    ...(level === 'record' && casesVerrouillees.length > 0
      ? { verrouillees: casesVerrouillees }
      : {}),
    /*
     * LA FICHE OUVERTE EST UN MODELE, et c'est la seule chose qui l'apprend a
     * ses cartes liees. NIVEAU FICHE UNIQUEMENT : une carte liee ne vit que sur
     * une fiche, et une vue de collection qui la recevrait montrerait les
     * enfants d'un modele a la place des fiches d'une porte.
     *
     * CE QU'ELLE CHANGE POUR LA CARTE : elle ne demande plus RIEN au port des
     * liens (un agregat cadre sur l'id d'un modele rendrait toujours zero,
     * puisqu'aucune fiche ne porte cet id), et elle projette `children` a la
     * place de la fenetre du serveur. C'est TOUT ce qu'elle change depuis le
     * pivot du 10/09/2026 : la carte LIT, elle n'ecrit plus. Ouvrir une ligne,
     * en ajouter une, en retirer une sont des gestes de FICHE, et ils passent
     * par les chemins de fiche (`onOpenLinked`, l'intention de creation, le menu
     * « ... » de l'enfant ouvert).
     *
     * ABSENTE HORS DU MODE, et jamais posee avec `brouillon` : `openId` y vaut
     * `~nouveau`, qui n'est l'id d'aucun modele.
     */
    ...(level === 'record' && modeleOuvert != null
      ? {
          modele: {
            id: modeleOuvert.id,
            /* LE NOM DU MODELE, pour la case VERROUILLEE du brouillon d'enfant :
               « Materiel : Camera de tournage » plutot qu'un id nu. */
            name: modeleOuvert.name,
            /* `children` ABSENT EST LE CAS ORDINAIRE (un modele d'avant ce
               chantier, un modele enregistre depuis un brouillon) : la carte lit
               un tableau, jamais une absence. */
            enfants: modeleOuvert.children ?? [],
          } satisfies ModeleOuvert,
        }
      : {}),
    ...(level === 'record'
      ? {
          archived: openArchived,
          ...(openArchived && openRecordValue != null && unarchiveRef.current != null
            ? {
                onUnarchive: (): void => {
                  void runRecordAction('unarchive', openRecordValue);
                },
              }
            : {}),
        }
      : {}),
  };

  /*
   * La SEULE porte d'une vue vers la rangee 3a. Les actions sont calculees ici
   * (le core), pas dans la vue, et forcees en secondaire : une vue ne peut pas
   * s'attribuer le bouton lime, qui appartient a l'ecran.
   */
  const viewActions: ScreenAction[] = (activeType?.actions?.(viewProps) ?? []).map((action) => ({
    ...action,
    variant: 'secondary',
  }));

  /*
   * LES GESTES RARES DE LA FICHE (menu "..." de la rangee 3c, chantier 2_0).
   * Calcules ici, par le core, pour la meme raison que `viewActions` : le
   * gabarit rend, il ne decide pas ; et les 55 apps n'ecrivent pas une ligne
   * pour ca.
   *
   * Recomposes a chaque rendu, comme le reste de la sortie : les verbes vivent
   * dans des refs que la porte active reassigne a chaque passage, memoiser
   * figerait le geste de la porte precedente.
   *
   * VIDE quand aucune fiche n'est ouverte (ou quand elle est introuvable) : il
   * n'y a alors rien sur quoi agir. Le menu n'est de toute facon rendu qu'au
   * niveau record.
   */
  /**
   * L'ENTREE « Enregistrer comme modele » DU MENU « ... », FABRIQUEE UNE FOIS
   * POUR DEUX APPELANTS : une fiche ordinaire, et un BROUILLON (la fiche vide
   * de la creation, qu'on enregistre comme modele sans la creer).
   *
   * ELLE PREND CE QUI CHANGE, ET RIEN D'AUTRE : le document a copier et le nom
   * a preremplir. Tout le reste (le port, le jeu de la porte, l'acces, la
   * fenetre a champ, la garde de session) est le meme des deux cotes, et l'ecrire
   * deux fois se serait paye au premier ajustement.
   *
   * DEPUIS LE CHANTIER `modeles-enfants`, LE MODELE EMPORTE AUSSI LES FICHES
   * LIEES DE LA FICHE (`children`), et deux choses s'y jouent :
   *  - LES LIENS ENTRANTS SONT RETIRES, de la racine comme de chaque enfant
   *    (`sansChampsDeLien`, `ScreenDoor.entrants`). Un modele est un PATRON : il
   *    ne retient AUCUN lien vers une fiche precise, sinon toutes les fiches
   *    creees depuis lui naitraient accrochees a l'original ;
   *  - LE BROUILLON N'EMPORTE AUCUNE FICHE LIEE LUE AU SERVEUR, et ce n'est pas
   *    une restriction : rien n'est encore lie a une fiche qui n'existe pas. SAUF
   *    le brouillon ISSU D'UN MODELE (23/09/2026) : il emporte les fiches liees
   *    que ses cartes montrent en apercu, celles du modele d'origine. Ce qu'on
   *    voit est ce qu'on enregistre, et c'est le seul geste du produit qui
   *    DUPLIQUE un modele.
   */
  const entreeDeModele = (record: T, defaut: string): ScreenAction => {
    /*
     * ENREGISTRER COMME MODELE : le troisieme geste rare de la fiche, et le
     * premier qui demande une SAISIE au lieu d'une confirmation.
     *
     * POURQUOI IL VIT ICI, avec Archiver et Supprimer, et pas dans les apps :
     * c'est le meme geste partout, sur toutes les portes qui declarent leur
     * jeu, et les 55 apps n'ecrivent pas une ligne pour l'avoir. Ce qu'il faut
     * pour l'offrir n'est pas un verbe d'app, c'est un SERVICE (le port) et une
     * ADRESSE (le jeu de la porte) : les deux se lisent d'ici.
     *
     * IL NE FAIT PAS LE GESTE, IL LE DECRIT. Le nom du modele n'existe pas
     * encore au moment du clic : l'action porte donc un `prompt`, le chassis
     * ouvre la fenetre a champ et rappelle `prompt.onSubmit` avec ce qui a ete
     * saisi. Meme partage que `confirm` : le moteur decide et ecrit, le gabarit
     * rend et demande.
     *
     * IL FAUT AUSSI QUE LA PORTE LES ALLUME. Le jeu dit ou ranger le modele, il
     * ne dit pas que cette porte en veut : `modeles: true` le dit, et son
     * absence est le cas ORDINAIRE, pas une panne.
     *
     * ABSENT = GRISE AVEC SA RAISON, jamais cache (G16, G18). Le calcul et les
     * phrases vivent dans `lib/templates.tsx` (`raisonDesModeles`), parce que le
     * chassis pose la MEME question pour la fleche de « Ajouter ».
     *
     * ET C'EST UNE ECRITURE : enregistrer un modele cree un document. L'acces
     * passe donc DEVANT ce qui manque aux modeles, comme partout ailleurs.
     */
    const jeuDeLaPorte = activeDoor?.jeu ?? null;
    const modeleRaison = raisonDeGeste(
      raisonAcces,
      raisonDesModeles({
        port: modelesPort,
        jeu: jeuDeLaPorte,
        modeles: activeDoor?.modeles === true,
      }),
    );
    /* LES LIENS DE LA PORTE ET SES ENTRANTS, CAPTURES AU RENDU, comme le jeu et
       le port juste au-dessus : l'entree de menu decrit le geste de la porte
       AFFICHEE, et c'est elle qu'on enregistre. */
    const liensDeLaPorte = activeDoor?.links ?? [];
    const entrantsDeLaPorte = activeDoor?.entrants ?? [];
    /* UN BROUILLON NE LIT AUCUN ENFANT AU SERVEUR : rien n'est encore lie a une
       fiche qui n'existe pas. C'est le seul point ou les deux appelants
       divergent. Le brouillon ISSU D'UN MODELE emporte, lui, les fiches liees
       de son apercu : celles du modele d'origine, telles quelles. */
    const surBrouillon = brouillon != null;
    const enfantsDApercu = brouillon?.depuisModele?.children ?? [];
    return {
      id: 'record.saveAsTemplate',
      label: 'Enregistrer comme modèle',
      variant: 'secondary',
      disabled: modeleRaison != null,
      ...(modeleRaison != null ? { disabledReason: modeleRaison } : {}),
      /*
       * SANS FENETRE, PAS DE NOM : ce chemin n'ecrit RIEN. Il n'existe que pour
       * une surface qui rendrait une `ScreenAction` en ignorant son `prompt` ;
       * elle obtient alors un bouton inerte, jamais un modele sans nom. C'est
       * la meme prudence que `confirm`, dans l'autre sens : mieux vaut ne rien
       * faire que faire sans ce que la fenetre devait apporter.
       */
      onClick: (): void => {},
      /* PAS DE FENETRE QUAND LE GESTE EST GRISE : `modeleRaison` tranche seul.
         Les deux tests qui suivent sont redondants a l'execution et NECESSAIRES
         au compilateur : c'est eux qui rendent `modelesPort` et `jeuDeLaPorte`
         non nuls dans la closure, sans un `!` qui ne prouverait rien. */
      ...(modeleRaison != null || modelesPort == null || jeuDeLaPorte == null
        ? {}
        : {
            prompt: {
              title: 'Enregistrer comme modèle',
              fieldLabel: 'Nom du modèle',
              /* Preremplir avec le titre de la fiche : c'est le nom que
                 l'utilisateur a sous les yeux, et le modele en est une copie.
                 Vide quand le coeur ne connait pas la ligne (fiche introuvable,
                 vue sans titre) : un champ vide se remplit, un mauvais defaut
                 se corrige, et le second coute plus cher. */
              defaultValue: defaut,
              confirmLabel: 'Enregistrer',
              errorText: "Le modèle n'a pas pu être enregistré. Réessaie.",
              onSubmit: async (nom: string): Promise<void> => {
                /* MEME GARDE DE SESSION QUE `runRecordAction` : ce qui atterrit
                   apres la fermeture de la fiche ne touche plus l'indicateur de
                   la fiche suivante. L'ECRITURE, elle, vaut toujours. */
                const session = recordSessionRef.current;
                setSaveState({ mode: 'saving' });
                try {
                  /* LES LIENS ENTRANTS QUITTENT LA FICHE RACINE ICI, avant toute
                     lecture : un modele est un PATRON, il ne retient aucun lien
                     vers une fiche precise. Ce tri-la appartient au SOCLE, parce
                     que les liens entrants sont une notion d'ECRAN (l'assemblage
                     les deduit) que le SDK ne connait pas ; lui retire les
                     champs standard et le champ du lien, que le socle ne
                     connait pas. Deux tris, deux savoirs. */
                  const fiche = sansChampsDeLien(
                    /* LE DOUBLE `as` EST LA PARCE QUE `T` N'EST PAS CONTRAINT :
                       le moteur ne lit pas ce document, il le TRANSPORTE. */
                    record as unknown as Record<string, unknown>,
                    entrantsDeLaPorte,
                  );
                  /*
                   * LES FICHES LIEES, LUES AVANT D'ENREGISTRER, et seulement
                   * quand il y a quelque chose a lire.
                   *
                   * `undefined` VEUT DIRE « AUCUNE LECTURE N'A EU LIEU », pas
                   * « aucune fiche liee » (contrat de `saveFromRecord`) : une
                   * porte sans lien, un brouillon, un hote sans port des liens.
                   * Le modele s'enregistre quand meme, il n'emporte simplement
                   * rien : le geste marchait avant ce chantier et doit continuer
                   * sans qu'une app ait une ligne a declarer.
                   *
                   * UN LIEN QUI ECHOUE FAIT ECHOUER L'ENSEMBLE, et c'est
                   * delibere (voir `enfantsAEnregistrer`) : un modele enregistre
                   * A MOITIE a l'air complet et recreera des fiches auxquelles
                   * il manque une porte entiere.
                   */
                  let children: EnfantAEnregistrer[] | undefined;
                  if (surBrouillon) {
                    /* L'APERCU D'UN BROUILLON ISSU D'UN MODELE : chaque enfant
                       part comme une fiche, son sous-type dans `record` (le SDK
                       l'y relit) et SANS son id, qui nomme l'enfant dans l'AUTRE
                       modele : le SDK en pose un neuf (`avecIdsDEnfants`). */
                    if (enfantsDApercu.length > 0) {
                      children = enfantsDApercu.map((enfant) => ({
                        family: enfant.family,
                        type: enfant.type,
                        linkKey: enfant.linkKey,
                        record: { ...enfant.fields, type: enfant.type },
                      }));
                    }
                  } else if (liensDeLaPorte.length > 0) {
                    const portDesLiens = liensPortRef.current;
                    if (portDesLiens == null) {
                      /* SANS PORT DES LIENS, ON N'INVENTE RIEN, et on le DIT au
                         developpeur : a l'ecran, un modele sans enfant est
                         indiscernable d'un modele dont la fiche n'avait aucune
                         fiche liee. */
                      if (import.meta.env?.DEV === true) {
                        console.warn(
                          `[taki/views] Modele enregistre SANS ses fiches liees : ` +
                            `l'hôte ne fournit pas de port des liens (LiensProvider).`,
                        );
                      }
                    } else {
                      children = await enfantsAEnregistrer(
                        portDesLiens,
                        liensDeLaPorte,
                        getIdRef.current(record),
                        /* LES ENTRANTS DE LA PORTE LIEE, pas ceux de la porte
                           hote : ce sont les champs de l'ENFANT qui referencent
                           ailleurs. Une porte inconnue ne retire rien. */
                        (door) => doorsRef.current.find((porte) => porte.id === door)?.entrants ?? [],
                      );
                    }
                  }
                  await modelesPort.saveFromRecord({
                    family: jeuDeLaPorte.family,
                    type: jeuDeLaPorte.type ?? '',
                    /* LA FICHE PART TELLE QUELLE POUR LE RESTE, champs standard
                       compris : c'est le SDK qui sait lesquels ne se recopient
                       pas. */
                    record: fiche,
                    name: nom,
                    ...(children === undefined ? {} : { children }),
                  });
                } catch (cause) {
                  console.error(
                    `[taki/views] L'enregistrement du modele a echoue sur ${getIdRef.current(record)} :`,
                    cause,
                  );
                  /* L'indicateur REDEVIENT MUET : il n'a rien a dire, c'est la
                     fenetre restee ouverte qui porte l'echec, avec le nom deja
                     tape. Un indicateur en "Enregistrement..." perpetuel ferait
                     croire qu'une ecriture est encore en vol. */
                  if (recordSessionRef.current === session) setSaveState({ mode: 'idle' });
                  throw cause;
                }
                /* LE MODELE EXISTE, quelle que soit la fiche affichee a
                   l'arrivee de la reponse : le compteur monte AVANT la garde de
                   session, sinon la fleche de « Ajouter » ne le verrait qu'au
                   prochain rechargement. */
                setModelesRevision((avant) => avant + 1);
                if (recordSessionRef.current !== session) return;
                /* LA CONFIRMATION EST CELLE DES AUTRES ECRITURES : "Enregistre
                   a 14:32" dans la rangee 3 de la fiche, qui reste ouverte. Pas
                   de fenetre de plus a refermer pour un geste reussi.
                   L'ECHEC, LUI, NE PASSE PAS PAR ICI : on laisse l'exception
                   remonter au chassis, qui garde la fenetre ouverte avec le nom
                   deja tape et affiche `errorText`. Un `saveState: error`
                   allumerait un "Reessayer" que `retrySave` ne sait pas rejouer,
                   c'est-a-dire un bouton qui ment. */
                setSaveState({ mode: 'saved', at: new Date() });
              },
            },
          }),
    };
  };

  /*
   * « CREER UN PLAN RECURRENT » (chantier `plan-sur-modele`), juste apres
   * « Enregistrer comme modele » et sur le meme patron : un PORT (l'hote), une
   * ADRESSE (le jeu de la porte), un ACCORD (`recurrences: true`), et l'acces
   * devant les trois.
   *
   * UN SEUL GESTE, DEUX POINTS DE DEPART, parce qu'un plan ne porte que son
   * RYTHME et cite un MODELE (`templateId`), qui porte le contenu :
   *  - sur une VRAIE fiche, le SDK tire d'abord un modele de la fiche (meme nom
   *    que le plan), puis le plan qui le cite (`port.fromRecord`) ;
   *  - sur un MODELE ouvert en mode Modeles, le plan seul, qui cite ce modele
   *    (`port.fromTemplate`).
   * Ni sur un brouillon (rien a repeter), ni sur un enfant de modele (ce n'est
   * pas le modele d'une fiche racine) : les appels vivent dans les deux seules
   * branches « fiche reelle » et « modele ouvert », plus bas.
   *
   * UNE FENETRE A CHAMP (le nom du plan), puis le plan nait SUSPENDU, avec des
   * defauts que la personne relit (le SDK en decide, `planDepuisFiche`). Il
   * s'ouvre ensuite dans la porte de l'ecran qui montre les plans, TOUJOURS
   * HORS du mode Modeles : un plan est une fiche ordinaire de sa porte, meme
   * quand on l'a cree depuis un modele. Sans porte des plans, on reste la ou
   * l'on etait, « Enregistré ».
   *
   * LES LIENS ENTRANTS RESTENT dans le modele tire d'une fiche, et c'est la
   * difference avec « Enregistrer comme modele » : un plan refait LA MEME
   * fiche, pour le meme client ou le meme materiel. Sans `contactId` ou
   * `assetId`, chaque occurrence naitrait orpheline. Le SDK retire seulement
   * les dates absolues et le lien vers un plan (`champsDuPlan`).
   *
   * LE MODELE NEUF COMPTE : il apparait dans la fleche de « Ajouter » et en
   * mode Modeles, et celui qui gagne un plan change de verrou (« Retirer »
   * grise). Le compteur des modeles monte donc dans les deux cas.
   */
  const recurrencesPort = useRecurrencesPort();
  const entreeDePlan = (
    defaut: string,
    creer: (port: RecurrencesPort, jeu: { family: string; type?: string }, nom: string) => Promise<{ id: string }>,
    origine: string,
  ): ScreenAction => {
    const jeuDeLaPorte = activeDoor?.jeu ?? null;
    const raison = raisonDeGeste(
      raisonAcces,
      raisonDesRecurrences({
        port: recurrencesPort,
        jeu: jeuDeLaPorte,
        recurrences: activeDoor?.recurrences === true,
      }),
    );
    const port = raison == null ? recurrencesPort : null;
    return {
      id: 'record.creerPlanRecurrent',
      label: 'Créer un plan récurrent',
      variant: 'secondary',
      disabled: raison != null,
      ...(raison != null ? { disabledReason: raison } : {}),
      onClick: (): void => {},
      ...(port == null || jeuDeLaPorte == null
        ? {}
        : {
            prompt: {
              title: 'Créer un plan récurrent',
              fieldLabel: 'Nom du plan',
              defaultValue: port.nomPropose?.(defaut) ?? defaut,
              confirmLabel: 'Créer le plan',
              errorText: "Le plan n'a pas pu être créé. Réessaie.",
              onSubmit: async (nom: string): Promise<void> => {
                const session = recordSessionRef.current;
                setSaveState({ mode: 'saving' });
                let id: string;
                try {
                  ({ id } = await creer(port, jeuDeLaPorte, nom));
                } catch (cause) {
                  console.error(`[taki/views] Le plan n'a pas pu etre cree depuis ${origine} :`, cause);
                  if (recordSessionRef.current === session) setSaveState({ mode: 'idle' });
                  throw cause;
                }
                setModelesRevision((avant) => avant + 1);
                /* LE NOM DU MODELE NEUF : la colonne Modele du plan le lit dans
                   l'annuaire des modeles, charge AVANT sa naissance. Sans cette
                   invalidation, le plan s'ouvrait sur l'id de son modele jusqu'au
                   prochain rechargement de la page. */
                invaliderAnnuaires();
                if (recordSessionRef.current !== session) return;
                /* LE PLAN S'OUVRE LA OU L'ECRAN MONTRE LES PLANS DE CE JEU, hors
                   du mode Modeles, sinon on reste avec l'accuse d'enregistrement. */
                const porteDesPlans = doorsRef.current.find(
                  (porte) =>
                    porte.jeu?.family === FAMILLE_DES_PLANS &&
                    (porte.jeu.type === undefined || porte.jeu.type === jeuDeLaPorte.family),
                );
                if (porteDesPlans == null || !openLinked(porteDesPlans.id, id, { modeles: false })) {
                  setSaveState({ mode: 'saved', at: new Date() });
                } else {
                  setSaveState({ mode: 'idle' });
                }
              },
            },
          }),
    };
  };
  /** Sur une VRAIE fiche : le modele tire de la fiche, puis le plan. */
  const planDeLaFiche = (record: T, defaut: string): ScreenAction => {
    const fiche = record as unknown as Record<string, unknown>;
    const liensDeLaPorte = activeDoor?.links ?? [];
    /* LES FICHES LIEES, lues comme pour un modele (`enfantsAEnregistrer`), mais
       SANS retirer leurs liens entrants (voir plus haut). `undefined` = aucune
       lecture n'a eu lieu : le modele n'emporte alors aucun enfant. */
    const lireEnfants = async (): Promise<EnfantAEnregistrer[] | undefined> => {
      const portDesLiens = liensPortRef.current;
      if (liensDeLaPorte.length === 0 || portDesLiens == null) return undefined;
      return await enfantsAEnregistrer(portDesLiens, liensDeLaPorte, getIdRef.current(record), () => []);
    };
    return entreeDePlan(
      defaut,
      async (port, jeu, nom) => {
        const children = await lireEnfants();
        return await port.fromRecord({
          family: jeu.family,
          type: jeu.type ?? '',
          record: fiche,
          name: nom,
          ...(children === undefined ? {} : { children }),
        });
      },
      getIdRef.current(record),
    );
  };
  /** Sur un MODELE ouvert en mode Modeles : le plan seul, qui le cite. */
  const planDuModele = (modele: ModeleDeFiche): ScreenAction =>
    entreeDePlan(
      modele.name,
      async (port, _jeu, nom) => await port.fromTemplate({ templateId: modele.id, name: nom }),
      `le modèle ${modele.id}`,
    );

  const recordMenuActions: ScreenAction[] = [];
  if (brouillon != null) {
    /*
     * SUR UN BROUILLON, LE MENU « ... » NE PORTE QU'UNE ENTREE, et les deux
     * autres sont ABSENTES, pas grisees : c'est la meme exception que le mode
     * Modeles, et pour la meme raison. Archiver et Supprimer portent sur une
     * fiche ; ici il n'y a AUCUN objet, rien n'a ete ecrit. Grisees, elles
     * demanderaient une explication qui n'existe pas ; « Annuler », juste a
     * cote dans la rangee 3, est le seul geste qui ait un sens.
     *
     * « ENREGISTRER COMME MODELE » RESTE, ET C'EST VOULU : le contenu saisi
     * suffit a faire un modele, et l'enregistrer ne cree pas la fiche. C'est
     * meme le seul moyen de garder une saisie qu'on ne veut pas creer tout de
     * suite. Le nom prerempli est le titre TAPE, pas celui d'une ligne
     * projetee : la fiche n'existe pas.
     *
     * SAUF QUAND CE BROUILLON EST DEJA UN MODELE OU UN ENFANT (pivot du
     * 10/09/2026) : le menu est alors VIDE. « Enregistrer comme modele » y
     * proposerait d'enregistrer un modele comme modele, ou de faire d'un enfant
     * un modele a part entiere : deux gestes qui ne veulent rien dire, et une
     * entree grisee demanderait une explication qui n'existe pas.
     */
    if (brouillon.enfantDe === undefined && !query.modelesMode) {
      recordMenuActions.push(entreeDeModele(brouillon.fiche, brouillon.titre));
    }
  } else if (cheminOuvert != null) {
    /*
     * LA FICHE D'UN ENFANT DE MODELE : UNE SEULE ENTREE, « Retirer du modele ».
     *
     * LES TROIS AUTRES SONT ABSENTES, PAS GRISEES, exactement comme sur la fiche
     * d'un modele et pour la meme raison : elles n'ont pas d'objet ici.
     * « Archiver » et « Supprimer » portent sur une fiche de la collection, et
     * un enfant n'en est pas une (il vit dans le document du modele) ;
     * « Enregistrer comme modele » ferait un modele a partir d'un morceau de
     * modele.
     *
     * LE RETRAIT REFERME LA FICHE ET RAMENE AU PARENT, par la MEME sortie que la
     * puce : l'enfant vient de quitter le modele, le garder ouvert laisserait
     * une fiche qui ne designe plus rien et un curseur qui compte dans un jeu
     * auquel elle n'appartient plus.
     */
    const parentDeLEnfant = modeleParent?.id === cheminOuvert.modeleId ? modeleParent : null;
    /* RETIRER EST UNE ECRITURE : l'acces passe devant, comme partout. */
    const retraitRaison = raisonDeGeste(
      raisonAcces,
      modelesPort == null
        ? MODELE_WRITE_REASON.sansPort
        : parentDeLEnfant == null
          ? MODELE_WRITE_REASON.disparu
          : null,
    );
    recordMenuActions.push({
      id: 'enfant.retirer',
      label: 'Retirer du modèle',
      variant: 'secondary',
      disabled: retraitRaison != null,
      ...(retraitRaison != null ? { disabledReason: retraitRaison } : {}),
      /* LA MEME FABRIQUE QUE PARTOUT, avec le nombre : un geste a l'unite porte
         quand meme son compte (G22), et le produit n'a qu'une formulation pour
         ce retrait. */
      confirm: enfantsRetraitConfirm(1),
      onClick: (): void => {
        const cible = cheminOuvert;
        void retirerEnfants(cible.modeleId, [cible.enfantId])
          .then(() => {
            /* LA MEME SORTIE QUE LA PUCE, PAR LA MEME REGLE PURE
               (`sortieDeFiche`) : on ne se contente pas de fermer la fiche
               (`openRecord(null)` retomberait dans la LISTE de la porte liee, ou
               l'enfant n'a jamais ete). Sans origine (un lien colle, un nouvel
               onglet : aucune etape ne la porte, et le modele parent n'est pas
               relu), la fermeture ordinaire reste le seul repli honnete. */
            const retour = sortieDeFiche(origineEffectiveRef.current, {
              niveau: 'record',
              porte: doorIdRef.current,
              id: openIdRef.current,
              rang: urlPort?.rang ?? null,
            });
            if (retour == null) openRecord(null);
            else retournerALOrigine(retour.geste);
          })
          .catch((cause: unknown) => {
            /* LA PASTILLE DE LA RANGEE 3 A DEJA DIT L'ECHEC (`majEnfantsDuModele`
               remet `saveState` a muet) : ici on ne fait que garder la cause
               pour qui code, et la fiche reste ouverte. */
            console.error('[taki/views] Le retrait de la fiche liee a echoue :', cause);
          });
      },
    });
  } else if (openRecordValue != null && query.modelesMode) {
    /*
     * EN MODE MODELES, LE MENU « ... » NE PORTE QUE DEUX ENTREES, « Retirer le
     * modele » et « Créer un plan récurrent » (le plan seul, qui cite ce
     * modele : voir `planDuModele`), ET LES TROIS AUTRES SONT ABSENTES, PAS
     * GRISEES. C'est la seule exception a "on grise
     * avec sa raison, on ne cache pas" (G16) de ce fichier, et elle tient a ce
     * que les trois ne veulent RIEN DIRE ici :
     *  - « Archiver » et « Supprimer » portent sur une fiche de la collection.
     *    Un modele n'en est pas une : on le RETIRE, et ce verbe-la est juste en
     *    dessous. Deux entrees pour un meme geste feraient chercher la
     *    difference entre elles ;
     *  - « Enregistrer comme modele » proposerait d'enregistrer un modele comme
     *    modele. Grisee, l'entree demanderait une explication qui n'existe pas.
     * On ne cache pas un geste que la porte pourrait offrir ailleurs : on cache
     * trois gestes qui n'ont pas d'objet sur ce qu'on regarde.
     *
     * PAS DE « RENOMMER » NON PLUS, et c'est un choix : le titre de la fiche
     * d'un modele EST son nom, et il s'edite en place comme n'importe quelle
     * autre case (voir `ecrireSurModele`). Une entree de menu pour ca ferait un
     * second chemin vers la meme ecriture.
     */
    /* LE MEME `modeleOuvert` QUE CELUI SERVI A LA FICHE (calcule plus haut) :
       le menu et la surface doivent parler du meme document, sinon « Retirer le
       modele » se griserait sur un modele dont la fiche montre les enfants. */
    const nomDuModele = modeleOuvert?.name ?? '';
    /* LE VERROU DES PLANS PASSE EN TETE (chantier `plan-sur-modele`) : un
       modele qu'un plan recurrent cite ne se retire pas, le SDK et les rules le
       refusent, et l'entree le dit avant le clic, avec le nombre de plans. C'est
       un fait du modele, vrai pour tout lecteur. Puis l'acces (retirer est une
       ecriture), puis ce qui manque a l'ecran. */
    const retraitRaison =
      (modeleOuvert == null ? null : raisonDeRetraitDuModele(modeleOuvert)) ??
      raisonDeGeste(
        raisonAcces,
        modelesPort == null
          ? MODELE_WRITE_REASON.sansPort
          : modeleOuvert == null
            ? MODELE_WRITE_REASON.disparu
            : null,
      );
    recordMenuActions.push({
      id: 'modele.retirer',
      label: 'Retirer le modèle',
      variant: 'secondary',
      disabled: retraitRaison != null,
      ...(retraitRaison != null ? { disabledReason: retraitRaison } : {}),
      /* LA MEME FENETRE QUE CELLE DU MENU DE LA FLECHE DE « Ajouter », par la
         MEME fabrique : le retrait n'a qu'une formulation dans le produit. */
      confirm: templateRemoveConfirm(nomDuModele),
      onClick: (): void => {
        const port = modelesPortRef.current;
        const id = openId;
        if (port == null || id == null) return;
        const session = recordSessionRef.current;
        setSaveState({ mode: 'saving' });
        void (async (): Promise<void> => {
          try {
            await port.archive(id);
          } catch (cause) {
            console.error(`[taki/views] Le retrait du modele ${id} a echoue :`, cause);
            /* PAS DE « REESSAYER » : `retrySave` ne sait rejouer que les
               ecritures de cellule et les trois gestes de fiche. Un bouton qui
               ne peut pas tenir sa promesse est pire qu'une explication, et la
               trace, elle, part en console. */
            if (recordSessionRef.current === session) setSaveState({ mode: 'idle' });
            return;
          }
          /* LA LISTE SUIT TOUT DE SUITE, avant meme la relecture : la ligne
             disparait de l'ecran au moment ou la fiche se referme, et pas une
             image plus tard. Le compteur, lui, fait relire le port ici comme
             dans le menu de la fleche de « Ajouter ». */
          majModeles((avant) => avant.filter((candidat) => candidat.id !== id));
          setModelesRevision((avant) => avant + 1);
          /* LA FICHE SE REFERME, comme apres un archivage : elle vient de
             quitter le jeu, la garder ouverte laisserait un curseur qui compte
             dans une liste a laquelle elle n'appartient plus. */
          if (recordSessionRef.current === session) openRecord(null);
        })();
      },
    });
    /* PAS SANS LE MODELE LUI-MEME : tant que la liste n'est pas arrivee (ou
       s'il a disparu), il n'y a aucun id a citer, et « Retirer » dit deja
       pourquoi. */
    if (modeleOuvert != null) recordMenuActions.push(planDuModele(modeleOuvert));
  } else if (openRecordValue != null) {
    const target = openRecordValue;
    /*
     * SUPPRIMER UNE FICHE LIEE : REFUSER, A LA ODOO (fondateur, 09/09/2026).
     *
     * LE COMPTE SE DEMANDE AU CLIC, ET SEULEMENT AU CLIC. Il coute une lecture
     * serveur par lien : le calculer a chaque rendu de la fiche (donc a chaque
     * frappe dans une case) ferait payer, en continu, un chiffre qui ne sert
     * qu'au moment ou l'on ouvre ce menu. D'ou la forme PARESSEUSE du `confirm`
     * (`ScreenAction.confirm`), que le chassis attend avant d'ouvrir.
     *
     * SANS LIEN OU SANS PORT, RIEN NE CHANGE : les deux gestes gardent leur
     * confirmation d'aujourd'hui, statique et immediate. C'est le cas de toutes
     * les portes qui ne declarent aucun lien, donc de presque tous les ecrans.
     */
    const compteLesLiees = liensPort != null && (activeDoor?.links ?? []).length > 0;
    const idDeLaFiche = getIdRef.current(target);
    /**
     * LE REFUS POSE SA RAISON DANS LA PASTILLE QUI EXISTE DEJA, jamais dans une
     * troisieme fenetre : l'annonce d'un geste est la fenetre, le refus d'un
     * geste est la pastille, et le produit n'a pas besoin d'une forme de plus.
     *
     * MEME GARDE DE SESSION QUE `runRecordAction` : le compte est parti sur la
     * fiche A, il peut revenir alors qu'on lit la fiche B. Afficher la un refus
     * qui parle d'une autre fiche serait pire que de ne rien dire.
     *
     * `failedRecordActionRef` REMIS A `null` : sans ca, un echec anterieur
     * laisserait un « Reessayer » a cote d'un refus, et ce bouton rejouerait un
     * geste sans rapport avec la phrase affichee.
     */
    const poserLeRefus = (session: number, raison: string): void => {
      if (recordSessionRef.current !== session) return;
      failedRecordActionRef.current = null;
      setSaveState({ mode: 'error', reason: raison });
    };
    /*
     * UNE SEULE ENTREE, DEUX SENS : "Archiver" devient "Desarchiver" quand la
     * fiche est archivee, jamais deux entrees dont une grisee. L'id, lui, ne
     * change PAS avec le sens : c'est le meme element de menu qui bascule, et
     * un id qui change le ferait remonter a chaque aller-retour.
     *
     * Desarchiver ne demande AUCUNE confirmation : le geste est reversible et
     * remet la fiche la ou on la cherchait. Archiver en demande une, parce que
     * la fiche quitte les listes et que rien a l'ecran ne dirait ou elle part.
     */
    const toggleKind: RecordActionKind = openArchived ? 'unarchive' : 'archive';
    const toggleVerb = openArchived ? unarchiveRef.current : archiveRef.current;
    /* ARCHIVER EST UNE ECRITURE : l'acces l'eteint avant le verbe manquant, et
       avec SA phrase (`raisonDeGeste`, plus haut dans ce fichier). */
    const toggleRaison = raisonDeGeste(
      raisonAcces,
      toggleVerb == null ? RECORD_ACTION_REASON[toggleKind] : null,
    );
    recordMenuActions.push({
      id: 'record.archive',
      label: openArchived ? 'Désarchiver' : 'Archiver',
      variant: 'secondary',
      disabled: toggleRaison != null,
      ...(toggleRaison != null ? { disabledReason: toggleRaison } : {}),
      /*
       * UNE fiche : l'accord de la fenetre suit ce nombre, jusque dans le corps
       * du texte (`archiveConfirm`, ConfirmDialog.tsx).
       *
       * ARCHIVER N'EST JAMAIS REFUSE, meme avec des fiches liees : la fiche
       * quitte les listes, elle n'emporte rien et ne casse aucun lien. La
       * fenetre le DIT (« 3 fiches liees (Interventions) restent visibles. »),
       * parce que c'est la seule question qu'on se pose devant ce bouton quand
       * une carte liee est ouverte juste dessous. Titre et bouton inchanges :
       * c'est le meme geste.
       *
       * UN COMPTE QUI ECHOUE N'EMPECHE PAS D'ARCHIVER : on ouvre la fenetre
       * ordinaire, sans la phrase. Refuser ici traiterait une lecture ratee
       * comme un danger, alors que le geste est reversible.
       */
      ...(openArchived
        ? {}
        : {
            confirm: compteLesLiees
              ? async (): Promise<ScreenConfirm> => {
                  try {
                    return archiveConfirm(1, phraseDesLies(await compterLesLiees(idDeLaFiche)));
                  } catch (cause) {
                    console.error(
                      `[taki/views] Comptage des fiches liees de ${idDeLaFiche} echoue :`,
                      cause,
                    );
                    return archiveConfirm(1);
                  }
                }
              : archiveConfirm(1),
          }),
      onClick: (): void => {
        void runRecordAction(toggleKind, target);
      },
    });
    /*
     * SUPPRIMER reste SECONDAIRE dans le menu : le rouge ne vit que dans la
     * fenetre, et il y est le DERNIER geste d'un chemin. Un aplat rouge
     * propose en premier attire le clic qu'il devrait retenir.
     *
     * La fenetre ne mentionne PAS que la ligne survit en base : c'est un
     * garde-fou d'exploitation pour le support Taki, pas une corbeille
     * utilisateur. Pour l'utilisateur, supprime veut dire supprime.
     */
    const removeVerb = removeRef.current;
    const removeRaison = raisonDeGeste(
      raisonAcces,
      removeVerb == null ? RECORD_ACTION_REASON.remove : null,
    );
    recordMenuActions.push({
      id: 'record.remove',
      label: 'Supprimer',
      variant: 'secondary',
      disabled: removeRaison != null,
      ...(removeRaison != null ? { disabledReason: removeRaison } : {}),
      /*
       * LE REFUS TOMBE AVANT LA FENETRE, ET C'EST TOUT LE POINT. On ne fait pas
       * confirmer un geste irreversible qu'on va refuser (meme regle que le
       * plafond d'un lot, G22) : le compte part au clic, et s'il n'est pas nul,
       * AUCUNE fenetre ne s'ouvre. La pastille rouge de la rangee 3 dit le
       * nombre, les portes concernees et les deux sorties.
       */
      confirm: compteLesLiees
        ? async (): Promise<ScreenConfirm | null> => {
            const session = recordSessionRef.current;
            let comptes: CompteLie[];
            try {
              comptes = await compterLesLiees(idDeLaFiche);
            } catch (cause) {
              console.error(
                `[taki/views] Comptage des fiches liees de ${idDeLaFiche} echoue :`,
                cause,
              );
              poserLeRefus(session, LIEES_ILLISIBLES);
              return null;
            }
            const refus = refusDeSuppression(comptes);
            if (refus == null) return removeConfirm(1);
            poserLeRefus(session, refus);
            return null;
          }
        : removeConfirm(1),
      onClick: (): void => {
        void runRecordAction('remove', target);
      },
    });
    recordMenuActions.push(entreeDeModele(target, openRow?.title ?? ''));
    recordMenuActions.push(planDeLaFiche(target, openRow?.title ?? ''));
  }

  /*
   * LA PORTE ACTIVE, RESOLUE pour l'ecran : ce qu'il passe au gabarit tel
   * quel. Les stats en forme fonction recoivent le jeu VIVANT (archives
   * exclues, avant recherche et filtres) : insensibles au filtrage par
   * construction, doctrine inchangee. Seule exception, le bouton
   * « Nouveautes » : allume, elles comptent les nouveautes (`liveDesStats`).
   * Recomposee a chaque rendu, comme le
   * reste de la sortie : ce sont les champs qui comptent, pas l'identite.
   */
  const doorStats: ScreenStat[] =
    activeDoor?.stats == null
      ? []
      : typeof activeDoor.stats === 'function'
        ? activeDoor.stats(projected.liveDesStats)
        : activeDoor.stats;
  /*
   * LES ACTIONS DE LA PORTE, RESOLUES : une action d'ajout tenue par le SOCLE
   * (`brouillon: true`, posee par `actionsDe` quand la declaration n'a pas de
   * `fn`) recoit ICI son geste, et nulle part ailleurs.
   *
   * POURQUOI ICI. L'assemblage (`views/screens.ts`) ne connait ni l'etat de
   * l'ecran, ni l'acces, ni le port de l'hote : il ne peut poser qu'un `onClick`
   * INERTE. Le moteur, lui, a les trois sous les yeux, donc c'est lui qui ouvre
   * le brouillon et qui GRISE avec la raison (G16) quand la creation ne peut
   * pas aboutir. Une action qui ouvrirait une fiche vide pour se faire refuser
   * a « Creer » ferait saisir une fiche pour rien.
   *
   * LE CONTEXTE PASSE TEL QUEL : le « + Ajouter » d'une carte liee arrive avec
   * ce que le geste impose (le champ du lien), et c'est le brouillon qui le
   * range. L'intention d'une carte liee lit ces actions-la, RESOLUES
   * (`actionsDeLaPorteRef` plus bas) : sans ca, elle appellerait l'inerte.
   */
  const actionsDeLaPorte: ScreenAction[] = (activeDoor?.actions ?? []).map((action) => {
    if (action.brouillon !== true) return action;
    /* EN MODE MODELES, CE N'EST PAS LE MEME MANQUE QUI ETEINT LE BOUTON. La
       fiche vide y cree un MODELE (pivot du 10/09/2026), donc ce qu'il faut
       n'est pas le port de CREATION mais celui des MODELES, plus le jeu et
       l'accord de la porte : les trois phrases de `raisonDesModeles`, jamais
       « L'hote ne fournit pas la creation de fiches. », qui enverrait monter le
       mauvais fournisseur. */
    const raison = query.modelesMode
      ? raisonDeGeste(
          raisonAcces,
          raisonDesModeles({
            port: modelesPort,
            jeu: activeDoor?.jeu ?? null,
            modeles: activeDoor?.modeles === true,
          }),
        )
      : raisonDeGeste(
          raisonDeCreation,
          activeDoor?.jeu == null ? RAISONS_CREATION.sansJeu : null,
        );
    return {
      ...action,
      onClick: (contexte?: {
        defaults?: ValeurImposee[];
        enfantDe?: { modele: string; lien: LienParLeChampLie; libelle: string };
        retour?: RetourDeCreation;
      }): void => {
        ouvrirBrouillon(contexte);
      },
      disabled: raison != null,
      ...(raison != null ? { disabledReason: raison } : {}),
    };
  });
  const door: ScreenDoorState = {
    id: doorId,
    canvas: activeDoor?.canvas != null ? { level: 'space', ...activeDoor.canvas } : null,
    // La porte implicite n'a pas de libelle : le titre appartient a l'ecran.
    label: activeDoor?.label ?? '',
    searchPlaceholder: activeDoor?.searchPlaceholder ?? DEFAULT_SEARCH_PLACEHOLDER,
    sections: doorSections,
    actions: actionsDeLaPorte,
    stats: doorStats,
    jeu: activeDoor?.jeu ?? null,
    /* `=== true` et pas `?? false` : la porte implicite (pas de portes du tout)
       n'allume rien non plus, et une valeur qui n'est pas `true` n'est pas un
       oui a moitie. */
    modeles: activeDoor?.modeles === true,
    /* `{ defauts: [] }` ET PAS `null` : le moteur lit `door.creation.defauts`
       sans garde, et une porte qui ne declare rien ouvre une fiche vide. */
    creation: activeDoor?.creation ?? { defauts: [] },
  };

  /*
   * LA PUCE DE SORTIE D'UNE FICHE ARRIVEE PAR UN LIEN, calculee A CHAQUE RENDU
   * comme `activeDoor` et `door` : elle depend de l'origine de l'etape (lue au
   * port d'adresse) et de deux etats (la fiche ouverte, le niveau). Memoiser
   * figerait la reponse du rendu precedent, exactement le piege deja paye sur
   * `activeDoor`.
   *
   * `undefined` QUAND IL N'Y A PAS DE SAUT : le gabarit retombe alors sur la
   * sortie ordinaire (« Liste », « Tableau »), et c'est ce qui garantit qu'il
   * n'y a JAMAIS deux puces (il n'en rend qu'une, et cette clef ne fait que
   * changer ce qu'elle dit et ce qu'elle fait).
   */
  const decisionDeSortie = sortieDeFiche(origineEffective, {
    niveau: level,
    porte: doorId,
    id: openId,
    rang: urlPort?.rang ?? null,
  });
  /* ---- Le CHEMIN : la porte ouverte dans une fiche (`porteDans.ts`) ---- */

  const dansResolu = hasDoors
    ? resoudreDans(doorsRef.current, query.dans, { section: query.section, openId })
    : null;
  /** La vue ou l'on retombe en ouvrant une porte : la meme regle qu'`openDoor`. */
  const arriveeDe = (porte: ScreenDoor<T>, imposee?: ViewId): ViewType | undefined => {
    const types = typesDePorte(porte, options.types);
    const bindings = porte.bindings ?? options.bindings;
    const essai = (valeur: unknown): ViewId | undefined => {
      const id = asViewId(valeur);
      return id != null && vueDeCollection(types, bindings, id) === id ? id : undefined;
    };
    const id = vueDeCollection(
      types,
      bindings,
      essai(imposee) ??
        essai(readPref<string | null>(pinnedViewPrefKey(appId, screenId, porte.id), null)) ??
        essai(readPref<string | null>(lastViewPrefKey(appId, screenId, porte.id), null)),
      porte.defaultView,
    );
    return types.find((type) => type.id === id);
  };
  const cheminDuRendu = ((): { chemin: CheminDePorte; avant: { libelle: string; retour(): void } | null } | null => {
    if (dansResolu == null) return null;
    const { dans, parente, enfant, place } = dansResolu;
    const vueDuRetour = parente.auClic?.link.id === dans.lien ? parente.auClic.view : undefined;
    const vueDebut = arriveeDe(parente)?.label ?? '';
    const vueFicheParente = typesDePorte(parente, options.types).find((type) => type.level === 'record');
    const debut = {
      libelle: libellesDuChemin.debut(vueDebut, parente.label),
      court: parente.label,
      aller: (): void => {
        demanderSortie(() => {
          openDoor(parente.id, [], false, false);
        });
      },
    };
    const avant =
      level !== 'record'
        ? null
        : place === 'enfant'
          ? {
              libelle: libellesDuChemin.etape(
                activeTypes.find((type) => type.id === lastCollectionViewId)?.label ?? '',
                enfant.label,
                titreDuParent,
              ),
              retour: (): void => {
                demanderSortie(() => {
                  openRecord(null);
                });
              },
            }
          : {
              libelle: libellesDuChemin.etape(
                arriveeDe(enfant, vueDuRetour)?.label ?? '',
                enfant.label,
                titreDuParent,
              ),
              retour: (): void => {
                demanderSortie(() => {
                  openDoor(enfant.id, [], false, false, null, {
                    dans,
                    ...(vueDuRetour === undefined ? {} : { vue: vueDuRetour }),
                  });
                });
              },
            };
    return {
      avant,
      chemin: {
        titre: place === 'enfant' ? libellesDuChemin.contenu(enfant.label, titreDuParent) : null,
        porteDuMenu: parente.id,
        debut,
        etape:
          avant == null
            ? null
            : { libelle: avant.libelle, court: libellesDuChemin.contenu(enfant.label, titreDuParent) },
        parent:
          place !== 'enfant' || level === 'record' || vueFicheParente === undefined || titreDuParent === ''
            ? null
            : {
                titre: titreDuParent,
                ouvrir: (): void => {
                  demanderSortie(() => {
                    openDoor(parente.id, [], false, false, dans.id, { dans });
                    openRecord(dans.id);
                  });
                },
              },
      },
    };
  })();
  const chemin = cheminDuRendu?.chemin ?? null;
  const sortieDuChemin = cheminDuRendu?.avant ?? undefined;

  const sortie =
    decisionDeSortie == null
      ? sortieDuChemin
      : {
          libelle: decisionDeSortie.libelle,
          retour: (): void => {
            /* LA GARDE DU BROUILLON PASSE D'ABORD : revenir au parent depuis
               une fiche vide touchee est une sortie comme une autre. */
            demanderSortie(() => {
              retournerALOrigine(decisionDeSortie.geste);
            });
          },
        };

  /* ---- L'intention adressee a une PORTE (le « + Ajouter » d'une carte liee) ---- */

  /**
   * UNE INTENTION QUI NOMME UNE PORTE EST CONSOMMEE PAR LE GABARIT, ET LA REGLE
   * EST ECRITE UNE FOIS (`destinataireDeLIntention`, `lib/appAction.tsx`).
   *
   * POURQUOI ICI ET PAS DANS L'APP. Le « + Ajouter » d'une carte liee cree une
   * fiche de l'AUTRE porte : la carte ne peut pas ouvrir cette creation la ou
   * elle est (la fenetre appartient a l'ECRAN de l'app, regle G6), donc elle
   * PASSE dans la porte liee et y pose une intention. A l'arrivee, le seul
   * endroit qui connaisse a la fois la porte affichee et son action « add »,
   * c'est ce moteur : l'app, elle, ne sait pas qu'on vient d'ailleurs.
   *
   * POURQUOI UNE INTENTION SANS PORTE NE PASSE PAS PAR ICI. Ce sont les
   * raccourcis du dock : ils visent l'APP et la laissent choisir ce qu'elle
   * ouvre (`usePendingAppAction`). Les deux chemins ne se croisent jamais, et
   * c'est le nom d'une porte qui tranche.
   *
   * UNE FOIS, PUIS CONSOMMEE : sans le `consume`, le rendu suivant rouvrirait la
   * meme fenetre, indefiniment.
   */
  const intentionDePorte = usePendingDoorAction(doorId, appId);
  const intention = intentionDePorte.action;
  const consommerIntention = intentionDePorte.consume;

  /*
   * UN RACCOURCI DU DOCK NOMME SA PORTE, ET C'EST LE MOTEUR QUI Y PASSE
   * (`PendingAppAction.changerDePorte`). Le shell ne connait pas la porte
   * affichee : lui faire reecrire l'adresse refermait le brouillon deja ouvert
   * quand on etait deja dans la bonne porte.
   *
   * L'INTENTION EST PRISE TOUT DE SUITE, ET REPOSEE SEULEMENT SI L'ON PART. Le
   * changement de porte passe par la garde du brouillon, comme un clic de menu :
   * sur « Revenir », rien ne doit rester en attente, sinon le prochain passage
   * dans cette porte y ouvrirait une fiche vide que personne n'a demandee.
   * Reposee sans `changerDePorte`, elle n'est plus qu'une intention de porte
   * ordinaire, que `usePendingDoorAction` consomme a l'arrivee.
   *
   * UNE PORTE QUE L'ECRAN NE DECLARE PAS est consommee sans suite (une
   * declaration d'org illisible, ou l'ecran monte celle du modele).
   */
  const raccourci = usePendingDoorTarget(appId);
  const poserIntention = usePendingAppActionSetter();
  useEffect(() => {
    if (raccourci == null || raccourci.door == null) return;
    const porteVisee = raccourci.door;
    const { changerDePorte: _dejaFait, ...deLaPorte } = raccourci;
    consommerIntention();
    if (!doorsRef.current.some((porte) => porte.id === porteVisee)) return;
    demanderSortie(() => {
      poserIntention(deLaPorte);
      setSection(porteVisee);
    });
  }, [raccourci, consommerIntention, demanderSortie, poserIntention, setSection]);

  /* Les actions de la porte AFFICHEE, lues par l'effet sans entrer dans ses
     dependances : l'app les refabrique a chaque rendu (elles ferment sur son
     etat), et l'effet repartirait sans fin. */
  const actionsDeLaPorteRef = useRef<ScreenAction[]>(door.actions);
  actionsDeLaPorteRef.current = door.actions;

  useEffect(() => {
    if (intention == null) return;
    /*
     * LA PORTE D'ARRIVEE EST EN LECTURE SEULE : ON CONSOMME SANS OUVRIR.
     *
     * L'acces se juge PAR PORTE (`docs/ACCES.md`), et la carte liee vit sur la
     * fiche de la porte PARENTE : on peut donc avoir le droit d'ecrire ici et
     * pas la-bas. Le bouton est deja retire quand l'ecran entier est en lecture
     * (`LinkedCard`, `ViewProps.lectureSeule`), mais l'arrivee est le seul
     * endroit qui connaisse le droit de la porte VISEE. Ouvrir la fenetre
     * quand meme aurait fait saisir une fiche pour rien : le refus serait tombe
     * a l'envoi, en « Echec de l'enregistrement » (G16).
     *
     * ON CONSOMME QUAND MEME : une intention laissee en attente rouvrirait la
     * fenetre au premier passage suivant dans cette porte, sans rapport avec le
     * geste qu'on vient de faire.
     */
    if (raisonAccesRef.current != null) {
      if (import.meta.env.DEV) {
        console.warn(
          `[taki/views] La porte "${doorId}" recoit une intention de creation, mais elle est en ` +
            `lecture seule : ${raisonAccesRef.current}`,
        );
      }
      consommerIntention();
      return;
    }
    /*
     * L'ACTION SE RETROUVE PAR SON ID, avec repli sur la premiere « add ».
     * L'intention porte l'`actionId` que la carte a lu sur la porte liee
     * (`LinkedCardDef.ajout`) : le chercher par le NOM du geste plutot que par
     * son RANG evite d'ouvrir la mauvaise fenetre le jour ou une porte declare
     * deux ajouts. Le repli reste, parce que l'id peut avoir change entre
     * l'assemblage et l'arrivee (mode Modeles, actions refabriquees) : mieux
     * vaut le seul ajout de la porte que rien du tout.
     */
    const actions = actionsDeLaPorteRef.current;
    const ajout =
      actions.find((action) => action.kind === 'add' && action.id === intention.actionId) ??
      actions.find((action) => action.kind === 'add');
    if (ajout == null) {
      /* DECLARATION INCOHERENTE : la carte n'affiche son bouton que si la porte
         liee declare une action « add » (`LinkedCardDef.ajout`), donc arriver
         ici veut dire que les deux declarations ne parlent pas du meme ecran.
         On CONSOMME quand meme : une intention qui reste en attente rouvrirait
         une fenetre au premier passage suivant dans cette porte. */
      if (import.meta.env.DEV) {
        console.warn(
          `[taki/views] La porte "${doorId}" recoit une intention de creation, mais ne declare ` +
            "aucune action `kind: 'add'` : rien n'a été ouvert.",
        );
      }
      consommerIntention();
      return;
    }
    /* L'INTENTION PART ENTIERE : les valeurs imposees, ET ce que « Creer » fera
       au bout (`enfantDe`). Sans le second, le « + Ajouter » d'une carte liee de
       MODELE creerait une vraie fiche accrochee a l'id d'un modele : un rebut
       qu'aucune carte ne montre et que rien ne signale. */
    const contexte =
      intention.defaults === undefined &&
      intention.enfantDe === undefined &&
      intention.retour === undefined
        ? undefined
        : {
            ...(intention.defaults === undefined ? {} : { defaults: intention.defaults }),
            ...(intention.enfantDe === undefined ? {} : { enfantDe: intention.enfantDe }),
            /* LE CHAMP A REMPLIR APRES « Creer » : sans lui, la fiche serait creee
               et le champ d'origine resterait vide, sans un mot. */
            ...(intention.retour === undefined ? {} : { retour: intention.retour }),
          };
    ajout.onClick(contexte);
    consommerIntention();
  }, [intention, consommerIntention, doorId, appId]);

  /* CE QUE L'ECRAN MONTRE, POUR LIA : porte, vue, recherche, filtres, fiche
     ouverte. Publie ici, par le moteur, pour que chaque app le donne sans
     rien brancher (`lib/liaScreenContext.ts`). */
  const liaFilterKey = query.filterIds.join('\u0000');
  const liaOwner = useRef({});
  const liaFilterLabels = filters.map((filter) => `${filter.id}:${filter.label}`).join('\u0000');
  useEffect(() => {
    const state = liaScreenState({
      appId, doorId: activeDoor?.id, doorLabel: activeDoor?.label, view: viewId, search: query.search,
      filters, activeFilterIds: query.filterIds, archives: query.archivesMode === true, recordId: openId,
      // Le titre projete de la fiche ouverte (jamais le brouillon : il n'a pas encore de nom).
      recordTitle: openId !== BROUILLON_ID ? openRow?.title : null,
      family: activeDoor?.jeu?.family ?? null,
    });
    // Un moteur sans porte (sous-vue) ne vole pas le contexte de l'ecran qui l'accueille.
    if (state !== null) publishLiaScreen(state, liaOwner.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, activeDoor?.id, activeDoor?.label, viewId, query.search, liaFilterKey, liaFilterLabels, query.archivesMode, openId, openRow?.title, activeDoor?.jeu?.family]);
  useEffect(() => () => publishLiaScreen(null, liaOwner.current), []);

  return {
    records,
    rows,
    openDeleted: openId != null && soloDeleted === openId,
    orderedIds: projected.orderedIds,
    columns: projected.columns,
    status,

    query,
    setQuery,
    setSearch,
    filters: [...filters, ...fieldConditions.map((condition) => ({
      id: condition.id,
      label: conditionLabel(condition, filterFields.find((field) => field.key === condition.field)!),
    }))],
    filterFields,
    fieldConditions,
    saveFieldCondition,
    fieldSortOptions,
    activeFilterId: query.filterId ?? undefined,
    setFilter,
    activeFilterIds: [...query.filterIds, ...fieldConditions.map((condition) => condition.id)],
    toggleFilter,
    removeFilter,
    clearFilters,
    sortKeys,
    sortStack: query.sortStack,
    toggleSort,
    invertSort,
    removeSort,
    clearSort,
    clearAll,
    // Le MODE ARCHIVES au PREMIER NIVEAU de l'etat, et pas dans un sous-objet :
    // c'est ce qui permet a `tools={screen}` de continuer a marcher sans un
    // seul recablage dans les apps.
    archivesMode: query.archivesMode,
    setArchivesMode: setArchivesModeDemande,
    // Le MODE MODELES a cote de son jumeau, au meme premier niveau et pour la
    // meme raison : `tools={screen}` le porte sans qu'aucune app ne recable
    // quoi que ce soit.
    modelesMode: query.modelesMode,
    setModelesMode: setModelesModeDemande,
    recordMenuActions,
    modelesRevision,
    drawerOpen,
    setDrawerOpen,
    nouveautesSeules,
    setNouveautesSeules,
    nouveautesOffertes,
    favorites,
    activeFavoriteId,
    applyFavorite,
    keepFavorite,
    canKeepFavorite,
    forgetFavorite,
    door,
    section: query.section,
    setSection: setSectionDemande,

    /*
     * LA SELECTION ET LES ACTIONS DE LOT au PREMIER NIVEAU de l'etat, et pas
     * dans un sous-objet : c'est ce qui permet a `tools={screen}` de porter la
     * barre de lot sans un seul recablage dans les apps, exactement comme
     * `archivesMode` et `recordMenuActions` au chantier 2_0.
     *
     * C'est `visibleSelection` qui sort, jamais l'etat brut : l'invariant est
     * tenu a la SORTIE, donc aucune surface ne peut lire un id qu'on ne voit
     * plus, meme le temps d'un rendu.
     */
    selection: visibleSelection,
    setSelection,
    clearSelection: dropSelection,
    selectionLevel,
    bulkEditFields,
    archiveOne,
    unarchiveOne,
    removeOne,
    writeCell,
    bulkAvailability,
    raisonAcces,

    views,
    viewId,
    setView: setViewDemande,
    lastCollectionViewId,
    activeType,
    viewOptions: projected.binding?.options ?? {},
    viewProps,
    viewActions,

    openId,
    /* L'APERCU D'UNE VUE HTML EN BROUILLON (`apercu` de l'adresse), et le
       geste qui le referme (publie, ou abandonne). */
    apercu,
    fermerApercu,
    openRecordValue,
    annuaires,
    /* OUVRIR UNE FICHE D'UNE AUTRE PORTE DE L'ECRAN, depuis une surface qui
       n'est pas une vue (une porte Canvas, chantier `fiche-suivi`) : le meme
       saut qu'une carte liee, puce et Retour compris. */
    openLinked: openLinkedDemande,
    creerDepuis,
    /* LA SORTIE DE FICHE PASSE PAR LA QUESTION DU BROUILLON : c'est la puce de
       la rangee 3 et le clic sur une autre ligne. Sans brouillon, c'est
       exactement `openRecord`, au geste pres. */
    openRecord: openRecordDemande,
    cursor,
    markRead,
    /*
     * LE BROUILLON, AU PREMIER NIVEAU comme les modes : `tools={screen}` le
     * porte sans qu'aucune app ne recable quoi que ce soit. Sa `fiche` ne sort
     * PAS : elle n'interesse que le moteur, et une surface qui l'aurait sous la
     * main finirait par l'ecrire.
     */
    brouillon:
      brouillon == null
        ? null
        : {
            porte: brouillon.porte,
            touche: brouillon.touche,
            enCreation: brouillon.enCreation,
            manquants: brouillon.manquants,
            sortieEnAttente: brouillon.sortieEnAttente,
            titre: brouillon.titre,
            /* CALCULEE AU RENDU, jamais rangee dans l'etat : l'acces et le port
               peuvent changer sous une fiche ouverte, et « Creer » doit dire la
               verite du moment.
               ET ELLE SUIT LA DESTINATION (pivot du 10/09/2026) : un brouillon
               d'ENFANT ou de MODELE ecrit par le port des MODELES, pas par celui
               de la creation. Pour un ENFANT, seul le PORT compte : la porte
               liee n'allume pas les modeles et n'a pas a le faire (elle montre
               une fiche d'enfant, pas une liste de modeles). Une fiche DEPUIS
               un MODELE aussi (23/09/2026) : le port des modeles relit le modele
               pour en recreer les fiches liees, c'est donc lui qu'il faut. */
            raison:
              brouillon.enfantDe !== undefined || brouillon.depuisModele !== undefined
                ? raisonDeGeste(
                    raisonAcces,
                    modelesPort == null ? MODELE_WRITE_REASON.sansPort : null,
                  )
                : query.modelesMode
                  ? raisonDeGeste(
                      raisonAcces,
                      raisonDesModeles({
                        port: modelesPort,
                        jeu: activeDoor?.jeu ?? null,
                        modeles: activeDoor?.modeles === true,
                      }),
                    )
                  : raisonDeCreation,
            /* CE QUE « CREER » FERA AU BOUT, relaye tel quel : un ecran qui lit
               `tools.brouillon` peut ainsi le savoir sans deviner. */
            ...(brouillon.enfantDe === undefined ? {} : { enfantDe: brouillon.enfantDe }),
            ...(brouillon.depuisModele === undefined
              ? {}
              : {
                  depuisModele: {
                    id: brouillon.depuisModele.id,
                    name: brouillon.depuisModele.name,
                  },
                }),
          },
    ouvrirBrouillon,
    creerBrouillon,
    annulerBrouillon,
    confirmerSortieDuBrouillon,
    reprendreBrouillon,
    // La puce de sortie d'une fiche arrivee par un lien, au PREMIER niveau de
    // l'etat comme tout ce que `tools={screen}` doit porter sans recablage.
    ...(sortie === undefined ? {} : { sortie }),
    chemin,

    patchRecord,
    patchColumn,
    valeurEnCours,
    saveState,
    retrySave,
    undo,
    redo,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,

    viewState: viewStates[viewId],
    setViewState,

    optionsOf,
    pagination,
    reload,
    relireLaFiche,
    loadMore,
    setRange,
    scrollRef,

    toSearch,
    fromSearch,
  };
}
